import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { Search, RotateCcw, Eye, Pencil, Trash2, ChevronLeft, ChevronRight, ChevronDown, ChevronUp, Filter, UtensilsCrossed, Clock, Zap, ShoppingBag, ChefHat, Coffee, Sun, Moon, Cookie, Sparkles, Send, MoreVertical, Check, X, Settings, LayoutDashboard, FileText, Download, Copy, MessageCircle } from 'lucide-react';
import {
  mealPlanService,
  MealPlanContext,
  MealPlanMode,
  MealPlanSummary,
  MealPlanVersionSummary,
  MealPlanVersionResponse,
  MealPlanTotalsSummary,
  MealPlanChatStatusResponse,
  ICMRComparisonResponse,
} from '../../services/nutrition/mealPlanService';
import { nutritionDictionaryService } from '../../services/dictionaries/nutritionDictionaryService';
import { CalendarDateRangePicker } from '../../components/CalendarDatePicker';
import PdfViewerModal from '../../components/common/PdfViewerModal';
import AlertModal from '../../components/common/AlertModal';
import { MealPlanOverviewCards } from './MealPlanOverviewCards';
import { sendDietPlanViaWhatsApp } from '../../services/messaging/messagingService';

/**
 * MEAL PLANS TAB — UI perspective for creating and managing meal plans
 * ----------------------------------------------------------------------
 * This page has two main views:
 *
 * 1. LIST VIEW (default when no create form open)
 *    - Table of existing Daily Meal Plans & Recipes for the patient/diet plan.
 *    - Actions: View, Edit, Pause, Archive, Delete.
 *    - "Create New Plan" opens the create flow at Step 1 (Settings).
 *    - "Edit" opens the create flow at Step 1 (Settings) for that plan.
 *
 * 2. CREATE FLOW (wizard with two steps)
 *    Step 1 — SETTINGS
 *    - Plan name, period (start/end dates), duration, mode (Adaptive/Fixed), variant,
 *      cuisine, meals per day, meal slots (type + time), optional recipe repeat settings.
 *    - Buttons: Cancel (back to list), Next (validate → go to Step 2).
 *
 *    Step 2 — DAILY MEAL PLAN & RECIPES DETAILS
 *    - Fixed manual: sub-views — Upload (document upload + progress + chat) | Versions (table) | Version detail (tabs: Nutrient Comparison, Daily Meal Plan).
 *    - Adaptive: draft versions selector, Create New Draft, then review/approve.
 *
 * Edit intent (URL ?editPlan= or initialEditPlanId) opens Settings first.
 */

/** URL-driven view mode when using separate pages for list / create / view / edit */
export type MealPlanViewMode = 'list' | 'new' | 'view' | 'edit';

type Props = {
  patientId: number;
  goalId?: number;
  /** When set, open in edit mode for this plan (e.g. when embedded on Meal Plan Detail page) */
  initialEditPlanId?: string;
  /** When true, only the meal plan chat is shown (e.g. in the Chat tab); no steps or version list */
  showOnlyChat?: boolean;
  /** When provided, view is driven by URL (separate pages: list, new, view, edit) */
  mealPlanViewMode?: MealPlanViewMode;
  /** Plan ID from URL for view or edit page */
  mealPlanIdFromUrl?: string;
  /** When true, do not show the "Back to list" button (e.g. parent page already has it) */
  hideBackToList?: boolean;
  /** When true, do not show the inline version selector below Plan Details (e.g. version is in page header) */
  hideInlineVersionSelector?: boolean;
  /** When on view page, the version selected in the parent (e.g. header dropdown); syncs to viewed version and chat */
  externalViewedVersionId?: string | null;
  /** When opening create page after clicking "+ Create New Plan" (create API was already called); show settings with this plan selected */
  initialCreatedPlanId?: string | null;
};

/** Only 4 views: settings, versions, upload, meal_plan */
type StepId = 'settings' | 'versions' | 'upload' | 'meal_plan';

/** Wizard has 3 steps; step 3 covers both upload and meal_plan views */
const steps: { id: StepId; title: string; subtitle: string }[] = [
  { id: 'settings', title: 'Settings', subtitle: 'Choose generation settings (mode, cuisine, meals, duration).' },
  { id: 'versions', title: 'Meal Plan Versions', subtitle: 'Manage drafts and history.' },
  { id: 'upload', title: 'Review & Modify', subtitle: 'Refine nutrition strategy.' },
];

const defaultMealTiming = {
  breakfast: '08:00',
  lunch: '13:00',
  dinner: '20:00',
  snacks: '15:00',
};

/** localStorage key for persisting only meal plan id and version id (per patient/goal). Each view fetches data from backend using these ids. */
const getMealPlanSelectionStorageKey = (patientId: number, goalId: number | undefined) =>
  `mealPlanSelection_${patientId}_${goalId ?? 'all'}`;

const MEAL_TYPE_OPTIONS = [
  'early_morning', 'pre_breakfast', 'breakfast', 'post_breakfast', 'brunch',
  'pre_lunch', 'lunch', 'post_lunch', 'snack', 'evening_snack', 'tea', 'high_tea',
  'dinner', 'post_dinner', 'supper', 'midnight_snack',
];
const MEAL_TYPE_LABELS: Record<string, string> = {
  early_morning: 'Early Morning',
  pre_breakfast: 'Pre Breakfast',
  breakfast: 'Breakfast',
  post_breakfast: 'Post Breakfast',
  brunch: 'Brunch',
  pre_lunch: 'Pre Lunch',
  lunch: 'Lunch',
  post_lunch: 'Post Lunch',
  snack: 'Snack',
  snacks: 'Snacks',
  evening_snack: 'Evening Snack',
  tea: 'Tea',
  high_tea: 'High Tea',
  dinner: 'Dinner',
  post_dinner: 'Post Dinner',
  supper: 'Supper',
  midnight_snack: 'Midnight Snack',
};
/** Format meal_key from API (e.g. "early morning", "morning drink") for display; uses MEAL_TYPE_LABELS when key matches, else title-case. */
function formatMealKeyForDisplay(mealKey: string): string {
  if (!mealKey || typeof mealKey !== 'string') return 'Meal';
  const normalized = mealKey.trim().toLowerCase().replace(/\s+/g, '_');
  const fromMap = MEAL_TYPE_LABELS[normalized];
  if (fromMap) return fromMap;
  return mealKey.trim().split(/\s+/).map((s) => s.charAt(0).toUpperCase() + s.slice(1).toLowerCase()).join(' ');
}
const MEAL_SLOT_ID_PREFIX = 'meal-slot';

const NUTRIENT_ORDER = [
  'calories', 'protein_g', 'carbs_g', 'fat_g', 'fiber_g', 'sugar_g', 'sodium_mg',
  'vitamin_a_mcg', 'vitamin_c_mg', 'vitamin_d_mcg', 'vitamin_e_mg', 'vitamin_k_mcg',
  'vitamin_b1_mg', 'vitamin_b2_mg', 'vitamin_b3_mg', 'vitamin_b6_mg', 'vitamin_b12_mcg', 'folate_mcg',
  'calcium_mg', 'iron_mg', 'magnesium_mg', 'phosphorus_mg', 'potassium_mg', 'zinc_mg',
  'copper_mg', 'manganese_mg', 'selenium_mcg',
];

const MEAL_TYPE_DEFAULT_TIMES: Record<string, string> = {
  early_morning: '06:00',
  pre_breakfast: '07:00',
  breakfast: '08:00',
  post_breakfast: '09:30',
  brunch: '10:30',
  pre_lunch: '11:30',
  lunch: '13:00',
  post_lunch: '14:30',
  snack: '15:00',
  evening_snack: '17:00',
  tea: '16:30',
  high_tea: '17:00',
  dinner: '20:00',
  post_dinner: '21:30',
  supper: '21:00',
  midnight_snack: '23:00',
};

/** Unit options for portion in table-verify (agent JSON uses portion_size + portion_unit). */
const PORTION_UNIT_OPTIONS = [
  'g', 'ml', 'cup', '1/2 cup', 'piece', 'tbsp', 'tsp', 'Glass', 'bowl', 'slice', 'serving', 'oz', 'Other',
];

/** All time slot options (every 30 min) for meal time dropdowns in verify table */
const ALL_TIME_SLOT_OPTIONS: string[] = (() => {
  const opts: string[] = [];
  for (let h = 0; h < 24; h++) {
    for (const m of [0, 30]) {
      opts.push(`${String(h).padStart(2, '0')}:${String(m).padStart(2, '0')}`);
    }
  }
  return opts;
})();

type MealSlot = {
  id: string;
  meal_type: string;
  time: string;
};

type MealTypeChoice = {
  value: string;
  label: string;
};

/** Snapshot of plan settings for "persist only when changed" on Next */
type PlanSettingsSnapshot = {
  planId: string;
  mode: string;
  variant: string;
  cuisine_pattern: string;
  meals_per_day: number;
  snacks_per_day: number;
  meal_timing_json: Record<string, unknown>;
  name: string;
  start_date: string;
  end_date: string;
};

function planSettingsEqual(a: PlanSettingsSnapshot, b: Omit<PlanSettingsSnapshot, 'planId'>): boolean {
  return (
    a.mode === b.mode &&
    a.variant === b.variant &&
    a.cuisine_pattern === b.cuisine_pattern &&
    a.meals_per_day === b.meals_per_day &&
    a.snacks_per_day === b.snacks_per_day &&
    JSON.stringify(a.meal_timing_json) === JSON.stringify(b.meal_timing_json) &&
    (a.name || '') === (b.name || '') &&
    (a.start_date || '') === (b.start_date || '') &&
    (a.end_date || '') === (b.end_date || '')
  );
}

const DEFAULT_MEAL_TYPE_CHOICES: MealTypeChoice[] = MEAL_TYPE_OPTIONS.map((value) => ({
  value,
  label: MEAL_TYPE_LABELS[value] || value.charAt(0).toUpperCase() + value.slice(1),
}));

const getDefaultTimeForMealType = (type?: string) => {
  if (!type) return MEAL_TYPE_DEFAULT_TIMES.breakfast;
  const normalized = type.toLowerCase();
  return MEAL_TYPE_DEFAULT_TIMES[normalized] || MEAL_TYPE_DEFAULT_TIMES.breakfast;
};

/** Normalize meal type for case-insensitive comparison (e.g. "Early Morning" and "early_morning" -> same key). */
const normalizeMealTypeKey = (s: string) => (s || '').toLowerCase().replace(/\s+/g, '_').trim();

/** Dedupe meal type list case-insensitively; prefer canonical form from MEAL_TYPE_OPTIONS when present. */
const dedupeMealTypes = (list: string[]): string[] => {
  const seen = new Set<string>();
  const canonicalByKey = new Map<string, string>();
  MEAL_TYPE_OPTIONS.forEach((opt) => {
    const k = normalizeMealTypeKey(opt);
    canonicalByKey.set(k, opt);
  });
  const result: string[] = [];
  for (const m of list) {
    const k = normalizeMealTypeKey(m);
    if (seen.has(k)) continue;
    seen.add(k);
    result.push(canonicalByKey.has(k) ? canonicalByKey.get(k)! : m);
  }
  return result;
};

const isFixedManualVariant = (variant?: string | null) =>
  variant === 'doctor_manual' || variant === 'doctor_uploaded' || variant === 'manual';

const buildMealSlotsFromTiming = (
  json: Record<string, any> | null | undefined,
  mealsPerDay: number,
  mealTypeOptions: string[] = MEAL_TYPE_OPTIONS,
): MealSlot[] => {
  const legacyTimes: Record<string, string | undefined> = {
    breakfast: json?.breakfast,
    lunch: json?.lunch,
    dinner: json?.dinner,
    snack: json?.snacks,
    snacks: json?.snacks,
  };
  const existingSlots: any[] = Array.isArray(json?.meal_slots) ? json!.meal_slots : [];
  const slots: MealSlot[] = [];
  for (let idx = 0; idx < mealsPerDay; idx += 1) {
    const existingSlot = existingSlots[idx] || {};
    const fallbackType = mealTypeOptions[idx % mealTypeOptions.length] || mealTypeOptions[0];
    const slotType = existingSlot.meal_type || fallbackType;
    const slotTime = existingSlot.time || legacyTimes[slotType] || getDefaultTimeForMealType(slotType);
    slots.push({
      id: existingSlot.id || `${MEAL_SLOT_ID_PREFIX}-${idx}`,
      meal_type: slotType,
      time: slotTime,
    });
  }
  return slots;
};

const ensureMealSlotsLength = (slots: MealSlot[], mealsPerDay: number, mealTypeOptions: string[] = MEAL_TYPE_OPTIONS): MealSlot[] => {
  const normalizedSlots = slots.map((slot, idx) => ({
    id: slot.id || `${MEAL_SLOT_ID_PREFIX}-${idx}`,
    meal_type: slot.meal_type || mealTypeOptions[idx % mealTypeOptions.length] || mealTypeOptions[0],
    time: slot.time || getDefaultTimeForMealType(slot.meal_type),
  }));
  if (normalizedSlots.length === mealsPerDay) {
    return normalizedSlots;
  }
  const nextSlots = normalizedSlots.slice(0, mealsPerDay);
  while (nextSlots.length < mealsPerDay) {
    const idx = nextSlots.length;
    const fallbackType = mealTypeOptions[idx % mealTypeOptions.length] || mealTypeOptions[0];
    nextSlots.push({
      id: `${MEAL_SLOT_ID_PREFIX}-${idx}`,
      meal_type: fallbackType,
      time: getDefaultTimeForMealType(fallbackType),
    });
  }
  return nextSlots;
};

const buildMealTimingJsonFromSlots = (mealSlots: MealSlot[]) => {
  const json: Record<string, any> = {
    meal_slots: mealSlots.map(({ meal_type, time }) => ({ meal_type, time })),
  };
  const seenTimes: Record<string, string | undefined> = {};
  mealSlots.forEach((slot) => {
    if (!seenTimes[slot.meal_type]) {
      seenTimes[slot.meal_type] = slot.time;
    }
  });
  if (seenTimes.breakfast) json.breakfast = seenTimes.breakfast;
  if (seenTimes.lunch) json.lunch = seenTimes.lunch;
  if (seenTimes.dinner) json.dinner = seenTimes.dinner;
  if (seenTimes.snack) {
    json.snacks = seenTimes.snack;
  }
  return json;
};

function buildTimeOptions(stepMinutes: number): Array<{ value: string; label: string }> {
  const opts: Array<{ value: string; label: string }> = [];
  const step = Math.max(1, Math.min(60, Math.floor(stepMinutes)));
  for (let h = 0; h < 24; h++) {
    for (let m = 0; m < 60; m += step) {
      const hh = String(h).padStart(2, '0');
      const mm = String(m).padStart(2, '0');
      const value = `${hh}:${mm}`;
      const hour12 = ((h + 11) % 12) + 1;
      const ampm = h < 12 ? 'AM' : 'PM';
      const label = `${String(hour12).padStart(2, '0')}:${mm} ${ampm}`;
      opts.push({ value, label });
    }
  }
  return opts;
}

type ValidationResult = { isValid: boolean; errors: string[] };

function validateSettingsStep(params: {
  planName: string;
  mode: MealPlanMode;
  variant: string;
  cuisinePattern: string;
  mealsPerDay: number;
  durationDays: number;
  mealSlots: MealSlot[];
  planStartDate: string;
}): ValidationResult {
  const errors: string[] = [];
  if (!params.planName?.trim()) errors.push('Plan name is required');
  if (!params.mode) errors.push('Mode is required');
  if (!params.variant?.trim()) errors.push('Variant is required');
  if (!params.cuisinePattern?.trim()) errors.push('Cuisine pattern is required');
  if (!Number.isFinite(params.durationDays) || params.durationDays < 1 || params.durationDays > 30) {
    errors.push('Duration must be between 1 and 30 days');
  }
  if (!Number.isFinite(params.mealsPerDay) || params.mealsPerDay < 1) errors.push('Meals/day must be at least 1');

  // Start date must not be in the future
  if (!params.planStartDate) {
    errors.push('Start date is required');
  } else {
    const start = new Date(params.planStartDate);
    const today = new Date();
    // Zero out time for comparison
    start.setHours(0, 0, 0, 0);
    today.setHours(0, 0, 0, 0);
    if (start > today) {
      errors.push('Start date cannot be in the future');
    }
  }

  const slots = params.mealSlots || [];
  if (slots.length !== params.mealsPerDay) {
    errors.push('Meal slots must match the number of meals per day');
  }
  slots.forEach((slot, idx) => {
    if (!slot.meal_type) {
      errors.push(`Meal ${idx + 1} type is required`);
    }
    if (!slot.time) {
      errors.push(`Meal ${idx + 1} time is required`);
    }
  });

  return { isValid: errors.length === 0, errors };
}


// Display version/plan status: show "Approved", "Draft", etc. (for plan-level or legacy)
function displayStatus(status: string | undefined): string {
  if (!status) return '';
  const s = String(status).toLowerCase();
  if (s === 'processed') return 'Approved';
  if (s === 'draft' || s === 'draft_upload') return 'Draft';
  if (s === 'in_progress') return 'In progress';
  if (s === 'superseded') return 'Superseded';
  return status;
}

// Simplified version status for lists: "Current" (active/approved) or "Old" (draft, superseded, archived, etc.)
function versionStatusLabel(status: string | undefined): 'Current' | 'Old' {
  if (!status) return 'Old';
  const s = String(status).toLowerCase();
  return s === 'active' || s === 'approved' || s === 'processed' ? 'Current' : 'Old';
}

function isCurrentVersion(status: string | undefined): boolean {
  return versionStatusLabel(status) === 'Current';
}

const PatientMealPlanTab: React.FC<Props> = ({
  patientId,
  goalId,
  initialEditPlanId,
  showOnlyChat = false,
  mealPlanViewMode,
  mealPlanIdFromUrl,
  hideBackToList = false,
  hideInlineVersionSelector = false,
  externalViewedVersionId = null,
  initialCreatedPlanId = null,
}) => {
  const navigate = useNavigate();
  const { search: locationSearch, pathname: locationPathname } = useLocation();
  const timeOptions = useMemo(() => buildTimeOptions(15), []);

  /** When using URL-driven pages (list/new/view/edit), base path for navigation */
  const mealsBasePath =
    goalId != null && mealPlanViewMode != null
      ? `/patients/${patientId}/diet-plan/${goalId}/daily-meals`
      : undefined;

  const [plans, setPlans] = useState<MealPlanSummary[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [validationErrors, setValidationErrors] = useState<string[]>([]);

  // Meal plan list (Lucid-style): search, filters, pagination
  const [mealPlanSearch, setMealPlanSearch] = useState('');
  const [mealPlanStatusFilter, setMealPlanStatusFilter] = useState<'all' | 'approved' | 'draft' | 'pending'>('all');
  const [mealPlanCuisineFilter, setMealPlanCuisineFilter] = useState<string>('all');
  const [mealPlanPage, setMealPlanPage] = useState(1);
  const [mealPlanStatusDropdownOpen, setMealPlanStatusDropdownOpen] = useState(false);
  const [mealPlanCuisineDropdownOpen, setMealPlanCuisineDropdownOpen] = useState(false);
  const mealPlanFilterBarRef = useRef<HTMLDivElement>(null);
  const MEAL_PLAN_PAGE_SIZE = 10;

  const todayIso = useMemo(() => {
    const d = new Date();
    const year = d.getFullYear();
    const month = String(d.getMonth() + 1).padStart(2, '0');
    const day = String(d.getDate()).padStart(2, '0');
    return `${year}-${month}-${day}`;
  }, []);

  const [mode, setMode] = useState<MealPlanMode>('adaptive');
  const [variant, setVariant] = useState<string>('optimize_existing');
  const [cuisinePattern, setCuisinePattern] = useState<string>('Indian');
  const [mealsPerDayInput, setMealsPerDayInput] = useState<string>('3');
  const mealsPerDay = useMemo(() => {
    const trimmed = mealsPerDayInput.trim();
    if (trimmed === '') return 1;
    const n = parseInt(trimmed, 10);
    if (Number.isNaN(n) || n < 1) return 1;
    return Math.min(10, n);
  }, [mealsPerDayInput]);
  const [allowRecipeRepeat, setAllowRecipeRepeat] = useState<boolean>(false);
  const [recipeRepeatPolicy, setRecipeRepeatPolicy] = useState<'smart' | 'unlimited' | null>(null);
  const [maxRepeatsPerDish, setMaxRepeatsPerDish] = useState<number>(2);
  const [repeatAfterDays, setRepeatAfterDays] = useState<number>(3);
  const [mealTypeChoices, setMealTypeChoices] = useState<MealTypeChoice[]>(DEFAULT_MEAL_TYPE_CHOICES);
  const mealTypeValues = useMemo(() => mealTypeChoices.map((choice) => choice.value), [mealTypeChoices]);
  const [mealSlots, setMealSlots] = useState<MealSlot[]>(() =>
    buildMealSlotsFromTiming(defaultMealTiming, 3, mealTypeValues),
  );
  const mealTimingJson = useMemo(() => buildMealTimingJsonFromSlots(mealSlots), [mealSlots]);
  // In this UI "Meals/day" is the total slot count; snack is just a label for one of those slots.
  // We use 0 for snacks_per_day when saving so list shows meals_per_day (not meals_per_day + snacks_per_day).
  const snacksPerDay = 0;
  const ensureSlotsLength = useCallback(
    (slots: MealSlot[]) => ensureMealSlotsLength(slots, mealsPerDay, mealTypeValues),
    [mealsPerDay, mealTypeValues],
  );
  const updateMealSlot = useCallback(
    (index: number, changes: Partial<MealSlot>) => {
      setMealSlots((slots) => {
        const next = [...slots];
        const prev = next[index];
        const updatedTime =
          changes.time ??
          (changes.meal_type
            ? prev.time || getDefaultTimeForMealType(changes.meal_type)
            : prev.time);
        next[index] = {
          ...prev,
          ...changes,
          time: updatedTime,
        };
        return ensureSlotsLength(next);
      });
    },
    [ensureSlotsLength],
  );
  useEffect(() => {
    setMealSlots((slots) => ensureSlotsLength(slots));
  }, [ensureSlotsLength]);
  useEffect(() => {
    let cancelled = false;
    nutritionDictionaryService
      .getMealType()
      .then((items) => {
        if (cancelled) return;
        const byCode = new Map<string, string>();
        (items || []).forEach((item) => {
          if (item.code) {
            byCode.set(item.code, item.display_name || item.name || item.code);
          }
        });
        // Always show full list (16 options); use API labels when available, else default labels
        const merged: MealTypeChoice[] = DEFAULT_MEAL_TYPE_CHOICES.map((choice) => ({
          value: choice.value,
          label: byCode.get(choice.value) ?? choice.label,
        }));
        setMealTypeChoices(merged);
      })
      .catch((error) => {
        if (!cancelled) {
          console.warn('Failed to load meal types dictionary:', error);
        }
      });
    return () => {
      cancelled = true;
    };
  }, []);
  // Calculate default end date (3 days inclusive: today + 2)
  const defaultEndDate = useMemo(() => {
    const endDate = new Date(todayIso);
    endDate.setDate(endDate.getDate() + 2); // 3 days inclusive (start + 2 more days)
    return `${endDate.getFullYear()}-${String(endDate.getMonth() + 1).padStart(2, '0')}-${String(endDate.getDate()).padStart(2, '0')}`;
  }, [todayIso]);

  const [planName, setPlanName] = useState<string>('');
  const [planStartDate, setPlanStartDate] = useState<string>(todayIso);
  const [planEndDate, setPlanEndDate] = useState<string>(defaultEndDate);

  const [durationDays, setDurationDays] = useState<number>(3);

  // When opening the tab without editPlan in URL (e.g. via sidebar), show list view
  const openWithListViewModel = useMemo(() => {
    const hasEditPlan = new URLSearchParams(locationSearch).has('editPlan');
    return !hasEditPlan;
  }, [locationSearch]);

  const [selectedPlanId, setSelectedPlanId] = useState<string | null>(null);
  const [viewedPlanId, setViewedPlanId] = useState<string | null>(null);
  const [viewedPlanVersion, setViewedPlanVersion] = useState<MealPlanVersionResponse | null>(null);
  const [viewedPlanTotalsSummary, setViewedPlanTotalsSummary] = useState<MealPlanTotalsSummary | null>(null);
  const [viewedPlanVersions, setViewedPlanVersions] = useState<MealPlanVersionSummary[]>([]);
  const [viewedPlanSelectedVersionId, setViewedPlanSelectedVersionId] = useState<string | null>(null);
  // In view mode with a plan from URL, start on Details so version is fetched on load/refresh (otherwise no API runs until user clicks Details)
  const [viewTab, setViewTab] = useState<'settings' | 'details'>(() =>
    mealPlanViewMode === 'view' && mealPlanIdFromUrl ? 'details' : 'settings'
  );
  const [draft, setDraft] = useState<MealPlanVersionResponse | null>(null);
  const [draftVersions, setDraftVersions] = useState<MealPlanVersionSummary[]>([]);
  const [selectedDraftVersionId, setSelectedDraftVersionId] = useState<string | null>(null);
  const [totalsSummary, setTotalsSummary] = useState<MealPlanTotalsSummary | null>(null);
  const [fixedManualTotalsSummary, setFixedManualTotalsSummary] = useState<MealPlanTotalsSummary | null>(null);
  const [fixedManualVersions, setFixedManualVersions] = useState<MealPlanVersionSummary[]>([]);
  const [fixedManualSelectedVersionId, setFixedManualSelectedVersionId] = useState<string | null>(null);
  const [fixedManualSelectedVersion, setFixedManualSelectedVersion] = useState<MealPlanVersionResponse | null>(null);
  const [showFixedManualUpload, setShowFixedManualUpload] = useState<boolean>(true);
  // When opening via New/Edit/View URL, always start at settings. Otherwise list view shows settings step.
  const [activeStep, setActiveStep] = useState<StepId>(() => {
    if (mealPlanViewMode === 'new' || mealPlanViewMode === 'edit' || mealPlanViewMode === 'view') return 'settings';
    if (openWithListViewModel) return 'settings';
    return 'settings';
  });

  /** The 4 views: settings, versions, upload, meal_plan. Derived from activeStep. */
  const fixedManualSubView = useMemo((): 'upload' | 'meal_plan' | 'versions' => {
    if (activeStep === 'settings') return 'versions';
    if (activeStep === 'versions' || activeStep === 'upload' || activeStep === 'meal_plan') return activeStep;
    return 'versions';
  }, [activeStep]);

  /** Set view: one of the 4 views (versions, upload, meal_plan). */
  const setFixedManualSubView = useCallback((view: 'upload' | 'meal_plan' | 'versions') => {
    setActiveStep(view);
  }, []);
  /** When in upload subview, clear only the full version object so we don't show another version's details in the pane.
   * Do NOT clear fixedManualSelectedVersionId — upload needs it for the API (Create new draft sets it before switching here).
   * Clear upload error when we have a version ID so we don't show "Create new version first" after a successful Create new draft. */
  useEffect(() => {
    if (fixedManualSubView === 'upload') {
      setFixedManualSelectedVersion(null);
      if (fixedManualSelectedVersionId) setUploadError(null);
    }
  }, [fixedManualSubView, fixedManualSelectedVersionId]);

  /** When on upload subview with a plan and versions in the list but no version selected, auto-select one so "Generate Plan" can run. */
  useEffect(() => {
    if (
      fixedManualSubView !== 'upload' ||
      !selectedPlanId ||
      fixedManualSelectedVersionId ||
      fixedManualVersions.length === 0
    ) return;
    const draft = fixedManualVersions.find((v) => ['draft', 'draft_upload'].includes(String(v.status).toLowerCase())) || fixedManualVersions[0];
    if (draft?.version_id) {
      setFixedManualSelectedVersionId(draft.version_id);
      setUploadError(null);
    }
  }, [fixedManualSubView, selectedPlanId, fixedManualSelectedVersionId, fixedManualVersions]);

  /** When in version_detail, which tab is active. */
  const [existingPlanDetailTab, setExistingPlanDetailTab] = useState<'overview' | 'nutrient_comparison' | 'daily_meal_plan' | 'chat'>('overview');
  /** Nutrient Comparison legend visibility (clickable like daily nutrition Food group comparison). */
  const [nutrientLegendVisible, setNutrientLegendVisible] = useState({ current: true, icmr: true, doctor: true });
  /** Nutrition Comparison tab: switch between table view and graph view (used in both view and edit). */
  const [nutrientComparisonViewMode, setNutrientComparisonViewMode] = useState<'table' | 'graph'>('graph');
  /** Macro bar group hovered for tooltip (shows all 3 values in one tooltip). */
  const [macroHoverKey, setMacroHoverKey] = useState<string | null>(null);
  /** Weekly Schedule: which day accordion is expanded (0-based index), null = none. */
  const [weeklyScheduleExpandedDay, setWeeklyScheduleExpandedDay] = useState<number | null>(0);
  /** Weekly Schedule: selected meal tab per day (breakfast | lunch | dinner | snack). */
  const [weeklyScheduleMealTab, setWeeklyScheduleMealTab] = useState<Record<number, string>>({});
  /** Weekly Schedule: week offset for navigation (0 = days 0-6, 1 = days 7-13, ...). */
  const [weeklyScheduleWeekOffset, setWeeklyScheduleWeekOffset] = useState(0);
  /** Weekly Schedule meal detail: which tab is active per meal type (key: `${dayIdx}-${mealType}`). */
  const [weeklyScheduleDetailTab, setWeeklyScheduleDetailTab] = useState<Record<string, 'ingredients' | 'preparations' | 'nutrients'>>({});
  /** Filter for existing versions list: all | approved | drafts (no "Recent" per design) */
  const [existingVersionsFilter, setExistingVersionsFilter] = useState<'all' | 'current' | 'old'>('all');
  /** Text filter for versions table (Version / Date / Status) in the simplified versions view */
  const [versionsTableFilter, setVersionsTableFilter] = useState('');

  const [messageToPatient, setMessageToPatient] = useState<string>('');
  const [generationProgress, setGenerationProgress] = useState<{ message: string; progress: number } | null>(null);
  const [expandedDraftDishes, setExpandedDraftDishes] = useState<Set<string>>(new Set());
  const [expandedRecipes, setExpandedRecipes] = useState<Set<string>>(new Set());
  const [expandedGroceryList, setExpandedGroceryList] = useState<boolean>(false);
  /** Plan Details card "Daily Meal Plan" tab: same weekly schedule as Edit > View version > Daily Meal Plan */
  const [viewPageWeekOffset, setViewPageWeekOffset] = useState(0);
  const [viewPageExpandedDay, setViewPageExpandedDay] = useState<number | null>(0);
  const [viewPageDetailTab, setViewPageDetailTab] = useState<Record<string, 'ingredients' | 'preparations' | 'nutrients'>>({});
  const [viewPageExpandedGroceryList, setViewPageExpandedGroceryList] = useState<boolean>(false);
  /** Inner tabs inside Weekly Schedule tab: Schedule (day-by-day) | Grocery List | Exclusions */
  const [weeklyScheduleInnerTab, setWeeklyScheduleInnerTab] = useState<'schedule' | 'grocery_list' | 'exclusions'>('schedule');
  const [viewDetailsPdfLoading, setViewDetailsPdfLoading] = useState<'view' | 'download' | null>(null);
  const [whatsappSending, setWhatsappSending] = useState(false);
  const [whatsappMessage, setWhatsappMessage] = useState<{ type: 'success' | 'error'; text: string } | null>(null);
  const [pdfViewerUrl, setPdfViewerUrl] = useState<string | null>(null);
  const pdfViewerUrlRef = useRef<string | null>(null);
  const [successMessage, setSuccessMessage] = useState<string | null>(null);
  const generationPollIntervalRef = React.useRef<NodeJS.Timeout | null>(null);
  /** Set after upload success; effect runs one initial fetch so chat/progress show immediately */
  const initialChatFetchAfterUploadRef = React.useRef<{ planId: string; sessionId: number } | null>(null);

  // We intentionally do NOT auto-restore editing mode on page load.
  // Users should land on the plans list first; they can explicitly resume editing/draft if desired.
  const [editingPlanId, setEditingPlanId] = useState<string | null>(null);
  /** Snapshot of plan settings when opened for edit; used to persist only when user changed something before clicking Next */
  const initialSettingsWhenEditingRef = useRef<PlanSettingsSnapshot | null>(null);
  /** Set when user opened plan via Edit (URL or prop); keeps user on Settings step for that plan */
  const openedViaEditPlanRef = useRef<boolean>(false);

  const [furthestStepIdx, setFurthestStepIdx] = useState<number>(0);
  // When opening via New/Edit URL, always show the form at settings step. Otherwise show list.
  const [showCreateForm, setShowCreateForm] = useState<boolean>(() => {
    if (mealPlanViewMode === 'new' || mealPlanViewMode === 'edit') return true;
    if (openWithListViewModel) return false;
    return false;
  });

  // Track previous step from URL for draft navigation
  const prevStepFromUrlRef = useRef<string | null>(null);

  // On refresh: use view from URL (step param). Each view then calls the corresponding API to refresh data: settings → fetch plan; versions → fetch versions; upload/meal_plan → getChatStatus (restore).
  useEffect(() => {
    if (!mealsBasePath || !locationPathname.includes('/daily-meals')) return;
    if (locationPathname.endsWith('/edit') || locationPathname.endsWith('/new')) return;
    const params = new URLSearchParams(locationSearch);
    const step = params.get('step');
    const tab = params.get('tab');
    const editPlan = params.get('editPlan');
    if (step === 'settings') {
      setActiveStep('settings');
      prevStepFromUrlRef.current = step;
    } else if (step === 'versions' || step === 'view') {
      setActiveStep('versions');
      setFixedManualSubView('versions');
      prevStepFromUrlRef.current = step;
    } else if (step === 'upload') {
      if (editPlan) {
        setActiveStep('upload');
      } else {
        setActiveStep('versions');
        if (prevStepFromUrlRef.current !== 'upload') setFixedManualSubView('versions');
      }
      prevStepFromUrlRef.current = step;
    } else if (step === 'meal_plan') {
      if (editPlan) {
        setActiveStep('meal_plan');
      } else {
        setActiveStep('versions');
        if (prevStepFromUrlRef.current !== 'meal_plan') setFixedManualSubView('versions');
      }
      prevStepFromUrlRef.current = step;
    } else {
      prevStepFromUrlRef.current = step;
    }
    if (step === 'upload' || step === 'meal_plan') {
      if (tab === 'daily_meal_plan') setExistingPlanDetailTab('daily_meal_plan');
      else if (tab === 'chat') setExistingPlanDetailTab('chat');
      else setExistingPlanDetailTab('nutrient_comparison');
    }
  }, [locationSearch, locationPathname, mealsBasePath]);

  // When user navigates TO "Meal Plans" from elsewhere (URL is daily-meals with no editPlan), show list view.
  // Also when URL has list=1 (sidebar "Meal Plans" click), force list view so we show meal list even if already on daily-meals.
  const prevLocationRef = useRef({ pathname: '', search: '' });
  useEffect(() => {
    const now = { pathname: locationPathname, search: locationSearch };
    const params = new URLSearchParams(now.search);
    const wantsList = params.has('list') && params.get('list') === '1';
    const isDailyMealsNoEdit =
      now.pathname.includes('/daily-meals') && !params.has('editPlan');
    const prev = prevLocationRef.current;
    const wasDailyMealsNoEdit =
      prev.pathname !== '' && prev.pathname.includes('/daily-meals') && !new URLSearchParams(prev.search).has('editPlan');
    const isActualNavigation = prev.pathname !== '' && prev.pathname !== now.pathname;

    if (wantsList && now.pathname.includes('/daily-meals')) {
      // Sidebar "Meal Plans" click: force list view and remove list=1 from URL
      openedViaEditPlanRef.current = false;
      setSelectedPlanId(null);
      setShowCreateForm(false);
      setActiveStep('settings');
      params.delete('list');
      const newSearch = params.toString();
      navigate(now.pathname + (newSearch ? `?${newSearch}` : ''), { replace: true });
    } else if (isDailyMealsNoEdit && !wasDailyMealsNoEdit && isActualNavigation) {
      openedViaEditPlanRef.current = false;
      setSelectedPlanId(null);
      setShowCreateForm(false);
      setActiveStep('settings');
    }
    prevLocationRef.current = now;
  }, [locationPathname, locationSearch, navigate]);

  // Clear "opened via Edit" ref when selection is cleared
  useEffect(() => {
    if (selectedPlanId == null) openedViaEditPlanRef.current = false;
  }, [selectedPlanId]);

  // Doctor Fixed Manual (document upload) flow state
  const isFixedManual = mode === 'fixed' && isFixedManualVariant(variant);
  const [uploadingDocument, setUploadingDocument] = useState(false);
  const [uploadError, setUploadError] = useState<string | null>(null);
  const [uploadedDocumentId, setUploadedDocumentId] = useState<string | null>(null);
  const [uploadedSessionId, setUploadedSessionId] = useState<number | null>(null);
  const [selectedFile, setSelectedFile] = useState<File | null>(null);

  const [, setUploadStepIndex] = useState<number>(0);
  // Backend workflow step index (matches LangGraph node names)
  const [uploadBackendStepIndex, setUploadBackendStepIndex] = useState<number>(0);
  const [currentProgressDescription, setCurrentProgressDescription] = useState<string>('');
  const [currentProgress, setCurrentProgress] = useState<number>(0);
  
  // Chat interface state for fixed manual plans
  const [chatStatus, setChatStatus] = useState<MealPlanChatStatusResponse | null>(null);
  const [chatInput, setChatInput] = useState<string>('');
  const [sendingChatMessage, setSendingChatMessage] = useState(false);
  const [, setIcmrComparison] = useState<ICMRComparisonResponse | null>(null);
  const [loadingChatStatus, setLoadingChatStatus] = useState(false);
  const uploadWsRef = React.useRef<WebSocket | null>(null);
  const chatPollIntervalRef = React.useRef<NodeJS.Timeout | null>(null);
  const lastUploadWsMessageAtRef = React.useRef<number>(0);
  const [uploadWsConnected, setUploadWsConnected] = useState(false);
  const restartInFlightRef = React.useRef<Record<string, boolean>>({});
  const [selectedOptions, setSelectedOptions] = useState<Record<string, string>>({});
  /** Table-verify flow: editable copy of canonical plan_preview; submit sends this to upload/verify */
  const [tableEditDraft, setTableEditDraft] = useState<Record<string, any> | null>(null);
  const [submittingTableVerify, setSubmittingTableVerify] = useState(false);
  const [tableVerifyValidationMessage, setTableVerifyValidationMessage] = useState<string | null>(null);
  const [tableVerifyWarnings, setTableVerifyWarnings] = useState<string[]>([]);
  const [tableVerifyWarningsModalOpen, setTableVerifyWarningsModalOpen] = useState(false);
  const tableVerifyPendingSubmitRef = useRef<{ draft: any; planId: string; sid: number; versionId?: string } | null>(null);
  /** Copy-slot popover: which cell (slotIdx, dayIdx) has the popover open (null = none). Target days for copy (when using checkboxes). */
  const [copyPopoverCell, setCopyPopoverCell] = useState<{ slotIdx: number; dayIdx: number } | null>(null);
  const [copyTargetDayIndexes, setCopyTargetDayIndexes] = useState<number[]>([]);
  const [copyTargetSlotIndexes, setCopyTargetSlotIndexes] = useState<number[]>([]);
  const copyPopoverAnchorRef = useRef<HTMLDivElement | null>(null);
  const chatBottomRef = React.useRef<HTMLDivElement | null>(null);
  const chatMessagesContainerRef = React.useRef<HTMLDivElement | null>(null);
  const uploadBackendStepIndexRef = React.useRef<number>(0);
  const selectedPlanIdRef = React.useRef<string | null>(selectedPlanId);
  selectedPlanIdRef.current = selectedPlanId;
  const step2FileInputRef = React.useRef<HTMLInputElement | null>(null);
  const progressChatSectionRef = React.useRef<HTMLDivElement | null>(null);
  /** When true, adaptive "Create New Draft" was just clicked; effect will send "Create new plan" to start chat. */
  const adaptiveStartCreateNewPlanChatRef = React.useRef<boolean>(false);

  /** Workflow steps must match backend mealgen_fixed_upload_workflow graph node names and order */
  const uploadBackendSteps = useMemo(
    () => [
      { key: 'extract_document_text', label: 'Extract document text' },
      { key: 'extract_meal_plan_template', label: 'Extract meal plan template' },
      { key: 'normalize_and_verify_plan', label: 'Normalize & verify plan' },
      { key: 'calculate_nutrients_from_ingredients', label: 'Calculate nutrients' },
      { key: 'generate_grocery_list', label: 'Generate grocery list' },
      { key: 'extract_recipes', label: 'Extract recipes' },
      { key: 'generate_meal_plan_instances', label: 'Generate meal plan instances' },
      { key: 'create_plan_in_db', label: 'Create plan in DB' },
      { key: 'present_plan_to_doctor', label: 'Present plan to doctor' },
    ],
    [],
  );

  const uploadBackendStepIndexByKey = useMemo(() => {
    const m: Record<string, number> = {};
    uploadBackendSteps.forEach((s, idx) => {
      m[s.key] = idx;
    });
    return m;
  }, [uploadBackendSteps]);

  const deriveProgressFromBackendStep = useCallback((stepKey?: string | null): number | null => {
    if (!stepKey) return null;
    const idx = uploadBackendStepIndexByKey[stepKey];
    if (typeof idx !== 'number') return null;
    const denom = Math.max(1, uploadBackendSteps.length);
    // Keep within (0,1) and avoid showing 0% when we're already inside the workflow.
    const raw = (idx + 1) / denom;
    return Math.max(0.05, Math.min(0.99, raw));
  }, [uploadBackendStepIndexByKey, uploadBackendSteps.length]);

  const lastUploadBackendStepKey = useMemo(
    () => uploadBackendSteps[uploadBackendSteps.length - 1]?.key ?? 'create_plan_in_db',
    [uploadBackendSteps],
  );

  // True only when plan has been approved and sent to patient (Cancel should be disabled then).
  const isPlanApprovedAndSent = useCallback((status?: MealPlanChatStatusResponse | null): boolean => {
    if (!status) return false;
    return status.plan_approved === true || status.status === 'approved';
  }, []);

  // Completed = workflow finished; Approved = plan approved by doctor.
  const isCompletedOrApproved = useCallback((status?: MealPlanChatStatusResponse | null): boolean => {
    if (!status) return false;
    if (status.plan_approved === true || status.status === 'approved') return true;
    if (status.workflow_task_status === 'completed') return true;
    return false;
  }, []);

  // Consider workflow done when it reaches final step or plan gets approved.
  const isWorkflowDone = useCallback((status?: MealPlanChatStatusResponse | null): boolean => {
    if (!status) return false;
    if (status.waiting_for_doctor_response) return false;
    if (isCompletedOrApproved(status)) return true;
    // Treat ready_for_review as terminal so polling stops even if workflow_task_status is delayed
    if (status.status === 'ready_for_review') return true;
    const reachedLastStep = status.current_step === lastUploadBackendStepKey;
    return reachedLastStep;
  }, [lastUploadBackendStepKey, isCompletedOrApproved]);

  // Show progress bar + chat when status is neither completed nor approved (and we have a session).
  const shouldShowFixedManualProgressChat = useMemo(() => {
    if (!isFixedManual) return false;
    if (uploadingDocument) return true;
    // Don't show if plan is already approved (even if session exists)
    if (isCompletedOrApproved(chatStatus)) return false;
    if (uploadedSessionId != null) return true;
    if (chatStatus?.session_id != null && !isCompletedOrApproved(chatStatus)) return true;
    return false;
  }, [isFixedManual, uploadingDocument, uploadedSessionId, chatStatus, isCompletedOrApproved]);


  // When upload returns session_id, scroll to progress section so user sees the progress bar (DOM may not have been ready at 80ms)
  useEffect(() => {
    if (!uploadedSessionId || !isFixedManual) return;
    const t = setTimeout(() => {
      progressChatSectionRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' });
    }, 150);
    return () => clearTimeout(t);
  }, [uploadedSessionId, isFixedManual]);

  useEffect(() => {
    uploadBackendStepIndexRef.current = uploadBackendStepIndex;
  }, [uploadBackendStepIndex]);

  const tableVerifyInitializedRef = React.useRef(false);
  const tableVerifyInitialSnapshotRef = React.useRef<string | null>(null);
  // Table-verify: initialize editable draft from plan_preview when first entering waiting_for_table_verify (do not overwrite on refetch)
  useEffect(() => {
    if (chatStatus?.waiting_for_table_verify && chatStatus?.plan_preview && typeof chatStatus.plan_preview === 'object' && Array.isArray((chatStatus.plan_preview as any).daily_plans)) {
      if (!tableVerifyInitializedRef.current) {
        tableVerifyInitializedRef.current = true;
        const draft = JSON.parse(JSON.stringify(chatStatus.plan_preview));
        setTableEditDraft(draft);
        tableVerifyInitialSnapshotRef.current = JSON.stringify(draft);
      }
    } else {
      tableVerifyInitializedRef.current = false;
      tableVerifyInitialSnapshotRef.current = null;
      if (!chatStatus?.waiting_for_table_verify) setTableEditDraft(null);
    }
  }, [chatStatus?.waiting_for_table_verify, chatStatus?.plan_preview]);

  useEffect(() => {
    if (tableEditDraft != null) {
      setTableVerifyValidationMessage(null);
      setTableVerifyWarnings([]);
    }
  }, [tableEditDraft]);

  useEffect(() => {
    if (copyPopoverCell == null) return;
    const onMouseDown = (e: MouseEvent) => {
      if (copyPopoverAnchorRef.current && !copyPopoverAnchorRef.current.contains(e.target as Node)) {
        setCopyPopoverCell(null);
        setCopyTargetDayIndexes([]);
        setCopyTargetSlotIndexes([]);
      }
    };
    document.addEventListener('mousedown', onMouseDown);
    return () => document.removeEventListener('mousedown', onMouseDown);
  }, [copyPopoverCell]);

  /** Copy cell (sourceDayIdx, slotIdx) to selected target days (same slot) and/or to selected target slots (same day). */
  const copyCellToDaysAndSlots = useCallback((
    slotIdx: number,
    targetDayIndexes: number[],
    sourceDayIdx: number,
    targetSlotIndexes: number[],
  ) => {
    if (targetDayIndexes.length === 0 && targetSlotIndexes.length === 0) return;
    setTableEditDraft((prev) => {
      if (!prev?.daily_plans?.length) return prev;
      const next = JSON.parse(JSON.stringify(prev));
      const sourceMeal = next.daily_plans[sourceDayIdx]?.meals?.[slotIdx];
      if (sourceMeal == null) return next;
      const cloned = JSON.parse(JSON.stringify(sourceMeal));
      for (const dayIdx of targetDayIndexes) {
        if (dayIdx === sourceDayIdx) continue;
        if (!next.daily_plans[dayIdx]) continue;
        if (!Array.isArray(next.daily_plans[dayIdx].meals)) next.daily_plans[dayIdx].meals = [];
        while (next.daily_plans[dayIdx].meals.length <= slotIdx) next.daily_plans[dayIdx].meals.push(null);
        next.daily_plans[dayIdx].meals[slotIdx] = JSON.parse(JSON.stringify(cloned));
      }
      const dayPlan = next.daily_plans[sourceDayIdx];
      if (dayPlan) {
        if (!Array.isArray(dayPlan.meals)) dayPlan.meals = [];
        for (const targetSlotIdx of targetSlotIndexes) {
          if (targetSlotIdx === slotIdx) continue;
          while (dayPlan.meals.length <= targetSlotIdx) dayPlan.meals.push(null);
          const existingTarget = dayPlan.meals[targetSlotIdx];
          const targetMealType = existingTarget?.meal_type ?? next.daily_plans[0]?.meals?.[targetSlotIdx]?.meal_type ?? MEAL_TYPE_OPTIONS[targetSlotIdx % MEAL_TYPE_OPTIONS.length];
          const targetTime = existingTarget?.time ?? getDefaultTimeForMealType(targetMealType);
          const contentOnly = JSON.parse(JSON.stringify(cloned));
          contentOnly.meal_type = targetMealType;
          contentOnly.time = targetTime;
          dayPlan.meals[targetSlotIdx] = contentOnly;
        }
      }
      return next;
    });
    setCopyPopoverCell(null);
    setCopyTargetDayIndexes([]);
    setCopyTargetSlotIndexes([]);
  }, []);

  /** Copy slot content from a source day to selected target days only (same slot). Kept for "All other days" button. */
  const copySlotToDays = useCallback((slotIdx: number, targetDayIndexes: number[], sourceDayIdx: number = 0) => {
    if (targetDayIndexes.length === 0) return;
    copyCellToDaysAndSlots(slotIdx, targetDayIndexes, sourceDayIdx, []);
  }, [copyCellToDaysAndSlots]);

  const activeStepIdx = useMemo(() => (activeStep === 'settings' ? 0 : activeStep === 'versions' ? 1 : 2), [activeStep]);

  const selectedPlan = useMemo(
    () => plans.find((p) => p.plan_id === selectedPlanId) || null,
    [plans, selectedPlanId],
  );

  // Plan approved/active from plans table (used for Approve/Cancel button enable/disable instead of chat status)
  const isPlanApprovedFromTable = useMemo(() => {
    if (!selectedPlan?.status) return false;
    const s = String(selectedPlan.status).toLowerCase();
    return s === 'approved' || s === 'active' || s === 'processed';
  }, [selectedPlan?.status]);

  // Enable Approve only when version status is in_review (workflow completed; doctor review). Do not use chat status.
  const approveDisabledNotInReview = useMemo(() => {
    const versionStatus = (
      fixedManualSelectedVersion?.status ??
      fixedManualVersions.find((v) => v.version_id === fixedManualSelectedVersionId)?.status ??
      ''
    ).toString().toLowerCase();
    return versionStatus !== 'in_review';
  }, [fixedManualSelectedVersion?.status, fixedManualSelectedVersionId, fixedManualVersions]);

  // Ensure start date defaults to today for new plans when nothing is set/restored.
  useEffect(() => {
    if (!planStartDate) {
      setPlanStartDate(todayIso);
    }
  }, [planStartDate, todayIso]);

  // Helper: recalculate durationDays based on start and end dates (inclusive)
  const recalcDurationFromDates = useCallback(
    (start: string, end: string) => {
      if (!start || !end) return;
      const startDate = new Date(start);
      const endDate = new Date(end);
      if (Number.isNaN(startDate.getTime()) || Number.isNaN(endDate.getTime())) return;
      if (endDate < startDate) return;
      const diffMs = endDate.getTime() - startDate.getTime();
      const days = Math.floor(diffMs / (1000 * 60 * 60 * 24)) + 1; // inclusive
      const clamped = Math.max(1, Math.min(30, days));
      setDurationDays(clamped);
    },
    [],
  );

  // When an existing plan is selected, sync its stored config back into the wizard settings.
  // BUT ONLY if we're in edit mode (showCreateForm is true), not when just viewing.
  useEffect(() => {
    if (!selectedPlan || !showCreateForm) return;
    setMode(selectedPlan.mode);
    setVariant(selectedPlan.variant || '');
    setCuisinePattern(selectedPlan.cuisine_pattern || 'Indian');
    const targetMeals = selectedPlan.meals_per_day || 3;
    setMealsPerDayInput(String(targetMeals));
    setMealSlots(buildMealSlotsFromTiming(selectedPlan.meal_timing_json || defaultMealTiming, targetMeals, mealTypeValues));
    // Set plan name and dates if available
    if ((selectedPlan as any).name) {
      setPlanName((selectedPlan as any).name);
    }
    if ((selectedPlan as any).start_date) {
      setPlanStartDate((selectedPlan as any).start_date);
    }
    if ((selectedPlan as any).end_date) {
      setPlanEndDate((selectedPlan as any).end_date);
    }
    // Clear dependent artifacts when switching plans
    setDraft(null);
    setMessageToPatient('');
  }, [selectedPlanId, showCreateForm, mealTypeValues]); // eslint-disable-line react-hooks/exhaustive-deps

  // Keep durationDays in sync with start/end dates for new/edited plans
  useEffect(() => {
    recalcDurationFromDates(planStartDate, planEndDate);
  }, [planStartDate, planEndDate, recalcDurationFromDates]);

  const buildContext = useCallback((): MealPlanContext => {
    return {
      patient_id: patientId,
      mode,
      variant,
      duration_days: durationDays,
      language: 'en', // Default to English
      cuisine_pattern: cuisinePattern,
      meals_per_day: mealsPerDay,
      snacks_per_day: snacksPerDay,
      meal_timing_json: mealTimingJson,
      allow_recipe_repeat: allowRecipeRepeat,
      recipe_repeat_policy: allowRecipeRepeat ? recipeRepeatPolicy : null,
      max_repeats_per_dish: allowRecipeRepeat && recipeRepeatPolicy === 'smart' ? maxRepeatsPerDish : null,
      repeat_after_days: allowRecipeRepeat && recipeRepeatPolicy === 'smart' ? repeatAfterDays : null,
    };
  }, [
    patientId,
    mode,
    variant,
    durationDays,
    cuisinePattern,
    mealsPerDay,
    snacksPerDay,
    mealTimingJson,
    allowRecipeRepeat,
    recipeRepeatPolicy,
    maxRepeatsPerDish,
    repeatAfterDays,
  ]);

  const loadPlans = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);
      const data = await mealPlanService.listPlansForPatient(patientId, goalId);
      setPlans(data || []);
    } catch (e: any) {
      console.error('Error loading plans:', e);
      setError(getErrorMessage(e) || 'Failed to load Daily Meal Plans & Recipes');
    } finally {
      setLoading(false);
    }
  }, [patientId, goalId]);

  useEffect(() => {
    if (isResettingRef.current) return;
    void loadPlans();
  }, [loadPlans]);

  // When inside a diet plan (goalId set), show only meal plans that belong to this diet plan
  const plansForCurrentDietPlan = useMemo(() => {
    if (goalId == null) return plans;
    return plans.filter((p) => p.goal_id != null && p.goal_id === goalId);
  }, [plans, goalId]);

  // Persist only plan id and version id to localStorage; each view uses these ids to fetch data from backend.
  useEffect(() => {
    const key = getMealPlanSelectionStorageKey(patientId, goalId);
    const payload = {
      planId: selectedPlanId ?? null,
      versionId: fixedManualSelectedVersionId ?? null,
    };
    try {
      localStorage.setItem(key, JSON.stringify(payload));
    } catch (e) {
      console.warn('Meal plan selection: failed to persist to localStorage', e);
    }
  }, [patientId, goalId, selectedPlanId, fixedManualSelectedVersionId]);

  // Restore plan id and version id from localStorage when on list/first load (no edit/view intent). URL (editPlan + step) remains source of truth when present.
  useEffect(() => {
    const params = new URLSearchParams(locationSearch);
    if (params.has('editPlan')) return;
    if (mealPlanViewMode === 'edit' || mealPlanViewMode === 'view' || mealPlanIdFromUrl) return;
    const key = getMealPlanSelectionStorageKey(patientId, goalId);
    try {
      const raw = localStorage.getItem(key);
      if (!raw) return;
      const data = JSON.parse(raw) as { planId?: string | null; versionId?: string | null };
      if (data.planId != null && data.planId !== '') {
        setSelectedPlanId(data.planId);
      }
      if (data.versionId != null && data.versionId !== '') {
        setFixedManualSelectedVersionId(data.versionId);
      }
    } catch {
      // ignore invalid stored value
    }
  }, [patientId, goalId, locationSearch, mealPlanViewMode, mealPlanIdFromUrl]);

  // URL-driven pages: sync state from mealPlanViewMode / mealPlanIdFromUrl (list, new, view, edit)
  const createdForNewUrlRef = useRef(false);
  const formPopulatedFromUrlEditRef = useRef(false);
  useEffect(() => {
    if (mealPlanViewMode == null) return;
    if (mealPlanViewMode === 'list') {
      setShowCreateForm(false);
      setSelectedPlanId(null);
      setEditingPlanId(null);
      setViewedPlanId(null);
      setActiveStep('meal_plan');
      setActiveStep('settings');
      openedViaEditPlanRef.current = false;
      createdForNewUrlRef.current = false;
      formPopulatedFromUrlEditRef.current = false;
      return;
    }
    if (mealPlanViewMode === 'view' && mealPlanIdFromUrl) {
      setShowCreateForm(false);
      setViewedPlanId(mealPlanIdFromUrl);
      setSelectedPlanId(null);
      setEditingPlanId(null);
      setActiveStep('meal_plan');
      if (externalViewedVersionId) setViewedPlanSelectedVersionId(externalViewedVersionId);
      createdForNewUrlRef.current = false;
      formPopulatedFromUrlEditRef.current = false;
      return;
    }
    if (mealPlanViewMode === 'edit' && mealPlanIdFromUrl) {
      openedViaEditPlanRef.current = true;
      createdForNewUrlRef.current = false;
      formPopulatedFromUrlEditRef.current = false;
      setViewedPlanId(null);
      setShowCreateForm(true);
      setActiveStep('settings');
      setSelectedPlanId(mealPlanIdFromUrl);
      setEditingPlanId(mealPlanIdFromUrl);
      return;
    }
    // New plan page: set UI state and optionally select plan created by "+ Create New Plan" button.
    if (mealPlanViewMode === 'new' && patientId && goalId && !createdForNewUrlRef.current) {
      createdForNewUrlRef.current = true;
      isResettingRef.current = true;
      setError(null);
      setShowCreateForm(true);
      setActiveStep('settings');
      if (initialCreatedPlanId) {
        setSelectedPlanId(initialCreatedPlanId);
        setEditingPlanId(initialCreatedPlanId);
      } else {
        setSelectedPlanId(null);
        setEditingPlanId(null);
      }
      setViewedPlanId(null);
      setDraft(null);
      setSelectedDraftVersionId(null);
      setTotalsSummary(null);
      setDraftVersions([]);
      setUploadedDocumentId(null);
      setUploadedSessionId(null);
      setUploadError(null);
      setSelectedFile(null);
      setUploadStepIndex(0);
      setChatStatus(null);
      setIcmrComparison(null);
      setPlanName('');
      const endDate = new Date(todayIso);
      endDate.setDate(endDate.getDate() + 2);
      const endDateIso = `${endDate.getFullYear()}-${String(endDate.getMonth() + 1).padStart(2, '0')}-${String(endDate.getDate()).padStart(2, '0')}`;
      setPlanStartDate(todayIso);
      setPlanEndDate(endDateIso);
      setDurationDays(3);
      setMode('fixed');
      setVariant('doctor_manual');
      setCuisinePattern('Indian');
      setMealsPerDayInput('3');
      setMealSlots(buildMealSlotsFromTiming(defaultMealTiming, 3, mealTypeValues));
      setAllowRecipeRepeat(false);
      setRecipeRepeatPolicy(null);
      setMaxRepeatsPerDish(2);
      setRepeatAfterDays(3);
      initialSettingsWhenEditingRef.current = null;
      setTimeout(() => {
        isResettingRef.current = false;
      }, 0);
    }
  }, [mealPlanViewMode, mealPlanIdFromUrl, externalViewedVersionId, patientId, goalId, todayIso, initialCreatedPlanId]);

  // When on edit page (URL), populate form from plan once plans are loaded
  useEffect(() => {
    if (mealPlanViewMode !== 'edit' || !mealPlanIdFromUrl || formPopulatedFromUrlEditRef.current) return;
    const plan = plansForCurrentDietPlan.find((p) => p.plan_id === mealPlanIdFromUrl);
    if (!plan) return;
    formPopulatedFromUrlEditRef.current = true;
    setMode(plan.mode as MealPlanMode);
    setVariant(plan.variant || (plan.mode === 'adaptive' ? 'optimize_existing' : 'doctor_manual'));
    setCuisinePattern(plan.cuisine_pattern || 'Indian');
    const planMeals = plan.meals_per_day || 3;
    setMealsPerDayInput(String(planMeals));
    setMealSlots(buildMealSlotsFromTiming((plan.meal_timing_json as Record<string, string>) || defaultMealTiming, planMeals, mealTypeValues));
    setPlanName(plan.name || '');
    setPlanStartDate(plan.start_date || '');
    setPlanEndDate(plan.end_date || '');
    initialSettingsWhenEditingRef.current = {
      planId: plan.plan_id,
      mode: plan.mode as string,
      variant: plan.variant || (plan.mode === 'adaptive' ? 'optimize_existing' : 'doctor_manual'),
      cuisine_pattern: plan.cuisine_pattern || '',
      meals_per_day: plan.meals_per_day ?? 3,
      snacks_per_day: plan.snacks_per_day ?? 0,
      meal_timing_json: (plan.meal_timing_json as Record<string, unknown>) || {},
      name: plan.name || '',
      start_date: plan.start_date || '',
      end_date: plan.end_date || '',
    };
  }, [mealPlanViewMode, mealPlanIdFromUrl, plansForCurrentDietPlan, mealTypeValues]);

  // Unique cuisines for filter dropdown
  const mealPlanCuisineOptions = useMemo(() => {
    const set = new Set<string>();
    plansForCurrentDietPlan.forEach((p) => {
      if (p.cuisine_pattern) set.add(p.cuisine_pattern);
    });
    return Array.from(set).sort();
  }, [plansForCurrentDietPlan]);

  // Map plan status to Lucid display: approved/active -> Approved, draft -> Draft, else -> Pending
  const planStatusDisplay = useCallback((status: string | undefined) => {
    const s = String(status || '').toLowerCase();
    if (s === 'approved' || s === 'active' || s === 'processed') return 'Approved';
    if (s === 'draft') return 'Draft';
    return 'Pending';
  }, []);

  // Filtered list by search (plan_id, name), status, cuisine
  const mealPlanFilteredList = useMemo(() => {
    let list = plansForCurrentDietPlan;
    const q = mealPlanSearch.trim().toLowerCase();
    if (q) {
      list = list.filter(
        (p) =>
          p.plan_id.toLowerCase().includes(q) ||
          (p.name || '').toLowerCase().includes(q) ||
          `${p.mode}/${p.variant || ''}`.toLowerCase().includes(q),
      );
    }
    if (mealPlanStatusFilter !== 'all') {
      list = list.filter((p) => {
        const display = planStatusDisplay(p.status);
        if (mealPlanStatusFilter === 'approved') return display === 'Approved';
        if (mealPlanStatusFilter === 'draft') return display === 'Draft';
        return display === 'Pending';
      });
    }
    if (mealPlanCuisineFilter !== 'all') {
      list = list.filter((p) => (p.cuisine_pattern || '') === mealPlanCuisineFilter);
    }
    return list;
  }, [plansForCurrentDietPlan, mealPlanSearch, mealPlanStatusFilter, mealPlanCuisineFilter, planStatusDisplay]);

  const mealPlanTotalCount = mealPlanFilteredList.length;
  const mealPlanTotalPages = Math.max(1, Math.ceil(mealPlanTotalCount / MEAL_PLAN_PAGE_SIZE));
  const mealPlanPageSafe = Math.min(mealPlanPage, mealPlanTotalPages);
  const mealPlanPaginatedList = useMemo(() => {
    const start = (mealPlanPageSafe - 1) * MEAL_PLAN_PAGE_SIZE;
    return mealPlanFilteredList.slice(start, start + MEAL_PLAN_PAGE_SIZE);
  }, [mealPlanFilteredList, mealPlanPageSafe, MEAL_PLAN_PAGE_SIZE]);

  // Close Lucid-style filter dropdowns on outside click
  useEffect(() => {
    const handleClickOutside = (e: MouseEvent) => {
      if (mealPlanFilterBarRef.current && !mealPlanFilterBarRef.current.contains(e.target as Node)) {
        setMealPlanStatusDropdownOpen(false);
        setMealPlanCuisineDropdownOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  // If we're in a diet plan and the selected plan is not in this diet plan's list, clear selection
  useEffect(() => {
    if (goalId == null || !selectedPlanId) return;
    // During initial load/edit bootstrap, avoid clearing selected plan before plans are loaded.
    if (loading) return;
    if (mealPlanViewMode === 'edit' && mealPlanIdFromUrl && selectedPlanId === mealPlanIdFromUrl) return;
    if (editingPlanId && selectedPlanId === editingPlanId) return;
    const belongsToCurrentGoal = plansForCurrentDietPlan.some((p) => p.plan_id === selectedPlanId);
    if (!belongsToCurrentGoal) {
      setSelectedPlanId(null);
      setFixedManualSelectedVersion(null);
      setFixedManualSelectedVersionId(null);
      setFixedManualTotalsSummary(null);
    }
  }, [goalId, selectedPlanId, plansForCurrentDietPlan, loading, mealPlanViewMode, mealPlanIdFromUrl, editingPlanId]);

  // When viewing "Existing Versions", use the already-selected meal plan (from Settings); if none, use first fixed manual plan for this diet plan
  const fixedManualPlansForVersions = useMemo(
    () => plansForCurrentDietPlan.filter((p) => (p.mode as any) === 'fixed' && isFixedManualVariant((p.variant as string) || null)),
    [plansForCurrentDietPlan],
  );
  useEffect(() => {
    // Only when in create flow and on Versions step: ensure we have a selected plan to load versions for.
    // Do not run on list view (showCreateForm false) to avoid unnecessary selection and API calls.
    if (!showCreateForm || activeStep !== 'versions' || isResettingRef.current) return;
    if (fixedManualSubView !== 'versions') return;
    if (editingPlanId || mealPlanIdFromUrl) return;
    // On "Create New Plan" page we must keep selectedPlanId null until user clicks "Create new draft" (which creates a new plan). Do not auto-select the first existing plan.
    if (mealPlanViewMode === 'new') return;
    if (selectedPlanId && fixedManualPlansForVersions.some((p) => p.plan_id === selectedPlanId)) return;
    const first = fixedManualPlansForVersions[0];
    if (first) {
      setSelectedPlanId(first.plan_id);
      setFixedManualSelectedVersion(null);
      setFixedManualSelectedVersionId(null);
      setFixedManualTotalsSummary(null);
    }
  }, [showCreateForm, activeStep, fixedManualSubView, fixedManualPlansForVersions, selectedPlanId, editingPlanId, mealPlanIdFromUrl, mealPlanViewMode]);

  // Handle editPlan: stepFromUrl is the view ('settings' | 'versions' | 'upload' | 'meal_plan'). Each view uses planId (and versionId when available) to fetch data from backend only — no other state persisted.
  useEffect(() => {
    const searchParams = new URLSearchParams(locationSearch);
    const editPlanIdFromUrl = searchParams.get('editPlan');
    const editPlanId = editPlanIdFromUrl || initialEditPlanId;
    const stepFromUrl = searchParams.get('step'); // view: settings | versions | upload | meal_plan

    if (!editPlanId || editingPlanId) return;

    // View = settings: fetch plan from backend by id and show settings form.
    if (stepFromUrl === 'settings' || stepFromUrl === null || stepFromUrl === '') {
      setEditingPlanId(editPlanId);
      setShowCreateForm(true);
      setActiveStep('settings');
      (async () => {
        setLoading(true);
        try {
          const data = await mealPlanService.listPlansForPatient(patientId, goalId);
          const planToEdit = (data || []).find((p) => p.plan_id === editPlanId);
          if (!planToEdit) {
            setLoading(false);
            return;
          }
          openedViaEditPlanRef.current = true;
          const planMeals = planToEdit.meals_per_day || 3;
          setMode(planToEdit.mode as MealPlanMode);
          setVariant(planToEdit.variant || (planToEdit.mode === 'adaptive' ? 'optimize_existing' : 'doctor_manual'));
          setCuisinePattern(planToEdit.cuisine_pattern);
          setMealsPerDayInput(String(planMeals));
          setMealSlots(buildMealSlotsFromTiming(planToEdit.meal_timing_json || defaultMealTiming, planMeals, mealTypeValues));
          setPlanName(planToEdit.name || '');
          setPlanStartDate(planToEdit.start_date || '');
          setPlanEndDate(planToEdit.end_date || '');
          setSelectedPlanId(planToEdit.plan_id);
          setPlans(data || []);
          initialSettingsWhenEditingRef.current = {
            planId: planToEdit.plan_id,
            mode: planToEdit.mode as string,
            variant: planToEdit.variant || (planToEdit.mode === 'adaptive' ? 'optimize_existing' : 'doctor_manual'),
            cuisine_pattern: planToEdit.cuisine_pattern || '',
            meals_per_day: planToEdit.meals_per_day ?? 3,
            snacks_per_day: planToEdit.snacks_per_day ?? 0,
            meal_timing_json: (planToEdit.meal_timing_json as Record<string, unknown>) || {},
            name: planToEdit.name || '',
            start_date: planToEdit.start_date || '',
            end_date: planToEdit.end_date || '',
          };
          if (planToEdit.status === 'approved' || planToEdit.status === 'active' || planToEdit.approved_at) {
            const currentVersion = await mealPlanService.getCurrentVersion(planToEdit.plan_id);
            setDraft({
              plan_id: currentVersion.plan_id,
              version_id: currentVersion.version_id,
              status: currentVersion.status,
              days: currentVersion.days,
            });
            setSelectedDraftVersionId(currentVersion.version_id);
          }
          if (editPlanIdFromUrl) {
            searchParams.set('step', 'settings');
            const newSearch = searchParams.toString();
            navigate(`${locationPathname}${newSearch ? `?${newSearch}` : ''}`, { replace: true });
          }
        } catch (e: any) {
          console.error('Failed to load plan for edit:', e);
          setError(getErrorMessage(e) || 'Failed to load plan settings');
        } finally {
          setLoading(false);
        }
      })();
      return;
    }

    // View = versions: set view from URL; versions-loading effect will call API to load versions. Fetch plan from API for form if not in list (refresh).
    if (stepFromUrl === 'versions') {
      setEditingPlanId(editPlanId);
      setSelectedPlanId(editPlanId);
      setShowCreateForm(true);
      setActiveStep('versions');
      const planToEdit = plans.find((p) => p.plan_id === editPlanId);
      if (planToEdit) {
        const planMeals = planToEdit.meals_per_day || 3;
        setMode(planToEdit.mode as MealPlanMode);
        setVariant(planToEdit.variant || (planToEdit.mode === 'adaptive' ? 'optimize_existing' : 'doctor_manual'));
        setCuisinePattern(planToEdit.cuisine_pattern);
        setMealsPerDayInput(String(planMeals));
        setMealSlots(buildMealSlotsFromTiming(planToEdit.meal_timing_json || defaultMealTiming, planMeals, mealTypeValues));
        setPlanName(planToEdit.name || '');
        setPlanStartDate(planToEdit.start_date || '');
        setPlanEndDate(planToEdit.end_date || '');
      } else {
        (async () => {
          try {
            setLoading(true);
            const data = await mealPlanService.listPlansForPatient(patientId, goalId);
            const p = (data || []).find((x) => x.plan_id === editPlanId);
            if (p) {
              setPlans(data || []);
              const planMeals = p.meals_per_day || 3;
              setMode(p.mode as MealPlanMode);
              setVariant(p.variant || (p.mode === 'adaptive' ? 'optimize_existing' : 'doctor_manual'));
              setCuisinePattern(p.cuisine_pattern);
              setMealsPerDayInput(String(planMeals));
              setMealSlots(buildMealSlotsFromTiming(p.meal_timing_json || defaultMealTiming, planMeals, mealTypeValues));
              setPlanName(p.name || '');
              setPlanStartDate(p.start_date || '');
              setPlanEndDate(p.end_date || '');
            }
          } catch (e) {
            console.warn('Failed to load plan for versions view:', e);
          } finally {
            setLoading(false);
          }
        })();
      }
      return;
    }

    // View = upload or meal_plan: set view and selectedPlanId from URL immediately (so restore effect can run on refresh). Then fetch plan for form from API if needed.
    if (stepFromUrl !== 'upload' && stepFromUrl !== 'meal_plan') return;
    openedViaEditPlanRef.current = true;
    setSelectedPlanId(editPlanId);
    setEditingPlanId(editPlanId);
    setShowCreateForm(true);
    setActiveStep(stepFromUrl === 'meal_plan' ? 'meal_plan' : 'upload');
    const planToEdit = plans.find((p) => p.plan_id === editPlanId);
    if (planToEdit) {
      const planMeals = planToEdit.meals_per_day || 3;
      setMode(planToEdit.mode as MealPlanMode);
      setVariant(planToEdit.variant || (planToEdit.mode === 'adaptive' ? 'optimize_existing' : 'doctor_manual'));
      setCuisinePattern(planToEdit.cuisine_pattern);
      setMealsPerDayInput(String(planMeals));
      setMealSlots(buildMealSlotsFromTiming(planToEdit.meal_timing_json || defaultMealTiming, planMeals, mealTypeValues));
      setPlanName(planToEdit.name || '');
      setPlanStartDate(planToEdit.start_date || '');
      setPlanEndDate(planToEdit.end_date || '');
      initialSettingsWhenEditingRef.current = {
        planId: planToEdit.plan_id,
        mode: planToEdit.mode as string,
        variant: planToEdit.variant || (planToEdit.mode === 'adaptive' ? 'optimize_existing' : 'doctor_manual'),
        cuisine_pattern: planToEdit.cuisine_pattern || '',
        meals_per_day: planToEdit.meals_per_day ?? 3,
        snacks_per_day: planToEdit.snacks_per_day ?? 0,
        meal_timing_json: (planToEdit.meal_timing_json as Record<string, unknown>) || {},
        name: planToEdit.name || '',
        start_date: planToEdit.start_date || '',
        end_date: planToEdit.end_date || '',
      };
      if (planToEdit.status === 'approved' || planToEdit.status === 'active' || planToEdit.approved_at) {
        (async () => {
          try {
            setLoading(true);
            const currentVersion = await mealPlanService.getCurrentVersion(planToEdit.plan_id);
            setDraft({
              plan_id: currentVersion.plan_id,
              version_id: currentVersion.version_id,
              status: currentVersion.status,
              days: currentVersion.days,
            });
            setSelectedDraftVersionId(currentVersion.version_id);
          } catch (err: any) {
            console.error('Failed to load current version:', err);
          } finally {
            setLoading(false);
          }
        })();
      }
    } else {
      (async () => {
        try {
          setLoading(true);
          const data = await mealPlanService.listPlansForPatient(patientId, goalId);
          const p = (data || []).find((x) => x.plan_id === editPlanId);
          if (p) {
            setPlans(data || []);
            const planMeals = p.meals_per_day || 3;
            setMode(p.mode as MealPlanMode);
            setVariant(p.variant || (p.mode === 'adaptive' ? 'optimize_existing' : 'doctor_manual'));
            setCuisinePattern(p.cuisine_pattern);
            setMealsPerDayInput(String(planMeals));
            setMealSlots(buildMealSlotsFromTiming(p.meal_timing_json || defaultMealTiming, planMeals, mealTypeValues));
            setPlanName(p.name || '');
            setPlanStartDate(p.start_date || '');
            setPlanEndDate(p.end_date || '');
            initialSettingsWhenEditingRef.current = {
              planId: p.plan_id,
              mode: p.mode as string,
              variant: p.variant || (p.mode === 'adaptive' ? 'optimize_existing' : 'doctor_manual'),
              cuisine_pattern: p.cuisine_pattern || '',
              meals_per_day: p.meals_per_day ?? 3,
              snacks_per_day: p.snacks_per_day ?? 0,
              meal_timing_json: (p.meal_timing_json as Record<string, unknown>) || {},
              name: p.name || '',
              start_date: p.start_date || '',
              end_date: p.end_date || '',
            };
          }
        } catch (e) {
          console.warn('Failed to load plan for upload/meal_plan view:', e);
        } finally {
          setLoading(false);
        }
      })();
    }
  }, [locationSearch, locationPathname, plans, editingPlanId, initialEditPlanId, navigate, mealTypeValues, patientId, goalId]);

  // Restore session when view = upload or meal_plan: use version API only (draft_upload → upload, in_progress → meal_plan + getChatStatus).
  useEffect(() => {
    if (!isFixedManual || !selectedPlanId || uploadedSessionId != null) return;
    const stepFromUrl = new URLSearchParams(locationSearch).get('step'); // view: settings | versions | upload | meal_plan
    if (stepFromUrl !== 'upload' && stepFromUrl !== 'meal_plan') return;
    if (activeStep === 'settings' || activeStep === 'versions') return;
    let cancelled = false;
    setLoadingChatStatus(true);
    (async () => {
      try {
        if (fixedManualSelectedVersionId) {
          const versionResp = await mealPlanService.getVersion(selectedPlanId, fixedManualSelectedVersionId);
          if (cancelled) return;
          const versionStatus = (versionResp.status ?? (versionResp as any).workflow_state ?? '').toString().toLowerCase();
          if (versionStatus === 'draft_upload') {
            setActiveStep('upload');
            setUploadedSessionId(null);
            setChatStatus(null);
            return;
          }
          if (versionStatus === 'in_progress') {
            const status = await mealPlanService.getChatStatus(selectedPlanId, undefined, fixedManualSelectedVersionId);
            if (cancelled) return;
            const hasActiveSession =
              status?.session_id != null &&
              status.workflow_task_status !== 'cancelled' &&
              status.workflow_task_status !== 'completed' &&
              status.status !== 'approved' &&
              !status.plan_approved;
            if (hasActiveSession) {
              setUploadedSessionId(status.session_id);
              const atTableVerify =
                (status as any)?.waiting_for_table_verify === true ||
                status?.current_step === 'present_plan_to_doctor';
              setActiveStep(atTableVerify ? 'meal_plan' : 'upload');
              if (atTableVerify) setExistingPlanDetailTab('chat'); // Restore from upload flow: show chat tab
              const normalizedPlanPreview =
                typeof (status as any)?.plan_preview === 'string'
                  ? (() => {
                      try {
                        return JSON.parse((status as any).plan_preview);
                      } catch {
                        return (status as any).plan_preview;
                      }
                    })()
                  : (status as any)?.plan_preview;
              setChatStatus({ ...status, plan_preview: normalizedPlanPreview });
              if (status.current_step && uploadBackendStepIndexByKey[status.current_step as keyof typeof uploadBackendStepIndexByKey] != null) {
                setUploadBackendStepIndex(uploadBackendStepIndexByKey[status.current_step as keyof typeof uploadBackendStepIndexByKey]);
              }
              const progressRaw = (status as any)?.current_progress;
              const progressNum = typeof progressRaw === 'number' ? progressRaw : progressRaw != null ? parseFloat(String(progressRaw)) : NaN;
              if (Number.isFinite(progressNum)) setCurrentProgress(progressNum);
            } else {
              setUploadedSessionId(null);
              setChatStatus(status ?? null);
            }
            return;
          }
        }
      } catch (e) {
        if (!cancelled) console.debug('Restore upload view: no active session or error', e);
      } finally {
        if (!cancelled) setLoadingChatStatus(false);
      }
    })();
    return () => {
      cancelled = true;
      setLoadingChatStatus(false);
    };
  }, [isFixedManual, selectedPlanId, fixedManualSelectedVersionId, uploadedSessionId, locationSearch, activeStep, uploadBackendStepIndexByKey]);

  // Keep URL in sync: step = current view ('settings' | 'versions' | 'upload' | 'meal_plan') so refresh restores the same view.
  useEffect(() => {
    if (!mealsBasePath || !locationPathname.includes('/daily-meals')) return;
    if (!isFixedManual || !selectedPlanId) return;
    const params = new URLSearchParams(locationSearch);
    const viewStep =
      activeStep === 'settings'
        ? 'settings'
        : activeStep === 'versions'
          ? 'versions'
          : fixedManualSubView === 'upload'
            ? 'upload'
            : 'meal_plan';
    if (params.get('editPlan') === selectedPlanId && params.get('step') === viewStep) {
      if (viewStep !== 'meal_plan' || params.get('tab') === existingPlanDetailTab) return;
    }
    params.set('editPlan', selectedPlanId);
    params.set('step', viewStep);
    if (viewStep === 'meal_plan') params.set('tab', existingPlanDetailTab);
    const newSearch = params.toString();
    const desired = `${locationPathname}${newSearch ? `?${newSearch}` : ''}`;
    if (locationPathname + (locationSearch ? `?${locationSearch}` : '') !== desired) {
      navigate(desired, { replace: true });
    }
  }, [isFixedManual, selectedPlanId, fixedManualSubView, activeStep, existingPlanDetailTab, locationPathname, locationSearch, mealsBasePath, navigate]);

  // Cleanup polling interval on unmount
  useEffect(() => {
    return () => {
      if (generationPollIntervalRef.current) {
        clearInterval(generationPollIntervalRef.current);
        generationPollIntervalRef.current = null;
      }
    };
  }, []);

  // Cleanup function for fixed upload workflow (user cancel or programmatic clear)
  const clearUploadWorkflowSession = useCallback(async (reason: string) => {
    console.log('🧹 Clearing upload workflow session:', { reason, sessionId: uploadedSessionId });
    const sessionIdToCancel = uploadedSessionId;

    // Clear UI state first
    setUploadingDocument(false);
    setUploadError(null);
    setUploadedSessionId(null);
    setUploadedDocumentId(null);
    setChatStatus(null);
    setChatInput('');
    setSelectedOptions({});
    setCurrentProgressDescription('');
    setCurrentProgress(0);
    setUploadStepIndex(0);
    setUploadBackendStepIndex(0);
    setFixedManualSubView('versions');

    // Close WebSocket connection
    if (uploadWsRef.current) {
      try {
        uploadWsRef.current.close();
      } catch (e) {
        console.warn('Error closing WebSocket:', e);
      }
      uploadWsRef.current = null;
      setUploadWsConnected(false);
    }

    // Clear polling interval
    if (chatPollIntervalRef.current) {
      clearInterval(chatPollIntervalRef.current);
      chatPollIntervalRef.current = null;
    }

    // Best-effort server cleanup (drops in-memory workflow state)
    if (sessionIdToCancel) {
      try {
        await mealPlanService.cancelUploadWorkflowSession(sessionIdToCancel);
      } catch (e) {
        // Don't block UX on cleanup errors
        console.warn('Failed to cancel server workflow state (non-fatal):', e);
      }
    }
  }, [uploadedSessionId]);

  // Cancel this draft run: delete only the in-progress version created for this upload and release backend resources. Plan is kept.
  const cancelAndDeleteCurrentDraft = useCallback(async () => {
    if (!selectedPlanId) {
      await clearUploadWorkflowSession('cancel_draft_no_plan');
      return;
    }
    if (
      !window.confirm(
        'Cancel this draft run? This will delete the in-progress version and release all resources. The plan will be kept. This cannot be undone.'
      )
    ) {
      return;
    }
    const sessionIdToCancel = uploadedSessionId;

    // Clear UI state first (same as clearUploadWorkflowSession)
    setUploadingDocument(false);
    setUploadError(null);
    setUploadedSessionId(null);
    setUploadedDocumentId(null);
    setChatStatus(null);
    setChatInput('');
    setSelectedOptions({});
    setCurrentProgressDescription('');
    setCurrentProgress(0);
    setUploadStepIndex(0);
    setUploadBackendStepIndex(0);
    setFixedManualSubView('versions');
    if (uploadWsRef.current) {
      try {
        uploadWsRef.current.close();
      } catch (e) {
        console.warn('Error closing WebSocket:', e);
      }
      uploadWsRef.current = null;
      setUploadWsConnected(false);
    }
    if (chatPollIntervalRef.current) {
      clearInterval(chatPollIntervalRef.current);
      chatPollIntervalRef.current = null;
    }

    try {
      if (sessionIdToCancel) {
        await mealPlanService.cancelUploadWorkflowSession(sessionIdToCancel);
      }
      // Refresh plans; keep current plan selected (backend deleted only the draft version, not the plan)
      await loadPlans();
    } catch (e) {
      console.error('Failed to cancel draft:', e);
      setError(getErrorMessage(e) || 'Failed to cancel draft');
      await loadPlans();
    }
  }, [selectedPlanId, uploadedSessionId, clearUploadWorkflowSession, loadPlans]);

  // Reset upload/session UI state when starting "Create new draft" so the page shows upload document, not the current active plan (current active is loadable from Versions only).
  const resetUploadStateForNewDraft = useCallback(() => {
    isResettingRef.current = true;
    setUploadingDocument(false);
    setUploadError(null);
    setUploadedSessionId(null);
    setUploadedDocumentId(null);
    setChatStatus(null);
    setChatInput('');
    setSelectedOptions({});
    setSelectedFile(null);
    setCurrentProgressDescription('');
    setCurrentProgress(0);
    setUploadStepIndex(0);
    setUploadBackendStepIndex(0);
    if (uploadWsRef.current) {
      try {
        uploadWsRef.current.close();
      } catch {
        // ignore
      }
      uploadWsRef.current = null;
      setUploadWsConnected(false);
    }
    if (chatPollIntervalRef.current) {
      clearInterval(chatPollIntervalRef.current);
      chatPollIntervalRef.current = null;
    }
  }, []);

  // Cleanup local resources on unmount (do NOT cancel server session automatically; user can click "Close")
  useEffect(() => {
    return () => {
      // Local resource cleanup (safe on unmount)
      if (uploadWsRef.current) {
        try {
          uploadWsRef.current.close();
        } catch {
          // ignore
        }
        uploadWsRef.current = null;
      }

      const interval = chatPollIntervalRef.current;
      if (interval) {
        try {
          clearInterval(interval);
        } catch {
          // ignore
        }
        chatPollIntervalRef.current = null;
      }
    };
  }, []);

  const isResettingRef = React.useRef(false);

  const loadDraftVersions = useCallback(async (planId: string) => {
    try {
      const versions = await mealPlanService.listVersions(planId);
      setDraftVersions(versions || []);
    } catch (e) {
      // Non-blocking: versions list is optional UI sugar
      console.warn('Failed to load plan versions:', e);
      setDraftVersions([]);
    }
  }, []);

  const loadFixedManualVersionsAndSelect = useCallback(
    async (planId: string, preferredVersionId?: string | null, options?: { preserveUploadView?: boolean }) => {
      const preserveUploadView = options?.preserveUploadView === true;
      try {
        const versions = await mealPlanService.listVersions(planId);
        let vlist = versions || [];
        // If we have a preferred version (e.g. just created) but the API list doesn't include it yet, add it so the table shows it
        if (preferredVersionId && !vlist.some((v) => v.version_id === preferredVersionId)) {
          vlist = [
            {
              version_id: preferredVersionId,
              version: 1,
              status: 'draft',
              workflow_state: null,
              generated_by: 'doctor',
              created_at: new Date().toISOString(),
            } as MealPlanVersionSummary,
            ...vlist,
          ];
        }
        setFixedManualVersions(vlist);

        // Prefer a DRAFT version when available (saved draft -> can approve)
        const draftCandidate = vlist.find((v) => ['draft', 'draft_upload'].includes(String(v.status).toLowerCase())) || null;
        const nextVersionId = preferredVersionId || draftCandidate?.version_id || (vlist.length > 0 ? vlist[0].version_id : null);

        setFixedManualSelectedVersionId(nextVersionId);

        if (nextVersionId) {
          const ver = await mealPlanService.getVersion(planId, nextVersionId);
          setFixedManualSelectedVersion(ver);
          try {
            const summary = await mealPlanService.getTotalsSummary(planId, nextVersionId, goalId);
            setFixedManualTotalsSummary(summary);
          } catch (e) {
            console.debug('Fixed manual totals summary not available for selected version:', e);
            setFixedManualTotalsSummary(null);
          }
          if (!preserveUploadView) setShowFixedManualUpload(false);
        } else {
          setFixedManualSelectedVersion(null);
          setFixedManualTotalsSummary(null);
          if (!preserveUploadView) setShowFixedManualUpload(true);
        }
      } catch (e) {
        console.warn('Failed to load fixed manual versions:', e);
        setFixedManualVersions([]);
        // If we were given a preferredVersionId (e.g. just created via "Create new draft"), keep it so upload can proceed
        if (!preferredVersionId) {
          setFixedManualSelectedVersionId(null);
          setFixedManualSelectedVersion(null);
        }
        setFixedManualTotalsSummary(null);
        if (!preserveUploadView) setShowFixedManualUpload(true);
      }
    },
    [goalId],
  );

  const uploadRecoveryFetchDoneRef = React.useRef(false);
  /** When on upload with a plan but no version selected (e.g. version was cleared by a race), fetch versions once and select one so the draft error goes away and Generate Plan works. */
  useEffect(() => {
    if (fixedManualSubView !== 'upload') {
      uploadRecoveryFetchDoneRef.current = false;
      return;
    }
    if (!selectedPlanId || fixedManualSelectedVersionId || uploadRecoveryFetchDoneRef.current) return;
    uploadRecoveryFetchDoneRef.current = true;
    void loadFixedManualVersionsAndSelect(selectedPlanId, undefined, { preserveUploadView: true });
  }, [fixedManualSubView, selectedPlanId, fixedManualSelectedVersionId, loadFixedManualVersionsAndSelect]);

  // Create new draft: creates one version (and plan if none). Upload is then purely upload into that version.
  const handleCreateNewDraft = useCallback(async () => {
    const hasRequiredIds = Boolean(patientId && goalId);
    if (!hasRequiredIds) {
      setError(mealPlanViewMode === 'new' ? 'Diet plan context is missing. Please open Create New Plan from the Meal Plans list for a specific diet plan.' : 'Patient and diet plan are required.');
      setLoading(false);
      return;
    }
    setLoading(true);
    setError(null);
    setUploadError(null);
    try {
      let planId = selectedPlanId;
      if (!planId) {
        const endDate = new Date(planStartDate || todayIso);
        endDate.setDate(endDate.getDate() + 2);
        const endDateIso = `${endDate.getFullYear()}-${String(endDate.getMonth() + 1).padStart(2, '0')}-${String(endDate.getDate()).padStart(2, '0')}`;
        const createPayload = {
          patient_id: patientId as number,
          goal_id: goalId as number,
          mode: 'fixed' as const,
          variant: 'doctor_manual',
          language: 'en',
          cuisine_pattern: cuisinePattern || 'Indian',
          meals_per_day: mealsPerDay || 3,
          snacks_per_day: snacksPerDay ?? 0,
          meal_timing_json: mealTimingJson || defaultMealTiming,
          name: planName?.trim() || undefined,
          start_date: planStartDate || todayIso,
          end_date: planEndDate || endDateIso,
        };
        const createResp = await mealPlanService.createPlan(createPayload);
        planId = createResp.plan_id;
        setSelectedPlanId(planId);
        await loadPlans();
      }
      if (!planId) {
        setError('Failed to create plan');
        return;
      }
      const versionResp = await mealPlanService.createDraftVersion(planId);
      setFixedManualSelectedVersionId(versionResp.version_id);
      setFixedManualSelectedVersion(versionResp);
      resetUploadStateForNewDraft();
      // Optimistically add the new version to the list so it shows in the Versions table (API list may be eventually consistent)
      const newVersionSummary: MealPlanVersionSummary = {
        version_id: versionResp.version_id,
        version: (versionResp as MealPlanVersionResponse & { version?: number }).version ?? 1,
        status: versionResp.status ?? 'draft',
        workflow_state: versionResp.workflow_state ?? null,
        generated_by: 'doctor',
        created_at: (versionResp as MealPlanVersionResponse & { created_at?: string }).created_at ?? new Date().toISOString(),
      };
      setFixedManualVersions((prev) =>
        prev.some((v) => v.version_id === versionResp.version_id) ? prev : [newVersionSummary, ...prev]
      );
      await loadFixedManualVersionsAndSelect(planId, versionResp.version_id);
      setShowFixedManualUpload(true);
      setFixedManualSubView('upload');
      // Clear reset flag so the versions-loading effect doesn't clear fixedManualSelectedVersionId on next render
      isResettingRef.current = false;
    } catch (e: any) {
      setError(getErrorMessage(e) || 'Failed to create new draft');
      isResettingRef.current = false;
    } finally {
      setLoading(false);
    }
  }, [
    patientId,
    goalId,
    selectedPlanId,
    planStartDate,
    planEndDate,
    planName,
    cuisinePattern,
    mealsPerDay,
    snacksPerDay,
    mealTimingJson,
    todayIso,
    mealPlanViewMode,
    loadPlans,
    loadFixedManualVersionsAndSelect,
    resetUploadStateForNewDraft,
  ]);

  // Load versions only when in create flow and on versions, upload, or meal_plan view (not on list or settings).
  // Avoid continuous calls: gate by activeStep and showCreateForm; use stable deps so chatStatus object identity doesn't retrigger.
  const chatSessionStable = chatStatus?.session_id ?? null;
  const chatCompletedStable = chatStatus != null ? isCompletedOrApproved(chatStatus) : true;
  useEffect(() => {
    if (!selectedPlanId || isResettingRef.current) {
      setFixedManualVersions([]);
      setFixedManualSelectedVersionId(null);
      setFixedManualSelectedVersion(null);
      setFixedManualTotalsSummary(null);
      setShowFixedManualUpload(true);
      return;
    }
    const onVersionsOrInProgress = activeStep === 'versions' || activeStep === 'upload' || activeStep === 'meal_plan';
    if (!showCreateForm || !onVersionsOrInProgress) return;
    const userViewingVersions = fixedManualSubView === 'versions' || fixedManualSubView === 'meal_plan';
    // When on upload subview we already have the correct version ID from "Create new draft"; do not reload
    // versions here or we may overwrite it (e.g. if list is empty or stale) and show "Create new version first".
    if (isFixedManual && fixedManualSubView === 'upload') return;
    if (isFixedManual) {
      const uploadInProgress = uploadedSessionId != null && chatSessionStable != null && !chatCompletedStable;
      if (uploadInProgress && !userViewingVersions) return;
    }
    if (!userViewingVersions && !isFixedManual) return;
    void loadFixedManualVersionsAndSelect(selectedPlanId, fixedManualSelectedVersionId);
  }, [
    showCreateForm,
    activeStep,
    isFixedManual,
    selectedPlanId,
    fixedManualSelectedVersionId,
    loadFixedManualVersionsAndSelect,
    uploadedSessionId,
    chatSessionStable,
    chatCompletedStable,
    fixedManualSubView,
  ]);


  const handleCreatePlan = async () => {
    try {
      // Validate Settings step (plan name, mode, dates, etc.) before proceeding
      if (activeStep === 'settings') {
        const vr = validateSettingsStep({
          planName,
          mode,
          variant,
          cuisinePattern,
          mealsPerDay,
          durationDays,
          mealSlots,
          planStartDate,
        });
        if (!vr.isValid) {
          setValidationErrors(vr.errors);
          return;
        }
      }
      setValidationErrors([]);
      setLoading(true);
      setError(null);

      if (editingPlanId) {
        // Editing existing plan: persist only if settings were changed in this step
        const current: Omit<PlanSettingsSnapshot, 'planId'> = {
          mode,
          variant,
          cuisine_pattern: cuisinePattern,
          meals_per_day: mealsPerDay,
          snacks_per_day: snacksPerDay,
          meal_timing_json: mealTimingJson as Record<string, unknown>,
          name: planName?.trim() || '',
          start_date: planStartDate?.trim() || '',
          end_date: planEndDate?.trim() || '',
        };
        const initial = initialSettingsWhenEditingRef.current;
        const hasChanges =
          !initial ||
          initial.planId !== editingPlanId ||
          !planSettingsEqual(initial, current);

        if (hasChanges) {
          await mealPlanService.updatePlan(editingPlanId, {
            mode,
            variant,
            cuisine_pattern: cuisinePattern,
            meals_per_day: mealsPerDay,
            snacks_per_day: snacksPerDay,
            meal_timing_json: mealTimingJson,
            name: planName?.trim() || undefined,
            start_date: planStartDate?.trim() || undefined,
            end_date: planEndDate?.trim() || undefined,
          });
          initialSettingsWhenEditingRef.current = { planId: editingPlanId, ...current };
          await loadPlans();
        }
      }

      // Plan is created only when user clicks "Create new draft" on the Versions step, not on Next. Next only updates existing plan (above) or just advances the step.

      // Continue to draft step
      const nextIdx = Math.min(steps.length - 1, activeStepIdx + 1);
      setFurthestStepIdx((prev) => Math.max(prev, nextIdx));
      // Settings -> Versions: land on versions step.
      if (activeStep === 'settings' && nextIdx > 0 && steps[nextIdx]?.id === 'versions') {
        setFixedManualSubView('versions');
      }
      goToStepIdx(nextIdx);
    } catch (e: any) {
      setError(getErrorMessage(e) || 'Failed to update plan');
    } finally {
      setLoading(false);
    }
  };

  const handleUploadDocument = useCallback(async () => {
    if (!isFixedManual) return;
    if (!planName.trim()) {
      setUploadError('Please enter a plan name before uploading a document.');
      return;
    }
    if (!planStartDate || !planEndDate) {
      setUploadError('Please select start and end dates before uploading a document.');
      return;
    }
    if (!selectedFile) {
      setUploadError('Please choose a PDF/DOC/DOCX file to upload.');
      return;
    }
    const versionId = fixedManualSelectedVersionId;
    if (!selectedPlanId || !versionId) {
      setUploadError('Please click "Create new version" first to create a draft, then upload your document.');
      return;
    }

    try {
      setUploadError(null);
      setUploadedSessionId(null);
      setChatStatus(null);
      setChatInput('');
      setSelectedOptions({});
      setUploadStepIndex(0);
      setUploadBackendStepIndex(0);
      setCurrentProgress(0);
      setCurrentProgressDescription('Uploading document...');
      setActiveStep('versions');
      setShowCreateForm(true);
      setUploadingDocument(true);
      setTimeout(() => {
        progressChatSectionRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' });
      }, 80);

      const ctx = buildContext();

      const resp = await mealPlanService.uploadDocument({
        file: selectedFile,
        patient_id: patientId,
        plan_name: planName.trim(),
        start_date: planStartDate,
        end_date: planEndDate,
        language: ctx.language,
        cuisine_pattern: ctx.cuisine_pattern,
        meals_per_day: ctx.meals_per_day || 3,
        snacks_per_day: ctx.snacks_per_day || 0,
        meal_timing_json: ctx.meal_timing_json || {},
        goal_id: goalId,
        plan_id: selectedPlanId,
        version_id: versionId,
      });

      setUploadedDocumentId(resp.document_id);
      setUploadedSessionId(resp.session_id ?? null);
      setActiveStep('meal_plan');
      setShowCreateForm(true);
      setFixedManualSubView('meal_plan');
      setExistingPlanDetailTab('chat'); // From upload: show chat tab
      if (resp.version_id) {
        setFixedManualSelectedVersionId(resp.version_id);
      }
      setCurrentProgress(0.1);
      setCurrentProgressDescription('Extracting text from uploaded document...');
      setUploadBackendStepIndex(0);
      initialChatFetchAfterUploadRef.current =
        resp.plan_id && resp.session_id != null
          ? { planId: resp.plan_id, sessionId: resp.session_id }
          : null;
      setTimeout(() => void loadPlans(), 2000);
    } catch (e: any) {
      console.error('Failed to upload meal plan document:', e);
      const msg = getErrorMessage(e) || 'Failed to upload document. Please try again.';
      setUploadError(msg);
      setCurrentProgress(0);
      setCurrentProgressDescription('');
    } finally {
      setUploadingDocument(false);
    }
  }, [
    isFixedManual,
    planName,
    planStartDate,
    planEndDate,
    selectedFile,
    buildContext,
    patientId,
    goalId,
    selectedPlanId,
    fixedManualSelectedVersionId,
    loadPlans,
  ]);

  // Chat interface functions for fixed manual plans
  const fetchChatStatus = useCallback(async (
    planId: string,
    sessionId?: number | null,
    versionId?: string | null
  ): Promise<MealPlanChatStatusResponse | null> => {
    if (!planId) return null;
    try {
      setLoadingChatStatus(true);
      const status = await mealPlanService.getChatStatus(planId, sessionId, versionId);
      // Normalize plan_preview in case backend sends it as a JSON string.
      const normalizedPlanPreview =
        typeof (status as any)?.plan_preview === 'string'
          ? (() => {
              try {
                return JSON.parse((status as any).plan_preview);
              } catch {
                return (status as any).plan_preview;
              }
            })()
          : (status as any)?.plan_preview;

      const normalizedStatus: MealPlanChatStatusResponse = {
        ...status,
        plan_preview: normalizedPlanPreview,
      };
      setChatStatus(normalizedStatus);

      if (status.session_id != null && !isCompletedOrApproved(status)) {
        setUploadedSessionId(status.session_id);
        if (status.plan_id && !selectedPlanIdRef.current) {
          setSelectedPlanId(status.plan_id);
        }
      }

      // For fixed manual plans, once we're in review/approved, fetch the 3-way comparison
      // (Actual from meals vs Base ICMR targets vs Doctor-approved targets) via totals-summary.
      if (
        isFixedManual &&
        !status.waiting_for_doctor_response &&
        (status.status === 'ready_for_review' || status.plan_approved)
      ) {
        try {
          const summary = await mealPlanService.getTotalsSummary(planId, undefined, goalId);
          setFixedManualTotalsSummary(summary);
        } catch (e) {
          // Non-blocking: comparison may not be available yet (e.g., version not saved)
          console.debug('Fixed manual totals summary not available yet:', e);
        }
      }

      // Progress and step from backend only (current_progress, current_step)
      const progressRaw = (status as any)?.current_progress;
      const progressNum = typeof progressRaw === 'number' ? progressRaw : (progressRaw != null ? parseFloat(String(progressRaw)) : NaN);
      const hasBackendProgress = Number.isFinite(progressNum);
      if (hasBackendProgress) setCurrentProgress(progressNum);

      if (status.current_step && status.current_step in uploadBackendStepIndexByKey) {
        const stepIdx = uploadBackendStepIndexByKey[status.current_step as keyof typeof uploadBackendStepIndexByKey];
        setUploadBackendStepIndex(stepIdx);
        if (!hasBackendProgress) {
          const derived = deriveProgressFromBackendStep(status.current_step);
          if (derived != null) setCurrentProgress(derived);
        }
      } else if (!hasBackendProgress && (status.session_id != null) && !isCompletedOrApproved(status)) {
        // If backend doesn't report a step but session is live, assume we're at least at the questions phase.
        const derived = deriveProgressFromBackendStep('normalize_and_verify_plan');
        if (derived != null) setCurrentProgress(derived);
      }
      if (status.plan_approved) {
        setUploadStepIndex(5);
        setCurrentProgress(1.0);
      }

      // Load ICMR comparison only for uploaded document plans (indicated by sessionId)
      // Avoid calling ICMR comparison when creating regular plans (no document upload)
      if (sessionId) {
        // Avoid hammering the backend with /icmr-comparison while the workflow is still "processing"
        // (which causes repeated 404s because no plan version exists yet).
        if (status.icmr_comparison) {
          const defaultSummary = { total_nutrients: 0, met_targets: 0, met_targets_percentage: 0 };
          const icmrAny: any = status.icmr_comparison as any;
          const isWrapped =
            icmrAny &&
            typeof icmrAny === 'object' &&
            !Array.isArray(icmrAny) &&
            Object.prototype.hasOwnProperty.call(icmrAny, 'comparison');

          if (isWrapped) {
            setIcmrComparison({
              plan_id: planId,
              comparison: icmrAny.comparison || {},
              summary: icmrAny.summary || defaultSummary,
            });
          } else {
            // Back-compat: backend may send the comparison map directly
            setIcmrComparison({
              plan_id: planId,
              comparison: icmrAny || {},
              summary: defaultSummary,
            });
          }
          // At this point, we are at least at the ICMR comparison step.
          setUploadStepIndex((prev) => Math.max(prev, 4));
        } else if (status.status !== 'processing' && status.status !== 'ready_for_review' && status.plan_approved) {
          // Only fetch ICMR comparison if plan is approved and has versions
          // Don't fetch during 'processing' or 'ready_for_review' to avoid errors
          try {
            const icmr = await mealPlanService.getIcmrComparison(planId);
            setIcmrComparison(icmr);
            setUploadStepIndex((prev) => Math.max(prev, 4));
          } catch (e) {
            // ICMR comparison not available yet, ignore silently
            console.debug('ICMR comparison not available:', e);
          }
        }
      }
      return normalizedStatus;
    } catch (e: any) {
      console.error('Failed to fetch chat status:', e);
      // 404 = no chat session for this version (e.g. new version not uploaded yet). Clear so we don't show old messages.
      if (e?.response?.status === 404 || e?.status === 404) {
        setChatStatus(null);
        if (versionId) setUploadedSessionId(null);
      }
      return null;
    } finally {
      setLoadingChatStatus(false);
    }
  }, [isFixedManual, goalId, uploadBackendStepIndexByKey, isCompletedOrApproved]);

  // After upload success run one initial fetch so chat/progress show immediately
  useEffect(() => {
    if (!uploadedSessionId) return;
    const pending = initialChatFetchAfterUploadRef.current;
    if (!pending) return;
    initialChatFetchAfterUploadRef.current = null;
    fetchChatStatus(pending.planId, pending.sessionId).catch((e) =>
      console.warn('Initial fetchChatStatus after upload:', e)
    );
  }, [uploadedSessionId, fetchChatStatus]);

  // Ensure view-mode details always has a version context loaded.
  useEffect(() => {
    if (!viewedPlanId) {
      setViewedPlanVersions([]);
      setViewedPlanSelectedVersionId(null);
      return;
    }
    let cancelled = false;
    (async () => {
      try {
        const versions = await mealPlanService.listVersions(viewedPlanId);
        if (cancelled) return;
        const sortedVersions = [...(versions || [])].sort((a, b) => b.version - a.version);
        setViewedPlanVersions(sortedVersions);
        setViewedPlanSelectedVersionId((prev) => {
          if (prev && sortedVersions.some((v) => v.version_id === prev)) return prev;
          return sortedVersions[0]?.version_id ?? null;
        });
      } catch (e) {
        console.warn('Failed to load viewed plan versions:', e);
        if (!cancelled) {
          setViewedPlanVersions([]);
          setViewedPlanSelectedVersionId(null);
        }
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [viewedPlanId]);

  // When on view page with external version (e.g. MealPlanViewPage header dropdown), load that version
  // so Chat tab can show messages without requiring user to open "Daily Meal Plan" first.
  useEffect(() => {
    if (mealPlanViewMode !== 'view' || !viewedPlanId || !externalViewedVersionId) return;
    let cancelled = false;
    (async () => {
      try {
        const ver = await mealPlanService.getVersion(viewedPlanId, externalViewedVersionId);
        if (!cancelled) {
          setViewedPlanVersion(ver);
          setViewedPlanSelectedVersionId(externalViewedVersionId);
        }
      } catch (e) {
        console.warn('Failed to load external viewed version:', e);
        if (!cancelled) setViewedPlanVersion(null);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [mealPlanViewMode, viewedPlanId, externalViewedVersionId]);

  // Ensure view-mode details always has a selected version loaded.
  useEffect(() => {
    if (viewTab !== 'details' || !viewedPlanId) {
      if (mealPlanViewMode !== 'view' || !externalViewedVersionId) setViewedPlanVersion(null);
      return;
    }

    let cancelled = false;
    (async () => {
      try {
        setLoading(true);
        const selectedVersion = viewedPlanSelectedVersionId
          ? await mealPlanService.getVersion(viewedPlanId, viewedPlanSelectedVersionId)
          : await mealPlanService.getCurrentVersion(viewedPlanId);
        if (!cancelled) {
          setViewedPlanVersion(selectedVersion);
          if (!viewedPlanSelectedVersionId) {
            setViewedPlanSelectedVersionId(selectedVersion.version_id);
          }
        }
      } catch (e) {
        console.warn('Failed to load viewed plan version:', e);
        if (!cancelled) setViewedPlanVersion(null);
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [viewTab, viewedPlanId, viewedPlanSelectedVersionId, mealPlanViewMode, externalViewedVersionId, setLoading]);

  // View page details: fetch totals summary as soon as we have a version (for Overview and Nutrition Comparison).
  // Fetch whenever we're on the details view with a version, not only when Overview/Nutrition tab is active,
  // so Overview has data immediately even if viewedPlanVersion was still loading when the user first opened Overview.
  useEffect(() => {
    if (viewTab !== 'details') return;
    if (!viewedPlanId || !viewedPlanVersion?.version_id) return;
    let cancelled = false;
    (async () => {
      try {
        const summary = await mealPlanService.getTotalsSummary(
          viewedPlanId,
          viewedPlanVersion.version_id,
          goalId
        );
        if (!cancelled) setViewedPlanTotalsSummary(summary);
      } catch {
        if (!cancelled) setViewedPlanTotalsSummary(null);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [viewTab, viewedPlanId, viewedPlanVersion?.version_id, goalId]);

  const handleViewPdfViewDetails = useCallback(async () => {
    const planId = viewedPlanId;
    const versionId = viewedPlanSelectedVersionId || viewedPlanVersion?.version_id;
    if (!planId || !versionId || viewDetailsPdfLoading) return;
    setViewDetailsPdfLoading('view');
    try {
      const { blob } = await mealPlanService.getWeeklySchedulePdf(planId, versionId, 'inline', true);
      const url = URL.createObjectURL(blob);
      pdfViewerUrlRef.current = url;
      setPdfViewerUrl(url);
    } catch (e: any) {
      const msg = e?.response?.data?.detail ?? e?.message ?? 'Failed to load PDF';
      alert(typeof msg === 'string' ? msg : JSON.stringify(msg));
    } finally {
      setViewDetailsPdfLoading(null);
    }
  }, [viewedPlanId, viewedPlanSelectedVersionId, viewedPlanVersion?.version_id, viewDetailsPdfLoading]);

  const handleClosePdfViewer = useCallback(() => {
    const url = pdfViewerUrlRef.current;
    if (url) {
      URL.revokeObjectURL(url);
      pdfViewerUrlRef.current = null;
    }
    setPdfViewerUrl(null);
  }, []);

  const handleDownloadPdfViewDetails = useCallback(async () => {
    const planId = viewedPlanId;
    const versionId = viewedPlanSelectedVersionId || viewedPlanVersion?.version_id;
    if (!planId || !versionId || viewDetailsPdfLoading) return;
    setViewDetailsPdfLoading('download');
    try {
      const { blob, filename } = await mealPlanService.getWeeklySchedulePdf(planId, versionId, 'attachment');
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = filename;
      a.click();
      setTimeout(() => URL.revokeObjectURL(url), 5000);
    } catch (e: any) {
      const msg = e?.response?.data?.detail ?? e?.message ?? 'Failed to download PDF';
      alert(typeof msg === 'string' ? msg : JSON.stringify(msg));
    } finally {
      setViewDetailsPdfLoading(null);
    }
  }, [viewedPlanId, viewedPlanSelectedVersionId, viewedPlanVersion?.version_id, viewDetailsPdfLoading]);

  const handleSendViaWhatsAppViewDetails = useCallback(async () => {
    const planId = viewedPlanId;
    const versionId = viewedPlanSelectedVersionId || viewedPlanVersion?.version_id;
    if (!planId || !versionId || whatsappSending) return;
    setWhatsappMessage(null);
    setWhatsappSending(true);
    try {
      const res = await sendDietPlanViaWhatsApp(planId, versionId);
      const status = res?.status ?? 'sent';
      setWhatsappMessage({
        type: 'success',
        text: `Diet plan was sent successfully (status: ${status}). You can check delivery status in Recent message logs.`,
      });
    } catch (e: any) {
      const msg = e?.response?.data?.detail ?? e?.message ?? 'Failed to send via WhatsApp';
      setWhatsappMessage({ type: 'error', text: typeof msg === 'string' ? msg : JSON.stringify(msg) });
    } finally {
      setWhatsappSending(false);
    }
  }, [viewedPlanId, viewedPlanSelectedVersionId, viewedPlanVersion?.version_id, whatsappSending]);

  // View page + Plan Details Chat tab: fetch chat for viewed plan/version (use externalViewedVersionId if version not loaded yet)
  useEffect(() => {
    if (viewTab !== 'details') return;
    if (existingPlanDetailTab !== 'chat') return;
    if (!viewedPlanId) return;
    const versionId = viewedPlanVersion?.version_id ?? externalViewedVersionId ?? undefined;
    void fetchChatStatus(viewedPlanId, chatStatus?.session_id, versionId);
  }, [viewTab, existingPlanDetailTab, viewedPlanId, viewedPlanVersion?.version_id, externalViewedVersionId, chatStatus?.session_id, fetchChatStatus]);

  // When opening Chat tab, fetch chat status so we show the conversation for this plan/version (including when plan is approved/sent).
  // Without this, approved plans would show "No processing" + "Create new version" instead of the existing chat.
  useEffect(() => {
    if (showOnlyChat) return;
    if (existingPlanDetailTab !== 'chat') return;
    if (!isFixedManual) return;

    const planIdToFetch = selectedPlanId || fixedManualSelectedVersion?.plan_id || viewedPlanId;
    if (!planIdToFetch) return;

    const sessionIdToUse = uploadedSessionId ?? chatStatus?.session_id ?? undefined;
    const versionIdToUse = fixedManualSelectedVersionId ?? chatStatus?.version_id ?? viewedPlanVersion?.version_id ?? externalViewedVersionId ?? undefined;
    void fetchChatStatus(planIdToFetch, sessionIdToUse, versionIdToUse).catch((e) =>
      console.warn('Failed to fetch chat status on Chat tab open:', e)
    );
  }, [
    showOnlyChat,
    existingPlanDetailTab,
    isFixedManual,
    selectedPlanId,
    fixedManualSelectedVersion?.plan_id,
    fixedManualSelectedVersionId,
    viewedPlanId,
    viewedPlanVersion?.version_id,
    externalViewedVersionId,
    uploadedSessionId,
    chatStatus?.session_id,
    chatStatus?.version_id,
    fetchChatStatus,
  ]);

  const sendChatMessage = useCallback(async (
    planId: string,
    message: string,
    sessionId?: number | null,
    versionId?: string | null
  ) => {
    if (!planId || !message.trim()) return;
    try {
      setSendingChatMessage(true);
      const idempotencyKey =
        typeof crypto !== 'undefined' && typeof crypto.randomUUID === 'function'
          ? crypto.randomUUID()
          : `${Date.now()}-${Math.random().toString(36).slice(2)}`;
      await mealPlanService.sendChatMessage(
        planId,
        { content: message.trim(), session_id: sessionId || undefined, version_id: versionId || undefined },
        { idempotencyKey },
      );
      setChatInput('');
      // Refresh chat status after sending
      await fetchChatStatus(planId, sessionId, versionId);
    } catch (e: any) {
      console.error('Failed to send chat message:', e);
      const errorMsg = typeof e === 'string' ? e : e?.message || e?.response?.data?.detail || 'Failed to send message. Please try again.';
      alert(errorMsg);
    } finally {
      setSendingChatMessage(false);
    }
  }, [fetchChatStatus]);

  // Scroll only the chat messages container to bottom so the outer page scroll is never moved when new messages arrive.
  const scrollChatToBottom = useCallback(() => {
    const container = chatMessagesContainerRef.current;
    if (!container) return;
    try {
      container.scrollTop = container.scrollHeight;
    } catch {
      // ignore
    }
  }, []);

  // Auto-scroll chat to latest message when conversation changes. Only scroll the inner chat container so outer page scroll stays where the user left it.
  useEffect(() => {
    if (!chatStatus?.conversation_log || chatStatus.conversation_log.length === 0) return;
    const t = setTimeout(() => scrollChatToBottom(), 80);
    return () => clearTimeout(t);
  }, [chatStatus?.conversation_log, chatStatus?.waiting_for_doctor_response, scrollChatToBottom]);

  // When user clicked "Create New Draft" in AI-generated (adaptive) workflow, start the chat with "Create new plan" so the backend begins the create flow.
  useEffect(() => {
    if (!adaptiveStartCreateNewPlanChatRef.current || isFixedManual || fixedManualSubView !== 'upload') return;
    if (!selectedPlanId || uploadedSessionId != null || chatStatus?.session_id != null || sendingChatMessage) return;
    adaptiveStartCreateNewPlanChatRef.current = false;
    void sendChatMessage(selectedPlanId, 'Create new plan', undefined);
  }, [isFixedManual, fixedManualSubView, selectedPlanId, uploadedSessionId, chatStatus?.session_id, sendingChatMessage, sendChatMessage]);

  const handleApprovePlan = useCallback(async (planId: string) => {
    if (!planId) return;
    if (isPlanApprovedFromTable) return; // Plan already approved per table; avoid creating a new version
    try {
      setLoading(true);
      // For fixed manual, approval should NOT activate; activation is done by the patient.
      // First, persist the latest upload draft as a mealplanversion, then approve that version.
      const sessionId = uploadedSessionId || chatStatus?.session_id;
      const saved = await mealPlanService.saveUploadedDraft(planId, sessionId);
      await mealPlanService.approve(planId, saved.version_id, messageToPatient);
      setSuccessMessage('Plan approved and sent to patient successfully');
      setTimeout(() => setSuccessMessage(null), 5000);
      // Refresh chat status for this session so plan_approved is true and Approve button hides
      await fetchChatStatus(planId, sessionId ?? undefined);
      // Reload plans
      await loadPlans();
      // Reload versions and select the approved version
      await loadFixedManualVersionsAndSelect(planId, saved.version_id);
    } catch (e: any) {
      console.error('Failed to approve plan:', e);
      setError(getErrorMessage(e) || 'Failed to approve plan. Please try again.');
    } finally {
      setLoading(false);
    }
  }, [isPlanApprovedFromTable, chatStatus?.session_id, uploadedSessionId, messageToPatient, fetchChatStatus, loadPlans, loadFixedManualVersionsAndSelect]);

  const handleSaveUploadedDraft = useCallback(async () => {
    if (!selectedPlanId) return;
    try {
      setLoading(true);
      setError(null);
      const sessionId = uploadedSessionId || chatStatus?.session_id;
      await mealPlanService.saveUploadedDraft(selectedPlanId, sessionId);
      setSuccessMessage('Draft saved successfully!');
      setTimeout(() => setSuccessMessage(null), 5000);
      await loadPlans();
      await fetchChatStatus(selectedPlanId, sessionId);
    } catch (e: any) {
      console.error('Failed to save uploaded draft:', e);
      setError(getErrorMessage(e) || 'Failed to save draft. Please try again.');
    } finally {
      setLoading(false);
    }
  }, [selectedPlanId, uploadedSessionId, chatStatus?.session_id, loadPlans, fetchChatStatus]);

  // Poll chat status for fixed manual plans only when we're on upload or meal_plan view.
  useEffect(() => {
    // Only poll when we're on the step that displays chat/progress; stop when user navigates away
    if (activeStep !== 'upload' && activeStep !== 'meal_plan') {
      if (chatPollIntervalRef.current) {
        clearInterval(chatPollIntervalRef.current);
        chatPollIntervalRef.current = null;
      }
      return;
    }
    // Only poll if we're in fixed manual mode AND have an uploaded document
    if (!isFixedManual || !selectedPlanId || !uploadedSessionId) {
      if (chatPollIntervalRef.current) {
        clearInterval(chatPollIntervalRef.current);
        chatPollIntervalRef.current = null;
      }
      return;
    }

    const selectedPlan = plans.find((p) => p.plan_id === selectedPlanId);
    if (!selectedPlan || selectedPlan.status === 'approved' || selectedPlan.status === 'active') {
      if (chatPollIntervalRef.current) {
        clearInterval(chatPollIntervalRef.current);
        chatPollIntervalRef.current = null;
      }
      return;
    }

    const sessionId = uploadedSessionId || chatStatus?.session_id;
    const interval = setInterval(() => {
      if (document.visibilityState === 'hidden') return;
      fetchChatStatus(selectedPlanId, sessionId);
    }, uploadWsConnected ? 8000 : 5000);

    chatPollIntervalRef.current = interval;

    return () => {
      if (interval) clearInterval(interval);
    };
  }, [activeStep, isFixedManual, selectedPlanId, plans, uploadedSessionId, chatStatus?.session_id, fetchChatStatus, uploadWsConnected]);

  // When we're in table-verify but plan_preview is missing (e.g. first response had waiting_for_table_verify before backend set table_verify_canonical), refetch once so the table can load without a full page refresh
  const tableVerifyRefetchRequestedRef = React.useRef(false);
  useEffect(() => {
    if (!chatStatus?.waiting_for_table_verify || !selectedPlanId) {
      tableVerifyRefetchRequestedRef.current = false;
      return;
    }
    const hasValidPreview =
      chatStatus.plan_preview &&
      typeof chatStatus.plan_preview === 'object' &&
      Array.isArray((chatStatus.plan_preview as any).daily_plans) &&
      (chatStatus.plan_preview as any).daily_plans.length > 0;
    if (hasValidPreview) return;
    if (tableVerifyRefetchRequestedRef.current) return;
    tableVerifyRefetchRequestedRef.current = true;
    fetchChatStatus(selectedPlanId, chatStatus.session_id ?? undefined, chatStatus.version_id ?? undefined);
  }, [chatStatus?.waiting_for_table_verify, chatStatus?.plan_preview, chatStatus?.session_id, chatStatus?.version_id, selectedPlanId, fetchChatStatus]);

  // Connect to WebSocket for real-time progress updates
  const buildUploadWsUrl = useCallback((sessionId: number) => {
    const apiBase = process.env.REACT_APP_API_BASE_URL || window.location.origin;
    let origin = window.location.origin;
    try {
      origin = new URL(apiBase, window.location.origin).origin;
    } catch {
      origin = window.location.origin;
    }
    const wsOrigin = origin.replace(/^http:/, 'ws:').replace(/^https:/, 'wss:');
    return `${wsOrigin}/api/v1/chat-sessions/status?session_id=${sessionId}`;
  }, []);

  useEffect(() => {
    // Only connect if we have a session ID and are in fixed manual mode
    if (!isFixedManual || !uploadedSessionId) {
      if (uploadWsRef.current) {
        try {
          uploadWsRef.current.close();
        } catch {
          // ignore
        }
        uploadWsRef.current = null;
      }
      setUploadWsConnected(false);
      return;
    }

    const sessionId = uploadedSessionId;
    const wsUrl = buildUploadWsUrl(sessionId);
    console.log('🔌 [UploadProgress WS] Connecting:', wsUrl);

    let ws: WebSocket | null = null;
    try {
      ws = new WebSocket(wsUrl);
      uploadWsRef.current = ws;
    } catch (e) {
      console.error('❌ [UploadProgress WS] Failed to create WebSocket:', e);
      setUploadWsConnected(false);
      return;
    }

    ws.onopen = () => {
      console.log('✅ [UploadProgress WS] Connected for session', sessionId);
      setUploadWsConnected(true);
    };

    ws.onmessage = (event) => {
      lastUploadWsMessageAtRef.current = Date.now();
      try {
        const statusMessage = JSON.parse(event.data);
        // Backend sends ChatStatusMessage: { session_id, status, message (JSON string), progress, agent_name }
        const status = statusMessage?.status;
        const progress = statusMessage?.progress ?? 0;
        let eventData: any = null;
        
        if (statusMessage?.message) {
          try {
            eventData =
              typeof statusMessage.message === 'string'
                ? JSON.parse(statusMessage.message)
                : statusMessage.message;
          } catch {
            eventData = null;
          }
        }

        // Handle progress updates: only set from backend-sent values (no frontend calculation)
        if (eventData?.type === 'meal_plan_progress' || status === 'processing') {
          console.log('📊 [UploadProgress WS] Progress update:', { progress, step: eventData?.step, description: eventData?.description });
          if (eventData?.description != null) {
            setCurrentProgressDescription(eventData.description);
          }
          if (progress !== undefined && progress !== null && progress >= 0) {
            setCurrentProgress(progress);
          }
          const backendStepKey =
            eventData?.step ||
            eventData?.step_name ||
            eventData?.stepName ||
            eventData?.node ||
            eventData?.node_name ||
            null;
          if (backendStepKey && typeof backendStepKey === 'string' && backendStepKey in uploadBackendStepIndexByKey) {
            const stepIndex = uploadBackendStepIndexByKey[backendStepKey as keyof typeof uploadBackendStepIndexByKey];
            setUploadBackendStepIndex(stepIndex);
            uploadBackendStepIndexRef.current = stepIndex;
            // When we enter table-verify step, refetch chat status so plan_preview (table_verify_canonical) is loaded and the table renders without waiting for poll/refresh
            if (backendStepKey === 'normalize_and_verify_plan') {
              const planId = selectedPlanIdRef.current;
              if (planId) {
                fetchChatStatus(planId, sessionId);
              }
            }
          }
        }

        // Handle new message notification (e.g., ambiguity questions)
        if (eventData?.type === 'new_message' || status === 'waiting_for_response') {
          console.log('💬 [UploadProgress WS] New message received, refreshing chat status');
          const planId = selectedPlanIdRef.current;
          if (planId) {
            fetchChatStatus(planId, sessionId);
          }
        }

        // Handle completion status
        if (eventData?.type === 'meal_plan_ready' || status === 'ready_for_review') {
          console.log('✅ [UploadProgress WS] Meal plan ready for review');
          setUploadBackendStepIndex(uploadBackendStepIndexByKey['present_plan_to_doctor'] ?? uploadBackendStepIndexRef.current);
          setCurrentProgress(1.0); // Complete
          // If backend included plan/icmr payload in this WS message, apply it immediately
          if (eventData && typeof eventData === 'object') {
            try {
              if (eventData.meal_plan_template || eventData.plan_preview) {
                setChatStatus((prev) => ({
                  ...(prev || ({} as any)),
                  ...(prev ? { plan_id: (prev as any).plan_id } : {}),
                  plan_preview: (eventData.plan_preview ?? eventData.meal_plan_template) || (prev as any)?.plan_preview,
                }));
              }
              if (eventData.icmr_comparison) {
                const defaultSummary = { total_nutrients: 0, met_targets: 0, met_targets_percentage: 0 };
                const icmrAny: any = eventData.icmr_comparison;
                const isWrapped =
                  icmrAny &&
                  typeof icmrAny === 'object' &&
                  !Array.isArray(icmrAny) &&
                  Object.prototype.hasOwnProperty.call(icmrAny, 'comparison');
                setIcmrComparison((prev) => ({
                  plan_id: (prev as any)?.plan_id || (selectedPlanIdRef.current as any),
                  comparison: isWrapped ? (icmrAny.comparison || {}) : (icmrAny || {}),
                  summary: isWrapped ? (icmrAny.summary || defaultSummary) : defaultSummary,
                }));
              }
            } catch {
              // ignore WS payload application errors; polling will reconcile
            }
          }
          const planId = selectedPlanIdRef.current;
          if (planId) {
            fetchChatStatus(planId, sessionId);
            // Refetch versions so version status updates to in_review and Approve button becomes enabled
            loadFixedManualVersionsAndSelect(planId, undefined);
          }
        }

        // Handle errors
        if (eventData?.type === 'meal_plan_error' || status === 'error') {
          console.error('❌ [UploadProgress WS] Error:', eventData?.message || statusMessage?.message);
          setUploadError(eventData?.message || 'An error occurred during processing');
        }
      } catch (err) {
        console.error('❌ [UploadProgress WS] Failed to parse WebSocket message:', err, event.data);
      }
    };

    ws.onerror = (e) => {
      console.error('❌ [UploadProgress WS] WebSocket error:', e);
      setUploadWsConnected(false);
    };

    ws.onclose = (e) => {
      console.log('🔌 [UploadProgress WS] WebSocket closed:', e.code, e.reason);
      setUploadWsConnected(false);
      if (uploadWsRef.current === ws) {
        uploadWsRef.current = null;
      }
    };

    return () => {
      try {
        ws?.close();
      } catch {
        // ignore
      }
      if (uploadWsRef.current === ws) {
        uploadWsRef.current = null;
      }
      setUploadWsConnected(false);
    };
  }, [isFixedManual, uploadedSessionId, buildUploadWsUrl, fetchChatStatus, loadFixedManualVersionsAndSelect, uploadBackendStepIndexByKey]);

  // Helper function to safely extract error message
  const getErrorMessage = (e: any): string => {
    if (typeof e === 'string') return e;
    if (e?.message) return e.message;
    if (e?.response?.data) {
      const data = e.response.data;
      if (typeof data === 'string') return data;
      
      // Handle FastAPI error format: { detail: { error_code, message, ... } }
      if (data?.detail) {
        if (typeof data.detail === 'string') return data.detail;
        
        // If detail is an object with error_code and message
        if (data.detail?.error_code && data.detail?.message) {
          // Special handling for specific error codes
          if (data.detail.error_code === 'DISH_PLAN_VERSION_CONFLICT_APPROVED_NOT_ACTIVATED') {
            return 'Cannot create new version: There is an approved plan version that has not been activated by the patient yet. Please wait for the patient to activate the approved version, or contact support if needed.';
          }
          return data.detail.message;
        }
        
        // Fallback: try to get message from detail object
        if (data.detail?.message) return data.detail.message;
      }
      
      // Try direct message field
      if (data?.message) return data.message;
    }
    return 'An unexpected error occurred';
  };

  const handleGenerateDraft = async () => {
    if (loading || generationProgress) return; // Prevent double-submit
    try {
      setLoading(true);
      setError(null);
      setValidationErrors([]);

      let planId: string | null = selectedPlanId;
      if (!planId) {
        // Create plan only when user commits to generating (avoids multiple drafts on Next/Back/Cancel)
        const createResp = await mealPlanService.createPlan({
          patient_id: patientId,
          mode,
          variant,
          language: 'en',
          cuisine_pattern: cuisinePattern,
          meals_per_day: mealsPerDay,
          snacks_per_day: snacksPerDay,
          meal_timing_json: mealTimingJson,
          name: planName?.trim() || undefined,
          start_date: planStartDate?.trim() || undefined,
          end_date: planEndDate?.trim() || undefined,
          goal_id: goalId,
        });
        planId = createResp.plan_id;
        setSelectedPlanId(planId);
        await loadPlans();
      }
      if (!planId) {
        setLoading(false);
        return;
      }
      const resolvedPlanId: string = planId;

      setGenerationProgress({ message: 'Starting full plan generation...', progress: 0.1 });
      const ctx = buildContext();

      try {
        const resp = await mealPlanService.generateDraft(resolvedPlanId, {
          context: ctx,
          duration_days: durationDays,
          seed_from: mode === 'adaptive' ? 'existing_meals' : 'ai',
          generation_scope: 'full_plan',
        });
        
        setDraft(resp);
        setSelectedDraftVersionId(resp.version_id);
        void loadDraftVersions(resolvedPlanId);
        
        // Check if generation is still in progress
        const metadata = resp.generation_metadata;
        console.log('🔍 [MealPlan] Generate response:', { 
          version_id: resp.version_id, 
          metadata, 
          days_count: resp.days?.length || 0 
        });
        
        if (metadata?.status === 'generating') {
          console.log('🔄 [MealPlan] Starting polling for generation status...');
          // Clear any existing polling interval
          if (generationPollIntervalRef.current) {
            clearInterval(generationPollIntervalRef.current);
          }
          
          // Start polling for generation status
          generationPollIntervalRef.current = setInterval(async () => {
            try {
              if (document.visibilityState === 'hidden') return;
              console.log('📊 [MealPlan] Polling generation status for version:', resp.version_id);
              const status = await mealPlanService.getGenerationStatus(resolvedPlanId, resp.version_id);
              console.log('📊 [MealPlan] Generation status:', status);
              
              if (status.status === 'generating') {
                // Reload draft to get updated days as they're generated
                let actualDaysCount = 0;
                try {
                  const updatedDraft = await mealPlanService.getVersion(resolvedPlanId, resp.version_id);
                  console.log('✅ [MealPlan] Reloaded draft:', { 
                    version_id: updatedDraft.version_id, 
                    days_count: updatedDraft.days?.length || 0 
                  });
                  actualDaysCount = updatedDraft.days?.length || 0;
                  setDraft(updatedDraft);
                } catch (err) {
                  console.error('❌ [MealPlan] Failed to reload draft during generation:', err);
                }
                
                // Update progress with real data
                // Prefer actual days count from draft, fallback to metadata
                const totalDays = status.total_days || durationDays;
                // Use actual days count if available, otherwise use metadata current_day
                const completedDays = actualDaysCount > 0 ? actualDaysCount : (status.current_day || 0);
                const nextDay = completedDays + 1; // Show next day being generated (1-based for user)
                // Calculate progress: if we have actual days, use that; otherwise use metadata progress
                const progress = actualDaysCount > 0 
                  ? (totalDays > 0 ? completedDays / totalDays : 0)
                  : (status.progress ?? (totalDays > 0 ? completedDays / totalDays : 0));
                
                setGenerationProgress({
                  message: `Generating day ${nextDay} of ${totalDays}...`,
                  progress: Math.min(progress, 0.99), // Cap at 99% until completed
                });
              } else if (status.status === 'completed') {
                if (generationPollIntervalRef.current) {
                  clearInterval(generationPollIntervalRef.current);
                  generationPollIntervalRef.current = null;
                }
                setGenerationProgress({ message: 'Finalizing draft...', progress: 0.95 });
                
                // Reload final draft with all days
                try {
                  const finalDraft = await mealPlanService.getVersion(resolvedPlanId, resp.version_id);
                  setDraft(finalDraft);
                  
                  // Load totals summary
                  try {
                    const summary = await mealPlanService.getTotalsSummary(resolvedPlanId, resp.version_id, goalId);
                    setTotalsSummary(summary);
                  } catch (summaryErr) {
                    console.warn('Failed to load totals summary:', summaryErr);
                    setTotalsSummary(null);
                  }
                } catch (err) {
                  console.error('Failed to reload final draft:', err);
                }
                
                setGenerationProgress(null);
                setSuccessMessage('Daily Meal Plan & Recipes generated successfully!');
                setTimeout(() => setSuccessMessage(null), 5000);
              } else if (status.status === 'failed') {
                if (generationPollIntervalRef.current) {
                  clearInterval(generationPollIntervalRef.current);
                  generationPollIntervalRef.current = null;
                }
                setGenerationProgress(null);
                setError(status.error || 'Generation failed');
              }
            } catch (err) {
              console.error('❌ [MealPlan] Failed to poll generation status:', err);
              // Don't clear progress on polling errors - keep showing it
            }
          }, 2000); // Poll every 2 seconds (skipped when tab is hidden)
          
          // Store interval ID so we can clear it if component unmounts
          // (In a real implementation, you'd use a ref to store this)
        } else {
          // Generation completed immediately (shouldn't happen, but handle it)
          console.log('✅ [MealPlan] Generation completed immediately');
          setGenerationProgress(null);
          try {
            const summary = await mealPlanService.getTotalsSummary(resolvedPlanId, resp.version_id, goalId);
            setTotalsSummary(summary);
          } catch (summaryErr) {
            console.warn('Failed to load totals summary:', summaryErr);
            setTotalsSummary(null);
          }
          setSuccessMessage('Dish plan generated successfully!');
          setTimeout(() => setSuccessMessage(null), 5000);
        }
        
        setError(null);
        setLoading(false); // Clear loading state immediately after starting generation
      } catch (e: any) {
        throw e;
      }
    } catch (e: any) {
      setError(getErrorMessage(e) || 'Failed to generate draft');
      setGenerationProgress(null);
      setLoading(false);
    }
  };

  const handleSaveDraft = async () => {
    if (!selectedPlanId || !draft?.version_id) return;
    try {
      setLoading(true);
      setError(null);
      setSuccessMessage(null);
      setValidationErrors([]);
      
      // Use current draft.days
      const daysToSave = draft.days || [];
      
      const resp = await mealPlanService.updateVersion(selectedPlanId, draft.version_id, { days: daysToSave });
      setDraft(resp);
      setSuccessMessage('Draft saved successfully');
      setError(null); // Clear any previous errors
      // Clear success message after 3 seconds
      setTimeout(() => setSuccessMessage(null), 3000);
    } catch (e: any) {
      setError(getErrorMessage(e) || 'Failed to save draft');
      setSuccessMessage(null);
    } finally {
      setLoading(false);
    }
  };

  // When showOnlyChat (e.g. Chat tab on Diet Plan Detail), resolve goalId -> plan_id (UUID) and force upload/chat view
  useEffect(() => {
    if (!showOnlyChat || !goalId || !patientId) return;
    let cancelled = false;
    const run = async () => {
      try {
        const plans = await mealPlanService.listPlansForPatient(patientId, goalId);
        if (cancelled) return;
        const plan = plans.find((p) => p.goal_id === goalId) ?? plans[0];
        if (plan?.plan_id) {
          setSelectedPlanId(plan.plan_id);
        }
      } catch (e) {
        if (!cancelled) console.warn('Failed to resolve goal to plan_id for Chat tab:', e);
      }
      if (!cancelled) {
        setShowCreateForm(true);
        setActiveStep('versions');
        setFixedManualSubView('upload');
        setMode('fixed');
        setVariant('doctor_manual');
      }
    };
    void run();
    return () => { cancelled = true; };
  }, [showOnlyChat, goalId, patientId]);

  // When entering "Meal Plan Details" step, load draft versions for dropdown
  useEffect(() => {
    if (!showCreateForm || isResettingRef.current) return;
    if (activeStep !== 'versions' && activeStep !== 'upload' && activeStep !== 'meal_plan') return;
    if (!selectedPlanId) {
      // Clear any stale data if we don't have a selected plan
      setDraft(null);
      setTotalsSummary(null);
      setSelectedDraftVersionId(null);
      setDraftVersions([]);
      return;
    }
    const loadVersions = async () => {
      await loadDraftVersions(selectedPlanId);
      // If we have a draft but no selected version ID, set it
      if (draft?.version_id && !selectedDraftVersionId) {
        setSelectedDraftVersionId(draft.version_id);
        // Also load totals summary for the current draft
        try {
          const summary = await mealPlanService.getTotalsSummary(selectedPlanId, draft.version_id, goalId);
          setTotalsSummary(summary);
        } catch (summaryErr) {
          console.warn('Failed to load totals summary:', summaryErr);
          setTotalsSummary(null);
        }
      }
    };
    void loadVersions();
  }, [showCreateForm, activeStep, selectedPlanId, loadDraftVersions, draft?.version_id, selectedDraftVersionId, goalId]);

  const handleApprove = async () => {
    if (!selectedPlanId || !draft?.version_id) return;
    try {
      setLoading(true);
      setError(null);
      setSuccessMessage(null);
      setValidationErrors([]);
      await mealPlanService.approve(selectedPlanId, draft.version_id, messageToPatient);
      setSuccessMessage('Plan approved and sent to patient successfully');
      setDraft(null);
      setMessageToPatient('');
      setShowCreateForm(false);
      setEditingPlanId(null);
      initialSettingsWhenEditingRef.current = null;
      setActiveStep('settings');
      setFurthestStepIdx(0);
      await loadPlans();
      if (mealsBasePath) {
        navigate(mealsBasePath);
      }
      // Clear success message after 5 seconds
      setTimeout(() => setSuccessMessage(null), 5000);
    } catch (e: any) {
      setError(getErrorMessage(e) || 'Failed to approve');
      setSuccessMessage(null);
    } finally {
      setLoading(false);
    }
  };

  const validateActiveStep = useCallback((): ValidationResult => {
    if (activeStep === 'settings') {
      return validateSettingsStep({
        planName,
        mode,
        variant,
        cuisinePattern,
        mealsPerDay,
        durationDays,
        mealSlots,
        planStartDate,
      });
    }
    return { isValid: true, errors: [] };
  }, [activeStep, planName, mode, variant, cuisinePattern, mealsPerDay, durationDays, mealSlots, planStartDate]);

  const goToStepIdx = useCallback(
    (nextIdx: number) => {
      const safeIdx = Math.max(0, Math.min(steps.length - 1, nextIdx));
      setActiveStep(steps[safeIdx].id);
    },
    [setActiveStep],
  );

  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  const handleNext = useCallback(async () => {
    const vr = validateActiveStep();
    if (!vr.isValid) {
      setValidationErrors(vr.errors);
      return;
    }
    setValidationErrors([]);
    
    // If moving from settings to draft:
    // - if editing an existing plan, persist updates first
    // - if creating new, do NOT create the plan here (avoids multiple drafts when user clicks Next then Back/Cancel).
    //   Plan is created only when user commits: fixed manual = on document upload; adaptive = on Generate.
    if (activeStep === 'settings' && selectedPlanId) {
      try {
        setLoading(true);
        setError(null);
        await mealPlanService.updatePlan(selectedPlanId, {
          mode,
          variant,
          cuisine_pattern: cuisinePattern,
          meals_per_day: mealsPerDay,
          snacks_per_day: snacksPerDay,
          meal_timing_json: mealTimingJson,
          name: planName?.trim() || undefined,
          start_date: planStartDate?.trim() || undefined,
          end_date: planEndDate?.trim() || undefined,
        });
        await loadPlans();
      } catch (e: any) {
        setError(getErrorMessage(e) || 'Failed to update plan settings');
        setLoading(false);
        return;
      } finally {
        setLoading(false);
      }
    }

    // Continue to versions step only: show version view and load versions for this plan (no other side effects).
    setActiveStep('versions');
    setFurthestStepIdx((prev) => Math.max(prev, 2));
    if (isFixedManual && selectedPlanId) {
      void loadFixedManualVersionsAndSelect(selectedPlanId, undefined);
    }
  }, [validateActiveStep, activeStepIdx, goToStepIdx, activeStep, editingPlanId, selectedPlanId, patientId, goalId, mode, variant, cuisinePattern, mealsPerDay, snacksPerDay, mealTimingJson, mealSlots, planName, planStartDate, planEndDate, loadPlans, steps, isFixedManual, loadFixedManualVersionsAndSelect]);

  const handleBack = useCallback(() => {
    setValidationErrors([]);
    goToStepIdx(activeStepIdx - 1);
  }, [activeStepIdx, goToStepIdx]);


  const renderNutrientComparisonGraphs = (
    summary: MealPlanTotalsSummary | null,
    opts?: { marginTop?: number; emptyMessage?: string },
  ) => {
    const marginTop = opts?.marginTop ?? 24;
    const emptyMessage = opts?.emptyMessage || 'Nutrient data is loading or not available for this version.';
    return (
      <div className="np-subcard" style={{ marginTop, padding: 16 }}>
        {summary &&
        (Object.keys(summary.totals || {}).length > 0 ||
          Object.keys(summary.meal_plan_totals || {}).length > 0 ||
          Object.keys(summary.meal_plan_base_icmr_totals || {}).length > 0 ||
          Object.keys(summary.meal_plan_doctor_modified_totals || {}).length > 0) ? (() => {
          const actuals = (summary.totals && Object.keys(summary.totals).length > 0)
            ? summary.totals
            : (summary.meal_plan_totals || {}) as Record<string, number>;
          const icmrTargets = summary.meal_plan_base_icmr_totals || {};
          const doctorTargetsRaw =
            summary.meal_plan_doctor_modified_totals ||
            summary.meal_plan_adjusted_totals || {};
          const fmt = (v: any) =>
            v === null || v === undefined || Number.isNaN(v) ? '-' : Number(v).toFixed(1);
          const getStatus = (actual: number, target: number | null) => {
            if (target === null || target === undefined || target === 0) return { label: '-', color: '#6b7280' };
            const pct = (actual / target) * 100;
            const tolerancePct = 10;
            if (Math.abs(pct - 100) <= tolerancePct) return { label: 'ON TRACK', color: '#10b981' };
            if (pct > 100) return { label: 'OVER', color: '#ef4444' };
            return { label: 'DEFICIT', color: '#ef4444' };
          };
          const macroKeys = ['calories', 'protein_g', 'carbs_g', 'fat_g', 'fiber_g'] as const;
          const vitaminKeys = [
            'vitamin_a_mcg', 'vitamin_c_mg', 'vitamin_d_mcg', 'vitamin_e_mg', 'vitamin_k_mcg',
            'vitamin_b1_mg', 'vitamin_b2_mg', 'vitamin_b3_mg', 'vitamin_b6_mg', 'vitamin_b12_mcg', 'folate_mcg',
          ];
          const mineralKeys = [
            'calcium_mg', 'iron_mg', 'magnesium_mg', 'phosphorus_mg', 'potassium_mg',
            'zinc_mg', 'copper_mg', 'manganese_mg', 'selenium_mcg',
          ];
          const barMaxHeight = 120;
          return (
            <>
              <h3 style={{ marginTop: 0, marginBottom: 12, fontSize: '0.85rem', fontWeight: 700, color: '#374151', letterSpacing: '0.05em' }}>
                MACRO OVERVIEW
              </h3>
              <div className="food-group-chart-legend-row" style={{ justifyContent: 'flex-end', marginBottom: 16 }}>
                <span
                  className={`food-group-legend-item ${nutrientLegendVisible.current ? '' : 'food-group-legend-inactive'}`}
                  onClick={() => setNutrientLegendVisible((v) => ({ ...v, current: !v.current }))}
                  role="button"
                  tabIndex={0}
                  onKeyDown={(e) => e.key === 'Enter' && setNutrientLegendVisible((v) => ({ ...v, current: !v.current }))}
                >
                  <span className="food-group-legend-dot" style={{ backgroundColor: '#1e3a5f' }} />
                  CURRENT PLAN
                </span>
                <span
                  className={`food-group-legend-item ${nutrientLegendVisible.icmr ? '' : 'food-group-legend-inactive'}`}
                  onClick={() => setNutrientLegendVisible((v) => ({ ...v, icmr: !v.icmr }))}
                  role="button"
                  tabIndex={0}
                  onKeyDown={(e) => e.key === 'Enter' && setNutrientLegendVisible((v) => ({ ...v, icmr: !v.icmr }))}
                >
                  <span className="food-group-legend-dot" style={{ backgroundColor: '#9ca3af' }} />
                  ICMR TARGET
                </span>
                <span
                  className={`food-group-legend-item ${nutrientLegendVisible.doctor ? '' : 'food-group-legend-inactive'}`}
                  onClick={() => setNutrientLegendVisible((v) => ({ ...v, doctor: !v.doctor }))}
                  role="button"
                  tabIndex={0}
                  onKeyDown={(e) => e.key === 'Enter' && setNutrientLegendVisible((v) => ({ ...v, doctor: !v.doctor }))}
                >
                  <span className="food-group-legend-dot" style={{ backgroundColor: '#93c5fd' }} />
                  DR. APPROVED
                </span>
              </div>
              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(5, 1fr)', gap: 24, marginBottom: 32 }}>
                {macroKeys.map((key) => {
                  const actualNum = Number(actuals[key]) || 0;
                  const icmrNum = Number(icmrTargets[key]) || 0;
                  const doctorNum = Number(doctorTargetsRaw[key]) || 0;
                  const visibleVals = [
                    nutrientLegendVisible.current ? actualNum : 0,
                    nutrientLegendVisible.icmr ? icmrNum : 0,
                    nutrientLegendVisible.doctor ? doctorNum : 0,
                  ];
                  const maxVal = Math.max(...visibleVals, 1);
                  const status = getStatus(actualNum, doctorNum || null);
                  const label = key === 'protein_g' ? 'PROTEIN' : key === 'carbs_g' ? 'CARBS' : key === 'fat_g' ? 'FATS' : key === 'fiber_g' ? 'FIBRE' : 'CALORIES';
                  const showTooltip = macroHoverKey === key;
                  return (
                    <div
                      key={key}
                      style={{ textAlign: 'center', position: 'relative' }}
                      onMouseEnter={() => setMacroHoverKey(key)}
                      onMouseLeave={() => setMacroHoverKey(null)}
                    >
                      <div style={{ fontSize: '0.8rem', fontWeight: 600, color: '#374151', marginBottom: 8 }}>{label}</div>
                      <div style={{ display: 'flex', alignItems: 'flex-end', justifyContent: 'center', gap: 6, height: barMaxHeight }}>
                        {nutrientLegendVisible.current && <div style={{ width: 28, height: Math.round((actualNum / maxVal) * barMaxHeight), backgroundColor: '#1e3a5f', borderRadius: '4px 4px 0 0', minHeight: 4 }} />}
                        {nutrientLegendVisible.icmr && <div style={{ width: 28, height: Math.round((icmrNum / maxVal) * barMaxHeight), backgroundColor: '#9ca3af', borderRadius: '4px 4px 0 0', minHeight: 4 }} />}
                        {nutrientLegendVisible.doctor && <div style={{ width: 28, height: Math.round((doctorNum / maxVal) * barMaxHeight), backgroundColor: '#93c5fd', borderRadius: '4px 4px 0 0', minHeight: 4 }} />}
                      </div>
                      {showTooltip && (
                        <div
                          style={{
                            position: 'absolute',
                            left: '50%',
                            transform: 'translateX(-50%)',
                            bottom: '100%',
                            marginBottom: 8,
                            padding: '10px 12px',
                            backgroundColor: '#fff',
                            border: '1px solid #e5e7eb',
                            borderRadius: 8,
                            boxShadow: '0 4px 12px rgba(0,0,0,0.1)',
                            fontSize: '0.75rem',
                            zIndex: 10,
                            whiteSpace: 'nowrap',
                            pointerEvents: 'none',
                          }}
                        >
                          <div style={{ fontWeight: 600, color: '#374151', marginBottom: 6 }}>{label}</div>
                          {nutrientLegendVisible.current && <div style={{ color: '#1e3a5f', marginBottom: 2 }}>CURRENT PLAN : {fmt(actualNum)}</div>}
                          {nutrientLegendVisible.icmr && <div style={{ color: '#6b7280', marginBottom: 2 }}>ICMR TARGET : {fmt(icmrNum)}</div>}
                          {nutrientLegendVisible.doctor && <div style={{ color: '#2563eb' }}>DR. APPROVED : {fmt(doctorNum)}</div>}
                        </div>
                      )}
                      <div style={{ marginTop: 6, fontSize: '0.85rem', fontWeight: 600, color: status.color }}>{status.label}</div>
                    </div>
                  );
                })}
              </div>
              <div style={{ borderTop: '1px solid #e5e7eb', marginTop: 24, marginBottom: 24 }} />
              <h3 style={{ marginTop: 0, marginBottom: 16, fontSize: '0.85rem', fontWeight: 700, color: '#374151', letterSpacing: '0.05em' }}>
                MICRONUTRIENTS
              </h3>
              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(2, 1fr)', gap: 24 }}>
                <div>
                  <div style={{ fontSize: '0.75rem', fontWeight: 700, color: '#6b7280', marginBottom: 12, letterSpacing: '0.03em' }}>VITAMINS</div>
                  {vitaminKeys.map((key) => {
                    const actualNum = Number(actuals[key]) || 0;
                    const icmrNum = Number(icmrTargets[key]) || 0;
                    const doctorNum = Number(doctorTargetsRaw[key]) || 0;
                    const dataMax = Math.max(actualNum, icmrNum, doctorNum, 1);
                    const scaleMax = dataMax * 1.15;
                    const pctActual = scaleMax > 0 ? Math.min(100, (actualNum / scaleMax) * 100) : 0;
                    const pctIcmr = scaleMax > 0 ? Math.min(100, (icmrNum / scaleMax) * 100) : 0;
                    const pctDoctor = scaleMax > 0 ? Math.min(100, (doctorNum / scaleMax) * 100) : 0;
                    const allPositions = [
                      { pct: pctActual, val: actualNum, color: '#1e3a5f', fontWeight: 600, visible: nutrientLegendVisible.current },
                      { pct: pctIcmr, val: icmrNum, color: '#6b7280', fontWeight: 400, visible: nutrientLegendVisible.icmr },
                      { pct: pctDoctor, val: doctorNum, color: '#2563eb', fontWeight: 500, visible: nutrientLegendVisible.doctor },
                    ];
                    const positions = allPositions.filter((p) => p.visible).sort((a, b) => a.pct - b.pct);
                    const minGap = 14;
                    const row2: number[] = [];
                    if (positions.length >= 2 && positions[1].pct - positions[0].pct < minGap) row2.push(1);
                    if (positions.length >= 3 && positions[2].pct - positions[1].pct < minGap) row2.push(2);
                    if (row2.length === 0 && positions.length >= 3 && positions[2].pct - positions[0].pct < minGap) row2.push(2);
                    const label = key.replace(/^vitamin_/, 'Vit ').replace(/_/g, ' ').replace(/\b\w/g, (l) => l.toUpperCase()).replace(/Mcg/g, '').replace(/Mg/g, '');
                    return (
                      <div key={key} style={{ marginBottom: 18 }}>
                        <div style={{ marginBottom: 4 }}>
                          <span style={{ fontSize: '0.8rem', fontWeight: 500, color: '#374151' }}>{label}</span>
                        </div>
                        <div style={{ height: 8, backgroundColor: '#e5e7eb', borderRadius: 4, position: 'relative', marginBottom: 20 }}>
                          {nutrientLegendVisible.current && <div style={{ position: 'absolute', left: `${pctActual}%`, top: '50%', transform: 'translate(-50%, -50%)', width: 12, height: 12, borderRadius: '50%', backgroundColor: '#1e3a5f', border: '2px solid #fff', boxShadow: '0 0 0 1px #e5e7eb', zIndex: 3 }} />}
                          {nutrientLegendVisible.icmr && <div style={{ position: 'absolute', left: `${pctIcmr}%`, top: '50%', transform: 'translate(-50%, -50%)', width: 10, height: 10, borderRadius: '50%', backgroundColor: '#9ca3af', border: '2px solid #fff', boxShadow: '0 0 0 1px #e5e7eb', zIndex: 2 }} />}
                          {nutrientLegendVisible.doctor && <div style={{ position: 'absolute', left: `${pctDoctor}%`, top: '50%', transform: 'translate(-50%, -50%)', width: 10, height: 10, borderRadius: '50%', backgroundColor: '#93c5fd', border: '2px solid #fff', boxShadow: '0 0 0 1px #e5e7eb', zIndex: 2 }} />}
                          {positions.map((pos, idx) => (
                            <div
                              key={idx}
                              style={{
                                position: 'absolute',
                                left: `${pos.pct}%`,
                                transform: 'translateX(-50%)',
                                top: row2.includes(idx) ? 14 : -14,
                                fontSize: '0.65rem',
                                whiteSpace: 'nowrap',
                                color: pos.color,
                                fontWeight: pos.fontWeight,
                              }}
                            >
                              {fmt(pos.val)}
                            </div>
                          ))}
                        </div>
                      </div>
                    );
                  })}
                </div>
                <div>
                  <div style={{ fontSize: '0.75rem', fontWeight: 700, color: '#6b7280', marginBottom: 12, letterSpacing: '0.03em' }}>MINERALS</div>
                  {mineralKeys.map((key) => {
                    const actualNum = Number(actuals[key]) || 0;
                    const icmrNum = Number(icmrTargets[key]) || 0;
                    const doctorNum = Number(doctorTargetsRaw[key]) || 0;
                    const dataMax = Math.max(actualNum, icmrNum, doctorNum, 1);
                    const scaleMax = dataMax * 1.15;
                    const pctActual = scaleMax > 0 ? Math.min(100, (actualNum / scaleMax) * 100) : 0;
                    const pctIcmr = scaleMax > 0 ? Math.min(100, (icmrNum / scaleMax) * 100) : 0;
                    const pctDoctor = scaleMax > 0 ? Math.min(100, (doctorNum / scaleMax) * 100) : 0;
                    const allPositions = [
                      { pct: pctActual, val: actualNum, color: '#1e3a5f', fontWeight: 600, visible: nutrientLegendVisible.current },
                      { pct: pctIcmr, val: icmrNum, color: '#6b7280', fontWeight: 400, visible: nutrientLegendVisible.icmr },
                      { pct: pctDoctor, val: doctorNum, color: '#2563eb', fontWeight: 500, visible: nutrientLegendVisible.doctor },
                    ];
                    const positions = allPositions.filter((p) => p.visible).sort((a, b) => a.pct - b.pct);
                    const minGap = 14;
                    const row2: number[] = [];
                    if (positions.length >= 2 && positions[1].pct - positions[0].pct < minGap) row2.push(1);
                    if (positions.length >= 3 && positions[2].pct - positions[1].pct < minGap) row2.push(2);
                    if (row2.length === 0 && positions.length >= 3 && positions[2].pct - positions[0].pct < minGap) row2.push(2);
                    const label = key.replace(/_mg$/, '').replace(/_mcg$/, '').replace(/_/g, ' ').replace(/\b\w/g, (l) => l.toUpperCase());
                    return (
                      <div key={key} style={{ marginBottom: 18 }}>
                        <div style={{ marginBottom: 4 }}>
                          <span style={{ fontSize: '0.8rem', fontWeight: 500, color: '#374151' }}>{label}</span>
                        </div>
                        <div style={{ height: 8, backgroundColor: '#e5e7eb', borderRadius: 4, position: 'relative', marginBottom: 20 }}>
                          {nutrientLegendVisible.current && <div style={{ position: 'absolute', left: `${pctActual}%`, top: '50%', transform: 'translate(-50%, -50%)', width: 12, height: 12, borderRadius: '50%', backgroundColor: '#1e3a5f', border: '2px solid #fff', boxShadow: '0 0 0 1px #e5e7eb', zIndex: 3 }} />}
                          {nutrientLegendVisible.icmr && <div style={{ position: 'absolute', left: `${pctIcmr}%`, top: '50%', transform: 'translate(-50%, -50%)', width: 10, height: 10, borderRadius: '50%', backgroundColor: '#9ca3af', border: '2px solid #fff', boxShadow: '0 0 0 1px #e5e7eb', zIndex: 2 }} />}
                          {nutrientLegendVisible.doctor && <div style={{ position: 'absolute', left: `${pctDoctor}%`, top: '50%', transform: 'translate(-50%, -50%)', width: 10, height: 10, borderRadius: '50%', backgroundColor: '#93c5fd', border: '2px solid #fff', boxShadow: '0 0 0 1px #e5e7eb', zIndex: 2 }} />}
                          {positions.map((pos, idx) => (
                            <div
                              key={idx}
                              style={{
                                position: 'absolute',
                                left: `${pos.pct}%`,
                                transform: 'translateX(-50%)',
                                top: row2.includes(idx) ? 14 : -14,
                                fontSize: '0.65rem',
                                whiteSpace: 'nowrap',
                                color: pos.color,
                                fontWeight: pos.fontWeight,
                              }}
                            >
                              {fmt(pos.val)}
                            </div>
                          ))}
                        </div>
                      </div>
                    );
                  })}
                </div>
              </div>
              {summary.days_count ? (
                <div style={{ marginTop: 20, padding: 12, backgroundColor: '#f0f9ff', borderRadius: 6, fontSize: '0.9rem' }}>
                  <strong>Note:</strong> Values shown are daily averages for the uploaded plan across{' '}
                  {summary.days_count} day{summary.days_count !== 1 ? 's' : ''}.
                </div>
              ) : null}
            </>
          );
        })() : (
          <div style={{ padding: 24, textAlign: 'center', color: '#6b7280', fontSize: '0.95rem' }}>
            {emptyMessage}
          </div>
        )}
      </div>
    );
  };

  /** Renders the nutrient comparison as a table (Generated Plan, Meal Plan Target, Difference, Status). Used when toggle is "Table". */
  const renderNutrientComparisonTable = (summary: MealPlanTotalsSummary) => {
    const mealPlanNutrients = new Set(Object.keys(summary.meal_plan_totals || {}));
    const allTotals = new Set(Object.keys(summary.totals || {}));
    const compared = new Set(Object.keys(summary.comparison || {}));
    const combined = Array.from(mealPlanNutrients).concat(Array.from(allTotals), Array.from(compared));
    const allNutrients = Array.from(new Set(combined)).filter((n) => {
      if (allTotals.has('calories') && (n === 'energy_kcal' || n === 'calories_kcal')) return false;
      if (allTotals.has('carbs_g') && n === 'carbohydrates_g') return false;
      if (allTotals.has('folate_mcg') && n === 'vitamin_b9_folate_mcg') return false;
      return true;
    });
    const sorted = allNutrients.sort((a, b) => {
      const ai = NUTRIENT_ORDER.indexOf(a);
      const bi = NUTRIENT_ORDER.indexOf(b);
      if (ai === -1 && bi === -1) return a.localeCompare(b);
      if (ai === -1) return 1;
      if (bi === -1) return -1;
      return ai - bi;
    });
    return (
      <div className="np-subcard" style={{ marginBottom: 16, padding: 16 }}>
        <div style={{ overflowX: 'auto' }}>
          <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '0.9rem' }}>
            <thead>
              <tr style={{ borderBottom: '2px solid #e5e7eb', backgroundColor: '#f9fafb' }}>
                <th style={{ padding: 12, textAlign: 'left', fontWeight: 600 }}>Nutrient</th>
                <th style={{ padding: 12, textAlign: 'right', fontWeight: 600 }}>Generated Plan</th>
                <th style={{ padding: 12, textAlign: 'right', fontWeight: 600 }}>Meal Plan Target</th>
                <th style={{ padding: 12, textAlign: 'right', fontWeight: 600 }}>Difference</th>
                <th style={{ padding: 12, textAlign: 'center', fontWeight: 600 }}>Status</th>
              </tr>
            </thead>
            <tbody>
              {sorted.map((nutrient) => {
                const actual = summary.totals[nutrient] || 0;
                let target: number | null = summary.meal_plan_totals?.[nutrient] ?? null;
                if (target == null) target = summary.comparison?.[nutrient]?.target ?? null;
                if (target == null && nutrient === 'calories') target = summary.meal_plan_totals?.['calories_kcal'] ?? summary.meal_plan_totals?.['energy_kcal'] ?? null;
                if (typeof target === 'string') target = parseFloat(target) || null;
                const comp = summary.comparison?.[nutrient];
                const diff = target !== null ? actual - target : null;
                const diffPct = target !== null && target > 0 ? (diff! / target) * 100 : null;
                const statusColor = !comp?.status ? '#6b7280' : comp.status === 'met' ? '#10b981' : comp.status === 'over' ? '#f59e0b' : '#ef4444';
                const statusLabel = !comp?.status ? '-' : comp.status === 'met' ? '✓ Met' : comp.status === 'over' ? '↑ Over' : '↓ Under';
                return (
                  <tr key={nutrient} style={{ borderBottom: '1px solid #e5e7eb' }}>
                    <td style={{ padding: '10px 12px', fontWeight: 500 }}>{nutrient.replace(/_/g, ' ').replace(/\b\w/g, (l: string) => l.toUpperCase())}</td>
                    <td style={{ padding: '10px 12px', textAlign: 'right' }}>{actual.toFixed(1)}</td>
                    <td style={{ padding: '10px 12px', textAlign: 'right' }}>{target !== null ? target.toFixed(1) : '-'}</td>
                    <td style={{ padding: '10px 12px', textAlign: 'right' }}>{diff !== null ? `${diff > 0 ? '+' : ''}${diff.toFixed(1)}${diffPct != null ? ` (${diffPct > 0 ? '+' : ''}${diffPct.toFixed(1)}%)` : ''}` : '-'}</td>
                    <td style={{ padding: '10px 12px', textAlign: 'center', color: statusColor, fontWeight: 600, fontSize: '0.85rem' }}>{statusLabel}</td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      </div>
    );
  };

  // ——— RENDER: List view vs Create flow (Step 1 Settings | Step 2 Daily Meal Plan & Recipes) ———
  return (
    <div className="patient-tab-panel">
      {error && <div className="error-message">{error}</div>}
      {successMessage && (
        <div style={{ padding: 12, backgroundColor: '#d1fae5', color: '#065f46', borderRadius: 8, marginBottom: 16 }}>
          {successMessage}
        </div>
      )}
      {validationErrors.length > 0 && (
        <div className="error-message" style={{ padding: 16 }}>
          <div style={{ fontWeight: 700, marginBottom: 8 }}>Please fix:</div>
          <ul style={{ margin: 0, paddingLeft: 18, textAlign: 'left' }}>
            {validationErrors.map((msg, idx) => (
              <li key={idx}>{msg}</li>
            ))}
          </ul>
        </div>
      )}

      {/* Existing Versions: single common view (filter + table + Back) for both fixed manual and AI-generated. Table always shown so it's never "missing". */}
      {activeStep === 'versions' ? (
        <div className="np-subcard" style={{ marginTop: 0, padding: 16, backgroundColor: '#f3f4f6' }}>
          <div style={{ backgroundColor: '#fff', borderRadius: 12, boxShadow: '0 1px 3px rgba(0,0,0,0.08)', padding: 24 }}>
            {error && (
              <div className="error-message" style={{ marginBottom: 12 }}>
                {error}
              </div>
            )}
            {/* Top row: Back left; filter (when plan selected) and Create new version right */}
            <div style={{ marginBottom: 16, display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 16, flexWrap: 'wrap' }}>
              <button
                type="button"
                className="secondary-button"
                onClick={() => {
                  if (mealsBasePath) {
                    navigate(mealsBasePath);
                    return;
                  }
                  setFixedManualSubView('versions');
                }}
                disabled={loading}
              >
                Back
              </button>
              <div style={{ display: 'flex', alignItems: 'center', gap: 12, flexWrap: 'wrap' }}>
                {selectedPlanId && (
                  <input
                    type="text"
                    placeholder="Filter by version, date, or status…"
                    value={versionsTableFilter}
                    onChange={(e) => setVersionsTableFilter(e.target.value)}
                    style={{
                      width: '100%',
                      maxWidth: 320,
                      padding: '10px 14px',
                      border: '1px solid #e5e7eb',
                      borderRadius: 8,
                      fontSize: '0.9rem',
                    }}
                    aria-label="Filter versions table"
                  />
                )}
                {isFixedManual && (
                  <button
                    type="button"
                    className="primary-button"
                    onClick={() => void handleCreateNewDraft()}
                    disabled={loading}
                    style={{ display: 'inline-flex', alignItems: 'center', gap: 8 }}
                  >
                    {loading ? 'Creating…' : 'Create new version'}
                  </button>
                )}
              </div>
            </div>
            {/* Version table: always visible so it's never "missing" after Next */}
            <div style={{ overflowX: 'auto', marginBottom: 24 }}>
              <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '0.9rem' }}>
                <thead>
                  <tr style={{ backgroundColor: '#f9fafb', borderBottom: '2px solid #e5e7eb' }}>
                    <th style={{ padding: '12px 16px', textAlign: 'left', fontWeight: 600 }}>Version</th>
                    <th style={{ padding: '12px 16px', textAlign: 'left', fontWeight: 600 }}>Date</th>
                    <th style={{ padding: '12px 16px', textAlign: 'left', fontWeight: 600 }}>Status</th>
                    <th style={{ padding: '12px 16px', width: 32 }} />
                    <th style={{ padding: '12px 16px', width: 80, textAlign: 'right', fontWeight: 600 }}>Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {(!selectedPlanId || fixedManualVersions.length === 0) ? (
                    <tr>
                      <td colSpan={5} style={{ padding: 24, textAlign: 'center', color: '#6b7280', fontSize: '0.9rem' }}>
                        {!selectedPlanId
                          ? 'No plan selected. Click "Create new version" to create your meal plan and first version.'
                          : 'No versions yet. Click "Create new version" to add one.'}
                      </td>
                    </tr>
                  ) : (
                    fixedManualVersions
                          .sort((a, b) => b.version - a.version)
                          .filter((v) => {
                            if (!versionsTableFilter.trim()) return true;
                            const q = versionsTableFilter.trim().toLowerCase();
                            const versionLabel = `version ${v.version_id.slice(-5)}`;
                            const dateStr = v.created_at ? new Date(v.created_at).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' }).toLowerCase() : '';
                            const statusLabel = (v.status ?? '').toLowerCase();
                            return versionLabel.includes(q) || dateStr.includes(q) || statusLabel.includes(q);
                          })
                          .map((v) => {
                            const isCurrent = isCurrentVersion(v.status);
                            return (
                              <tr
                                key={v.version_id}
                                style={{ borderBottom: '1px solid #e5e7eb', cursor: 'pointer' }}
                                onClick={async () => {
                                  setFixedManualSelectedVersionId(v.version_id);
                                  if (!selectedPlanId) return;
                                  try {
                                    const ver = await mealPlanService.getVersion(selectedPlanId, v.version_id);
                                    setFixedManualSelectedVersion(ver);
                                    try {
                                      const summary = await mealPlanService.getTotalsSummary(selectedPlanId, v.version_id, goalId);
                                      setFixedManualTotalsSummary(summary);
                                    } catch (e2) {
                                      console.debug('Fixed manual totals summary not available:', e2);
                                    }
                                    setExistingPlanDetailTab('nutrient_comparison');
                                    setShowCreateForm(true);
                                    // Use version status (version API) to decide upload vs meal_plan view for fixed manual.
                                    const versionStatus = (v.status ?? (v as any).workflow_state ?? '').toString().toLowerCase();
                                    if (versionStatus === 'draft_upload' || versionStatus === 'draft') {
                                      setActiveStep('upload');
                                      setUploadedSessionId(null);
                                      setChatStatus(null);
                                      if (versionStatus === 'draft') {
                                        mealPlanService.updateVersion(selectedPlanId, v.version_id, { status: 'draft_upload' }).catch(() => {});
                                      }
                                    } else if (versionStatus === 'in_progress') {
                                      setActiveStep('meal_plan');
                                      try {
                                        const status = await mealPlanService.getChatStatus(selectedPlanId, undefined, v.version_id);
                                        const hasActiveSession =
                                          status?.session_id != null &&
                                          status.workflow_task_status !== 'cancelled' &&
                                          status.workflow_task_status !== 'completed' &&
                                          status.status !== 'approved' &&
                                          !status.plan_approved;
                                        if (hasActiveSession) {
                                          const atTableVerify =
                                            (status as any)?.waiting_for_table_verify === true ||
                                            status?.current_step === 'present_plan_to_doctor';
                                          setActiveStep(atTableVerify ? 'meal_plan' : 'upload');
                                          setUploadedSessionId(status.session_id);
                                          const normalizedPlanPreview =
                                            typeof (status as any)?.plan_preview === 'string'
                                              ? (() => {
                                                  try {
                                                    return JSON.parse((status as any).plan_preview);
                                                  } catch {
                                                    return (status as any).plan_preview;
                                                  }
                                                })()
                                              : (status as any)?.plan_preview;
                                          setChatStatus({ ...status, plan_preview: normalizedPlanPreview });
                                          if (status.current_step && uploadBackendStepIndexByKey[status.current_step as keyof typeof uploadBackendStepIndexByKey] != null) {
                                            setUploadBackendStepIndex(uploadBackendStepIndexByKey[status.current_step as keyof typeof uploadBackendStepIndexByKey]);
                                          }
                                          const progressRaw = (status as any)?.current_progress;
                                          const progressNum = typeof progressRaw === 'number' ? progressRaw : progressRaw != null ? parseFloat(String(progressRaw)) : NaN;
                                          if (Number.isFinite(progressNum)) setCurrentProgress(progressNum);
                                        } else {
                                          setUploadedSessionId(null);
                                          setChatStatus(status ?? null);
                                        }
                                      } catch (eChat) {
                                        console.debug('Chat status for version:', eChat);
                                        setUploadedSessionId(null);
                                      }
                                    } else {
                                      setActiveStep('meal_plan');
                                      try {
                                        const status = await mealPlanService.getChatStatus(selectedPlanId, undefined, v.version_id);
                                        const hasActiveSession =
                                          status?.session_id != null &&
                                          status.workflow_task_status !== 'cancelled' &&
                                          status.workflow_task_status !== 'completed' &&
                                          status.status !== 'approved' &&
                                          !status.plan_approved;
                                        if (hasActiveSession) {
                                          const atTableVerify =
                                            (status as any)?.waiting_for_table_verify === true ||
                                            status?.current_step === 'present_plan_to_doctor';
                                          setActiveStep(atTableVerify ? 'meal_plan' : 'upload');
                                          setUploadedSessionId(status.session_id);
                                          const normalizedPlanPreview =
                                            typeof (status as any)?.plan_preview === 'string'
                                              ? (() => {
                                                  try {
                                                    return JSON.parse((status as any).plan_preview);
                                                  } catch {
                                                    return (status as any).plan_preview;
                                                  }
                                                })()
                                              : (status as any)?.plan_preview;
                                          setChatStatus({ ...status, plan_preview: normalizedPlanPreview });
                                          if (status.current_step && uploadBackendStepIndexByKey[status.current_step as keyof typeof uploadBackendStepIndexByKey] != null) {
                                            setUploadBackendStepIndex(uploadBackendStepIndexByKey[status.current_step as keyof typeof uploadBackendStepIndexByKey]);
                                          }
                                          const progressRaw = (status as any)?.current_progress;
                                          const progressNum = typeof progressRaw === 'number' ? progressRaw : progressRaw != null ? parseFloat(String(progressRaw)) : NaN;
                                          if (Number.isFinite(progressNum)) setCurrentProgress(progressNum);
                                        } else {
                                          setUploadedSessionId(null);
                                          setChatStatus(status ?? null);
                                        }
                                      } catch (eChat) {
                                        console.debug('Chat status for version:', eChat);
                                        setUploadedSessionId(null);
                                      }
                                    }
                                  } catch (err) {
                                    console.error('Failed to load selected version:', err);
                                  }
                                }}
                              >
                                <td style={{ padding: '12px 16px' }}>Version {v.version_id.slice(-5)}</td>
                                <td style={{ padding: '12px 16px', color: '#6b7280' }}>
                                  {v.created_at ? new Date(v.created_at).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' }) : '—'}
                                </td>
                                <td style={{ padding: '12px 16px' }}>
                                  <span className={`np-existing-plans-row-pill ${isCurrent ? 'approved' : 'draft'}`} style={{ display: 'inline-block' }}>
                                    {displayStatus(v.status)}
                                  </span>
                                </td>
                                <td style={{ padding: '12px 16px' }}>
                                  <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" style={{ verticalAlign: 'middle' }}>
                                    <polyline points="9 18 15 12 9 6" />
                                  </svg>
                                </td>
                                <td style={{ padding: '12px 16px', textAlign: 'right' }}>
                                  <button
                                    type="button"
                                    className="secondary-button"
                                    onClick={async (e) => {
                                      e.stopPropagation();
                                      const message = isCurrent
                                        ? 'Delete this version? It is the current (active) version. Workflow resources will be released and the plan will be paused. This cannot be undone.'
                                        : 'Delete this version? This cannot be undone.';
                                      if (!window.confirm(message)) return;
                                      if (!selectedPlanId) return;
                                      try {
                                        setLoading(true);
                                        setError(null);
                                        await mealPlanService.deleteVersion(selectedPlanId, v.version_id);
                                        if (fixedManualSelectedVersionId === v.version_id) {
                                          setFixedManualSubView('versions');
                                          setFixedManualSelectedVersionId(null);
                                          setFixedManualSelectedVersion(null);
                                          setFixedManualTotalsSummary(null);
                                        }
                                        const versions = await mealPlanService.listVersions(selectedPlanId);
                                        setFixedManualVersions(versions || []);
                                      } catch (err) {
                                        console.error('Failed to delete version:', err);
                                        setError(getErrorMessage(err) || 'Failed to delete version');
                                      } finally {
                                        setLoading(false);
                                      }
                                    }}
                                    disabled={loading}
                                    title={isCurrent ? 'Delete version and release workflow resources' : 'Delete this version'}
                                    style={{ fontSize: '0.8rem', padding: '4px 10px', color: '#b91c1c', borderColor: '#b91c1c' }}
                                  >
                                    Delete
                                  </button>
                                </td>
                              </tr>
                            );
                          })
                  )}
                </tbody>
              </table>
            </div>
          </div>
        </div>
      ) : !showCreateForm ? (
        // ——— LIST vs VIEW: view URL = only Plan Details; list URL = only list (no card below) ———
        <>
          {mealPlanViewMode === 'view' && mealPlanIdFromUrl && !hideBackToList && (
            <div style={{ marginBottom: 16 }}>
              <button
                type="button"
                className="secondary-button"
                onClick={() => mealsBasePath && navigate(mealsBasePath)}
                style={{ display: 'inline-flex', alignItems: 'center', gap: 8 }}
              >
                <ChevronLeft size={18} strokeWidth={2} />
                <span>Back to list</span>
              </button>
            </div>
          )}
          {!(mealPlanViewMode === 'view' && mealPlanIdFromUrl) && (
        <div className="np-card">
          <div style={{ display: 'flex', justifyContent: 'flex-end', marginBottom: 24 }}>
            <button
              type="button"
              className="primary-button"
              onClick={async () => {
                if (!patientId || !goalId) {
                  setError('Patient and diet plan are required to create a new plan.');
                  return;
                }
                if (mealsBasePath) {
                  setLoading(true);
                  setError(null);
                  try {
                    const endDate = new Date(todayIso);
                    endDate.setDate(endDate.getDate() + 2);
                    const endDateIso = `${endDate.getFullYear()}-${String(endDate.getMonth() + 1).padStart(2, '0')}-${String(endDate.getDate()).padStart(2, '0')}`;
                    const createResp = await mealPlanService.createPlan({
                      patient_id: patientId,
                      goal_id: goalId,
                      mode: 'fixed',
                      variant: 'doctor_manual',
                      language: 'en',
                      cuisine_pattern: 'Indian',
                      meals_per_day: 3,
                      snacks_per_day: 0,
                      meal_timing_json: defaultMealTiming as Record<string, unknown>,
                      name: undefined,
                      start_date: todayIso,
                      end_date: endDateIso,
                    });
                    if (!createResp?.plan_id) {
                      setError('Failed to create plan');
                      return;
                    }
                    navigate(`${mealsBasePath}/new`, { state: { createdPlanId: createResp.plan_id } });
                  } catch (e: unknown) {
                    setError(getErrorMessage(e) || 'Failed to create plan');
                  } finally {
                    setLoading(false);
                  }
                  return;
                }
                isResettingRef.current = true;
                setLoading(true);
                setError(null);
                try {
                  const endDate = new Date(todayIso);
                  endDate.setDate(endDate.getDate() + 2);
                  const endDateIso = `${endDate.getFullYear()}-${String(endDate.getMonth() + 1).padStart(2, '0')}-${String(endDate.getDate()).padStart(2, '0')}`;
                  const createResp = await mealPlanService.createPlan({
                    patient_id: patientId,
                    goal_id: goalId,
                    mode: 'fixed',
                    variant: 'doctor_manual',
                    language: 'en',
                    cuisine_pattern: 'Indian',
                    meals_per_day: 3,
                    snacks_per_day: 0,
                    meal_timing_json: defaultMealTiming as Record<string, unknown>,
                    name: undefined,
                    start_date: todayIso,
                    end_date: endDateIso,
                  });
                  if (!createResp?.plan_id) {
                    setError('Failed to create plan');
                    return;
                  }
                  await loadPlans();
                  React.startTransition(() => {
                    setEditingPlanId(createResp.plan_id);
                    initialSettingsWhenEditingRef.current = {
                      planId: createResp.plan_id,
                      mode: 'fixed',
                      variant: 'doctor_manual',
                      cuisine_pattern: 'Indian',
                      meals_per_day: 3,
                      snacks_per_day: 0,
                      meal_timing_json: defaultMealTiming as Record<string, unknown>,
                      name: '',
                      start_date: todayIso,
                      end_date: endDateIso,
                    };
                    setSelectedPlanId(createResp.plan_id);
                    setDraft(null);
                    setSelectedDraftVersionId(null);
                    setTotalsSummary(null);
                    setDraftVersions([]);
                    setUploadedDocumentId(null);
                    setUploadedSessionId(null);
                    setUploadError(null);
                    setSelectedFile(null);
                    setUploadStepIndex(0);
                    setChatStatus(null);
                    setIcmrComparison(null);
                    setPlanName('');
                    setPlanStartDate(todayIso);
                    setPlanEndDate(endDateIso);
                    setDurationDays(3);
                    setMode('fixed');
                    setVariant('doctor_manual');
                    setCuisinePattern('Indian');
                    setMealsPerDayInput('3');
                    setMealSlots(buildMealSlotsFromTiming(defaultMealTiming, 3, mealTypeValues));
                    setAllowRecipeRepeat(false);
                    setRecipeRepeatPolicy(null);
                    setMaxRepeatsPerDish(2);
                    setRepeatAfterDays(3);
                    setShowCreateForm(true);
                    setActiveStep('settings');
                    setTimeout(() => {
                      isResettingRef.current = false;
                    }, 300);
                  });
                } catch (e: unknown) {
                  setError(getErrorMessage(e) || 'Failed to create plan');
                } finally {
                  setLoading(false);
                }
              }}
              disabled={loading}
              style={{ marginLeft: 0, flexShrink: 0 }}
            >
              + Create New Plan
            </button>
          </div>

          {/* Search and filters — Lucid-style bar: container, search, button-style dropdowns, RESET */}
          <div
            ref={mealPlanFilterBarRef}
            style={{
              display: 'flex',
              flexWrap: 'wrap',
              gap: 12,
              marginBottom: 20,
              alignItems: 'center',
              padding: '12px 16px',
              backgroundColor: '#fff',
              border: '1px solid #e5e7eb',
              borderRadius: 12,
              boxShadow: '0 1px 2px rgba(0,0,0,0.04)',
            }}
          >
            <div style={{ position: 'relative', flex: '1 1 280px', minWidth: 200 }}>
              <Search size={18} style={{ position: 'absolute', left: 12, top: '50%', transform: 'translateY(-50%)', color: '#9ca3af' }} aria-hidden />
              <input
                type="text"
                placeholder="Search by Plan ID or Client Name..."
                value={mealPlanSearch}
                onChange={(e) => { setMealPlanSearch(e.target.value); setMealPlanPage(1); }}
                style={{
                  width: '100%',
                  padding: '10px 12px 10px 40px',
                  border: 'none',
                  borderRadius: 8,
                  fontSize: '0.9rem',
                  background: 'transparent',
                  outline: 'none',
                }}
              />
            </div>
            <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', alignItems: 'center', position: 'relative' }}>
              {/* All Statuses — Lucid-style dropdown trigger */}
              <div style={{ position: 'relative' }}>
                <button
                  type="button"
                  onClick={() => {
                    setMealPlanStatusDropdownOpen((o) => !o);
                    setMealPlanCuisineDropdownOpen(false);
                  }}
                  style={{
                    display: 'inline-flex',
                    alignItems: 'center',
                    gap: 8,
                    padding: '8px 14px',
                    border: '1px solid #e5e7eb',
                    borderRadius: 8,
                    fontSize: '0.9rem',
                    background: '#f9fafb',
                    color: '#374151',
                    cursor: 'pointer',
                    fontWeight: 500,
                  }}
                >
                  <Filter size={16} style={{ flexShrink: 0, color: '#6b7280' }} />
                  <span>{mealPlanStatusFilter === 'all' ? 'All Statuses' : mealPlanStatusFilter.charAt(0).toUpperCase() + mealPlanStatusFilter.slice(1)}</span>
                  <ChevronDown size={16} style={{ flexShrink: 0, opacity: 0.7 }} />
                </button>
                {mealPlanStatusDropdownOpen && (
                  <div
                    style={{
                      position: 'absolute',
                      top: '100%',
                      left: 0,
                      marginTop: 4,
                      minWidth: '100%',
                      backgroundColor: '#fff',
                      border: '1px solid #e5e7eb',
                      borderRadius: 8,
                      boxShadow: '0 4px 12px rgba(0,0,0,0.1)',
                      zIndex: 50,
                      padding: 4,
                    }}
                  >
                    {(['all', 'approved', 'draft', 'pending'] as const).map((opt) => (
                      <button
                        key={opt}
                        type="button"
                        onClick={() => {
                          setMealPlanStatusFilter(opt);
                          setMealPlanPage(1);
                          setMealPlanStatusDropdownOpen(false);
                        }}
                        style={{
                          display: 'block',
                          width: '100%',
                          padding: '8px 12px',
                          border: 'none',
                          borderRadius: 6,
                          background: mealPlanStatusFilter === opt ? '#eff6ff' : 'transparent',
                          color: mealPlanStatusFilter === opt ? '#2563eb' : '#374151',
                          fontSize: '0.9rem',
                          textAlign: 'left',
                          cursor: 'pointer',
                        }}
                      >
                        {opt === 'all' ? 'All Statuses' : opt.charAt(0).toUpperCase() + opt.slice(1)}
                      </button>
                    ))}
                  </div>
                )}
              </div>
              {/* Cuisine Type — Lucid-style dropdown trigger */}
              <div style={{ position: 'relative' }}>
                <button
                  type="button"
                  onClick={() => {
                    setMealPlanCuisineDropdownOpen((o) => !o);
                    setMealPlanStatusDropdownOpen(false);
                  }}
                  style={{
                    display: 'inline-flex',
                    alignItems: 'center',
                    gap: 8,
                    padding: '8px 14px',
                    border: '1px solid #e5e7eb',
                    borderRadius: 8,
                    fontSize: '0.9rem',
                    background: '#f9fafb',
                    color: '#374151',
                    cursor: 'pointer',
                    fontWeight: 500,
                  }}
                >
                  <UtensilsCrossed size={16} style={{ flexShrink: 0, color: '#6b7280' }} />
                  <span>{mealPlanCuisineFilter === 'all' ? 'Cuisine Type' : mealPlanCuisineFilter}</span>
                  <ChevronDown size={16} style={{ flexShrink: 0, opacity: 0.7 }} />
                </button>
                {mealPlanCuisineDropdownOpen && (
                  <div
                    style={{
                      position: 'absolute',
                      top: '100%',
                      left: 0,
                      marginTop: 4,
                      minWidth: '100%',
                      maxHeight: 240,
                      overflowY: 'auto',
                      backgroundColor: '#fff',
                      border: '1px solid #e5e7eb',
                      borderRadius: 8,
                      boxShadow: '0 4px 12px rgba(0,0,0,0.1)',
                      zIndex: 50,
                      padding: 4,
                    }}
                  >
                    <button
                      type="button"
                      onClick={() => {
                        setMealPlanCuisineFilter('all');
                        setMealPlanPage(1);
                        setMealPlanCuisineDropdownOpen(false);
                      }}
                      style={{
                        display: 'block',
                        width: '100%',
                        padding: '8px 12px',
                        border: 'none',
                        borderRadius: 6,
                        background: mealPlanCuisineFilter === 'all' ? '#eff6ff' : 'transparent',
                        color: mealPlanCuisineFilter === 'all' ? '#2563eb' : '#374151',
                        fontSize: '0.9rem',
                        textAlign: 'left',
                        cursor: 'pointer',
                      }}
                    >
                      Cuisine Type
                    </button>
                    {mealPlanCuisineOptions.map((c) => (
                      <button
                        key={c}
                        type="button"
                        onClick={() => {
                          setMealPlanCuisineFilter(c);
                          setMealPlanPage(1);
                          setMealPlanCuisineDropdownOpen(false);
                        }}
                        style={{
                          display: 'block',
                          width: '100%',
                          padding: '8px 12px',
                          border: 'none',
                          borderRadius: 6,
                          background: mealPlanCuisineFilter === c ? '#eff6ff' : 'transparent',
                          color: mealPlanCuisineFilter === c ? '#2563eb' : '#374151',
                          fontSize: '0.9rem',
                          textAlign: 'left',
                          cursor: 'pointer',
                        }}
                      >
                        {c}
                      </button>
                    ))}
                  </div>
                )}
              </div>
              <button
                type="button"
                onClick={() => {
                  setMealPlanSearch('');
                  setMealPlanStatusFilter('all');
                  setMealPlanCuisineFilter('all');
                  setMealPlanPage(1);
                }}
                style={{
                  display: 'inline-flex',
                  alignItems: 'center',
                  gap: 6,
                  padding: '8px 14px',
                  border: '1px solid #e5e7eb',
                  borderRadius: 8,
                  background: '#f9fafb',
                  fontSize: '0.85rem',
                  fontWeight: 600,
                  cursor: 'pointer',
                  color: '#374151',
                }}
              >
                <RotateCcw size={14} /> RESET
              </button>
            </div>
          </div>

          {/* Table */}
          {loading && plansForCurrentDietPlan.length === 0 ? (
            <div style={{ padding: 24, textAlign: 'center', color: '#6b7280' }}>Loading plans...</div>
          ) : plansForCurrentDietPlan.length === 0 ? (
            <div style={{ padding: 24, textAlign: 'center', color: '#6b7280' }}>
              {goalId != null
                ? 'No meal plans for this diet plan yet. Click "Create New Plan" above to create one.'
                : 'No existing plans found. Click "Create New Plan" above to create one.'}
            </div>
          ) : (
            <>
              <div style={{ overflowX: 'auto' }}>
                <table style={{ width: '100%', borderCollapse: 'separate', borderSpacing: 0, fontSize: '0.9rem' }}>
                  <thead>
                    <tr style={{ backgroundColor: '#f9fafb', borderBottom: '2px solid #e5e7eb' }}>
                      <th style={{ padding: '12px 16px', textAlign: 'left', fontWeight: 600, color: '#6b7280', fontSize: '0.75rem', textTransform: 'uppercase' }}>Meal Plan Name</th>
                      <th style={{ padding: '12px 16px', textAlign: 'left', fontWeight: 600, color: '#6b7280', fontSize: '0.75rem', textTransform: 'uppercase' }}>Mode / Variant</th>
                      <th style={{ padding: '12px 16px', textAlign: 'left', fontWeight: 600, color: '#6b7280', fontSize: '0.75rem', textTransform: 'uppercase' }}>Status</th>
                      <th style={{ padding: '12px 16px', textAlign: 'left', fontWeight: 600, color: '#6b7280', fontSize: '0.75rem', textTransform: 'uppercase' }}>Cuisine</th>
                      <th style={{ padding: '12px 16px', textAlign: 'center', fontWeight: 600, color: '#6b7280', fontSize: '0.75rem', textTransform: 'uppercase' }}>Meals per day</th>
                      <th style={{ padding: '12px 16px', textAlign: 'left', fontWeight: 600, color: '#6b7280', fontSize: '0.75rem', textTransform: 'uppercase' }}>Duration</th>
                      <th style={{ padding: '12px 16px', textAlign: 'center', fontWeight: 600, color: '#6b7280', fontSize: '0.75rem', textTransform: 'uppercase' }}>Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    {mealPlanPaginatedList.map((p) => {
                      const canDelete = p.status !== 'active';
                      const isSelected = p.plan_id === selectedPlanId;
                      const statusDisplay = planStatusDisplay(p.status);
                      const statusPillStyle =
                        statusDisplay === 'Approved'
                          ? { bg: '#d1fae5', color: '#065f46' }
                          : statusDisplay === 'Draft'
                            ? { bg: '#fef3c7', color: '#92400e' }
                            : { bg: '#dbeafe', color: '#1e40af' };

                      const openViewer = () => {
                        if (mealsBasePath) {
                          navigate(`${mealsBasePath}/${p.plan_id}`);
                          return;
                        }
                        navigate(`/patients/${patientId}/daily-meals-plan/${p.plan_id}`);
                      };

                      const openEdit = (e: React.MouseEvent) => {
                        e.stopPropagation();
                        e.preventDefault();
                        // Always load settings view (Edit Plan page): never go to version or view page.
                        const planGoalId = (p as any).goal_id ?? goalId;
                        if (patientId && planGoalId != null) {
                          navigate(`/patients/${patientId}/diet-plan/${planGoalId}/daily-meals/${p.plan_id}/edit`, { replace: false });
                          return;
                        }
                        if (patientId && goalId == null) {
                          navigate(`/patients/${patientId}?tab=mealPlan&editPlan=${p.plan_id}&showMealPlans=true`, { replace: false });
                        }
                        setSelectedPlanId(p.plan_id);
                        setEditingPlanId(p.plan_id);
                        openedViaEditPlanRef.current = true;
                        setMode(p.mode as MealPlanMode);
                        setVariant(p.variant || (p.mode === 'adaptive' ? 'optimize_existing' : 'doctor_manual'));
                        setCuisinePattern(p.cuisine_pattern || 'Indian');
                        const planMeals = p.meals_per_day || 3;
                        setMealsPerDayInput(String(planMeals));
                        setMealSlots(buildMealSlotsFromTiming((p.meal_timing_json as Record<string, string>) || defaultMealTiming, planMeals, mealTypeValues));
                        setPlanName(p.name || '');
                        setPlanStartDate(p.start_date || '');
                        setPlanEndDate(p.end_date || '');
                        initialSettingsWhenEditingRef.current = {
                          planId: p.plan_id,
                          mode: p.mode as string,
                          variant: p.variant || (p.mode === 'adaptive' ? 'optimize_existing' : 'doctor_manual'),
                          cuisine_pattern: p.cuisine_pattern || '',
                          meals_per_day: p.meals_per_day ?? 3,
                          snacks_per_day: p.snacks_per_day ?? 0,
                          meal_timing_json: (p.meal_timing_json as Record<string, unknown>) || {},
                          name: p.name || '',
                          start_date: p.start_date || '',
                          end_date: p.end_date || '',
                        };
                        setShowCreateForm(true);
                        setActiveStep('settings');
                        setActiveStep('meal_plan');
                      };

                      return (
                        <tr
                          key={p.plan_id}
                          style={{
                            borderBottom: '1px solid #e5e7eb',
                            backgroundColor: isSelected ? '#eff6ff' : undefined,
                            cursor: 'pointer',
                          }}
                          onClick={(e) => {
                            if ((e.target as HTMLElement).closest('button')) return;
                            e.preventDefault();
                            openViewer();
                          }}
                        >
                          <td style={{ padding: '12px 16px' }}>
                            <button
                              type="button"
                              onClick={(e) => { e.stopPropagation(); openViewer(); }}
                              style={{
                                background: 'none',
                                border: 'none',
                                padding: 0,
                                cursor: 'pointer',
                                color: '#2563eb',
                                fontSize: '0.9rem',
                                fontWeight: 500,
                                textDecoration: 'underline',
                              }}
                            >
                              {p.name || `${p.mode} - ${p.variant || 'Default'}` || 'Unnamed plan'}
                            </button>
                          </td>
                          <td style={{ padding: '12px 16px' }}>
                            <div style={{ fontSize: '0.85rem', color: '#374151' }}>{p.mode}/{p.variant || '-'}</div>
                          </td>
                          <td style={{ padding: '12px 16px' }}>
                            <span
                              style={{
                                display: 'inline-flex',
                                alignItems: 'center',
                                gap: 6,
                                padding: '4px 10px',
                                borderRadius: 9999,
                                fontSize: '0.8rem',
                                fontWeight: 600,
                                backgroundColor: statusPillStyle.bg,
                                color: statusPillStyle.color,
                              }}
                            >
                              <span style={{ width: 6, height: 6, borderRadius: '50%', backgroundColor: statusPillStyle.color }} />
                              {statusDisplay}
                            </span>
                          </td>
                          <td style={{ padding: '12px 16px', color: '#374151' }}>{p.cuisine_pattern || '-'}</td>
                          <td style={{ padding: '12px 16px', textAlign: 'center', color: '#374151' }}>
                            {(p.meals_per_day ?? 0) + (p.snacks_per_day ?? 0)}
                          </td>
                          <td style={{ padding: '12px 16px', fontSize: '0.85rem', color: '#6b7280' }}>
                            {p.start_date && p.end_date
                              ? `${new Date(p.start_date).toLocaleDateString()} - ${new Date(p.end_date).toLocaleDateString()}`
                              : '-'}
                          </td>
                          <td style={{ padding: '12px 16px', textAlign: 'center' }}>
                            <div style={{ display: 'flex', gap: 4, justifyContent: 'center' }} onClick={(e) => e.stopPropagation()}>
                              <button type="button" onClick={(e) => { e.stopPropagation(); openViewer(); }} disabled={loading} title="View" style={{ padding: 6, border: 'none', borderRadius: 6, background: '#f3f4f6', color: '#374151', cursor: loading ? 'not-allowed' : 'pointer' }}><Eye size={16} /></button>
                              <button type="button" onClick={openEdit} disabled={loading} title="Edit" style={{ padding: 6, border: 'none', borderRadius: 6, background: '#eff6ff', color: '#2563eb', cursor: loading ? 'not-allowed' : 'pointer' }}><Pencil size={16} /></button>
                              {canDelete && (
                                <button
                                  type="button"
                                  title="Delete"
                                  disabled={loading}
                                  onClick={async (e) => {
                                    e.stopPropagation();
                                    if (window.confirm('Are you sure you want to delete this plan? This action cannot be undone.')) {
                                      try {
                                        setLoading(true);
                                        setError(null);
                                        await mealPlanService.deletePlan(p.plan_id);
                                        await loadPlans();
                                        if (selectedPlanId === p.plan_id) setSelectedPlanId(null);
                                        setSuccessMessage('Plan deleted successfully');
                                        setTimeout(() => setSuccessMessage(null), 3000);
                                      } catch (err: any) {
                                        setError(getErrorMessage(err) || 'Failed to delete plan');
                                      } finally {
                                        setLoading(false);
                                      }
                                    }
                                  }}
                                  style={{ padding: 6, border: 'none', borderRadius: 6, background: '#fef2f2', color: '#dc2626', cursor: loading ? 'not-allowed' : 'pointer' }}
                                >
                                  <Trash2 size={16} />
                                </button>
                              )}
                            </div>
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>

              {/* Pagination */}
              {mealPlanTotalCount > 0 && (
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginTop: 16, paddingTop: 16, borderTop: '1px solid #e5e7eb', flexWrap: 'wrap', gap: 12 }}>
                  <span style={{ fontSize: '0.875rem', color: '#6b7280' }}>
                    Showing {(mealPlanPageSafe - 1) * MEAL_PLAN_PAGE_SIZE + 1} to {Math.min(mealPlanPageSafe * MEAL_PLAN_PAGE_SIZE, mealPlanTotalCount)} of {mealPlanTotalCount} results
                  </span>
                  <div style={{ display: 'flex', alignItems: 'center', gap: 4 }}>
                    <button
                      type="button"
                      onClick={() => setMealPlanPage(1)}
                      disabled={mealPlanPageSafe <= 1}
                      title="First page"
                      style={{ padding: '6px 10px', border: '1px solid #e5e7eb', borderRadius: 6, background: 'white', cursor: mealPlanPageSafe <= 1 ? 'not-allowed' : 'pointer', opacity: mealPlanPageSafe <= 1 ? 0.5 : 1, fontSize: '1rem' }}
                    >
                      &#171;
                    </button>
                    <button
                      type="button"
                      onClick={() => setMealPlanPage((x) => Math.max(1, x - 1))}
                      disabled={mealPlanPageSafe <= 1}
                      style={{ padding: '6px 10px', border: '1px solid #e5e7eb', borderRadius: 6, background: 'white', cursor: mealPlanPageSafe <= 1 ? 'not-allowed' : 'pointer', opacity: mealPlanPageSafe <= 1 ? 0.5 : 1 }}
                    >
                      <ChevronLeft size={18} />
                    </button>
                    {Array.from({ length: Math.min(5, mealPlanTotalPages) }, (_, i) => {
                      let pageNum: number;
                      if (mealPlanTotalPages <= 5) pageNum = i + 1;
                      else if (mealPlanPageSafe <= 3) pageNum = i + 1;
                      else if (mealPlanPageSafe >= mealPlanTotalPages - 2) pageNum = mealPlanTotalPages - 4 + i;
                      else pageNum = mealPlanPageSafe - 2 + i;
                      return (
                        <button
                          key={pageNum}
                          type="button"
                          onClick={() => setMealPlanPage(pageNum)}
                          style={{
                            minWidth: 36,
                            padding: '6px 10px',
                            border: '1px solid #e5e7eb',
                            borderRadius: 6,
                            background: mealPlanPage === pageNum ? '#2563eb' : 'white',
                            color: mealPlanPage === pageNum ? 'white' : '#374151',
                            cursor: 'pointer',
                            fontWeight: mealPlanPage === pageNum ? 600 : 400,
                          }}
                        >
                          {pageNum}
                        </button>
                      );
                    })}
                    <button
                      type="button"
                      onClick={() => setMealPlanPage((x) => Math.min(mealPlanTotalPages, x + 1))}
                      disabled={mealPlanPageSafe >= mealPlanTotalPages}
                      style={{ padding: '6px 10px', border: '1px solid #e5e7eb', borderRadius: 6, background: 'white', cursor: mealPlanPageSafe >= mealPlanTotalPages ? 'not-allowed' : 'pointer', opacity: mealPlanPageSafe >= mealPlanTotalPages ? 0.5 : 1 }}
                    >
                      <ChevronRight size={18} />
                    </button>
                    <button
                      type="button"
                      onClick={() => setMealPlanPage(mealPlanTotalPages)}
                      disabled={mealPlanPageSafe >= mealPlanTotalPages}
                      title="Last page"
                      style={{ padding: '6px 10px', border: '1px solid #e5e7eb', borderRadius: 6, background: 'white', cursor: mealPlanPageSafe >= mealPlanTotalPages ? 'not-allowed' : 'pointer', opacity: mealPlanPageSafe >= mealPlanTotalPages ? 0.5 : 1, fontSize: '1rem' }}
                    >
                      &#187;
                    </button>
                  </div>
                </div>
              )}
            </>
          )}
        </div>
          )}
              {/* Plan Details Card: on view page only (or legacy in-page view when !mealPlanViewMode) */}
              {viewedPlanId && (mealPlanViewMode === 'view' || !mealPlanViewMode) && (() => {
                const viewedPlan = plans.find(p => p.plan_id === viewedPlanId);
                if (!viewedPlan) return null;
                
                const canPause = viewedPlan.status === 'active';
                const canArchive = viewedPlan.status === 'active' || viewedPlan.status === 'paused';
                const canDelete = viewedPlan.status !== 'active';
                
                return (
                  <div className="np-card plan-details-card-lucide" style={{ marginTop: 0 }}>
                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: 12, marginBottom: 16 }}>
                      <h3 style={{ margin: 0 }}>Plan Details</h3>
                      <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
                        <button type="button" onClick={handleViewPdfViewDetails} disabled={!!viewDetailsPdfLoading} className="secondary-button" style={{ display: 'inline-flex', alignItems: 'center', gap: 6, padding: '8px 12px', fontSize: '0.875rem' }}>
                          <FileText size={16} />{viewDetailsPdfLoading === 'view' ? 'Opening…' : 'Generate and View'}
                        </button>
                        <button type="button" onClick={handleSendViaWhatsAppViewDetails} disabled={whatsappSending || !!viewDetailsPdfLoading} className="primary-button" style={{ display: 'inline-flex', alignItems: 'center', gap: 6, padding: '8px 12px', fontSize: '0.875rem' }}>
                          <MessageCircle size={16} />{whatsappSending ? 'Sending…' : 'Send diet plan via WhatsApp'}
                        </button>
                        <button type="button" onClick={handleDownloadPdfViewDetails} disabled={!!viewDetailsPdfLoading} className="secondary-button" style={{ display: 'inline-flex', alignItems: 'center', gap: 6, padding: '8px 12px', fontSize: '0.875rem' }}>
                          <Download size={16} />{viewDetailsPdfLoading === 'download' ? 'Downloading…' : 'Download PDF'}
                        </button>
                      </div>
                    </div>

                    {/* Tabs — Lucide icons */}
                    <div className="patient-tabs" style={{ marginBottom: 16 }}>
                      <button
                        type="button"
                        className={`patient-tab ${viewTab === 'settings' ? 'active' : ''}`}
                        onClick={() => setViewTab('settings')}
                        style={{ display: 'inline-flex', alignItems: 'center', gap: 8 }}
                      >
                        <Settings size={16} strokeWidth={2} />
                        <span>Settings</span>
                      </button>
                      <button
                        type="button"
                        className={`patient-tab ${viewTab === 'details' ? 'active' : ''}`}
                        onClick={() => {
                          setViewTab('details');
                          setExistingPlanDetailTab('overview');
                        }}
                        style={{ display: 'inline-flex', alignItems: 'center', gap: 8 }}
                      >
                        <UtensilsCrossed size={16} strokeWidth={2} />
                        Daily Meal Plan
                      </button>
                    </div>

                    {viewTab === 'settings' && (
                      <>
                    <div className="np-subcard" style={{ marginBottom: 16 }}>
                      <div className="np-subhead">
                        <h4>Plan Information</h4>
                      </div>
                      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', gap: 16, marginTop: 12 }}>
                        <div>
                          <div className="plan-info-label">Name</div>
                          <div className="plan-info-value">{viewedPlan.name || 'No name'}</div>
                        </div>
                        <div>
                          <div className="plan-info-label">Mode / Variant</div>
                          <div className="plan-info-value">{viewedPlan.mode} / {viewedPlan.variant || '-'}</div>
                        </div>
                        <div>
                          <div className="plan-info-label">Status</div>
                          <span
                            className={`plan-info-status-badge ${
                              viewedPlan.status === 'active' ? 'status-active' :
                              viewedPlan.status === 'approved' ? 'status-approved' :
                              viewedPlan.status === 'paused' || viewedPlan.status === 'archived' ? 'status-archived' : 'status-draft'
                            }`}
                          >
                            {displayStatus(viewedPlan.status)}
                          </span>
                        </div>
                        <div>
                          <div className="plan-info-label">Cuisine</div>
                          <div className="plan-info-value">{viewedPlan.cuisine_pattern}</div>
                        </div>
                        <div>
                          <div className="plan-info-label">Meals per day</div>
                          <div className="plan-info-value">{(viewedPlan.meals_per_day ?? 0) + (viewedPlan.snacks_per_day ?? 0)}</div>
                        </div>
                        <div>
                          <div className="plan-info-label">Start date</div>
                          <div className="plan-info-value">{viewedPlan.start_date ? new Date(viewedPlan.start_date).toLocaleDateString() : '-'}</div>
                        </div>
                        <div>
                          <div className="plan-info-label">End date</div>
                          <div className="plan-info-value">{viewedPlan.end_date ? new Date(viewedPlan.end_date).toLocaleDateString() : '-'}</div>
                        </div>
                        <div>
                          <div className="plan-info-label">Approved at</div>
                          <div className="plan-info-value">{viewedPlan.approved_at ? new Date(viewedPlan.approved_at).toLocaleDateString() : '-'}</div>
                        </div>
                        <div>
                          <div className="plan-info-label">Activated at</div>
                          <div className="plan-info-value">{viewedPlan.activated_at ? new Date(viewedPlan.activated_at).toLocaleDateString() : '-'}</div>
                        </div>
                      </div>
                    </div>

                    {viewedPlanVersion && (
                      <div className="np-subcard" style={{ marginBottom: 16 }}>
                        <div className="np-subhead">
                          <h4>Current Version</h4>
                          <span style={{ fontSize: '0.85rem', color: '#6b7280' }}>
                            Version: <code>{viewedPlanVersion.version_id}</code> • Status: {displayStatus(viewedPlanVersion.status)}
                          </span>
                        </div>
                        {viewedPlanVersion.days && viewedPlanVersion.days.length > 0 && (
                          <div style={{ marginTop: 12 }}>
                            <div style={{ fontSize: '0.9rem', color: '#6b7280', marginBottom: 8 }}>
                              {viewedPlanVersion.days.length} day{viewedPlanVersion.days.length !== 1 ? 's' : ''} • {viewedPlanVersion.days.reduce((sum, day) => sum + (day.items?.length || 0), 0)} total meals
                            </div>
                          </div>
                        )}
                        {/* In-progress with empty days: show CTA to open workflow so user sees progress/chat instead of blank */}
                        {(viewedPlanVersion.status === 'in_progress' || (viewedPlanVersion as any).workflow_state === 'in_progress') && (!viewedPlanVersion.days || viewedPlanVersion.days.length === 0) && (
                          <div style={{ marginTop: 16, padding: 16, background: '#f0f9ff', border: '1px solid #bae6fd', borderRadius: 8 }}>
                            <p style={{ margin: '0 0 12px', fontSize: '0.95rem', color: '#075985' }}>This version is still generating. Open the workflow to see progress, complete table review, or approve when ready.</p>
                            <button
                              type="button"
                              className="primary-button"
                              onClick={() => {
                                const pid = viewedPlanId ?? selectedPlanId;
                                const vid = viewedPlanVersion.version_id;
                                if (pid) {
                                  setSelectedPlanId(pid);
                                  setEditingPlanId(pid);
                                  setFixedManualSelectedVersionId(vid);
                                  setActiveStep('meal_plan');
                                  setViewedPlanId(null);
                                  setViewedPlanVersion(null);
                                  setViewedPlanTotalsSummary(null);
                                  if (mealsBasePath) {
                                    navigate(`${mealsBasePath}/${pid}?editPlan=${pid}&step=meal_plan`, { replace: false });
                                  } else {
                                    navigate({ pathname: locationPathname, search: `?tab=mealPlan&editPlan=${pid}&step=meal_plan&showMealPlans=true` }, { replace: false });
                                  }
                                }
                              }}
                              style={{ display: 'inline-flex', alignItems: 'center', gap: 8 }}
                            >
                              Show workflow
                            </button>
                          </div>
                        )}
                      </div>
                    )}

                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: 12, marginTop: 16, paddingTop: 16, borderTop: '1px solid #e5e7eb' }}>
                      <div style={{ display: 'flex', gap: 12, flexWrap: 'wrap', alignItems: 'center' }}>
                        {canDelete && (
                          <button
                            type="button"
                            className="btn-danger-ghost"
                            onClick={async () => {
                              if (window.confirm(`Are you sure you want to delete this plan? This action cannot be undone.`)) {
                                try {
                                  setLoading(true);
                                  setError(null);
                                  await mealPlanService.deletePlan(viewedPlan.plan_id);
                                  await loadPlans();
                                  if (selectedPlanId === viewedPlan.plan_id) {
                                    setSelectedPlanId(null);
                                  }
                                  setViewedPlanId(null);
                                  setViewedPlanVersion(null);
                                  setViewedPlanTotalsSummary(null);
                                  setSuccessMessage('Plan deleted successfully');
                                  setTimeout(() => setSuccessMessage(null), 3000);
                                } catch (err: any) {
                                  setError(getErrorMessage(err) || 'Failed to delete plan');
                                } finally {
                                  setLoading(false);
                                }
                              }
                            }}
                            disabled={loading}
                          >
                            <Trash2 size={16} strokeWidth={2} />
                            <span>Delete Plan</span>
                          </button>
                        )}
                      </div>
                      <div style={{ display: 'flex', gap: 12, flexWrap: 'wrap', alignItems: 'center' }}>
                        <button
                          type="button"
                          className="secondary-button"
                          onClick={() => {
                            if (mealsBasePath) {
                              navigate(mealsBasePath);
                              return;
                            }
                            setViewedPlanId(null);
                            setViewedPlanVersion(null);
                            setViewedPlanTotalsSummary(null);
                            setViewTab('settings');
                          }}
                          style={{ display: 'inline-flex', alignItems: 'center', gap: 8 }}
                        >
                          <span>Cancel</span>
                        </button>
                        <button
                          type="button"
                          onClick={async () => {
                            if (mealsBasePath) {
                              navigate(`${mealsBasePath}/${viewedPlan.plan_id}/edit`);
                              return;
                            }
                            setMode(viewedPlan.mode as MealPlanMode);
                            setVariant(viewedPlan.variant || (viewedPlan.mode === 'adaptive' ? 'optimize_existing' : 'doctor_manual'));
                            setCuisinePattern(viewedPlan.cuisine_pattern);
                            const planMeals = viewedPlan.meals_per_day || 3;
                            setMealsPerDayInput(String(planMeals));
                            setMealSlots(buildMealSlotsFromTiming(viewedPlan.meal_timing_json || defaultMealTiming, planMeals, mealTypeValues));
                            setPlanName(viewedPlan.name || '');
                            setPlanStartDate(viewedPlan.start_date || '');
                            setPlanEndDate(viewedPlan.end_date || '');
                            setSelectedPlanId(viewedPlan.plan_id);
                            setEditingPlanId(viewedPlan.plan_id);
                            initialSettingsWhenEditingRef.current = {
                              planId: viewedPlan.plan_id,
                              mode: viewedPlan.mode as string,
                              variant: viewedPlan.variant || (viewedPlan.mode === 'adaptive' ? 'optimize_existing' : 'doctor_manual'),
                              cuisine_pattern: viewedPlan.cuisine_pattern || '',
                              meals_per_day: viewedPlan.meals_per_day ?? 3,
                              snacks_per_day: viewedPlan.snacks_per_day ?? 0,
                              meal_timing_json: (viewedPlan.meal_timing_json as Record<string, unknown>) || {},
                              name: viewedPlan.name || '',
                              start_date: viewedPlan.start_date || '',
                              end_date: viewedPlan.end_date || '',
                            };
                            setShowCreateForm(true);
                            setActiveStep('settings');
                            setViewedPlanId(null);
                            setViewedPlanVersion(null);
                            setViewedPlanTotalsSummary(null);
                            if (viewedPlan.status === 'approved' || viewedPlan.status === 'active' || viewedPlan.approved_at) {
                              try {
                                setLoading(true);
                                const currentVersion = await mealPlanService.getCurrentVersion(viewedPlan.plan_id);
                                setDraft({
                                  plan_id: currentVersion.plan_id,
                                  version_id: currentVersion.version_id,
                                  status: currentVersion.status,
                                  days: currentVersion.days,
                                });
                                setSelectedDraftVersionId(currentVersion.version_id);
                              } catch (err: any) {
                                console.error('Failed to load current version:', err);
                              } finally {
                                setLoading(false);
                              }
                            }
                          }}
                          disabled={loading}
                          className="primary-button"
                          style={{ display: 'inline-flex', alignItems: 'center', gap: 8 }}
                        >
                          <Pencil size={16} strokeWidth={2} />
                          <span>Edit Plan</span>
                        </button>
                      </div>
                    </div>
                    {(canPause || canArchive) && (
                    <div style={{ display: 'flex', gap: 12, flexWrap: 'wrap', marginTop: 12 }}>
                      {canPause && (
                        <button
                          type="button"
                          onClick={async () => {
                            if (window.confirm(`Are you sure you want to pause this plan?`)) {
                              try {
                                setLoading(true);
                                setError(null);
                                await mealPlanService.pausePlan(viewedPlan.plan_id);
                                await loadPlans();
                                setViewedPlanId(null);
                                setViewedPlanVersion(null);
                                setViewedPlanTotalsSummary(null);
                                setSuccessMessage('Plan paused successfully');
                                setTimeout(() => setSuccessMessage(null), 3000);
                              } catch (err: any) {
                                setError(getErrorMessage(err) || 'Failed to pause plan');
                              } finally {
                                setLoading(false);
                              }
                            }
                          }}
                          disabled={loading}
                          style={{
                            padding: '8px 16px',
                            fontSize: '0.9rem',
                            backgroundColor: '#f59e0b',
                            color: 'white',
                            border: 'none',
                            borderRadius: 4,
                            cursor: loading ? 'not-allowed' : 'pointer',
                          }}
                        >
                          Pause
                        </button>
                      )}
                      {canArchive && (
                        <button
                          type="button"
                          onClick={async () => {
                            if (window.confirm(`Are you sure you want to archive this plan?`)) {
                              try {
                                setLoading(true);
                                setError(null);
                                await mealPlanService.archivePlan(viewedPlan.plan_id);
                                await loadPlans();
                                setViewedPlanId(null);
                                setViewedPlanVersion(null);
                                setViewedPlanTotalsSummary(null);
                                setSuccessMessage('Plan archived successfully');
                                setTimeout(() => setSuccessMessage(null), 3000);
                              } catch (err: any) {
                                setError(getErrorMessage(err) || 'Failed to archive plan');
                              } finally {
                                setLoading(false);
                              }
                            }
                          }}
                          disabled={loading}
                          style={{
                            padding: '8px 16px',
                            fontSize: '0.9rem',
                            backgroundColor: '#8b5cf6',
                            color: 'white',
                            border: 'none',
                            borderRadius: 4,
                            cursor: loading ? 'not-allowed' : 'pointer',
                          }}
                        >
                          Archive
                        </button>
                      )}
                    </div>
                    )}
                    </>
                    )}

                    {viewTab === 'details' && !viewedPlanVersion && (
                      <div className="np-subcard" style={{ marginTop: 0, marginBottom: 16, padding: 16 }}>
                        <div style={{ color: '#6b7280' }}>
                          Unable to load version details for this plan yet.
                        </div>
                      </div>
                    )}

                    {viewTab === 'details' && viewedPlanVersion && (
                      <>
                        {!hideInlineVersionSelector && viewedPlanVersions.length > 0 && (
                          <div className="np-subcard" style={{ marginBottom: 16, padding: 16, display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: 12 }}>
                            <div style={{ fontSize: '0.9rem', color: '#6b7280' }}>
                              Version: <code style={{ background: '#f3f4f6', padding: '2px 6px', borderRadius: 4 }}>{viewedPlanVersion.version_id}</code>
                            </div>
                            <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                              <label htmlFor="view-details-version-select" style={{ fontSize: '0.85rem', color: '#6b7280' }}>Version</label>
                              <select
                                id="view-details-version-select"
                                value={viewedPlanSelectedVersionId ?? ''}
                                onChange={(e) => setViewedPlanSelectedVersionId(e.target.value || null)}
                                style={{ minWidth: 220, padding: '8px 10px', border: '1px solid #e5e7eb', borderRadius: 8, background: '#fff', fontSize: '0.9rem' }}
                              >
                                {viewedPlanVersions.map((v) => {
                                  const shortId = v.version_id.slice(-5);
                                  const dateStr = v.created_at ? new Date(v.created_at).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' }) : '';
                                  return (
                                    <option key={v.version_id} value={v.version_id}>
                                      Version {shortId} • {dateStr}
                                    </option>
                                  );
                                })}
                              </select>
                            </div>
                          </div>
                        )}
                        <div className="patient-tabs" style={{ marginBottom: 16 }}>
                          <button type="button" className={`patient-tab ${existingPlanDetailTab === 'overview' ? 'active' : ''}`} onClick={() => setExistingPlanDetailTab('overview')}>Overview</button>
                          <button type="button" className={`patient-tab ${existingPlanDetailTab === 'nutrient_comparison' ? 'active' : ''}`} onClick={() => setExistingPlanDetailTab('nutrient_comparison')}>Nutrition Comparison</button>
                          <button type="button" className={`patient-tab ${existingPlanDetailTab === 'daily_meal_plan' ? 'active' : ''}`} onClick={() => setExistingPlanDetailTab('daily_meal_plan')} disabled={!viewedPlanVersion?.days?.length}>Daily Meal Plan</button>
                          <button type="button" className={`patient-tab ${existingPlanDetailTab === 'chat' ? 'active' : ''}`} onClick={() => setExistingPlanDetailTab('chat')}>Chat</button>
                        </div>
                        {existingPlanDetailTab === 'overview' && (
                          <MealPlanOverviewCards summary={viewedPlanTotalsSummary} marginTop={0} emptyMessage="Overview data is not available for this version yet." />
                        )}
                        {existingPlanDetailTab === 'nutrient_comparison' && viewedPlanTotalsSummary && (
                          <>
                            <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 12 }}>
                              <span style={{ fontSize: '0.85rem', fontWeight: 600, color: '#374151' }}>View:</span>
                              <button
                                type="button"
                                className={`patient-tab ${nutrientComparisonViewMode === 'table' ? 'active' : ''}`}
                                onClick={() => setNutrientComparisonViewMode('table')}
                                style={{ padding: '6px 12px', fontSize: '0.8rem' }}
                              >
                                Table
                              </button>
                              <button
                                type="button"
                                className={`patient-tab ${nutrientComparisonViewMode === 'graph' ? 'active' : ''}`}
                                onClick={() => setNutrientComparisonViewMode('graph')}
                                style={{ padding: '6px 12px', fontSize: '0.8rem' }}
                              >
                                Graph
                              </button>
                            </div>
                            {nutrientComparisonViewMode === 'table'
                              ? renderNutrientComparisonTable(viewedPlanTotalsSummary)
                              : renderNutrientComparisonGraphs(viewedPlanTotalsSummary, {
                                  marginTop: 0,
                                  emptyMessage: 'Nutrient data is not available for this version yet.',
                                })}
                          </>
                        )}
                        {existingPlanDetailTab === 'daily_meal_plan' && viewedPlanVersion?.days && viewedPlanVersion.days.length > 0 && (() => {
                          const days = viewedPlanVersion.days;
                          const DAYS_PER_WEEK = 7;
                          const start = viewPageWeekOffset * DAYS_PER_WEEK;
                          const weekDays = days.slice(start, start + DAYS_PER_WEEK);
                          const selectedIdx = viewPageExpandedDay != null && viewPageExpandedDay >= start && viewPageExpandedDay < start + weekDays.length ? viewPageExpandedDay : start;
                          const selectedDay = weekDays[selectedIdx - start];
                          const weekStart = weekDays[0]?.date ? new Date((weekDays[0] as any).date) : null;
                          const weekEnd = weekDays[weekDays.length - 1]?.date ? new Date((weekDays[weekDays.length - 1] as any).date) : null;
                          const dateRange = weekStart && weekEnd ? `${weekStart.toLocaleDateString('en-US', { month: 'short', day: 'numeric' })} - ${weekEnd.toLocaleDateString('en-US', { month: 'short', day: 'numeric' })}` : '—';
                          const totalPages = Math.max(1, Math.ceil(days.length / DAYS_PER_WEEK));
                          const maxOffset = Math.max(0, totalPages - 1);
                          const mealTimeByKey: Record<string, string> = {
                            ...Object.fromEntries(MEAL_TYPE_OPTIONS.map((k) => [k, MEAL_TYPE_DEFAULT_TIMES[k] || '08:00'])),
                            breakfast: defaultMealTiming.breakfast,
                            lunch: defaultMealTiming.lunch,
                            dinner: defaultMealTiming.dinner,
                            snack: defaultMealTiming.snacks,
                          };
                          const mealMeta: Record<string, { label: string; icon: React.ReactNode; color: string }> = {
                            breakfast: { label: 'Breakfast', icon: <Coffee size={22} />, color: '#c2410c' },
                            lunch: { label: 'Lunch', icon: <Sun size={22} />, color: '#ca8a04' },
                            dinner: { label: 'Dinner', icon: <Moon size={22} />, color: '#1e40af' },
                            snack: { label: 'Snacks', icon: <Cookie size={22} />, color: '#16a34a' },
                          };
                          const getMetaForView = (mealKey: string) => {
                            const known = mealMeta[mealKey] || mealMeta[(mealKey || '').toLowerCase().replace(/\s+/g, '_')];
                            if (known) return known;
                            return { label: formatMealKeyForDisplay(mealKey || 'snack'), icon: mealMeta.snack.icon, color: mealMeta.snack.color };
                          };
                          const mealOrderFallback: Record<string, number> = { breakfast: 0, lunch: 1, dinner: 2, snack: 3 };
                          const macroColors = { protein_g: '#8b5cf6', carbs_g: '#7dd3fc', fat_g: '#1e40af', fiber_g: '#9ca3af' };
                          const formatTime = (t: string) => { const [h, m] = (t || '00:00').split(':').map(Number); return `${h % 12 || 12}:${String(m || 0).padStart(2, '0')} ${h < 12 ? 'AM' : 'PM'}`; };
                          const timeToMinutes = (t: string | null | undefined): number | null => {
                            if (!t || typeof t !== 'string') return null;
                            const parts = t.trim().split(':').map(Number);
                            const h = parts[0] ?? 0, m = parts[1] ?? 0;
                            if (!Number.isFinite(h)) return null;
                            return h * 60 + m;
                          };
                          const getTimeForItem = (item: any) => item?.meal_time || mealTimeByKey[item?.meal_key] || mealTimeByKey[(item?.meal_key || '').toLowerCase().replace(/\s+/g, '_')];
                          return (
                            <div className="np-subcard" style={{ padding: 16 }}>
                              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 12, flexWrap: 'wrap', gap: 12 }}>
                                <h3 style={{ margin: 0, fontSize: '1.1rem', fontWeight: 600 }}>Weekly Schedule</h3>
                                <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
                                  <button type="button" disabled={viewPageWeekOffset <= 0} onClick={() => setViewPageWeekOffset((o) => Math.max(0, o - 1))} style={{ padding: 6, border: '1px solid #e5e7eb', borderRadius: 6, background: '#fff', cursor: viewPageWeekOffset <= 0 ? 'not-allowed' : 'pointer', opacity: viewPageWeekOffset <= 0 ? 0.5 : 1 }}><ChevronLeft size={18} /></button>
                                  <span style={{ fontSize: '0.9rem', fontWeight: 500, color: '#374151', minWidth: 72, textAlign: 'center' }}>Page {viewPageWeekOffset + 1} of {totalPages}</span>
                                  <button type="button" disabled={viewPageWeekOffset >= maxOffset} onClick={() => setViewPageWeekOffset((o) => Math.min(maxOffset, o + 1))} style={{ padding: 6, border: '1px solid #e5e7eb', borderRadius: 6, background: '#fff', cursor: viewPageWeekOffset >= maxOffset ? 'not-allowed' : 'pointer', opacity: viewPageWeekOffset >= maxOffset ? 0.5 : 1 }}><ChevronRight size={18} /></button>
                                </div>
                              </div>
                              <div style={{ display: 'flex', gap: 4, marginBottom: 16, borderBottom: '1px solid #e5e7eb' }}>
                                <button type="button" onClick={() => setWeeklyScheduleInnerTab('schedule')} style={{ padding: '8px 14px', border: 'none', background: 'none', fontSize: '0.9rem', fontWeight: 500, color: weeklyScheduleInnerTab === 'schedule' ? '#2563eb' : '#6b7280', borderBottom: weeklyScheduleInnerTab === 'schedule' ? '2px solid #2563eb' : '2px solid transparent', marginBottom: -1, cursor: 'pointer' }}>Schedule</button>
                                <button type="button" onClick={() => setWeeklyScheduleInnerTab('grocery_list')} style={{ padding: '8px 14px', border: 'none', background: 'none', fontSize: '0.9rem', fontWeight: 500, color: weeklyScheduleInnerTab === 'grocery_list' ? '#2563eb' : '#6b7280', borderBottom: weeklyScheduleInnerTab === 'grocery_list' ? '2px solid #2563eb' : '2px solid transparent', marginBottom: -1, cursor: 'pointer' }}>Grocery List</button>
                                <button type="button" onClick={() => setWeeklyScheduleInnerTab('exclusions')} style={{ padding: '8px 14px', border: 'none', background: 'none', fontSize: '0.9rem', fontWeight: 500, color: weeklyScheduleInnerTab === 'exclusions' ? '#2563eb' : '#6b7280', borderBottom: weeklyScheduleInnerTab === 'exclusions' ? '2px solid #2563eb' : '2px solid transparent', marginBottom: -1, cursor: 'pointer' }}>Exclusions</button>
                              </div>
                              {weeklyScheduleInnerTab === 'schedule' && (
                              <>
                              <div style={{ marginBottom: 16 }}>
                                <h4 style={{ margin: 0, fontSize: '1rem', fontWeight: 700, color: '#111827' }}>Current Week</h4>
                                <span style={{ fontSize: '0.9rem', color: '#6b7280' }}>{dateRange}</span>
                              </div>
                              <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', width: '100%', marginBottom: 16 }}>
                                {weekDays.map((day, idx) => {
                                  const dayIdx = start + idx;
                                  const isSelected = selectedIdx === dayIdx;
                                  const d = day.date ? new Date(day.date as string) : new Date();
                                  const label = d.toLocaleDateString('en-US', { weekday: 'short' }) + ' ' + d.getDate();
                                  return (
                                    <button key={dayIdx} type="button" onClick={() => setViewPageExpandedDay(dayIdx)} style={{ flex: '1 1 0', minWidth: 0, padding: '10px 8px', borderRadius: 8, border: '1px solid #e5e7eb', background: isSelected ? '#1e40af' : '#f9fafb', color: isSelected ? '#fff' : '#374151', cursor: 'pointer', fontSize: '0.9rem', fontWeight: 600, boxShadow: isSelected ? '0 1px 3px rgba(0,0,0,0.15)' : 'none' }}>{label}</button>
                                  );
                                })}
                              </div>
                              {selectedDay && (
                                <div style={{ padding: '16px 0' }}>
                                  {(() => {
                                    const dayItems = ((selectedDay as any)?.items || []).slice().sort((a: any, b: any) => {
                                      const ma = timeToMinutes(getTimeForItem(a)), mb = timeToMinutes(getTimeForItem(b));
                                      if (ma != null && mb != null) return ma - mb;
                                      if (ma != null) return -1;
                                      if (mb != null) return 1;
                                      return (mealOrderFallback[a?.meal_key] ?? 99) - (mealOrderFallback[b?.meal_key] ?? 99);
                                    });
                                    if (dayItems.length === 0) return <div style={{ padding: 16, color: '#9ca3af', fontSize: '0.9rem' }}>No meals for this day.</div>;
                                    const mealKeyNorm = (item: any) => (item?.meal_key ?? 'snack').toString().toLowerCase().replace(/\s+/g, '_');
                                    const groupedByMeal = new Map<string, any[]>();
                                    for (const item of dayItems) {
                                      const k = mealKeyNorm(item);
                                      if (!groupedByMeal.has(k)) groupedByMeal.set(k, []);
                                      groupedByMeal.get(k)!.push(item);
                                    }
                                    const allGroupKeys = Array.from(groupedByMeal.keys());
                                    const orderedGroups = allGroupKeys.sort((a, b) => {
                                      const itemsA = groupedByMeal.get(a)!;
                                      const itemsB = groupedByMeal.get(b)!;
                                      const timeA = timeToMinutes(getTimeForItem(itemsA[0]));
                                      const timeB = timeToMinutes(getTimeForItem(itemsB[0]));
                                      if (timeA != null && timeB != null) return timeA - timeB;
                                      if (timeA != null) return -1;
                                      if (timeB != null) return 1;
                                      return (mealOrderFallback[a] ?? 99) - (mealOrderFallback[b] ?? 99);
                                    });
                                    return orderedGroups.map((mealKey) => {
                                      const items = groupedByMeal.get(mealKey)!;
                                      const first = items[0];
                                      const mt = first?.meal_key ?? mealKey;
                                      const meta = getMetaForView(mt);
                                      const rawTime = first?.meal_time || mealTimeByKey[mealKey] || mealTimeByKey[mt];
                                      const timeStr = rawTime ? formatTime(rawTime) : '—';
                                      const groupKcal = items.reduce((sum, m) => sum + (m?.dish_nutrients?.values?.calories != null ? Number(m.dish_nutrients.values.calories) : 0), 0);
                                      const groupKcalRounded = Math.round(groupKcal);
                                      const tabKey = `v-${selectedIdx}-${mealKey}`;
                                      const activeDetail = viewPageDetailTab[tabKey] ?? 'ingredients';
                                      const setActive = (tab: 'ingredients' | 'preparations') => setViewPageDetailTab((prev) => ({ ...prev, [tabKey]: tab }));
                                      const summedMacros = items.reduce((acc, m) => {
                                        const v = m?.dish_nutrients?.values || {};
                                        acc.protein_g += Number(v.protein_g) || 0;
                                        acc.carbs_g += Number(v.carbs_g) || 0;
                                        acc.fat_g += Number(v.fat_g) || 0;
                                        acc.fiber_g += Number(v.fiber_g) || 0;
                                        return acc;
                                      }, { protein_g: 0, carbs_g: 0, fat_g: 0, fiber_g: 0 });
                                      const macroTotal = summedMacros.protein_g + summedMacros.carbs_g + summedMacros.fat_g + summedMacros.fiber_g;
                                      const segments = macroTotal > 0 ? [
                                        { key: 'protein_g', value: summedMacros.protein_g, color: macroColors.protein_g, label: 'Protein' },
                                        { key: 'carbs_g', value: summedMacros.carbs_g, color: macroColors.carbs_g, label: 'Carbs' },
                                        { key: 'fat_g', value: summedMacros.fat_g, color: macroColors.fat_g, label: 'Fats' },
                                        { key: 'fiber_g', value: summedMacros.fiber_g, color: macroColors.fiber_g, label: 'Fiber' },
                                      ].filter((s) => s.value > 0) : [];
                                      const chartSize = 120;
                                      return (
                                        <div key={mealKey} style={{ marginBottom: 28, border: '1px solid #e5e7eb', borderRadius: 10, overflow: 'hidden', background: '#fff', boxShadow: '0 1px 3px rgba(0,0,0,0.06)' }}>
                                          <div style={{ padding: '16px 16px 12px', borderBottom: `2px solid ${meta.color}`, backgroundColor: '#fafafa' }}>
                                            <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                                              <span style={{ color: meta.color, flexShrink: 0 }}>{meta.icon}</span>
                                              <span style={{ fontSize: '1.1rem', fontWeight: 700, color: '#111827' }}>{meta.label}</span>
                                              <span style={{ fontSize: '0.9rem', color: '#6b7280', fontWeight: 500 }}>• {timeStr}{groupKcalRounded > 0 ? ` • ${groupKcalRounded} kcal total` : ''}</span>
                                            </div>
                                          </div>
                                          <div style={{ padding: '12px 16px 8px', borderBottom: '1px solid #e5e7eb', display: 'flex', gap: 0 }}>
                                            <button type="button" onClick={() => setActive('ingredients')} style={{ padding: '8px 14px', border: 'none', background: 'none', fontSize: '0.85rem', fontWeight: 500, color: activeDetail === 'ingredients' ? '#2563eb' : '#6b7280', borderBottom: activeDetail === 'ingredients' ? '2px solid #2563eb' : '2px solid transparent', marginBottom: -1, cursor: 'pointer' }}>Ingredients & Nutrients</button>
                                            <button type="button" onClick={() => setActive('preparations')} style={{ padding: '8px 14px', border: 'none', background: 'none', fontSize: '0.85rem', fontWeight: 500, color: activeDetail === 'preparations' ? '#2563eb' : '#6b7280', borderBottom: activeDetail === 'preparations' ? '2px solid #2563eb' : '2px solid transparent', marginBottom: -1, cursor: 'pointer' }}>Preparation</button>
                                          </div>
                                          <div style={{ padding: 16 }}>
                                            {activeDetail === 'ingredients' && (
                                              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(220px, 1fr))', gap: 24, alignItems: 'start' }}>
                                                <div>
                                                  <div style={{ fontSize: '0.7rem', fontWeight: 700, color: '#6b7280', letterSpacing: '0.05em', marginBottom: 10 }}>INGREDIENTS</div>
                                                  <div style={{ fontSize: '0.9rem', lineHeight: 1.8, color: '#374151' }}>
                                                    {items.flatMap((meal: any) => (meal?.ingredients || []).map((ing: any) => typeof ing === 'string' ? ing : (ing.name || '') + (ing.portion_display ? ` ${ing.portion_display}` : '') + (ing.exception ? ` (${ing.exception})` : ''))).map((line: string, idx: number) => (
                                                      <div key={idx}>{line}</div>
                                                    ))}
                                                    {items.some((m: any) => m?.exception) && items.filter((m: any) => m?.exception).map((meal: any, i: number) => (
                                                      <div key={`ex-${i}`} style={{ color: '#b45309', marginTop: 4 }}>{meal.dish_name || 'Dish'}: {meal.exception}</div>
                                                    ))}
                                                    {items.every((m: any) => !(m?.ingredients?.length)) && !items.some((m: any) => m?.exception) && <div style={{ color: '#9ca3af' }}>No ingredients</div>}
                                                  </div>
                                                </div>
                                                <div>
                                                  <div style={{ fontSize: '0.7rem', fontWeight: 700, color: '#6b7280', letterSpacing: '0.05em', marginBottom: 10 }}>MACRONUTRIENTS DISTRIBUTION</div>
                                                  <div style={{ display: 'flex', alignItems: 'center', gap: 20, flexWrap: 'wrap' }}>
                                                    <div style={{ position: 'relative', width: chartSize, height: chartSize, flexShrink: 0 }}>{segments.length > 0 ? <svg width={chartSize} height={chartSize} viewBox="0 0 120 120" style={{ transform: 'rotate(-90deg)' }}>{segments.reduce((acc: { offset: number; elements: React.ReactNode[] }, s) => { const pct = s.value / macroTotal; const start = acc.offset; const end = start + pct; const x1 = 60 + 48 * Math.cos(start * 2 * Math.PI); const y1 = 60 + 48 * Math.sin(start * 2 * Math.PI); const x2 = 60 + 48 * Math.cos(end * 2 * Math.PI); const y2 = 60 + 48 * Math.sin(end * 2 * Math.PI); acc.elements.push(<path key={s.key} d={`M 60 60 L ${x1} ${y1} A 48 48 0 ${pct > 0.5 ? 1 : 0} 1 ${x2} ${y2} Z`} fill={s.color} />); acc.offset = end; return acc; }, { offset: 0, elements: [] }).elements}<circle cx={60} cy={60} r={28} fill="#fff" /></svg> : <div style={{ width: chartSize, height: chartSize, borderRadius: '50%', background: '#f3f4f6', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: '0.75rem', color: '#9ca3af' }}>No data</div>}<div style={{ position: 'absolute', inset: 0, display: 'flex', alignItems: 'center', justifyContent: 'center', fontWeight: 700, fontSize: '0.9rem' }}>{groupKcalRounded} KCAL</div></div>
                                                    <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>{segments.map((s) => <div key={s.key} style={{ display: 'flex', alignItems: 'center', gap: 8, fontSize: '0.9rem' }}><span style={{ width: 10, height: 10, borderRadius: '50%', backgroundColor: s.color }} /><span>{s.label}: {Number(summedMacros[s.key as keyof typeof summedMacros])?.toFixed(0) ?? 0}g</span></div>)}</div>
                                                  </div>
                                                </div>
                                              </div>
                                            )}
                                            {activeDetail === 'preparations' && (
                                              <div>
                                                <div style={{ fontSize: '0.7rem', fontWeight: 700, color: '#6b7280', letterSpacing: '0.05em', marginBottom: 10 }}>STEP-BY-STEP INSTRUCTIONS</div>
                                                {items.map((meal: any, mealIdx: number) => {
                                                  const steps = meal?.recipe_steps || meal?.premium_recipe?.steps || [];
                                                  return (
                                                    <div key={mealIdx} style={{ marginBottom: mealIdx < items.length - 1 ? 16 : 0 }}>
                                                      {items.length > 1 && <div style={{ fontSize: '0.85rem', fontWeight: 600, color: '#374151', marginBottom: 6 }}>{meal?.dish_name || 'Dish'}</div>}
                                                      <ol style={{ margin: 0, paddingLeft: 20, fontSize: '0.9rem', lineHeight: 1.7 }}>{steps.map((step: string, i: number) => <li key={i} style={{ marginBottom: 8 }}>{step}</li>)}{steps.length === 0 && <li style={{ color: '#9ca3af' }}>No preparation steps</li>}</ol>
                                                    </div>
                                                  );
                                                })}
                                              </div>
                                            )}
                                          </div>
                                        </div>
                                      );
                                    });
                                  })()}
                                </div>
                              )}
                              </>
                              )}
                              {weeklyScheduleInnerTab === 'grocery_list' && (() => {
                                const gl = (viewedPlanVersion as any).grocery_list || [];
                                if (!Array.isArray(gl) || gl.length === 0) return <div style={{ padding: 24, color: '#6b7280', fontSize: '0.95rem' }}>No grocery list for this version.</div>;
                                return (
                                  <div style={{ padding: '16px 0', overflowX: 'auto' }}>
                                    <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '0.9rem' }}>
                                      <thead><tr style={{ borderBottom: '2px solid #e5e7eb', backgroundColor: '#f9fafb' }}><th style={{ padding: '8px 12px', textAlign: 'left', fontWeight: 600 }}>Ingredient</th><th style={{ padding: '8px 12px', textAlign: 'right', fontWeight: 600 }}>Amount</th><th style={{ padding: '8px 12px', textAlign: 'left', fontWeight: 600 }}>Category</th><th style={{ padding: '8px 12px', textAlign: 'left', fontWeight: 600 }}>Notes</th></tr></thead>
                                      <tbody>{gl.map((gi: any, gIdx: number) => { const name = typeof gi === 'string' ? gi : gi.ingredient_name || gi.name || gi.item || 'Unknown'; const amount = typeof gi === 'object' ? (gi.total_amount || gi.quantity || gi.amount || '') : ''; const unit = typeof gi === 'object' ? (gi.unit || gi.portion_display || '') : ''; const displayAmount = amount && unit ? `${amount} ${unit}` : amount || unit || '-'; const category = typeof gi === 'object' ? (gi.category || gi.food_group || '') : ''; const notes = typeof gi === 'object' ? (gi.notes || gi.source || '') : ''; return <tr key={gIdx} style={{ borderBottom: '1px solid #f3f4f6' }}><td style={{ padding: '8px 12px', fontWeight: 500 }}>{name}</td><td style={{ padding: '8px 12px', textAlign: 'right' }}>{displayAmount}</td><td style={{ padding: '8px 12px', color: '#6b7280' }}>{category || '-'}</td><td style={{ padding: '8px 12px', color: '#6b7280', fontSize: '0.85em' }}>{notes || '-'}</td></tr>; })}</tbody>
                                    </table>
                                  </div>
                                );
                              })()}
                              {weeklyScheduleInnerTab === 'exclusions' && (() => {
                                const exclusions = (viewedPlanVersion as any).overall_exceptions ?? (viewedPlanVersion as any).diff_summary_json?.overall_exceptions ?? [];
                                const list = Array.isArray(exclusions) ? exclusions : [];
                                if (list.length === 0) return <div style={{ padding: 24, color: '#6b7280', fontSize: '0.95rem' }}>No exclusions (to avoid) for this version.</div>;
                                return (
                                  <div style={{ padding: '16px 0' }}>
                                    <p style={{ margin: '0 0 12px', fontSize: '0.85rem', color: '#6b7280' }}>Items to avoid plan-wide:</p>
                                    <ul style={{ margin: 0, paddingLeft: 20, fontSize: '0.95rem', lineHeight: 1.8 }}>{list.map((item: string, i: number) => <li key={i}>{typeof item === 'string' ? item : String(item)}</li>)}</ul>
                                  </div>
                                );
                              })()}
                            </div>
                          );
                        })()}
                        {existingPlanDetailTab === 'daily_meal_plan' && (!viewedPlanVersion?.days?.length) && (
                          <div className="np-subcard" style={{ padding: 16, color: '#6b7280', textAlign: 'center' }}>No daily meal plan data for this version yet.</div>
                        )}
                        {existingPlanDetailTab === 'chat' && (
                          <div className="np-subcard" style={{ padding: 16 }}>
                            <div ref={chatMessagesContainerRef} style={{ maxHeight: 400, overflowY: 'auto', marginBottom: 16 }}>
                              {chatStatus?.conversation_log && chatStatus.conversation_log.length > 0 ? (
                                <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
                                  {chatStatus.conversation_log.map((msg: any, idx: number) => {
                                    const isDoctor = msg.role === 'user' || msg.role === 'doctor';
                                    const questionText = msg.content || '';
                                    const options = (chatStatus?.current_ambiguity_question && chatStatus.waiting_for_doctor_response) ? (chatStatus.current_ambiguity_question.options || chatStatus.current_ambiguity_question.suggestions || []) : [];
                                    return (
                                      <div key={idx} style={{ alignSelf: isDoctor ? 'flex-end' : 'flex-start', maxWidth: '70%' }}>
                                        <div style={{ display: 'flex', alignItems: 'flex-start', gap: 8, flexDirection: isDoctor ? 'row-reverse' : 'row' }}>
                                          {!isDoctor && <span style={{ color: '#94a3b8', flexShrink: 0 }}><Sparkles size={16} /></span>}
                                          <div>
                                            <div style={{ padding: '12px 16px', borderRadius: 12, backgroundColor: isDoctor ? '#2563eb' : '#f1f5f9', color: isDoctor ? '#fff' : '#0f172a', fontSize: 14, lineHeight: 1.5, whiteSpace: 'pre-wrap', wordBreak: 'break-word' }}>{questionText}</div>
                                            <div style={{ fontSize: '0.7rem', color: '#94a3b8', marginTop: 6, textAlign: isDoctor ? 'right' : 'left' }}>{isDoctor ? 'YOU' : 'AI ASSISTANT'} {msg.created_at ? ` · ${new Date(msg.created_at).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}` : ''}</div>
                                          </div>
                                        </div>
                                        {!isDoctor && options.length > 0 && (
                                          <div style={{ marginTop: 12, display: 'flex', flexWrap: 'wrap', gap: 8 }}>
                                            {options.map((opt: string, oi: number) => (
                                              <button key={oi} type="button" onClick={() => viewedPlanId && sendChatMessage(viewedPlanId, opt, chatStatus?.session_id ?? undefined, (viewedPlanSelectedVersionId || viewedPlanVersion?.version_id) ?? undefined)} disabled={sendingChatMessage} style={{ padding: '6px 12px', backgroundColor: '#fff', color: '#374151', border: '1px solid #e2e8f0', borderRadius: 8, fontSize: 13, cursor: sendingChatMessage ? 'not-allowed' : 'pointer' }}>{opt}</button>
                                            ))}
                                          </div>
                                        )}
                                      </div>
                                    );
                                  })}
                                </div>
                              ) : (
                                <div style={{ color: '#9ca3af', fontSize: 14, textAlign: 'center', padding: 20 }}>{chatStatus?.waiting_for_doctor_response ? 'Loading question...' : chatStatus?.status === 'processing' ? 'Processing...' : 'No messages yet. Type below to start.'}</div>
                              )}
                            </div>
                            <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                              <input type="text" value={chatInput} onChange={(e) => setChatInput(e.target.value)} onKeyDown={(e) => { if (e.key === 'Enter' && !e.shiftKey && chatInput.trim() && !sendingChatMessage && viewedPlanId) { e.preventDefault(); void sendChatMessage(viewedPlanId, chatInput.trim(), chatStatus?.session_id ?? undefined, (viewedPlanSelectedVersionId || viewedPlanVersion?.version_id) ?? undefined); } }} placeholder="Type a message or e.g. Create new plan..." style={{ flex: 1, padding: '12px 16px', border: '1px solid #e2e8f0', borderRadius: 10, fontSize: 14, backgroundColor: '#f8fafc' }} disabled={sendingChatMessage} />
                              <button type="button" onClick={() => viewedPlanId && chatInput.trim() && void sendChatMessage(viewedPlanId, chatInput.trim(), chatStatus?.session_id ?? undefined, (viewedPlanSelectedVersionId || viewedPlanVersion?.version_id) ?? undefined)} disabled={sendingChatMessage || !chatInput.trim()} style={{ width: 44, height: 44, borderRadius: '50%', border: 'none', background: '#2563eb', color: '#fff', cursor: sendingChatMessage || !chatInput.trim() ? 'not-allowed' : 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center', opacity: sendingChatMessage || !chatInput.trim() ? 0.7 : 1 }}><Send size={18} /></button>
                            </div>
                          </div>
                        )}
                      </>
                    )}

                  </div>
                );
              })()}
        </>
      ) : (
        // ——— CREATE FLOW: Step content (step nav moved to left pane under Meal Plans) ———
      <div className="np-stepper">
        <div className="np-stepper-body">
          {/* Step 1: Settings — plan name, period, mode, cuisine, meals, meal slots (hidden when showOnlyChat) */}
          {!showOnlyChat && activeStep === 'settings' && (
            <>
              <div className="np-step-title">
                <h2>{editingPlanId ? 'Edit Plan Settings' : 'Plan settings'}</h2>
                <p className="np-muted">These settings will be used for preview + full plan generation.</p>
              </div>

              <div className="np-grid">
                <div className="np-field np-field--full">
                  <label>Plan Name</label>
                  <input
                    type="text"
                    value={planName}
                    onChange={(e) => setPlanName(e.target.value)}
                    placeholder="e.g., Weight Loss Plan - January 2025"
                    autoComplete="off"
                  />
                </div>
                {/* Plan period and Duration (days) kept on same row */}
                <div className="np-plan-period-row np-field--full">
                  <div className="np-field np-field-date-range">
                    <label>Plan period</label>
                    <CalendarDateRangePicker
                      startDate={planStartDate || ''}
                      endDate={planEndDate || ''}
                      onChange={(range) => {
                        setPlanStartDate(range.start || '');
                        setPlanEndDate(range.end || '');
                      }}
                      placeholder="Select start and end date"
                    />
                  </div>
                  <div className="np-field">
                    <label>Duration (days)</label>
                    <input
                      type="number"
                      value={durationDays}
                      min={1}
                      max={30}
                      disabled
                    />
                  </div>
                </div>
                <div className="np-field">
                  <label>Mode</label>
                  <select
                    className="np-select--styled"
                    value={mode}
                    onChange={(e) => {
                      const newMode = e.target.value as MealPlanMode;
                      setMode(newMode);
                      if (newMode === 'adaptive') {
                        setVariant('optimize_existing');
                      } else {
                        setVariant('doctor_manual');
                      }
                    }}
                  >
                    <option value="adaptive">Adaptive</option>
                    <option value="fixed">Fixed</option>
                  </select>
                </div>
                <div className="np-field">
                  <label>Variant</label>
                  <select
                    className="np-select--styled"
                    value={variant}
                    onChange={(e) => setVariant(e.target.value)}
                  >
                    {mode === 'adaptive' ? (
                      <>
                        <option value="optimize_existing">Optimize Existing</option>
                        <option value="ai_generated_initial">AI Generated</option>
                      </>
                    ) : (
                      <>
                        <option value="doctor_manual">Manual</option>
                        <option value="ai_generated_fixed">AI Generated</option>
                      </>
                    )}
                  </select>
                </div>
                <div className="np-field">
                  <label>Cuisine pattern</label>
                  <select
                    className="np-select--styled"
                    value={cuisinePattern}
                    onChange={(e) => setCuisinePattern(e.target.value)}
                  >
                    <option value="Indian">Indian</option>
                    <option value="South Indian">South Indian</option>
                    <option value="North Indian">North Indian</option>
                    <option value="Mediterranean">Mediterranean</option>
                    <option value="Middle Eastern">Middle Eastern</option>
                    <option value="Asian">Asian</option>
                    <option value="Chinese">Chinese</option>
                    <option value="Japanese">Japanese</option>
                    <option value="Thai">Thai</option>
                    <option value="Korean">Korean</option>
                    <option value="Italian">Italian</option>
                    <option value="Mexican">Mexican</option>
                    <option value="American">American</option>
                    <option value="Continental">Continental</option>
                  </select>
                </div>
                <div className="np-field">
                  <label>Meals/day</label>
                  <input
                    type="number"
                    min={1}
                    max={10}
                    value={mealsPerDayInput}
                    onChange={(e) => {
                      const raw = e.target.value;
                      if (raw === '') {
                        setMealsPerDayInput('');
                        return;
                      }
                      const n = parseInt(raw, 10);
                      if (!Number.isNaN(n)) {
                        const clamped = Math.min(10, Math.max(1, n));
                        setMealsPerDayInput(String(clamped));
                      }
                    }}
                    onBlur={() => {
                      const n = parseInt(mealsPerDayInput.trim(), 10);
                      if (mealsPerDayInput.trim() === '' || Number.isNaN(n) || n < 1) {
                        setMealsPerDayInput('1');
                      } else if (n > 10) {
                        setMealsPerDayInput('10');
                      } else {
                        setMealsPerDayInput(String(n));
                      }
                    }}
                    style={{ width: '100%', padding: '8px 10px', border: '1px solid #d1d5db', borderRadius: 6 }}
                  />
                </div>

                {!isFixedManual && (
                <div className="np-field" style={{ gridColumn: '1 / -1' }}>
                  <label style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                  <input
                      type="checkbox"
                      checked={allowRecipeRepeat}
                      onChange={(e) => {
                        const checked = e.target.checked;
                        setAllowRecipeRepeat(checked);
                        if (!checked) {
                          setRecipeRepeatPolicy(null);
                        } else if (!recipeRepeatPolicy) {
                          setRecipeRepeatPolicy('smart');
                        }
                      }}
                    />
                    Allow repeating recipes across days
                  </label>
                  <div className="np-muted" style={{ marginTop: 4 }}>
                    If enabled, the generator can reuse some dishes across days (useful for realistic adherence).
                </div>
                </div>
                )}

                {allowRecipeRepeat && (
                  <>
                <div className="np-field">
                      <label>Repeat policy</label>
                      <select
                        value={recipeRepeatPolicy || 'smart'}
                        onChange={(e) => setRecipeRepeatPolicy(e.target.value as any)}
                      >
                        <option value="smart">Smart (limit repeats)</option>
                        <option value="unlimited">Unlimited</option>
                  </select>
                </div>

                    {recipeRepeatPolicy !== 'unlimited' && (
                      <>
                        <div className="np-field">
                          <label>Max repeats per dish</label>
                  <input
                            type="number"
                            min={1}
                            max={10}
                            value={maxRepeatsPerDish}
                            onChange={(e) => setMaxRepeatsPerDish(Number(e.target.value))}
                  />
                </div>
                <div className="np-field">
                          <label>Repeat after (days)</label>
                  <input
                            type="number"
                            min={1}
                            max={30}
                            value={repeatAfterDays}
                            onChange={(e) => setRepeatAfterDays(Number(e.target.value))}
                          />
              </div>
                      </>
                    )}
            </>
          )}

                <div className="np-field" style={{ gridColumn: '1 / -1' }}>
                  <label style={{ marginBottom: 8, display: 'block' }}>Meal slots</label>
                  <div style={{ display: 'flex', flexDirection: 'column', gap: 12, maxWidth: 520 }}>
                    {mealSlots.map((slot, index) => (
                      <div
                        key={slot.id}
                        style={{
                          display: 'grid',
                          gridTemplateColumns: 'minmax(0, 160px) minmax(0, 130px) auto',
                          gap: 12,
                          alignItems: 'flex-end',
                        }}
                      >
                        <div style={{ minWidth: 0 }}>
                          <div style={{ fontSize: '0.85rem', color: '#4b5563', marginBottom: 4 }}>
                            {`Meal ${index + 1} type`}
                          </div>
                          <select
                            className="np-select--styled"
                            value={slot.meal_type}
                            onChange={(e) => {
                              const value = e.target.value || MEAL_TYPE_OPTIONS[0];
                              updateMealSlot(index, { meal_type: value });
                            }}
                            style={{ width: '100%', maxWidth: 160 }}
                          >
                            {mealTypeChoices.map((choice) => (
                              <option key={choice.value} value={choice.value}>
                                {choice.label}
                              </option>
                            ))}
                          </select>
                        </div>
                        <div style={{ minWidth: 0 }}>
                          <div style={{ fontSize: '0.85rem', color: '#4b5563', marginBottom: 4 }}>Time</div>
                          <select
                            className="np-select--styled"
                            value={slot.time}
                            onChange={(e) => updateMealSlot(index, { time: e.target.value })}
                            style={{ width: '100%', maxWidth: 130 }}
                          >
                            {timeOptions.map((option) => (
                              <option key={option.value} value={option.value}>
                                {option.label}
                              </option>
                            ))}
                          </select>
                        </div>
                        <div style={{ display: 'flex', gap: 6, alignItems: 'flex-end', flexShrink: 0 }}>
                          <button
                            type="button"
                            onClick={() => {
                              const defaultType = mealTypeValues[0] ?? MEAL_TYPE_OPTIONS[0];
                              const newSlot: MealSlot = {
                                id: `${MEAL_SLOT_ID_PREFIX}-${Date.now()}-${index}`,
                                meal_type: defaultType,
                                time: getDefaultTimeForMealType(defaultType),
                              };
                              setMealSlots((slots) => [
                                ...slots.slice(0, index + 1),
                                newSlot,
                                ...slots.slice(index + 1),
                              ]);
                              setMealsPerDayInput((prev) => {
                                const n = Math.min(10, (parseInt(prev, 10) || 1) + 1);
                                return String(n);
                              });
                            }}
                            disabled={mealsPerDay >= 10}
                            title="Add meal slot after this one"
                            style={{
                              width: 28,
                              height: 32,
                              padding: 0,
                              fontSize: '1.1rem',
                              lineHeight: 1,
                              fontWeight: 600,
                              border: '1px solid #d1d5db',
                              borderRadius: 6,
                              background: mealsPerDay >= 10 ? '#f3f4f6' : '#fff',
                              color: mealsPerDay >= 10 ? '#9ca3af' : '#374151',
                              cursor: mealsPerDay >= 10 ? 'not-allowed' : 'pointer',
                            }}
                          >
                            +
                          </button>
                          <button
                            type="button"
                            onClick={() => {
                              setMealSlots((slots) => slots.filter((_, i) => i !== index));
                              setMealsPerDayInput((prev) => {
                                const n = Math.max(1, (parseInt(prev, 10) || 1) - 1);
                                return String(n);
                              });
                            }}
                            disabled={mealsPerDay <= 1}
                            title="Remove this meal slot"
                            style={{
                              width: 28,
                              height: 32,
                              padding: 0,
                              fontSize: '1.1rem',
                              lineHeight: 1,
                              fontWeight: 600,
                              border: '1px solid #d1d5db',
                              borderRadius: 6,
                              background: mealsPerDay <= 1 ? '#f3f4f6' : '#fff',
                              color: mealsPerDay <= 1 ? '#9ca3af' : '#374151',
                              cursor: mealsPerDay <= 1 ? 'not-allowed' : 'pointer',
                            }}
                          >
                            −
                          </button>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              </div>

              <div className="np-actions">
                <button
                  type="button"
                  className="secondary-button"
                  onClick={() => {
                    if (mealsBasePath) {
                      navigate(mealsBasePath);
                      return;
                    }
                    setShowCreateForm(false);
                    setEditingPlanId(null);
                    initialSettingsWhenEditingRef.current = null;
                  }}
                  disabled={loading}
                  style={{ display: 'inline-flex', alignItems: 'center', gap: 8 }}
                >
                  <X size={16} strokeWidth={2} />
                  <span>Cancel</span>
                </button>
                <button type="button" className="primary-button" onClick={handleCreatePlan} disabled={loading}>
                  Next
                </button>
              </div>
            </>
          )}

          {/* Step 2: Daily Meal Plan & Recipes — fixed: upload/versions/version_detail; adaptive: draft selector + approve */}
          {(activeStep === 'upload' || activeStep === 'meal_plan') && (
            <>
              <div className="np-step-title" style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: 16 }}>
                      <div>
                  {!isFixedManual && fixedManualSubView === 'meal_plan' && (
                    <p className="np-muted">
                      Create/select a draft version, review details, then approve &amp; send to the patient.
                    </p>
                  )}
              </div>
                {!isFixedManual && fixedManualSubView === 'meal_plan' && (
                <button
                  type="button"
                  className="secondary-button"
                  onClick={handleGenerateDraft}
                  disabled={loading || !!generationProgress}
                  style={{ marginTop: 0 }}
                >
                  {generationProgress ? 'Generating...' : 'Create New Draft'}
                </button>
                )}
                {/* Fixed manual: plan is created by backend on document upload; no separate "Create Meal Plan" button */}
              </div>

              {!isFixedManual && fixedManualSubView === 'meal_plan' && (
              <div style={{ display: 'flex', gap: 12, flexWrap: 'wrap', marginBottom: 12, alignItems: 'center' }}>
                {draftVersions.length > 0 && (
                  <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
                    <span style={{ fontSize: '0.9rem', color: '#6b7280' }}>Version:</span>
                    <select
                      value={selectedDraftVersionId || draft?.version_id || ''}
                      onChange={async (e) => {
                        const vid = e.target.value;
                        setSelectedDraftVersionId(vid || null);
                        if (!selectedPlanId || !vid) return;
                        try {
                          setLoading(true);
                          setError(null);
                          const v = await mealPlanService.getVersion(selectedPlanId, vid);
                          setDraft(v);
                          // Load totals summary
                          try {
                            const summary = await mealPlanService.getTotalsSummary(selectedPlanId, vid, goalId);
                            setTotalsSummary(summary);
                          } catch (summaryErr) {
                            console.warn('Failed to load totals summary:', summaryErr);
                            setTotalsSummary(null);
                          }
                        } catch (err: any) {
                          setError(getErrorMessage(err) || 'Failed to load selected version');
                        } finally {
                          setLoading(false);
                        }
                      }}
                      disabled={loading || !selectedPlanId}
                      style={{ padding: '6px 10px', border: '1px solid #d1d5db', borderRadius: 6, minWidth: 250 }}
                    >
                      <option value="">Select a version…</option>
                      {draftVersions
                        .sort((a, b) => b.version - a.version)
                        .map((v) => (
                          <option key={v.version_id} value={v.version_id}>
                            {`v${v.version} • ${v.status ?? ''} • ${v.generated_by}${v.created_at ? ` • ${new Date(v.created_at).toLocaleDateString()}` : ''}`}
                          </option>
                        ))}
                    </select>
              </div>
                    )}
                  </div>
              )}

              {/* Choice screen removed - direct navigation to versions/upload */}

              {/* Step 2: Upload Document – when user chose "Create new draft with upload"; hide while uploading or once upload done so progress + chat show */}
              {!showOnlyChat && isFixedManual && fixedManualSubView === 'upload' && !uploadedSessionId && !uploadingDocument && (
                <div className="np-upload-step2-wrap">
                  <h2 className="np-upload-step2-title">Step 2: Upload Document</h2>
                  <p className="np-upload-step2-subtitle">
                    Upload a doctor&apos;s manual or a handwritten plan to get started.
                  </p>
                  <div className="np-upload-step2-document-wrap">
                    <span className="np-upload-step2-label">Document upload</span>
                    <div
                      className={`np-upload-step2-zone ${selectedFile ? 'has-file' : ''}`}
                    onClick={() => {
                      if (!selectedFile && step2FileInputRef.current) step2FileInputRef.current.click();
                    }}
                    onKeyDown={(e) => {
                      if (!selectedFile && (e.key === 'Enter' || e.key === ' ')) {
                        e.preventDefault();
                        step2FileInputRef.current?.click();
                      }
                    }}
                    onDragOver={(e) => {
                      if (!selectedFile) {
                        e.preventDefault();
                        e.stopPropagation();
                      }
                    }}
                    onDrop={(e) => {
                      if (selectedFile) return;
                      e.preventDefault();
                      e.stopPropagation();
                      const file = e.dataTransfer?.files?.[0];
                      if (file && /\.(pdf|doc|docx)$/i.test(file.name)) {
                        setSelectedFile(file);
                        setUploadError(null);
                      }
                    }}
                    role={selectedFile ? undefined : 'button'}
                    tabIndex={selectedFile ? undefined : 0}
                    aria-label={selectedFile ? undefined : 'Choose meal plan document'}
                  >
                    <input
                      ref={step2FileInputRef}
                      type="file"
                      accept=".pdf,.doc,.docx,application/pdf,application/msword,application/vnd.openxmlformats-officedocument.wordprocessingml.document"
                      onChange={(e) => {
                        const file = e.target.files?.[0] || null;
                        setSelectedFile(file);
                        setUploadError(null);
                      }}
                      style={{ position: 'absolute', width: 1, height: 1, opacity: 0, pointerEvents: 'none' }}
                      aria-hidden
                    />
                    {selectedFile ? (
                      <div className="np-upload-step2-file-row">
                        <svg className="np-upload-step2-file-icon" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                          <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z" />
                          <polyline points="14 2 14 8 20 8" />
                          <line x1="16" y1="13" x2="8" y2="13" />
                          <line x1="16" y1="17" x2="8" y2="17" />
                          <polyline points="10 9 9 9 8 9" />
                        </svg>
                        <span className="np-upload-step2-file-name">{selectedFile.name}</span>
                        <button
                          type="button"
                          className="np-upload-step2-file-remove"
                          onClick={(e) => {
                            e.stopPropagation();
                            setSelectedFile(null);
                            setUploadError(null);
                            if (step2FileInputRef.current) step2FileInputRef.current.value = '';
                          }}
                          aria-label="Remove file"
                        >
                          <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                            <line x1="18" y1="6" x2="6" y2="18" />
                            <line x1="6" y1="6" x2="18" y2="18" />
                          </svg>
                        </button>
                      </div>
                    ) : (
                      <div className="np-upload-step2-browse">
                        <svg className="np-upload-step2-browse-icon" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
                          <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4" />
                          <polyline points="17 8 12 3 7 8" />
                          <line x1="12" y1="3" x2="12" y2="15" />
                        </svg>
                        <span>Drag and drop your file here, or <strong>browse</strong> to choose</span>
                      </div>
                    )}
                    </div>
                  </div>
                  {uploadError && (
                    <div style={{ color: '#b91c1c', fontSize: '0.85rem', marginTop: 12 }}>
                      {uploadError}
                    </div>
                  )}
                </div>
              )}

              {!isFixedManual && generationProgress && (
                <div className="np-subcard" style={{ marginBottom: 16, padding: 16 }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginBottom: 8 }}>
                    <div style={{ flex: 1 }}>
                      <div style={{ fontSize: '0.9rem', color: '#6b7280', marginBottom: 4 }}>
                        {generationProgress.message}
                    </div>
                      <div style={{ width: '100%', height: 8, backgroundColor: '#e5e7eb', borderRadius: 4, overflow: 'hidden' }}>
                        <div
                          style={{
                            width: `${generationProgress.progress * 100}%`,
                            height: '100%',
                            backgroundColor: '#3b82f6',
                            transition: 'width 0.3s ease',
                          }}
                  />
                </div>
                </div>
                    <div style={{ fontSize: '0.85rem', color: '#6b7280', minWidth: 50, textAlign: 'right' }}>
                      {Math.round(generationProgress.progress * 100)}%
              </div>
                  </div>
                </div>
              )}

              {draft ? (
                <>
                  <div style={{ display: 'flex', gap: 12, marginBottom: 12, alignItems: 'center' }}>
                    {draft.days && draft.days.length > 0 && (
                      <span style={{ color: '#6b7280', fontSize: '0.9rem' }}>
                        {draft.days.length} day{draft.days.length !== 1 ? 's' : ''} • {draft.days.reduce((sum, day) => sum + (day.items?.length || 0), 0)} total meals
                      </span>
                    )}
              </div>

                  {/* Totals Summary - Table Format */}
                  {totalsSummary && (
                    <div className="np-subcard" style={{ marginBottom: 16, padding: 16 }}>
                      <h3 style={{ marginTop: 0, marginBottom: 16, fontSize: '1.1rem', fontWeight: 600 }}>
                        {Object.keys(totalsSummary.comparison).length > 0 || Object.keys(totalsSummary.meal_plan_totals || {}).length > 0
                          ? 'Nutrient Comparison: Generated Plan vs Meal Plan'
                          : 'Nutrient Summary (Daily Averages)'}
                      </h3>
                      
                      {/* Table format for comparison */}
                      <div style={{ overflowX: 'auto' }}>
                        <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '0.9rem' }}>
                          <thead>
                            <tr style={{ borderBottom: '2px solid #e5e7eb', backgroundColor: '#f9fafb' }}>
                              <th style={{ padding: '12px', textAlign: 'left', fontWeight: 600, position: 'sticky', left: 0, backgroundColor: '#f9fafb', zIndex: 10 }}>
                                Nutrient
                              </th>
                              <th style={{ padding: '12px', textAlign: 'right', fontWeight: 600 }}>Generated Plan</th>
                              <th style={{ padding: '12px', textAlign: 'right', fontWeight: 600 }}>Meal Plan Target</th>
                              <th style={{ padding: '12px', textAlign: 'right', fontWeight: 600 }}>Difference</th>
                              <th style={{ padding: '12px', textAlign: 'center', fontWeight: 600 }}>Status</th>
                            </tr>
                          </thead>
                          <tbody>
                            {(() => {
                              // Get all nutrients - prioritize meal_plan_totals (source of truth), then generated plan totals
                              const mealPlanNutrients = new Set(Object.keys(totalsSummary.meal_plan_totals || {}));
                              const allTotals = new Set(Object.keys(totalsSummary.totals || {}));
                              const comparedNutrients = new Set(Object.keys(totalsSummary.comparison));
                              
                              // Combine all nutrients - prioritize meal plan nutrients (they have all vitamins/minerals)
                              // Exclude duplicate keys (avoid showing both carbs_g/carbohydrates_g, folate_mcg/vitamin_b9_folate_mcg, etc.)
                              const hasCalories = allTotals.has('calories') || mealPlanNutrients.has('calories') || comparedNutrients.has('calories');
                              const hasCarbsG = allTotals.has('carbs_g') || mealPlanNutrients.has('carbs_g') || comparedNutrients.has('carbs_g');
                              const hasFolateMcg = allTotals.has('folate_mcg') || mealPlanNutrients.has('folate_mcg') || comparedNutrients.has('folate_mcg');
                              
                              const allNutrients = Array.from(new Set([
                                ...Array.from(mealPlanNutrients), // Meal plan first (has all nutrients)
                                ...Array.from(allTotals), // Then generated plan totals
                                ...Array.from(comparedNutrients) // Then comparison (subset)
                              ])).filter(n => {
                                // Remove duplicate calorie keys
                                if (hasCalories && (n === 'energy_kcal' || n === 'calories_kcal')) return false;
                                // Remove duplicate carbs keys (prefer carbs_g)
                                if (hasCarbsG && n === 'carbohydrates_g') return false;
                                // Remove duplicate folate keys (prefer folate_mcg)
                                if (hasFolateMcg && n === 'vitamin_b9_folate_mcg') return false;
                                return true;
                              });
                              
                              // Sort nutrients: macros first, then vitamins, then minerals
                              const nutrientOrder = [
                                'calories', 'protein_g', 'carbs_g', 'fat_g', 'fiber_g', 'sugar_g', 'sodium_mg',
                                'vitamin_a_mcg', 'vitamin_c_mg', 'vitamin_d_mcg', 'vitamin_e_mg', 'vitamin_k_mcg',
                                'vitamin_b1_mg', 'vitamin_b2_mg', 'vitamin_b3_mg', 'vitamin_b6_mg', 'vitamin_b12_mcg', 'folate_mcg',
                                'calcium_mg', 'iron_mg', 'magnesium_mg', 'phosphorus_mg', 'potassium_mg', 'zinc_mg',
                                'copper_mg', 'manganese_mg', 'selenium_mcg'
                              ];
                              
                              const sortedNutrients = allNutrients.sort((a, b) => {
                                const aIdx = nutrientOrder.indexOf(a);
                                const bIdx = nutrientOrder.indexOf(b);
                                if (aIdx === -1 && bIdx === -1) return a.localeCompare(b);
                                if (aIdx === -1) return 1;
                                if (bIdx === -1) return -1;
                                return aIdx - bIdx;
                              });
                              
                              return sortedNutrients.map((nutrient) => {
                                const actual = totalsSummary.totals[nutrient] || 0;
                                // Check meal_plan_totals first, then comparison target, also check for calories_kcal variant
                                let target = totalsSummary.meal_plan_totals?.[nutrient];
                                if (target === null || target === undefined) {
                                  // Try comparison target as fallback
                                  target = totalsSummary.comparison?.[nutrient]?.target ?? null;
                                }
                                if (target === null && nutrient === 'calories') {
                                  // Try calories_kcal or energy_kcal as fallback
                                  target = totalsSummary.meal_plan_totals?.['calories_kcal'] || totalsSummary.meal_plan_totals?.['energy_kcal'] || null;
                                }
                                // Convert target to number if it's a string
                                if (typeof target === 'string') {
                                  target = parseFloat(target) || null;
                                }
                                const comp = totalsSummary.comparison[nutrient];
                                
                                const getStatusColor = (status?: string) => {
                                  if (!status) return '#6b7280'; // gray for no comparison
                                  if (status === 'met') return '#10b981'; // green
                                  if (status === 'over') return '#f59e0b'; // amber
                                  return '#ef4444'; // red
                                };
                                
                                const getStatusLabel = (status?: string) => {
                                  if (!status) return '-';
                                  if (status === 'met') return '✓ Met';
                                  if (status === 'over') return '↑ Over';
                                  return '↓ Under';
                                };
                                
                                const diff = target !== null ? actual - target : null;
                                const diffPercent = target !== null && target > 0 ? ((diff! / target) * 100) : null;
                                
                                return (
                                  <tr key={nutrient} style={{ borderBottom: '1px solid #e5e7eb' }}>
                                    <td style={{ padding: '10px 12px', fontWeight: 500, position: 'sticky', left: 0, backgroundColor: 'white', whiteSpace: 'nowrap' }}>
                                      {nutrient.replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase())}
                                    </td>
                                    <td style={{ padding: '10px 12px', textAlign: 'right' }}>
                                      {actual.toFixed(1)}
                                    </td>
                                    <td style={{ padding: '10px 12px', textAlign: 'right', color: target !== null ? '#374151' : '#9ca3af' }}>
                                      {target !== null ? target.toFixed(1) : '-'}
                                    </td>
                                    <td style={{ padding: '10px 12px', textAlign: 'right', color: diff !== null ? (diff > 0 ? '#f59e0b' : diff < 0 ? '#ef4444' : '#10b981') : '#9ca3af' }}>
                                      {diff !== null ? `${diff > 0 ? '+' : ''}${diff.toFixed(1)}${diffPercent !== null ? ` (${diffPercent > 0 ? '+' : ''}${diffPercent.toFixed(1)}%)` : ''}` : '-'}
                                    </td>
                                    <td style={{ padding: '10px 12px', textAlign: 'center' }}>
                                      <span style={{ 
                                        color: getStatusColor(comp?.status),
                                        fontWeight: 600,
                                        fontSize: '0.85rem'
                                      }}>
                                        {getStatusLabel(comp?.status)}
                                      </span>
                                    </td>
                                  </tr>
                                );
                              });
                            })()}
                          </tbody>
                        </table>
              </div>
                    </div>
          )}

                  <div style={{ marginTop: 16, overflowX: 'auto' }}>
                      {draft.days && draft.days.length > 0 ? (
                        draft.days.map((day, dayIdx) => (
                          <div key={dayIdx} style={{ marginBottom: 24, border: '1px solid #e5e7eb', borderRadius: 8, overflow: 'hidden' }}>
                            <div style={{ padding: 12, backgroundColor: '#f9fafb', borderBottom: '1px solid #e5e7eb', fontWeight: 600 }}>
                              {new Date(day.date).toLocaleDateString('en-US', { weekday: 'long', year: 'numeric', month: 'long', day: 'numeric' })}
              </div>
                            <div style={{ padding: 16 }}>
                              {day.items.map((item, itemIdx) => {
                                const dishKey = `draft-${dayIdx}-${itemIdx}`;
                                const isExpanded = expandedDraftDishes.has(dishKey);
                                const toggleExpanded = () => {
                                  setExpandedDraftDishes((prev) => {
                                    const next = new Set(prev);
                                    if (next.has(dishKey)) {
                                      next.delete(dishKey);
                                    } else {
                                      next.add(dishKey);
                                    }
                                    return next;
                                  });
                                };
                                const dishNutrients = item.dish_nutrients?.values || {};
                                
                                return (
                                  <div key={itemIdx} style={{ marginBottom: itemIdx < day.items.length - 1 ? 20 : 0, paddingBottom: itemIdx < day.items.length - 1 ? 20 : 0, borderBottom: itemIdx < day.items.length - 1 ? '1px solid #e5e7eb' : 'none' }}>
                                    <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginBottom: 12 }}>
                <button
                  type="button"
                                        onClick={toggleExpanded}
                                        style={{
                                          background: 'none',
                                          border: 'none',
                                          cursor: 'pointer',
                                          padding: '4px 8px',
                                          display: 'flex',
                                          alignItems: 'center',
                                          color: '#6b7280',
                                          fontSize: '0.9rem'
                                        }}
                                        title={isExpanded ? 'Collapse ingredients' : 'Expand ingredients'}
                >
                                        {isExpanded ? '▼' : '▶'}
                </button>
                                      <span style={{ 
                                        padding: '4px 12px', 
                                        borderRadius: 4, 
                                        backgroundColor: '#eff6ff', 
                                        color: '#1e40af', 
                                        fontSize: '0.85rem', 
                                        fontWeight: 600,
                                        textTransform: 'capitalize'
                                      }}>
                                        {item.meal_key}
                                      </span>
                                      <h4 style={{ margin: 0, fontSize: '1.1rem', fontWeight: 600, flex: 1 }}>{item.dish_name}</h4>
                                      {!isExpanded && dishNutrients.calories && (
                                        <span style={{ color: '#6b7280', fontSize: '0.9rem' }}>
                                          {dishNutrients.calories.toFixed(0)} cal • {dishNutrients.protein_g?.toFixed(1) || 0}g protein • {item.ingredients?.length || 0} ingredients
                  </span>
                )}
              </div>

                                    {isExpanded && item.ingredients && item.ingredients.length > 0 && (
                                      <div style={{ overflowX: 'auto', marginBottom: 12 }}>
                                        <table style={{ width: '100%', borderCollapse: 'separate', borderSpacing: 0, fontSize: '0.85rem', minWidth: 1400, whiteSpace: 'nowrap' }}>
                                          <thead>
                                            <tr style={{ backgroundColor: '#f9fafb', borderBottom: '2px solid #e5e7eb' }}>
                                              <th style={{ padding: '8px 16px', textAlign: 'left', fontWeight: 600, position: 'sticky', left: 0, backgroundColor: '#f9fafb', zIndex: 10, minWidth: 180 }}>Ingredient</th>
                                              <th style={{ padding: '8px 16px', textAlign: 'left', fontWeight: 600, position: 'sticky', left: 196, backgroundColor: '#f9fafb', zIndex: 10, minWidth: 200 }}>Food Group</th>
                                              <th style={{ padding: '8px 16px', textAlign: 'right', fontWeight: 600, minWidth: 120 }}>Portion</th>
                                              {/* Macros */}
                                              <th style={{ padding: '8px 16px', textAlign: 'right', fontWeight: 600, backgroundColor: '#eff6ff', minWidth: 70 }}>Cal</th>
                                              <th style={{ padding: '8px 16px', textAlign: 'right', fontWeight: 600, backgroundColor: '#eff6ff', minWidth: 75 }}>Prot (g)</th>
                                              <th style={{ padding: '8px 16px', textAlign: 'right', fontWeight: 600, backgroundColor: '#eff6ff', minWidth: 80 }}>Carbs (g)</th>
                                              <th style={{ padding: '8px 16px', textAlign: 'right', fontWeight: 600, backgroundColor: '#eff6ff', minWidth: 70 }}>Fat (g)</th>
                                              <th style={{ padding: '8px 16px', textAlign: 'right', fontWeight: 600, backgroundColor: '#eff6ff', minWidth: 75 }}>Fiber (g)</th>
                                              <th style={{ padding: '8px 16px', textAlign: 'right', fontWeight: 600, backgroundColor: '#eff6ff', minWidth: 75 }}>Sugar (g)</th>
                                              <th style={{ padding: '8px 16px', textAlign: 'right', fontWeight: 600, backgroundColor: '#eff6ff', minWidth: 75 }}>Na (mg)</th>
                                              {/* Vitamins */}
                                              <th style={{ padding: '8px 16px', textAlign: 'right', fontWeight: 600, backgroundColor: '#fef3c7', minWidth: 85 }}>Vit A (mcg)</th>
                                              <th style={{ padding: '8px 16px', textAlign: 'right', fontWeight: 600, backgroundColor: '#fef3c7', minWidth: 80 }}>Vit C (mg)</th>
                                              <th style={{ padding: '8px 16px', textAlign: 'right', fontWeight: 600, backgroundColor: '#fef3c7', minWidth: 85 }}>Vit D (mcg)</th>
                                              <th style={{ padding: '8px 16px', textAlign: 'right', fontWeight: 600, backgroundColor: '#fef3c7', minWidth: 80 }}>Vit E (mg)</th>
                                              <th style={{ padding: '8px 16px', textAlign: 'right', fontWeight: 600, backgroundColor: '#fef3c7', minWidth: 85 }}>Vit K (mcg)</th>
                                              <th style={{ padding: '8px 16px', textAlign: 'right', fontWeight: 600, backgroundColor: '#fef3c7', minWidth: 70 }}>B1 (mg)</th>
                                              <th style={{ padding: '8px 16px', textAlign: 'right', fontWeight: 600, backgroundColor: '#fef3c7', minWidth: 70 }}>B2 (mg)</th>
                                              <th style={{ padding: '8px 16px', textAlign: 'right', fontWeight: 600, backgroundColor: '#fef3c7', minWidth: 70 }}>B3 (mg)</th>
                                              <th style={{ padding: '8px 16px', textAlign: 'right', fontWeight: 600, backgroundColor: '#fef3c7', minWidth: 70 }}>B6 (mg)</th>
                                              <th style={{ padding: '8px 16px', textAlign: 'right', fontWeight: 600, backgroundColor: '#fef3c7', minWidth: 80 }}>B12 (mcg)</th>
                                              <th style={{ padding: '8px 16px', textAlign: 'right', fontWeight: 600, backgroundColor: '#fef3c7', minWidth: 85 }}>Folate (mcg)</th>
                                              {/* Minerals */}
                                              <th style={{ padding: '8px 16px', textAlign: 'right', fontWeight: 600, backgroundColor: '#d1fae5', minWidth: 75 }}>Ca (mg)</th>
                                              <th style={{ padding: '8px 16px', textAlign: 'right', fontWeight: 600, backgroundColor: '#d1fae5', minWidth: 70 }}>Fe (mg)</th>
                                              <th style={{ padding: '8px 16px', textAlign: 'right', fontWeight: 600, backgroundColor: '#d1fae5', minWidth: 75 }}>Mg (mg)</th>
                                              <th style={{ padding: '8px 16px', textAlign: 'right', fontWeight: 600, backgroundColor: '#d1fae5', minWidth: 70 }}>P (mg)</th>
                                              <th style={{ padding: '8px 16px', textAlign: 'right', fontWeight: 600, backgroundColor: '#d1fae5', minWidth: 70 }}>K (mg)</th>
                                              <th style={{ padding: '8px 16px', textAlign: 'right', fontWeight: 600, backgroundColor: '#d1fae5', minWidth: 70 }}>Zn (mg)</th>
                                              <th style={{ padding: '8px 16px', textAlign: 'right', fontWeight: 600, backgroundColor: '#d1fae5', minWidth: 70 }}>Cu (mg)</th>
                                              <th style={{ padding: '8px 16px', textAlign: 'right', fontWeight: 600, backgroundColor: '#d1fae5', minWidth: 70 }}>Mn (mg)</th>
                                              <th style={{ padding: '8px 16px', textAlign: 'right', fontWeight: 600, backgroundColor: '#d1fae5', minWidth: 75 }}>Se (mcg)</th>
                                            </tr>
                                          </thead>
                                          <tbody>
                                            {item.ingredients.map((ing, ingIdx) => {
                                              const nut = ing.nutrients?.values || {};
                                              return (
                                                <tr key={ingIdx} style={{ borderBottom: '1px solid #f3f4f6' }}>
                                                  <td style={{ padding: '8px 16px', position: 'sticky', left: 0, backgroundColor: 'white', zIndex: 5 }}>{ing.name}</td>
                                                  <td style={{ padding: '8px 16px', color: '#6b7280', fontSize: '0.8rem', position: 'sticky', left: 196, backgroundColor: 'white', zIndex: 5 }}>
                                                    {ing.food_group_key || '-'}
                                                  </td>
                                                  <td style={{ padding: '8px 16px', textAlign: 'right', color: '#6b7280', fontSize: '0.8rem' }}>
                                                    {ing.portion_display || '-'}
                                                  </td>
                                                  {/* Macros */}
                                                  <td style={{ padding: '8px 16px', textAlign: 'right' }}>{nut.calories?.toFixed(0) || '-'}</td>
                                                  <td style={{ padding: '8px 16px', textAlign: 'right' }}>{nut.protein_g?.toFixed(1) || '-'}</td>
                                                  <td style={{ padding: '8px 16px', textAlign: 'right' }}>{nut.carbs_g?.toFixed(1) || '-'}</td>
                                                  <td style={{ padding: '8px 16px', textAlign: 'right' }}>{nut.fat_g?.toFixed(1) || '-'}</td>
                                                  <td style={{ padding: '8px 16px', textAlign: 'right' }}>{nut.fiber_g?.toFixed(1) || '-'}</td>
                                                  <td style={{ padding: '8px 16px', textAlign: 'right' }}>{nut.sugar_g?.toFixed(1) || '-'}</td>
                                                  <td style={{ padding: '8px 16px', textAlign: 'right' }}>{nut.sodium_mg?.toFixed(0) || '-'}</td>
                                                  {/* Vitamins */}
                                                  <td style={{ padding: '8px 16px', textAlign: 'right' }}>{nut.vitamin_a_mcg?.toFixed(1) || '-'}</td>
                                                  <td style={{ padding: '8px 16px', textAlign: 'right' }}>{nut.vitamin_c_mg?.toFixed(1) || '-'}</td>
                                                  <td style={{ padding: '8px 16px', textAlign: 'right' }}>{nut.vitamin_d_mcg?.toFixed(1) || '-'}</td>
                                                  <td style={{ padding: '8px 16px', textAlign: 'right' }}>{nut.vitamin_e_mg?.toFixed(1) || '-'}</td>
                                                  <td style={{ padding: '8px 16px', textAlign: 'right' }}>{nut.vitamin_k_mcg?.toFixed(1) || '-'}</td>
                                                  <td style={{ padding: '8px 16px', textAlign: 'right' }}>{nut.vitamin_b1_mg?.toFixed(2) || '-'}</td>
                                                  <td style={{ padding: '8px 16px', textAlign: 'right' }}>{nut.vitamin_b2_mg?.toFixed(2) || '-'}</td>
                                                  <td style={{ padding: '8px 16px', textAlign: 'right' }}>{nut.vitamin_b3_mg?.toFixed(1) || '-'}</td>
                                                  <td style={{ padding: '8px 16px', textAlign: 'right' }}>{nut.vitamin_b6_mg?.toFixed(2) || '-'}</td>
                                                  <td style={{ padding: '8px 16px', textAlign: 'right' }}>{nut.vitamin_b12_mcg?.toFixed(1) || '-'}</td>
                                                  <td style={{ padding: '8px 16px', textAlign: 'right' }}>{nut.folate_mcg?.toFixed(0) || '-'}</td>
                                                  {/* Minerals */}
                                                  <td style={{ padding: '8px 16px', textAlign: 'right' }}>{nut.calcium_mg?.toFixed(0) || '-'}</td>
                                                  <td style={{ padding: '8px 16px', textAlign: 'right' }}>{nut.iron_mg?.toFixed(1) || '-'}</td>
                                                  <td style={{ padding: '8px 16px', textAlign: 'right' }}>{nut.magnesium_mg?.toFixed(0) || '-'}</td>
                                                  <td style={{ padding: '8px 16px', textAlign: 'right' }}>{nut.phosphorus_mg?.toFixed(0) || '-'}</td>
                                                  <td style={{ padding: '8px 16px', textAlign: 'right' }}>{nut.potassium_mg?.toFixed(0) || '-'}</td>
                                                  <td style={{ padding: '8px 16px', textAlign: 'right' }}>{nut.zinc_mg?.toFixed(1) || '-'}</td>
                                                  <td style={{ padding: '8px 16px', textAlign: 'right' }}>{nut.copper_mg?.toFixed(2) || '-'}</td>
                                                  <td style={{ padding: '8px 16px', textAlign: 'right' }}>{nut.manganese_mg?.toFixed(1) || '-'}</td>
                                                  <td style={{ padding: '8px 16px', textAlign: 'right' }}>{nut.selenium_mcg?.toFixed(0) || '-'}</td>
                                                </tr>
                                              );
                                            })}
                                            {item.dish_nutrients && (() => {
                                              const nut = item.dish_nutrients.values || {};
                                              return (
                                                <tr style={{ backgroundColor: '#f9fafb', fontWeight: 600, borderTop: '2px solid #e5e7eb' }}>
                                                  <td colSpan={3} style={{ padding: '8px 16px', position: 'sticky', left: 0, backgroundColor: '#f9fafb', zIndex: 5 }}>Dish Total</td>
                                                  {/* Macros */}
                                                  <td style={{ padding: '8px 16px', textAlign: 'right' }}>{nut.calories?.toFixed(0) || '-'}</td>
                                                  <td style={{ padding: '8px 16px', textAlign: 'right' }}>{nut.protein_g?.toFixed(1) || '-'}</td>
                                                  <td style={{ padding: '8px 16px', textAlign: 'right' }}>{nut.carbs_g?.toFixed(1) || '-'}</td>
                                                  <td style={{ padding: '8px 16px', textAlign: 'right' }}>{nut.fat_g?.toFixed(1) || '-'}</td>
                                                  <td style={{ padding: '8px 16px', textAlign: 'right' }}>{nut.fiber_g?.toFixed(1) || '-'}</td>
                                                  <td style={{ padding: '8px 16px', textAlign: 'right' }}>{nut.sugar_g?.toFixed(1) || '-'}</td>
                                                  <td style={{ padding: '8px 16px', textAlign: 'right' }}>{nut.sodium_mg?.toFixed(0) || '-'}</td>
                                                  {/* Vitamins */}
                                                  <td style={{ padding: '8px 16px', textAlign: 'right' }}>{nut.vitamin_a_mcg?.toFixed(1) || '-'}</td>
                                                  <td style={{ padding: '8px 16px', textAlign: 'right' }}>{nut.vitamin_c_mg?.toFixed(1) || '-'}</td>
                                                  <td style={{ padding: '8px 16px', textAlign: 'right' }}>{nut.vitamin_d_mcg?.toFixed(1) || '-'}</td>
                                                  <td style={{ padding: '8px 16px', textAlign: 'right' }}>{nut.vitamin_e_mg?.toFixed(1) || '-'}</td>
                                                  <td style={{ padding: '8px 16px', textAlign: 'right' }}>{nut.vitamin_k_mcg?.toFixed(1) || '-'}</td>
                                                  <td style={{ padding: '8px 16px', textAlign: 'right' }}>{nut.vitamin_b1_mg?.toFixed(2) || '-'}</td>
                                                  <td style={{ padding: '8px 16px', textAlign: 'right' }}>{nut.vitamin_b2_mg?.toFixed(2) || '-'}</td>
                                                  <td style={{ padding: '8px 16px', textAlign: 'right' }}>{nut.vitamin_b3_mg?.toFixed(1) || '-'}</td>
                                                  <td style={{ padding: '8px 16px', textAlign: 'right' }}>{nut.vitamin_b6_mg?.toFixed(2) || '-'}</td>
                                                  <td style={{ padding: '8px 16px', textAlign: 'right' }}>{nut.vitamin_b12_mcg?.toFixed(1) || '-'}</td>
                                                  <td style={{ padding: '8px 16px', textAlign: 'right' }}>{nut.folate_mcg?.toFixed(0) || '-'}</td>
                                                  {/* Minerals */}
                                                  <td style={{ padding: '8px 16px', textAlign: 'right' }}>{nut.calcium_mg?.toFixed(0) || '-'}</td>
                                                  <td style={{ padding: '8px 16px', textAlign: 'right' }}>{nut.iron_mg?.toFixed(1) || '-'}</td>
                                                  <td style={{ padding: '8px 16px', textAlign: 'right' }}>{nut.magnesium_mg?.toFixed(0) || '-'}</td>
                                                  <td style={{ padding: '8px 16px', textAlign: 'right' }}>{nut.phosphorus_mg?.toFixed(0) || '-'}</td>
                                                  <td style={{ padding: '8px 16px', textAlign: 'right' }}>{nut.potassium_mg?.toFixed(0) || '-'}</td>
                                                  <td style={{ padding: '8px 16px', textAlign: 'right' }}>{nut.zinc_mg?.toFixed(1) || '-'}</td>
                                                  <td style={{ padding: '8px 16px', textAlign: 'right' }}>{nut.copper_mg?.toFixed(2) || '-'}</td>
                                                  <td style={{ padding: '8px 16px', textAlign: 'right' }}>{nut.manganese_mg?.toFixed(1) || '-'}</td>
                                                  <td style={{ padding: '8px 16px', textAlign: 'right' }}>{nut.selenium_mcg?.toFixed(0) || '-'}</td>
                                                </tr>
                                              );
                                            })()}
                                          </tbody>
                                        </table>
                                      </div>
                                    )}

                                    {/* Recipe: directly below ingredients (draft edit screen) */}
                                    {isExpanded && (() => {
                                      const recipeSteps = (item as any)?.recipe_steps || (item as any)?.premium_recipe?.steps || [];
                                      const hasRecipe = Array.isArray(recipeSteps) && recipeSteps.length > 0;
                                      if (!hasRecipe) return null;
                                      return (
                                        <div style={{ marginTop: 12, paddingTop: 12, borderTop: '1px solid #e5e7eb' }}>
                                          <div style={{ fontSize: '0.9rem', fontWeight: 600, marginBottom: 8, color: '#374151' }}>📝 Recipe</div>
                                          <ol style={{ margin: 0, paddingLeft: 20, fontSize: '0.9rem', lineHeight: 1.6 }}>
                                            {recipeSteps.map((step: string, stepIdx: number) => (
                                              <li key={stepIdx} style={{ marginBottom: stepIdx < recipeSteps.length - 1 ? 6 : 0 }}>
                                                {step}
                                              </li>
                                            ))}
                                          </ol>
                                        </div>
                                      );
                                    })()}
                                  </div>
                                );
                              })}
                            </div>
                          </div>
                        ))
                      ) : (
                        <div style={{ padding: 16, backgroundColor: '#f9fafb', borderRadius: 8, color: '#6b7280' }}>
                          <p style={{ margin: 0 }}>No days in draft.</p>
                        </div>
                      )}
                  </div>

                  <div style={{ display: 'flex', gap: 12, marginTop: 12, flexWrap: 'wrap', alignItems: 'center' }}>
                    <button type="button" className="secondary-button" onClick={handleSaveDraft} disabled={loading}>
                      Save Draft Changes
                    </button>
                    <div style={{ flex: 1 }} />
                    <input
                      placeholder="Optional message to patient…"
                      value={messageToPatient}
                      onChange={(e) => setMessageToPatient(e.target.value)}
                      style={{ minWidth: 320, padding: '8px 12px', border: '1px solid #d1d5db', borderRadius: 4 }}
                    />
                    <button
                      type="button"
                      className="primary-button"
                      onClick={handleApprove}
                      disabled={loading || !draft?.version_id || generationProgress !== null}
                    >
                      Approve & Send to Patient
                    </button>
                  </div>
                </>
              ) : null}

              {/* Draft step: version detail or upload/chat. Versions list uses single table view at top level for both fixed manual and AI-generated. */}
              {((isFixedManual && ((fixedManualSubView === 'versions' || fixedManualSubView === 'meal_plan' || fixedManualSubView === 'upload') || shouldShowFixedManualProgressChat)) || (!isFixedManual && (fixedManualSubView === 'versions' || fixedManualSubView === 'meal_plan' || fixedManualSubView === 'upload'))) || showOnlyChat ? (
                <>
                  {/* Version detail content, or upload+progress+chat (fixed), or chat only (adaptive when subview is upload) */}
                  {(fixedManualSubView === 'meal_plan' || (fixedManualSubView === 'upload' && (shouldShowFixedManualProgressChat || !isFixedManual)) || showOnlyChat) && (
                    <>
                  {!showOnlyChat && fixedManualSubView === 'meal_plan' && (
                    <div
                      style={{
                        display: 'flex',
                        gap: 8,
                        flexWrap: 'wrap',
                        marginTop: 20,
                        marginBottom: 8,
                      }}
                    >
                      <button
                        type="button"
                        className={`patient-tab ${existingPlanDetailTab === 'overview' ? 'active' : ''}`}
                        onClick={() => setExistingPlanDetailTab('overview')}
                        style={{ display: 'inline-flex', alignItems: 'center', gap: 8 }}
                      >
                        <LayoutDashboard size={16} strokeWidth={2} />
                        <span>Overview</span>
                      </button>
                      <button
                        type="button"
                        className={`patient-tab ${existingPlanDetailTab === 'nutrient_comparison' ? 'active' : ''}`}
                        onClick={() => setExistingPlanDetailTab('nutrient_comparison')}
                        style={{ display: 'inline-flex', alignItems: 'center', gap: 8 }}
                      >
                        <Sparkles size={16} strokeWidth={2} />
                        <span>Nutrition Comparison</span>
                      </button>
                      <button
                        type="button"
                        className={`patient-tab ${existingPlanDetailTab === 'daily_meal_plan' ? 'active' : ''}`}
                        onClick={() => setExistingPlanDetailTab('daily_meal_plan')}
                        style={{ display: 'inline-flex', alignItems: 'center', gap: 8 }}
                      >
                        <UtensilsCrossed size={16} strokeWidth={2} />
                        <span>Weekly Schedule</span>
                      </button>
                      <button
                        type="button"
                        className={`patient-tab ${existingPlanDetailTab === 'chat' ? 'active' : ''}`}
                        onClick={() => setExistingPlanDetailTab('chat')}
                        style={{ display: 'inline-flex', alignItems: 'center', gap: 8 }}
                      >
                        <Send size={16} strokeWidth={2} />
                        <span>Chat</span>
                      </button>
                    </div>
                  )}
                  {/* Overview tab (edit flow): daily average pie chart + vitamins/minerals */}
                  {!showOnlyChat && fixedManualSubView === 'meal_plan' && existingPlanDetailTab === 'overview' && (
                    <MealPlanOverviewCards
                      summary={fixedManualTotalsSummary}
                      marginTop={0}
                      emptyMessage="Overview data is not available for this version yet."
                    />
                  )}
                  {/* Nutrient Comparison tab: toggle between table and graph view. */}
                  {!showOnlyChat && fixedManualSubView === 'meal_plan' &&
                    existingPlanDetailTab === 'nutrient_comparison' &&
                    !chatStatus?.waiting_for_doctor_response && (
                    <>
                      <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginTop: 24, marginBottom: 12 }}>
                        <span style={{ fontSize: '0.85rem', fontWeight: 600, color: '#374151' }}>View:</span>
                        <button
                          type="button"
                          className={`patient-tab ${nutrientComparisonViewMode === 'table' ? 'active' : ''}`}
                          onClick={() => setNutrientComparisonViewMode('table')}
                          style={{ padding: '6px 12px', fontSize: '0.8rem' }}
                        >
                          Table
                        </button>
                        <button
                          type="button"
                          className={`patient-tab ${nutrientComparisonViewMode === 'graph' ? 'active' : ''}`}
                          onClick={() => setNutrientComparisonViewMode('graph')}
                          style={{ padding: '6px 12px', fontSize: '0.8rem' }}
                        >
                          Graph
                        </button>
                      </div>
                      {fixedManualTotalsSummary ? (
                        nutrientComparisonViewMode === 'table'
                          ? renderNutrientComparisonTable(fixedManualTotalsSummary)
                          : renderNutrientComparisonGraphs(fixedManualTotalsSummary, {
                              marginTop: 0,
                              emptyMessage: 'Nutrient data is loading or not available for this version.',
                            })
                      ) : (
                        <div style={{ padding: 24, textAlign: 'center', color: '#6b7280', fontSize: '0.95rem' }}>
                          Nutrient data is loading or not available for this version.
                        </div>
                      )}
                    </>
                  )}

                  {/* Uploaded Plan Preview (workflow state) - hide in Chat tab; hide once we have a saved version selected */}
                  {existingPlanDetailTab !== 'chat' &&
                    !fixedManualSelectedVersion &&
                    !chatStatus?.waiting_for_doctor_response &&
                    chatStatus?.plan_preview &&
                    (chatStatus.status === 'ready_for_review' || chatStatus.plan_approved) &&
                    Array.isArray((chatStatus.plan_preview as any).daily_plans) &&
                    (chatStatus.plan_preview as any).daily_plans.length > 0 && (
                      <div className="np-subcard" style={{ marginTop: 24, padding: 16 }}>
                        <h3 style={{ marginTop: 0, marginBottom: 12, fontSize: '1.1rem', fontWeight: 600 }}>
                          Uploaded Meal Plan Details
                        </h3>
                        <div style={{ marginBottom: 12, color: '#6b7280', fontSize: '0.9rem' }}>
                          {(chatStatus.plan_preview as any).daily_plans.length} day
                          {(chatStatus.plan_preview as any).daily_plans.length !== 1 ? 's' : ''}{' '}
                          •{' '}
                          {(chatStatus.plan_preview as any).daily_plans.reduce((sum: number, day: any) => {
                            const mealsOrItems = (day?.meals || day?.items || []) as any[];
                            return sum + (Array.isArray(mealsOrItems) ? mealsOrItems.length : 0);
                          }, 0)}{' '}
                          total meals
                    </div>

                        <div style={{ marginTop: 8, overflowX: 'auto' }}>
                          {(chatStatus.plan_preview as any).daily_plans.map((day: any, dayIdx: number) => (
                            <div
                              key={dayIdx}
                              style={{
                                marginBottom: 24,
                                border: '1px solid #e5e7eb',
                                borderRadius: 8,
                                overflow: 'hidden',
                              }}
                            >
                              <div
                                style={{
                                  padding: 12,
                                  backgroundColor: '#f9fafb',
                                  borderBottom: '1px solid #e5e7eb',
                                  fontWeight: 600,
                                }}
                              >
                                {day.date
                                  ? new Date(day.date).toLocaleDateString('en-US', {
                                      weekday: 'long',
                                      year: 'numeric',
                                      month: 'long',
                                      day: 'numeric',
                                    })
                                  : `Day ${dayIdx + 1}`}
                              </div>
                              <div style={{ padding: 16 }}>
                                {(() => {
                                  // Support both shapes:
                                  // - upload template: day.meals[]
                                  // - persisted version: day.items[]
                                  const mealsOrItems = (day?.meals || day?.items || []) as any[];
                                  if (!Array.isArray(mealsOrItems) || mealsOrItems.length === 0) {
                                    return (
                                      <div style={{ padding: 12, color: '#9ca3af', fontSize: '0.9rem' }}>
                                        No meals available for this day.
                                      </div>
                                    );
                                  }

                                  return mealsOrItems.map((meal: any, mealIdx: number) => {
                                  const dishKey = `uploaded-${dayIdx}-${mealIdx}`;
                                  const isExpanded = expandedDraftDishes.has(dishKey);
                                  const toggleExpanded = () => {
                                    setExpandedDraftDishes((prev) => {
                                      const next = new Set(prev);
                                      if (next.has(dishKey)) {
                                        next.delete(dishKey);
                                      } else {
                                        next.add(dishKey);
                                      }
                                      return next;
                                    });
                                  };
                                  const dishNutrients =
                                    (meal.dish_nutrients && (meal.dish_nutrients.values || meal.dish_nutrients)) || {};

                                  return (
                                    <div
                                      key={mealIdx}
                                      style={{
                                        marginBottom: mealIdx < mealsOrItems.length - 1 ? 20 : 0,
                                        paddingBottom: mealIdx < mealsOrItems.length - 1 ? 20 : 0,
                                        borderBottom:
                                          mealIdx < mealsOrItems.length - 1 ? '1px solid #e5e7eb' : 'none',
                                      }}
                                    >
                                      <div
                                        style={{
                                          display: 'flex',
                                          alignItems: 'center',
                                          gap: 12,
                                          marginBottom: 12,
                                        }}
                                      >
                                        <button
                                          type="button"
                                          onClick={toggleExpanded}
                                          style={{
                                            background: 'none',
                                            border: 'none',
                                            cursor: 'pointer',
                                            padding: '4px 8px',
                                            display: 'flex',
                                            alignItems: 'center',
                                            color: '#6b7280',
                                            fontSize: '0.9rem',
                                          }}
                                          title={isExpanded ? 'Collapse ingredients' : 'Expand ingredients'}
                                        >
                                          {isExpanded ? '▼' : '▶'}
                                        </button>
                                        <span
                                          style={{
                                            padding: '4px 12px',
                                            borderRadius: 4,
                                            backgroundColor: '#eff6ff',
                                            color: '#1e40af',
                                            fontSize: '0.85rem',
                                            fontWeight: 600,
                                            textTransform: 'capitalize',
                                          }}
                                        >
                                          {meal.meal_key || meal.meal_type || 'meal'}
                                        </span>
                                        <h4
                                          style={{
                                            margin: 0,
                                            fontSize: '1.1rem',
                                            fontWeight: 600,
                                            flex: 1,
                                          }}
                                        >
                                          {meal.dish_name || 'Dish'}
                                        </h4>
                                        {!isExpanded && dishNutrients.calories && (
                                          <span style={{ color: '#6b7280', fontSize: '0.9rem' }}>
                                            {dishNutrients.calories.toFixed(0)} cal •{' '}
                                            {(dishNutrients.protein_g ?? 0).toFixed(1)}g protein •{' '}
                                            {(meal.ingredients || []).length} ingredients
                                          </span>
                                        )}
                                      </div>

                                      {/* Recipe Dropdown */}
                                      {(() => {
                                        const recipeSteps = meal.recipe_steps || meal.premium_recipe?.steps || [];
                                        const hasRecipe = Array.isArray(recipeSteps) && recipeSteps.length > 0;
                                        const recipeKey = `recipe-${dayIdx}-${mealIdx}`;
                                        const isRecipeExpanded = expandedRecipes.has(recipeKey);
                                        
                                        if (!hasRecipe) return null;
                                        
                                        return (
                                          <div style={{ marginTop: 12, marginBottom: 12 }}>
                                            <button
                                              type="button"
                                              onClick={() => {
                                                setExpandedRecipes((prev) => {
                                                  const next = new Set(prev);
                                                  if (next.has(recipeKey)) {
                                                    next.delete(recipeKey);
                                                  } else {
                                                    next.add(recipeKey);
                                                  }
                                                  return next;
                                                });
                                              }}
                                              style={{
                                                width: '100%',
                                                padding: '10px 16px',
                                                backgroundColor: isRecipeExpanded ? '#f3f4f6' : '#f9fafb',
                                                border: '1px solid #e5e7eb',
                                                borderRadius: 6,
                                                cursor: 'pointer',
                                                display: 'flex',
                                                alignItems: 'center',
                                                justifyContent: 'space-between',
                                                fontSize: '0.9rem',
                                                fontWeight: 600,
                                                color: '#374151',
                                                transition: 'background-color 0.2s',
                                              }}
                                            >
                                              <span>📝 Recipe</span>
                                              <span>{isRecipeExpanded ? '▼' : '▶'}</span>
                                            </button>
                                            {isRecipeExpanded && (
                                              <div
                                                style={{
                                                  marginTop: 8,
                                                  padding: '12px 16px',
                                                  backgroundColor: '#ffffff',
                                                  border: '1px solid #e5e7eb',
                                                  borderRadius: 6,
                                                  fontSize: '0.9rem',
                                                  lineHeight: 1.6,
                                                }}
                                              >
                                                <ol style={{ margin: 0, paddingLeft: 20 }}>
                                                  {recipeSteps.map((step: string, stepIdx: number) => (
                                                    <li key={stepIdx} style={{ marginBottom: stepIdx < recipeSteps.length - 1 ? 8 : 0 }}>
                                                      {step}
                                                    </li>
                                                  ))}
                                                </ol>
                                              </div>
                                            )}
                                          </div>
                                        );
                                      })()}

                                      {isExpanded && Array.isArray(meal.ingredients) && meal.ingredients.length > 0 && (
                                        <div style={{ overflowX: 'auto', marginBottom: 12 }}>
                                          <table
                                            style={{
                                              width: '100%',
                                              borderCollapse: 'separate',
                                              borderSpacing: 0,
                                              fontSize: '0.85rem',
                                              minWidth: 1400,
                                              whiteSpace: 'nowrap',
                                            }}
                                          >
                                            <thead>
                                              <tr
                                                style={{
                                                  backgroundColor: '#f9fafb',
                                                  borderBottom: '2px solid #e5e7eb',
                                                }}
                                              >
                                                <th
                                                  style={{
                                                    padding: '8px 16px',
                                                    textAlign: 'left',
                                                    fontWeight: 600,
                                                    position: 'sticky',
                                                    left: 0,
                                                    backgroundColor: '#f9fafb',
                                                    zIndex: 10,
                                                    minWidth: 180,
                                                  }}
                                                >
                                                  Ingredient
                                                </th>
                                                <th
                                                  style={{
                                                    padding: '8px 16px',
                                                    textAlign: 'left',
                                                    fontWeight: 600,
                                                    position: 'sticky',
                                                    left: 196,
                                                    backgroundColor: '#f9fafb',
                                                    zIndex: 10,
                                                    minWidth: 200,
                                                  }}
                                                >
                                                  Food Group
                                                </th>
                                                <th
                                                  style={{
                                                    padding: '8px 16px',
                                                    textAlign: 'right',
                                                    fontWeight: 600,
                                                    minWidth: 120,
                                                  }}
                                                >
                                                  Portion
                                                </th>
                                                {/* Macros */}
                                                <th
                                                  style={{
                                                    padding: '8px 16px',
                                                    textAlign: 'right',
                                                    fontWeight: 600,
                                                    backgroundColor: '#eff6ff',
                                                    minWidth: 70,
                                                  }}
                                                >
                                                  Cal
                                                </th>
                                                <th
                                                  style={{
                                                    padding: '8px 16px',
                                                    textAlign: 'right',
                                                    fontWeight: 600,
                                                    backgroundColor: '#eff6ff',
                                                    minWidth: 75,
                                                  }}
                                                >
                                                  Prot (g)
                                                </th>
                                                <th
                                                  style={{
                                                    padding: '8px 16px',
                                                    textAlign: 'right',
                                                    fontWeight: 600,
                                                    backgroundColor: '#eff6ff',
                                                    minWidth: 80,
                                                  }}
                                                >
                                                  Carbs (g)
                                                </th>
                                                <th
                                                  style={{
                                                    padding: '8px 16px',
                                                    textAlign: 'right',
                                                    fontWeight: 600,
                                                    backgroundColor: '#eff6ff',
                                                    minWidth: 70,
                                                  }}
                                                >
                                                  Fat (g)
                                                </th>
                                                <th
                                                  style={{
                                                    padding: '8px 16px',
                                                    textAlign: 'right',
                                                    fontWeight: 600,
                                                    backgroundColor: '#eff6ff',
                                                    minWidth: 75,
                                                  }}
                                                >
                                                  Fiber (g)
                                                </th>
                                                <th
                                                  style={{
                                                    padding: '8px 16px',
                                                    textAlign: 'right',
                                                    fontWeight: 600,
                                                    backgroundColor: '#eff6ff',
                                                    minWidth: 75,
                                                  }}
                                                >
                                                  Sugar (g)
                                                </th>
                                                <th
                                                  style={{
                                                    padding: '8px 16px',
                                                    textAlign: 'right',
                                                    fontWeight: 600,
                                                    backgroundColor: '#eff6ff',
                                                    minWidth: 75,
                                                  }}
                                                >
                                                  Na (mg)
                                                </th>
                                                {/* Vitamins */}
                                                <th
                                                  style={{
                                                    padding: '8px 16px',
                                                    textAlign: 'right',
                                                    fontWeight: 600,
                                                    backgroundColor: '#fef3c7',
                                                    minWidth: 85,
                                                  }}
                                                >
                                                  Vit A (mcg)
                                                </th>
                                                <th
                                                  style={{
                                                    padding: '8px 16px',
                                                    textAlign: 'right',
                                                    fontWeight: 600,
                                                    backgroundColor: '#fef3c7',
                                                    minWidth: 80,
                                                  }}
                                                >
                                                  Vit C (mg)
                                                </th>
                                                <th
                                                  style={{
                                                    padding: '8px 16px',
                                                    textAlign: 'right',
                                                    fontWeight: 600,
                                                    backgroundColor: '#fef3c7',
                                                    minWidth: 85,
                                                  }}
                                                >
                                                  Vit D (mcg)
                                                </th>
                                                <th
                                                  style={{
                                                    padding: '8px 16px',
                                                    textAlign: 'right',
                                                    fontWeight: 600,
                                                    backgroundColor: '#fef3c7',
                                                    minWidth: 80,
                                                  }}
                                                >
                                                  Vit E (mg)
                                                </th>
                                                <th
                                                  style={{
                                                    padding: '8px 16px',
                                                    textAlign: 'right',
                                                    fontWeight: 600,
                                                    backgroundColor: '#fef3c7',
                                                    minWidth: 85,
                                                  }}
                                                >
                                                  Vit K (mcg)
                                                </th>
                                                <th
                                                  style={{
                                                    padding: '8px 16px',
                                                    textAlign: 'right',
                                                    fontWeight: 600,
                                                    backgroundColor: '#fef3c7',
                                                    minWidth: 70,
                                                  }}
                                                >
                                                  B1 (mg)
                                                </th>
                                                <th
                                                  style={{
                                                    padding: '8px 16px',
                                                    textAlign: 'right',
                                                    fontWeight: 600,
                                                    backgroundColor: '#fef3c7',
                                                    minWidth: 70,
                                                  }}
                                                >
                                                  B2 (mg)
                                                </th>
                                                <th
                                                  style={{
                                                    padding: '8px 16px',
                                                    textAlign: 'right',
                                                    fontWeight: 600,
                                                    backgroundColor: '#fef3c7',
                                                    minWidth: 70,
                                                  }}
                                                >
                                                  B3 (mg)
                                                </th>
                                                <th
                                                  style={{
                                                    padding: '8px 16px',
                                                    textAlign: 'right',
                                                    fontWeight: 600,
                                                    backgroundColor: '#fef3c7',
                                                    minWidth: 70,
                                                  }}
                                                >
                                                  B6 (mg)
                                                </th>
                                                <th
                                                  style={{
                                                    padding: '8px 16px',
                                                    textAlign: 'right',
                                                    fontWeight: 600,
                                                    backgroundColor: '#fef3c7',
                                                    minWidth: 80,
                                                  }}
                                                >
                                                  B12 (mcg)
                                                </th>
                                                <th
                                                  style={{
                                                    padding: '8px 16px',
                                                    textAlign: 'right',
                                                    fontWeight: 600,
                                                    backgroundColor: '#fef3c7',
                                                    minWidth: 85,
                                                  }}
                                                >
                                                  Folate (mcg)
                                                </th>
                                                {/* Minerals */}
                                                <th
                                                  style={{
                                                    padding: '8px 16px',
                                                    textAlign: 'right',
                                                    fontWeight: 600,
                                                    backgroundColor: '#d1fae5',
                                                    minWidth: 75,
                                                  }}
                                                >
                                                  Ca (mg)
                                                </th>
                                                <th
                                                  style={{
                                                    padding: '8px 16px',
                                                    textAlign: 'right',
                                                    fontWeight: 600,
                                                    backgroundColor: '#d1fae5',
                                                    minWidth: 70,
                                                  }}
                                                >
                                                  Fe (mg)
                                                </th>
                                                <th
                                                  style={{
                                                    padding: '8px 16px',
                                                    textAlign: 'right',
                                                    fontWeight: 600,
                                                    backgroundColor: '#d1fae5',
                                                    minWidth: 75,
                                                  }}
                                                >
                                                  Mg (mg)
                                                </th>
                                                <th
                                                  style={{
                                                    padding: '8px 16px',
                                                    textAlign: 'right',
                                                    fontWeight: 600,
                                                    backgroundColor: '#d1fae5',
                                                    minWidth: 70,
                                                  }}
                                                >
                                                  P (mg)
                                                </th>
                                                <th
                                                  style={{
                                                    padding: '8px 16px',
                                                    textAlign: 'right',
                                                    fontWeight: 600,
                                                    backgroundColor: '#d1fae5',
                                                    minWidth: 70,
                                                  }}
                                                >
                                                  K (mg)
                                                </th>
                                                <th
                                                  style={{
                                                    padding: '8px 16px',
                                                    textAlign: 'right',
                                                    fontWeight: 600,
                                                    backgroundColor: '#d1fae5',
                                                    minWidth: 70,
                                                  }}
                                                >
                                                  Zn (mg)
                                                </th>
                                                <th
                                                  style={{
                                                    padding: '8px 16px',
                                                    textAlign: 'right',
                                                    fontWeight: 600,
                                                    backgroundColor: '#d1fae5',
                                                    minWidth: 70,
                                                  }}
                                                >
                                                  Cu (mg)
                                                </th>
                                                <th
                                                  style={{
                                                    padding: '8px 16px',
                                                    textAlign: 'right',
                                                    fontWeight: 600,
                                                    backgroundColor: '#d1fae5',
                                                    minWidth: 70,
                                                  }}
                                                >
                                                  Mn (mg)
                                                </th>
                                                <th
                                                  style={{
                                                    padding: '8px 16px',
                                                    textAlign: 'right',
                                                    fontWeight: 600,
                                                    backgroundColor: '#d1fae5',
                                                    minWidth: 75,
                                                  }}
                                                >
                                                  Se (mcg)
                                                </th>
                                              </tr>
                                            </thead>
                                            <tbody>
                                              {meal.ingredients.map((ing: any, ingIdx: number) => {
                                                const nut =
                                                  (ing.nutrients && (ing.nutrients.values || ing.nutrients)) || {};
                                                return (
                                                  <tr key={ingIdx} style={{ borderBottom: '1px solid #f3f4f6' }}>
                                                    <td
                                                      style={{
                                                        padding: '8px 16px',
                                                        position: 'sticky',
                                                        left: 0,
                                                        backgroundColor: 'white',
                                                        zIndex: 5,
                                                      }}
                                                    >
                                                      {ing.name}
                                                    </td>
                                                    <td
                                                      style={{
                                                        padding: '8px 16px',
                                                        color: '#6b7280',
                                                        fontSize: '0.8rem',
                                                        position: 'sticky',
                                                        left: 196,
                                                        backgroundColor: 'white',
                                                        zIndex: 5,
                                                      }}
                                                    >
                                                      {ing.food_group_key || '-'}
                                                    </td>
                                                    <td
                                                      style={{
                                                        padding: '8px 16px',
                                                        textAlign: 'right',
                                                        color: '#6b7280',
                                                        fontSize: '0.8rem',
                                                      }}
                                                    >
                                                      {ing.portion_display || '-'}
                                                    </td>
                                                    {/* Macros */}
                                                    <td style={{ padding: '8px 16px', textAlign: 'right' }}>
                                                      {nut.calories?.toFixed(0) || '-'}
                                                    </td>
                                                    <td style={{ padding: '8px 16px', textAlign: 'right' }}>
                                                      {nut.protein_g?.toFixed(1) || '-'}
                                                    </td>
                                                    <td style={{ padding: '8px 16px', textAlign: 'right' }}>
                                                      {nut.carbs_g?.toFixed(1) || '-'}
                                                    </td>
                                                    <td style={{ padding: '8px 16px', textAlign: 'right' }}>
                                                      {nut.fat_g?.toFixed(1) || '-'}
                                                    </td>
                                                    <td style={{ padding: '8px 16px', textAlign: 'right' }}>
                                                      {nut.fiber_g?.toFixed(1) || '-'}
                                                    </td>
                                                    <td style={{ padding: '8px 16px', textAlign: 'right' }}>
                                                      {nut.sugar_g?.toFixed(1) || '-'}
                                                    </td>
                                                    <td style={{ padding: '8px 16px', textAlign: 'right' }}>
                                                      {nut.sodium_mg?.toFixed(0) || '-'}
                                                    </td>
                                                    {/* Vitamins */}
                                                    <td style={{ padding: '8px 16px', textAlign: 'right' }}>
                                                      {nut.vitamin_a_mcg?.toFixed(1) || '-'}
                                                    </td>
                                                    <td style={{ padding: '8px 16px', textAlign: 'right' }}>
                                                      {nut.vitamin_c_mg?.toFixed(1) || '-'}
                                                    </td>
                                                    <td style={{ padding: '8px 16px', textAlign: 'right' }}>
                                                      {nut.vitamin_d_mcg?.toFixed(1) || '-'}
                                                    </td>
                                                    <td style={{ padding: '8px 16px', textAlign: 'right' }}>
                                                      {nut.vitamin_e_mg?.toFixed(1) || '-'}
                                                    </td>
                                                    <td style={{ padding: '8px 16px', textAlign: 'right' }}>
                                                      {nut.vitamin_k_mcg?.toFixed(1) || '-'}
                                                    </td>
                                                    <td style={{ padding: '8px 16px', textAlign: 'right' }}>
                                                      {nut.vitamin_b1_mg?.toFixed(2) || '-'}
                                                    </td>
                                                    <td style={{ padding: '8px 16px', textAlign: 'right' }}>
                                                      {nut.vitamin_b2_mg?.toFixed(2) || '-'}
                                                    </td>
                                                    <td style={{ padding: '8px 16px', textAlign: 'right' }}>
                                                      {nut.vitamin_b3_mg?.toFixed(1) || '-'}
                                                    </td>
                                                    <td style={{ padding: '8px 16px', textAlign: 'right' }}>
                                                      {nut.vitamin_b6_mg?.toFixed(2) || '-'}
                                                    </td>
                                                    <td style={{ padding: '8px 16px', textAlign: 'right' }}>
                                                      {nut.vitamin_b12_mcg?.toFixed(1) || '-'}
                                                    </td>
                                                    <td style={{ padding: '8px 16px', textAlign: 'right' }}>
                                                      {nut.folate_mcg?.toFixed(0) || '-'}
                                                    </td>
                                                    {/* Minerals */}
                                                    <td style={{ padding: '8px 16px', textAlign: 'right' }}>
                                                      {nut.calcium_mg?.toFixed(0) || '-'}
                                                    </td>
                                                    <td style={{ padding: '8px 16px', textAlign: 'right' }}>
                                                      {nut.iron_mg?.toFixed(1) || '-'}
                                                    </td>
                                                    <td style={{ padding: '8px 16px', textAlign: 'right' }}>
                                                      {nut.magnesium_mg?.toFixed(0) || '-'}
                                                    </td>
                                                    <td style={{ padding: '8px 16px', textAlign: 'right' }}>
                                                      {nut.phosphorus_mg?.toFixed(0) || '-'}
                                                    </td>
                                                    <td style={{ padding: '8px 16px', textAlign: 'right' }}>
                                                      {nut.potassium_mg?.toFixed(0) || '-'}
                                                    </td>
                                                    <td style={{ padding: '8px 16px', textAlign: 'right' }}>
                                                      {nut.zinc_mg?.toFixed(1) || '-'}
                                                    </td>
                                                    <td style={{ padding: '8px 16px', textAlign: 'right' }}>
                                                      {nut.copper_mg?.toFixed(2) || '-'}
                                                    </td>
                                                    <td style={{ padding: '8px 16px', textAlign: 'right' }}>
                                                      {nut.manganese_mg?.toFixed(1) || '-'}
                                                    </td>
                                                    <td style={{ padding: '8px 16px', textAlign: 'right' }}>
                                                      {nut.selenium_mcg?.toFixed(0) || '-'}
                                                    </td>
                                                  </tr>
                                                );
                                              })}
                                            </tbody>
                                          </table>
                                        </div>
                                      )}
                                    </div>
                                  );
                                });
                                })()}
                              </div>
                            </div>
                          ))}
                        </div>

                        {/* Grocery List Dropdown */}
                        {(() => {
                          const groceryList = (chatStatus?.plan_preview as any)?.grocery_list || [];
                          const hasGroceryList = Array.isArray(groceryList) && groceryList.length > 0;
                          
                          if (!hasGroceryList) return null;
                          
                          return (
                            <div style={{ marginTop: 24 }}>
                              <button
                                type="button"
                                onClick={() => setExpandedGroceryList((prev) => !prev)}
                                style={{
                                  width: '100%',
                                  padding: '12px 16px',
                                  backgroundColor: expandedGroceryList ? '#f3f4f6' : '#f9fafb',
                                  border: '1px solid #e5e7eb',
                                  borderRadius: 6,
                                  cursor: 'pointer',
                                  display: 'flex',
                                  alignItems: 'center',
                                  justifyContent: 'space-between',
                                  fontSize: '1rem',
                                  fontWeight: 600,
                                  color: '#374151',
                                  transition: 'background-color 0.2s',
                                }}
                              >
                                <span>🛒 Grocery List ({groceryList.length} items)</span>
                                <span>{expandedGroceryList ? '▼' : '▶'}</span>
                              </button>
                              {expandedGroceryList && (
                                <div
                                  style={{
                                    marginTop: 8,
                                    padding: '16px',
                                    backgroundColor: '#ffffff',
                                    border: '1px solid #e5e7eb',
                                    borderRadius: 6,
                                    overflowX: 'auto',
                                  }}
                                >
                                  <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '0.9rem' }}>
                                    <thead>
                                      <tr style={{ borderBottom: '2px solid #e5e7eb', backgroundColor: '#f9fafb' }}>
                                        <th style={{ padding: '8px 12px', textAlign: 'left', fontWeight: 600 }}>Ingredient</th>
                                        <th style={{ padding: '8px 12px', textAlign: 'right', fontWeight: 600 }}>Amount</th>
                                        <th style={{ padding: '8px 12px', textAlign: 'left', fontWeight: 600 }}>Category</th>
                                        <th style={{ padding: '8px 12px', textAlign: 'left', fontWeight: 600 }}>Notes</th>
                                      </tr>
                                    </thead>
                                    <tbody>
                                      {groceryList.map((item: any, idx: number) => {
                                        const ingredientName = typeof item === 'string' 
                                          ? item 
                                          : item.ingredient_name || item.name || item.item || 'Unknown';
                                        const amount = typeof item === 'object' 
                                          ? (item.total_amount || item.quantity || item.amount || '') 
                                          : '';
                                        const unit = typeof item === 'object' 
                                          ? (item.unit || item.portion_display || '') 
                                          : '';
                                        const category = typeof item === 'object' 
                                          ? (item.category || item.food_group || '') 
                                          : '';
                                        const notes = typeof item === 'object' 
                                          ? (item.notes || item.source || '') 
                                          : '';
                                        const displayAmount = amount && unit ? `${amount}${unit}` : amount || unit || '-';
                                        return (
                                          <tr key={idx} style={{ borderBottom: '1px solid #f3f4f6' }}>
                                            <td style={{ padding: '8px 12px', fontWeight: 500 }}>{ingredientName}</td>
                                            <td style={{ padding: '8px 12px', textAlign: 'right' }}>{displayAmount}</td>
                                            <td style={{ padding: '8px 12px', color: '#6b7280' }}>{category || '-'}</td>
                                            <td style={{ padding: '8px 12px', color: '#6b7280', fontSize: '0.85em' }}>{notes || '-'}</td>
                                          </tr>
                                        );
                                      })}
                                    </tbody>
                                  </table>
                                </div>
                              )}
                            </div>
                          );
                        })()}
                      </div>
                    )}

                  {/* Progress + Chat: show when Chat tab, or showOnlyChat, or adaptive Create New Draft (chat only) */}
                  {(existingPlanDetailTab === 'chat' || showOnlyChat || (!isFixedManual && fixedManualSubView === 'upload')) && (
                    <div ref={progressChatSectionRef} className="np-subcard" style={{ marginTop: 24, padding: 0, overflow: 'hidden', borderRadius: 12, fontFamily: "'Inter', -apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif" }}>
                      {/* Header: icon + title + subtitle + menu */}
                      <div style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', gap: 12, padding: '16px 20px', background: '#f8fafc', borderBottom: '1px solid #e5e7eb' }}>
                        <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
                          <span style={{ color: '#64748b', flexShrink: 0 }}><UtensilsCrossed size={22} /></span>
                          <div>
                            <h3 style={{ margin: 0, fontSize: '1.15rem', fontWeight: 700, color: '#0f172a', fontFamily: 'inherit' }}>
                              Review & Modify Meal Plan
                            </h3>
                            <p style={{ margin: '2px 0 0', fontSize: '0.8rem', color: '#64748b', fontFamily: 'inherit' }}>
                              Refining nutrition strategy
                            </p>
                          </div>
                        </div>
                        <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
                          {mealPlanViewMode === 'view' && selectedPlan && (
                            <span className={`np-existing-plans-row-pill ${isPlanApprovedFromTable ? 'approved' : 'draft'}`} style={{ display: 'inline-block', padding: '6px 12px', fontSize: '0.85rem' }}>
                              Status: {selectedPlan.status ?? '—'}
                            </span>
                          )}
                          {(uploadedSessionId || chatStatus?.session_id) && uploadedSessionId && (
                            <>
                              <button
                                type="button"
                                className="secondary-button"
                                onClick={() => {
                                  if (isPlanApprovedFromTable || chatStatus?.plan_approved) {
                                    if (mealsBasePath) navigate(mealsBasePath);
                                    else setFixedManualSubView('versions');
                                  } else {
                                    setFixedManualSubView('versions');
                                  }
                                }}
                                disabled={uploadingDocument || sendingChatMessage}
                                title={isPlanApprovedFromTable || chatStatus?.plan_approved ? 'Close and go back to meal plan list' : 'Close'}
                                style={{ display: 'inline-flex', alignItems: 'center', gap: 8 }}
                              >
                                <X size={16} />
                                Close
                              </button>
                              {!isPlanApprovedFromTable && (
                                <button
                                  type="button"
                                  className="secondary-button"
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    void cancelAndDeleteCurrentDraft();
                                  }}
                                  disabled={uploadingDocument || sendingChatMessage}
                                  title="Cancel draft"
                                  style={{ color: '#b91c1c', borderColor: '#b91c1c', display: 'inline-flex', alignItems: 'center', gap: 8 }}
                                >
                                  <X size={16} />
                                  Cancel
                                </button>
                              )}
                            </>
                          )}
                          <button type="button" style={{ padding: 6, border: 'none', background: 'none', borderRadius: 6, color: '#64748b', cursor: 'pointer' }} aria-label="More options"><MoreVertical size={20} /></button>
                        </div>
                      </div>

                      {/* When no session: show Restoring... when refreshing from table-verify; else CTA to upload or chat message; when plan approved show Close to list */}
                      {!uploadedSessionId && !chatStatus?.session_id && !uploadingDocument && (
                        <div style={{
                          padding: 16,
                          backgroundColor: '#f8fafc',
                          border: '1px solid #e2e8f0',
                          borderRadius: 8,
                          marginBottom: 16,
                          textAlign: 'center',
                        }}>
                          {isFixedManual && (isPlanApprovedFromTable || chatStatus?.plan_approved) ? (
                            <>
                              <p style={{ margin: '0 0 12px', fontSize: '0.9rem', color: '#64748b' }}>
                                Plan approved and sent to patient.
                              </p>
                              <button
                                type="button"
                                className="secondary-button"
                                onClick={() => mealsBasePath && navigate(mealsBasePath)}
                                style={{ display: 'inline-flex', alignItems: 'center', gap: 8 }}
                              >
                                <X size={16} />
                                Close — Back to meal plan list
                              </button>
                            </>
                          ) : isFixedManual && loadingChatStatus && (['upload', 'meal_plan'].includes(new URLSearchParams(locationSearch).get('step') || '')) && selectedPlanId ? (
                            <p style={{ margin: 0, fontSize: '0.9rem', color: '#64748b', display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 8 }}>
                              <span style={{ display: 'inline-block', width: 18, height: 18, border: '2px solid #e2e8f0', borderTopColor: '#2563eb', borderRadius: '50%', animation: 'spin 0.8s linear infinite' }} />
                              Restoring your session…
                            </p>
                          ) : isFixedManual ? (
                            <>
                              <p style={{ margin: '0 0 12px', fontSize: '0.9rem', color: '#64748b' }}>
                                No processing in progress. Upload a document to start.
                              </p>
                              <button
                                type="button"
                                className="primary-button"
                                onClick={() => void handleCreateNewDraft()}
                              >
                                Create new version & upload
                              </button>
                            </>
                          ) : (
                            <p style={{ margin: 0, fontSize: '0.9rem', color: '#64748b' }}>
                              Use the chat below to refine requirements and generate a new draft.
                            </p>
                          )}
                        </div>
                      )}

                      {/* When backend is waiting for doctor response, show prominent banner */}
                      {chatStatus?.waiting_for_doctor_response && (
                        <div
                          style={{
                            marginBottom: 16,
                            padding: 16,
                            backgroundColor: '#fef3c7',
                            border: '1px solid #f59e0b',
                            borderRadius: 8,
                            display: 'flex',
                            alignItems: 'flex-start',
                            gap: 12,
                          }}
                        >
                          <span style={{ fontSize: '1.25rem' }} role="img" aria-label="Alert">⚠️</span>
                          <div>
                            <strong style={{ display: 'block', marginBottom: 4, color: '#92400e' }}>
                              Your response is needed
                            </strong>
                            <span style={{ fontSize: '0.9rem', color: '#b45309' }}>
                              The system is waiting for your answer. Please respond in the chat below (type your answer or use the suggested options if shown).
                            </span>
                          </div>
                        </div>
                      )}

                      {/* Progress Bar - show when status is neither completed nor approved. */}
                      {(uploadingDocument || (chatStatus && !isCompletedOrApproved(chatStatus))) && (
                        <div style={{
                          backgroundColor: '#ffffff',
                          border: '1px solid #e5e7eb',
                          borderRadius: '8px',
                          padding: '16px',
                          marginBottom: '16px',
                          boxShadow: '0 1px 3px rgba(0, 0, 0, 0.1)'
                        }}>
                          <div style={{ marginBottom: '12px' }}>
                            <div style={{ 
                              display: 'flex', 
                              justifyContent: 'space-between', 
                              alignItems: 'center',
                              marginBottom: '8px'
                            }}>
                              <h4 style={{ margin: 0, fontSize: '14px', fontWeight: 600 }}>Processing Progress</h4>
                              <span style={{ fontSize: '13px', color: '#6b7280' }}>
                                {Math.round(currentProgress * 100)}%
                              </span>
                            </div>
                            <div style={{
                              width: '100%',
                              height: '8px',
                              backgroundColor: '#e5e7eb',
                              borderRadius: '4px',
                              overflow: 'hidden'
                            }}>
                              <div style={{
                                width: `${currentProgress * 100}%`,
                                height: '100%',
                                backgroundColor: '#2563eb',
                                transition: 'width 0.3s ease'
                              }} />
                            </div>
                          </div>
                          {currentProgressDescription && (
                            <div style={{ 
                              fontSize: '13px', 
                              color: '#6b7280',
                              marginTop: '8px'
                            }}>
                              {currentProgressDescription}
                            </div>
                          )}
                          <div style={{ display: 'flex', gap: '8px', paddingTop: '12px', paddingBottom: '4px', overflowX: 'auto' }}>
                            {uploadBackendSteps.map((step, index) => {
                              const isComplete = index < uploadBackendStepIndex;
                              const isActive = index === uploadBackendStepIndex;
                              
                              return (
                                <div
                                  key={step.key}
                                  style={{
                                    flex: '0 0 180px',
                                    minWidth: 180,
                                    display: 'flex',
                                    flexDirection: 'column',
                                    alignItems: 'center',
                                    gap: '6px',
                                    padding: '8px',
                                    borderRadius: '6px',
                                    backgroundColor: isActive ? '#eff6ff' : isComplete ? '#f0fdf4' : 'transparent',
                                    transition: 'background-color 0.2s'
                                  }}
                                >
                                  <div style={{
                                    width: '28px',
                                    height: '28px',
                                    borderRadius: '50%',
                                    display: 'flex',
                                    alignItems: 'center',
                                    justifyContent: 'center',
                                    backgroundColor: isComplete ? '#10b981' : isActive ? '#2563eb' : '#e5e7eb',
                                    color: isComplete || isActive ? '#ffffff' : '#9ca3af',
                                    fontWeight: 600,
                                    fontSize: '12px',
                                    transition: 'all 0.2s'
                                  }}>
                                    {isComplete ? '✓' : index + 1}
                                  </div>
                                  <div style={{
                                    fontSize: '11px',
                                    textAlign: 'center',
                                    color: isActive ? '#2563eb' : isComplete ? '#059669' : '#9ca3af',
                                    fontWeight: isActive ? 600 : 400,
                                    lineHeight: '1.3'
                                  }}>
                                    {step.label}
                                  </div>
                                  {isActive && (
                                    <div style={{
                                      fontSize: '9px',
                                      color: '#2563eb',
                                      fontWeight: 500
                                    }}>
                                      In Progress
                                    </div>
                                  )}
                                </div>
                              );
                            })}
                          </div>
                        </div>
                      )}

                      {/* Table-verify: editable table + Verify & Continue when waiting_for_table_verify */}
                      {chatStatus?.waiting_for_table_verify && tableEditDraft && Array.isArray(tableEditDraft.daily_plans) && (
                        <div className="np-subcard" style={{ marginBottom: 16, padding: 16 }}>
                          <p style={{ marginTop: 0, marginBottom: 12, color: '#374151', fontSize: '0.9rem' }}>
                            Review and edit the table below, then click <strong>Verify & Continue</strong>.
                          </p>
                          <div style={{ overflowX: 'auto', marginBottom: 12, WebkitOverflowScrolling: 'touch' }}>
                            <table style={{ width: 'max-content', minWidth: Math.max(600, 180 + 280 * (tableEditDraft.daily_plans?.length || 0)), borderCollapse: 'collapse', fontSize: '0.85rem', tableLayout: 'fixed' }}>
                              <thead>
                                <tr style={{ borderBottom: '2px solid #e5e7eb', backgroundColor: '#f9fafb' }}>
                                  <th style={{ padding: '10px 12px', textAlign: 'left', fontWeight: 600, position: 'sticky', left: 0, backgroundColor: '#f9fafb', zIndex: 10, minWidth: 180, width: 180 }}>
                                    Meal slot
                                  </th>
                                  {(tableEditDraft.daily_plans as any[]).map((day: any, dayIdx: number) => (
                                    <th key={dayIdx} style={{ padding: '10px 12px', textAlign: 'left', fontWeight: 600, minWidth: 280, width: 280 }}>
                                      {day.date ? new Date(day.date).toLocaleDateString('en-US', { weekday: 'short', month: 'short', day: 'numeric' }) : `Day ${dayIdx + 1}`}
                                    </th>
                                  ))}
                                </tr>
                              </thead>
                              <tbody>
                                {(() => {
                                  const portionUnitOptions = PORTION_UNIT_OPTIONS;
                                  const dailyPlans = (tableEditDraft.daily_plans || []) as any[];
                                  const maxMeals = dailyPlans.length === 0 ? 0 : Math.max(...dailyPlans.map((d: any) => (d.meals || []).length));
                                  if (maxMeals === 0) return null;
                                  const fromData = new Set<string>();
                                  dailyPlans.forEach((d: any) => (d.meals || []).forEach((m: any) => { if (m.meal_type) fromData.add(m.meal_type); }));
                                  // Prefer meal type strings from data so we don't coerce e.g. "Dinner" -> "dinner"; keep same value during verification.
                                  const uniqueMealTypeOptions = (() => {
                                    const byKey = new Map<string, string>();
                                    Array.from(fromData).forEach((m) => byKey.set(normalizeMealTypeKey(m), m));
                                    MEAL_TYPE_OPTIONS.forEach((opt) => {
                                      const k = normalizeMealTypeKey(opt);
                                      if (!byKey.has(k)) byKey.set(k, opt);
                                    });
                                    return Array.from(byKey.values());
                                  })();
                                  const timeSet = new Set<string>(ALL_TIME_SLOT_OPTIONS);
                                  dailyPlans.forEach((d: any) => (d.meals || []).forEach((m: any) => { if (m.meal_time) timeSet.add(m.meal_time); }));
                                  const allTimeOptions = Array.from(timeSet).sort();
                                  return Array.from({ length: maxMeals }, (_, slotIdx) => {
                                    const rowMeal = dailyPlans[0]?.meals?.[slotIdx];
                                    const rowMealType = rowMeal?.meal_type ?? '';
                                    const rowMealTime = rowMeal?.meal_time ?? '';
                                    return (
                                    <tr key={slotIdx} style={{ borderBottom: '1px solid #e5e7eb' }}>
                                      <td style={{ padding: '10px 12px', backgroundColor: '#f9fafb', position: 'sticky', left: 0, zIndex: 5, minWidth: 200, width: 200, verticalAlign: 'top', fontSize: '0.85rem' }}>
                                        <div style={{ display: 'flex', alignItems: 'center', gap: 6, marginBottom: 6 }}>
                                          <span style={{ fontWeight: 600 }}>Slot {slotIdx + 1}</span>
                                        </div>
                                        <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
                                          <select
                                            value={rowMealType}
                                            onChange={(e) => {
                                              const v = e.target.value || undefined;
                                              setTableEditDraft((prev) => {
                                                if (!prev?.daily_plans) return prev;
                                                const next = JSON.parse(JSON.stringify(prev));
                                                next.daily_plans.forEach((_: any, dayIdx: number) => {
                                                  const m = next.daily_plans[dayIdx]?.meals?.[slotIdx];
                                                  if (m) m.meal_type = v;
                                                });
                                                return next;
                                              });
                                            }}
                                            style={{
                                              width: '100%',
                                              padding: '6px 8px',
                                              fontSize: '0.8rem',
                                              ...(rowMealType === '' || !rowMealType ? { backgroundColor: '#dcfce7', border: '1px solid #22c55e' } : {}),
                                            }}
                                          >
                                            <option value="">Meal type</option>
                                            {uniqueMealTypeOptions.map((mt: string) => (
                                              <option key={mt} value={mt}>{MEAL_TYPE_LABELS[mt] || mt.replace(/_/g, ' ')}</option>
                                            ))}
                                          </select>
                                          <select
                                            value={rowMealTime}
                                            onChange={(e) => {
                                              const v = e.target.value || undefined;
                                              setTableEditDraft((prev) => {
                                                if (!prev?.daily_plans) return prev;
                                                const next = JSON.parse(JSON.stringify(prev));
                                                next.daily_plans.forEach((_: any, dayIdx: number) => {
                                                  const m = next.daily_plans[dayIdx]?.meals?.[slotIdx];
                                                  if (m) m.meal_time = v;
                                                });
                                                return next;
                                              });
                                            }}
                                            style={{
                                              width: '100%',
                                              padding: '6px 8px',
                                              fontSize: '0.8rem',
                                              ...(rowMealTime === '' || !rowMealTime ? { backgroundColor: '#dcfce7', border: '1px solid #22c55e' } : {}),
                                            }}
                                          >
                                            <option value="">Time</option>
                                            {allTimeOptions.map((t) => (
                                              <option key={t} value={t}>{t}</option>
                                            ))}
                                          </select>
                                        </div>
                                      </td>
                                      {dailyPlans.map((day: any, dayIdx: number) => {
                                        const dayMeals = day.meals || [];
                                        const meal = dayMeals[slotIdx];
                                        const isThisCellPopoverOpen = copyPopoverCell?.slotIdx === slotIdx && copyPopoverCell?.dayIdx === dayIdx;
                                        const otherDayIndexesForCell = dailyPlans.length <= 1 ? [] : dailyPlans.map((_: any, i: number) => i).filter((i: number) => i !== dayIdx);
                                        const openPopoverUpward = slotIdx >= maxMeals - 2;
                                        return (
                                          <td key={dayIdx} style={{ padding: dailyPlans.length > 1 ? '36px 12px 8px 12px' : '8px 12px', verticalAlign: 'top', minWidth: 280, width: 280, position: 'relative', zIndex: isThisCellPopoverOpen ? 10000 : undefined }}>
                                            {dailyPlans.length > 1 && (
                                              <div style={{ position: 'absolute', top: 8, right: 12, zIndex: 10 }} ref={isThisCellPopoverOpen ? (el) => { copyPopoverAnchorRef.current = el; } : undefined}>
                                                <button
                                                  type="button"
                                                  onClick={() => {
                                                    setCopyPopoverCell((prev) => (prev?.slotIdx === slotIdx && prev?.dayIdx === dayIdx ? null : { slotIdx, dayIdx }));
                                                    setCopyTargetDayIndexes([]);
                                                    setCopyTargetSlotIndexes([]);
                                                  }}
                                                  title="Copy this cell to other days or slots"
                                                  style={{ display: 'inline-flex', alignItems: 'center', justifyContent: 'center', width: 24, height: 24, padding: 0, border: '1px solid #e5e7eb', borderRadius: 4, background: isThisCellPopoverOpen ? '#e5e7eb' : '#fff', color: '#374151', cursor: 'pointer' }}
                                                >
                                                  <Copy size={12} />
                                                </button>
                                                {isThisCellPopoverOpen && (
                                                  <div
                                                    style={{
                                                      position: 'absolute',
                                                      right: 0,
                                                      ...(openPopoverUpward
                                                        ? { bottom: '100%', marginBottom: 4 }
                                                        : { top: '100%', marginTop: 4 }),
                                                      minWidth: 180,
                                                      padding: 10,
                                                      background: '#fff',
                                                      border: '1px solid #e5e7eb',
                                                      borderRadius: 8,
                                                      boxShadow: '0 4px 12px rgba(0,0,0,0.15)',
                                                      zIndex: 10000,
                                                      fontSize: '0.8rem',
                                                    }}
                                                  >
                                                    <div style={{ fontWeight: 600, marginBottom: 8, color: '#374151' }}>Copy this cell to:</div>
                                                    <div style={{ marginBottom: 8 }}>
                                                      <div style={{ fontSize: '0.75rem', fontWeight: 600, color: '#6b7280', marginBottom: 4 }}>Same slot, other days</div>
                                                      <button
                                                        type="button"
                                                        onClick={() => copySlotToDays(slotIdx, otherDayIndexesForCell, dayIdx)}
                                                        style={{ display: 'block', width: '100%', marginBottom: 6, padding: '6px 8px', textAlign: 'left', border: '1px solid #d1d5db', borderRadius: 4, background: '#f9fafb', cursor: 'pointer', fontSize: '0.8rem' }}
                                                      >
                                                        All other days
                                                      </button>
                                                      {otherDayIndexesForCell.length > 0 && (
                                                        <div style={{ display: 'flex', flexDirection: 'column', gap: 2, maxHeight: 120, overflowY: 'auto' }}>
                                                          {otherDayIndexesForCell.map((dIdx: number) => {
                                                            const dateLabel = dailyPlans[dIdx]?.date ? new Date(dailyPlans[dIdx].date).toLocaleDateString('en-US', { weekday: 'short', month: 'short', day: 'numeric' }) : `Day ${dIdx + 1}`;
                                                            const checked = copyTargetDayIndexes.includes(dIdx);
                                                            return (
                                                              <label key={dIdx} style={{ display: 'flex', alignItems: 'center', gap: 6, cursor: 'pointer', fontSize: '0.8rem' }}>
                                                                <input
                                                                  type="checkbox"
                                                                  checked={checked}
                                                                  onChange={() => setCopyTargetDayIndexes((prev) => (prev.includes(dIdx) ? prev.filter((i) => i !== dIdx) : [...prev, dIdx]))}
                                                                />
                                                                <span>{dateLabel}</span>
                                                              </label>
                                                            );
                                                          })}
                                                        </div>
                                                      )}
                                                    </div>
                                                    {maxMeals > 1 && (
                                                      <div style={{ marginBottom: 8 }}>
                                                        <div style={{ fontSize: '0.75rem', fontWeight: 600, color: '#6b7280', marginBottom: 4 }}>Same day, other slots</div>
                                                        <button
                                                          type="button"
                                                          onClick={() => {
                                                            const otherSlots = Array.from({ length: maxMeals }, (_, i) => i).filter((i) => i !== slotIdx);
                                                            copyCellToDaysAndSlots(slotIdx, [], dayIdx, otherSlots);
                                                          }}
                                                          style={{ display: 'block', width: '100%', marginBottom: 6, padding: '6px 8px', textAlign: 'left', border: '1px solid #d1d5db', borderRadius: 4, background: '#f9fafb', cursor: 'pointer', fontSize: '0.8rem' }}
                                                        >
                                                          All other slots
                                                        </button>
                                                        <div style={{ display: 'flex', flexDirection: 'column', gap: 2, maxHeight: 100, overflowY: 'auto' }}>
                                                          {Array.from({ length: maxMeals }, (_, i) => i).filter((s) => s !== slotIdx).map((sIdx: number) => {
                                                            const checked = copyTargetSlotIndexes.includes(sIdx);
                                                            const slotMealType = dailyPlans[dayIdx]?.meals?.[sIdx]?.meal_type;
                                                            const slotLabel = slotMealType ? (MEAL_TYPE_LABELS[slotMealType] || slotMealType.replace(/_/g, ' ')) : `Slot ${sIdx + 1}`;
                                                            return (
                                                              <label key={sIdx} style={{ display: 'flex', alignItems: 'center', gap: 6, cursor: 'pointer', fontSize: '0.8rem' }}>
                                                                <input
                                                                  type="checkbox"
                                                                  checked={checked}
                                                                  onChange={() => setCopyTargetSlotIndexes((prev) => (prev.includes(sIdx) ? prev.filter((i) => i !== sIdx) : [...prev, sIdx]))}
                                                                />
                                                                <span>{slotLabel}</span>
                                                              </label>
                                                            );
                                                          })}
                                                        </div>
                                                      </div>
                                                    )}
                                                    <button
                                                      type="button"
                                                      onClick={() => copyCellToDaysAndSlots(slotIdx, copyTargetDayIndexes, dayIdx, copyTargetSlotIndexes)}
                                                      disabled={copyTargetDayIndexes.length === 0 && copyTargetSlotIndexes.length === 0}
                                                      style={{ width: '100%', padding: '6px 8px', border: '1px solid #22c55e', borderRadius: 4, background: (copyTargetDayIndexes.length === 0 && copyTargetSlotIndexes.length === 0) ? '#f3f4f6' : '#dcfce7', color: (copyTargetDayIndexes.length === 0 && copyTargetSlotIndexes.length === 0) ? '#9ca3af' : '#166534', cursor: (copyTargetDayIndexes.length === 0 && copyTargetSlotIndexes.length === 0) ? 'not-allowed' : 'pointer', fontWeight: 500, fontSize: '0.8rem' }}
                                                    >
                                                      Copy
                                                    </button>
                                                    <button
                                                      type="button"
                                                      onClick={() => { setCopyPopoverCell(null); setCopyTargetDayIndexes([]); setCopyTargetSlotIndexes([]); }}
                                                      style={{ marginTop: 6, padding: '4px 0', background: 'none', border: 'none', color: '#6b7280', cursor: 'pointer', fontSize: '0.75rem' }}
                                                    >
                                                      Cancel
                                                    </button>
                                                  </div>
                                                )}
                                              </div>
                                            )}
                                            {!meal ? (
                                              <span style={{ color: '#9ca3af', fontSize: '0.8rem' }}>—</span>
                                            ) : Array.isArray(meal.dish_groups) && meal.dish_groups.length > 0 ? (
                                              <div style={{ marginBottom: 0 }}>
                                                {(meal.dish_groups as any[]).map((grp: any, gi: number) => (
                                                  <div key={gi} style={{ marginBottom: 12 }}>
                                                    <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
                                                      {(grp.alternatives || []).map((alt: any, ai: number) => (
                                                        <div key={ai} style={{ padding: 8, border: '1px solid #e2e8f0', borderRadius: 6, backgroundColor: (gi > 0 || ai > 0) ? '#f8fafc' : '#fff' }}>
                                                          {gi > 0 && ai === 0 ? <div style={{ marginBottom: 4, fontWeight: 600, color: '#059669', fontSize: '0.75rem' }}>and</div> : null}
                                                          {ai > 0 ? <div style={{ marginBottom: 4, fontWeight: 600, color: '#2563eb', fontSize: '0.75rem' }}>or</div> : null}
                                                          <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 6 }}>
                                                            <input
                                                              type="text"
                                                              value={alt.dish_name || ''}
                                                              onChange={(e) => {
                                                                const v = e.target.value;
                                                                setTableEditDraft((prev) => {
                                                                  if (!prev?.daily_plans) return prev;
                                                                  const next = JSON.parse(JSON.stringify(prev));
                                                                  const altRef = next.daily_plans[dayIdx]?.meals?.[slotIdx]?.dish_groups?.[gi]?.alternatives?.[ai];
                                                                  if (altRef) altRef.dish_name = v;
                                                                  return next;
                                                                });
                                                              }}
                                                              placeholder="Dish name"
                                                              style={{ flex: 1, minWidth: 0, padding: '6px 8px', fontSize: '0.8rem' }}
                                                            />
                                                            <button
                                                              type="button"
                                                              onClick={() => {
                                                                setTableEditDraft((prev) => {
                                                                  if (!prev?.daily_plans) return prev;
                                                                  const next = JSON.parse(JSON.stringify(prev));
                                                                  const groups = next.daily_plans[dayIdx]?.meals?.[slotIdx]?.dish_groups;
                                                                  if (!Array.isArray(groups)) return next;
                                                                  const grp = groups[gi];
                                                                  if (grp && Array.isArray(grp.alternatives)) {
                                                                    grp.alternatives.splice(ai, 1);
                                                                    if (grp.alternatives.length === 0) groups.splice(gi, 1);
                                                                    if (groups.length === 0) {
                                                                      const m = next.daily_plans[dayIdx].meals[slotIdx];
                                                                      if (m) {
                                                                        delete m.dish_groups;
                                                                        m.dish_name = '';
                                                                        m.ingredients = [];
                                                                      }
                                                                    }
                                                                  }
                                                                  return next;
                                                                });
                                                              }}
                                                              title="Remove dish"
                                                              style={{ flexShrink: 0, width: 28, height: 28, padding: 0, border: '1px solid #e5e7eb', borderRadius: 4, background: '#fff', color: '#dc2626', fontSize: '1rem', lineHeight: 1, cursor: 'pointer' }}
                                                            >−</button>
                                                          </div>
                                                          {((alt.ingredients || []) as any[]).map((ing: any, ingIdx: number) => {
                                                            let portionSize: string = ing.portion_size != null && ing.portion_size !== '' ? String(ing.portion_size) : '';
                                                            let portionUnit: string = ing.portion_unit ?? '';
                                                            if ((!portionSize || !portionUnit) && ing.portion_display) {
                                                              const match = (ing.portion_display as string).trim().match(/^([\d./]+)\s*(.*)$/);
                                                              if (match) {
                                                                if (!portionSize) portionSize = match[1];
                                                                if (!portionUnit) portionUnit = match[2].trim() || '';
                                                              }
                                                            }
                                                            const unitOptions = portionUnit && !portionUnitOptions.includes(portionUnit) ? [portionUnit, ...portionUnitOptions] : portionUnitOptions;
                                                            const conditionBefore = (ing.condition_before === 'and' || ing.condition_before === 'or') ? ing.condition_before : '';
                                                            return (
                                                              <div key={ingIdx} style={{ marginBottom: 6 }}>
                                                                {ingIdx >= 1 ? (
                                                                  <div style={{ marginBottom: 2 }}>
                                                                    <select
                                                                      value={conditionBefore}
                                                                      onChange={(e) => {
                                                                        const v = e.target.value;
                                                                        const val = (v === 'and' || v === 'or') ? v : undefined;
                                                                        setTableEditDraft((prev) => {
                                                                          if (!prev?.daily_plans) return prev;
                                                                          const next = JSON.parse(JSON.stringify(prev));
                                                                          const ingRef = next.daily_plans[dayIdx]?.meals?.[slotIdx]?.dish_groups?.[gi]?.alternatives?.[ai]?.ingredients?.[ingIdx];
                                                                          if (ingRef) ingRef.condition_before = val ?? null;
                                                                          return next;
                                                                        });
                                                                      }}
                                                                      style={{ minWidth: 56, padding: '2px 6px', fontSize: '0.75rem', fontWeight: 600, color: '#059669', border: '1px solid #d1d5db', borderRadius: 4 }}
                                                                    >
                                                                      <option value="">—</option>
                                                                      <option value="and">and</option>
                                                                      <option value="or">or</option>
                                                                    </select>
                                                                  </div>
                                                                ) : null}
                                                                <div style={{ display: 'flex', alignItems: 'center', gap: 6, flexWrap: 'nowrap' }}>
                                                                  <input type="text" value={ing.name || ''} onChange={(e) => {
                                                                    const v = e.target.value;
                                                                    setTableEditDraft((prev) => {
                                                                      if (!prev?.daily_plans) return prev;
                                                                      const next = JSON.parse(JSON.stringify(prev));
                                                                      const ingRef = next.daily_plans[dayIdx]?.meals?.[slotIdx]?.dish_groups?.[gi]?.alternatives?.[ai]?.ingredients?.[ingIdx];
                                                                      if (ingRef) ingRef.name = v;
                                                                      return next;
                                                                    });
                                                                  }} placeholder="Ingredient" style={{ flex: '1 1 auto', minWidth: 0, fontSize: '0.8rem', padding: '4px 6px' }} />
                                                                  <input type="text" inputMode="decimal" value={portionSize} onChange={(e) => {
                                                                    const v = e.target.value;
                                                                    const num = v === '' ? null : parseFloat(v);
                                                                    setTableEditDraft((prev) => {
                                                                      if (!prev?.daily_plans) return prev;
                                                                      const next = JSON.parse(JSON.stringify(prev));
                                                                      const ingRef = next.daily_plans[dayIdx]?.meals?.[slotIdx]?.dish_groups?.[gi]?.alternatives?.[ai]?.ingredients?.[ingIdx];
                                                                      if (ingRef) {
                                                                        ingRef.portion_size = num;
                                                                        ingRef.portion_display = [num, ingRef.portion_unit].filter(Boolean).join(' ').trim() || undefined;
                                                                      }
                                                                      return next;
                                                                    });
                                                                  }} placeholder="Qty" style={{ flex: '0 0 52px', width: 52, padding: '4px 6px', fontSize: '0.75rem', ...(portionSize === '' || portionSize == null ? { backgroundColor: '#dcfce7', border: '1px solid #22c55e' } : {}) }} />
                                                                  <select value={portionUnit} onChange={(e) => {
                                                                    const v = e.target.value;
                                                                    setTableEditDraft((prev) => {
                                                                      if (!prev?.daily_plans) return prev;
                                                                      const next = JSON.parse(JSON.stringify(prev));
                                                                      const ingRef = next.daily_plans[dayIdx]?.meals?.[slotIdx]?.dish_groups?.[gi]?.alternatives?.[ai]?.ingredients?.[ingIdx];
                                                                      if (ingRef) {
                                                                        ingRef.portion_unit = v;
                                                                        if (v && (v.includes('/') || /\d/.test(v))) {
                                                                          ingRef.portion_size = v;
                                                                          ingRef.portion_display = v;
                                                                        } else {
                                                                          ingRef.portion_display = [ingRef.portion_size, v].filter(Boolean).join(' ').trim() || undefined;
                                                                        }
                                                                      }
                                                                      return next;
                                                                    });
                                                                  }} style={{ flex: '0 0 auto', minWidth: 72, padding: '4px 6px', fontSize: '0.75rem', ...(portionUnit === '' || !portionUnit ? { backgroundColor: '#dcfce7', border: '1px solid #22c55e' } : {}) }}>
                                                                    <option value="">Unit</option>
                                                                    {unitOptions.map((opt) => <option key={opt} value={opt}>{opt}</option>)}
                                                                  </select>
                                                                  <input type="text" value={ing.exception ?? ''} onChange={(e) => {
                                                                    const v = e.target.value;
                                                                    setTableEditDraft((prev) => {
                                                                      if (!prev?.daily_plans) return prev;
                                                                      const next = JSON.parse(JSON.stringify(prev));
                                                                      const ingRef = next.daily_plans[dayIdx]?.meals?.[slotIdx]?.dish_groups?.[gi]?.alternatives?.[ai]?.ingredients?.[ingIdx];
                                                                      if (ingRef) ingRef.exception = v || undefined;
                                                                      return next;
                                                                    });
                                                                  }} placeholder="e.g. except banana" title="Dish exception" style={{ flex: '1 1 100px', minWidth: 0, fontSize: '0.75rem', padding: '4px 6px' }} />
                                                                  <button type="button" onClick={() => {
                                                                    setTableEditDraft((prev) => {
                                                                      if (!prev?.daily_plans) return prev;
                                                                      const next = JSON.parse(JSON.stringify(prev));
                                                                      const ingList = next.daily_plans[dayIdx]?.meals?.[slotIdx]?.dish_groups?.[gi]?.alternatives?.[ai]?.ingredients;
                                                                      if (Array.isArray(ingList)) ingList.splice(ingIdx, 1);
                                                                      return next;
                                                                    });
                                                                  }} title="Remove ingredient" style={{ flex: '0 0 auto', width: 28, height: 28, padding: 0, border: '1px solid #e5e7eb', borderRadius: 4, background: '#fff', color: '#dc2626', fontSize: '1rem', lineHeight: 1, cursor: 'pointer' }}>−</button>
                                                                </div>
                                                              </div>
                                                            );
                                                          })}
                                                          <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8, marginTop: 8 }}>
                                                            <button type="button" onClick={() => {
                                                              setTableEditDraft((prev) => {
                                                                if (!prev?.daily_plans) return prev;
                                                                const next = JSON.parse(JSON.stringify(prev));
                                                                const grpRef = next.daily_plans[dayIdx]?.meals?.[slotIdx]?.dish_groups?.[gi];
                                                                if (grpRef) {
                                                                  if (!Array.isArray(grpRef.alternatives)) grpRef.alternatives = [];
                                                                  grpRef.alternatives.push({ dish_name: '', ingredients: [] });
                                                                }
                                                                return next;
                                                              });
                                                            }} title="Add dish as alternative (or)" style={{ flex: '1 1 auto', minWidth: 100, padding: '6px 8px', border: '1px dashed #d1d5db', borderRadius: 4, background: '#f9fafb', color: '#2563eb', fontSize: '0.8rem', cursor: 'pointer' }}>+ Add dish (or)</button>
                                                            <button type="button" onClick={() => {
                                                              setTableEditDraft((prev) => {
                                                                if (!prev?.daily_plans) return prev;
                                                                const next = JSON.parse(JSON.stringify(prev));
                                                                const groups = next.daily_plans[dayIdx]?.meals?.[slotIdx]?.dish_groups;
                                                                if (Array.isArray(groups)) {
                                                                  groups.push({ link_before: 'and', alternatives: [{ dish_name: '', ingredients: [] }] });
                                                                }
                                                                return next;
                                                              });
                                                            }} title="Add dish with previous (and)" style={{ flex: '1 1 auto', minWidth: 100, padding: '6px 8px', border: '1px dashed #d1d5db', borderRadius: 4, background: '#f9fafb', color: '#059669', fontSize: '0.8rem', cursor: 'pointer' }}>+ Add dish (and)</button>
                                                            <button type="button" onClick={() => {
                                                              setTableEditDraft((prev) => {
                                                                if (!prev?.daily_plans) return prev;
                                                                const next = JSON.parse(JSON.stringify(prev));
                                                                const altRef = next.daily_plans[dayIdx]?.meals?.[slotIdx]?.dish_groups?.[gi]?.alternatives?.[ai];
                                                                if (altRef) {
                                                                  if (!Array.isArray(altRef.ingredients)) altRef.ingredients = [];
                                                                  altRef.ingredients.push({ name: '', portion_display: '', portion_size: null, portion_unit: '' });
                                                                }
                                                                return next;
                                                              });
                                                            }} title="Add ingredient" style={{ flex: '1 1 auto', minWidth: 100, padding: '6px 8px', border: '1px dashed #d1d5db', borderRadius: 4, background: '#f9fafb', color: '#6b7280', fontSize: '0.8rem', cursor: 'pointer' }}>+ Add ingredient</button>
                                                          </div>
                                                        </div>
                                                      ))}
                                                    </div>
                                                  </div>
                                                ))}
                                              </div>
                                            ) : (
                                              <div style={{ marginBottom: 0 }}>
                                                <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 6 }}>
                                                  <input
                                                    type="text"
                                                    value={meal.dish_name || ''}
                                                    onChange={(e) => {
                                                      const v = e.target.value;
                                                      setTableEditDraft((prev) => {
                                                        if (!prev?.daily_plans) return prev;
                                                        const next = JSON.parse(JSON.stringify(prev));
                                                        const m = next.daily_plans[dayIdx]?.meals?.[slotIdx];
                                                        if (m) m.dish_name = v;
                                                        return next;
                                                      });
                                                    }}
                                                    placeholder="Dish name"
                                                    style={{ flex: 1, minWidth: 0, padding: '6px 8px', fontSize: '0.8rem' }}
                                                  />
                                                  <button
                                                    type="button"
                                                    onClick={() => {
                                                      setTableEditDraft((prev) => {
                                                        if (!prev?.daily_plans) return prev;
                                                        const next = JSON.parse(JSON.stringify(prev));
                                                        const m = next.daily_plans[dayIdx]?.meals?.[slotIdx];
                                                        if (m) {
                                                          m.dish_name = '';
                                                          m.ingredients = [];
                                                        }
                                                        return next;
                                                      });
                                                    }}
                                                    title="Remove dish"
                                                    style={{ flexShrink: 0, width: 28, height: 28, padding: 0, border: '1px solid #e5e7eb', borderRadius: 4, background: '#fff', color: '#dc2626', fontSize: '1rem', lineHeight: 1, cursor: 'pointer' }}
                                                  >−</button>
                                                </div>
                                                {(meal.ingredients || []).map((ing: any, ingIdx: number) => {
                                                        let portionSize: string = ing.portion_size != null && ing.portion_size !== '' ? String(ing.portion_size) : '';
                                                        let portionUnit: string = ing.portion_unit ?? '';
                                                        if ((!portionSize || !portionUnit) && ing.portion_display) {
                                                          const match = (ing.portion_display as string).trim().match(/^([\d./]+)\s*(.*)$/);
                                                          if (match) {
                                                            if (!portionSize) portionSize = match[1];
                                                            if (!portionUnit) portionUnit = match[2].trim() || '';
                                                          }
                                                        }
                                                        const unitOptions = portionUnit && !portionUnitOptions.includes(portionUnit)
                                                          ? [portionUnit, ...portionUnitOptions]
                                                          : portionUnitOptions;
                                                        const conditionBefore = (ing.condition_before === 'and' || ing.condition_before === 'or') ? ing.condition_before : '';
                                                        return (
                                                          <div key={ingIdx} style={{ marginBottom: 6 }}>
                                                            {ingIdx >= 1 ? (
                                                              <div style={{ display: 'flex', alignItems: 'center', marginBottom: 2 }}>
                                                                <select
                                                                  value={conditionBefore}
                                                                  onChange={(e) => {
                                                                    const v = e.target.value;
                                                                    const val = (v === 'and' || v === 'or') ? v : undefined;
                                                                    setTableEditDraft((prev) => {
                                                                      if (!prev?.daily_plans) return prev;
                                                                      const next = JSON.parse(JSON.stringify(prev));
                                                                      const ingRef = next.daily_plans[dayIdx]?.meals?.[slotIdx]?.ingredients?.[ingIdx];
                                                                      if (ingRef) ingRef.condition_before = val ?? null;
                                                                      return next;
                                                                    });
                                                                  }}
                                                                  style={{ minWidth: 56, padding: '2px 6px', fontSize: '0.75rem', fontWeight: 600, color: '#059669', border: '1px solid #d1d5db', borderRadius: 4 }}
                                                                  title="Link to previous item: and / or"
                                                                >
                                                                  <option value="">—</option>
                                                                  <option value="and">and</option>
                                                                  <option value="or">or</option>
                                                                </select>
                                                              </div>
                                                            ) : null}
                                                            <div
                                                              style={{ display: 'flex', alignItems: 'center', gap: 6, flexWrap: 'nowrap' }}
                                                            >
                                                            <input
                                                              type="text"
                                                              value={ing.name || ''}
                                                              onChange={(e) => {
                                                                const v = e.target.value;
                                                                setTableEditDraft((prev) => {
                                                                  if (!prev?.daily_plans) return prev;
                                                                  const next = JSON.parse(JSON.stringify(prev));
                                                                  const ingRef = next.daily_plans[dayIdx]?.meals?.[slotIdx]?.ingredients?.[ingIdx];
                                                                  if (ingRef) ingRef.name = v;
                                                                  return next;
                                                                });
                                                              }}
                                                              placeholder="Ingredient"
                                                              style={{ flex: '1 1 auto', minWidth: 0, fontSize: '0.8rem', padding: '4px 6px' }}
                                                            />
                                                            <input
                                                              type="text"
                                                              inputMode="decimal"
                                                              value={portionSize}
                                                              onChange={(e) => {
                                                                const v = e.target.value;
                                                                const num = v === '' ? null : parseFloat(v);
                                                                setTableEditDraft((prev) => {
                                                                  if (!prev?.daily_plans) return prev;
                                                                  const next = JSON.parse(JSON.stringify(prev));
                                                                  const ingRef = next.daily_plans[dayIdx]?.meals?.[slotIdx]?.ingredients?.[ingIdx];
                                                                  if (ingRef) {
                                                                    ingRef.portion_size = num;
                                                                    ingRef.portion_display = [num, ingRef.portion_unit].filter(Boolean).join(' ').trim() || undefined;
                                                                  }
                                                                  return next;
                                                                });
                                                              }}
                                                              placeholder="Qty"
                                                              style={{ flex: '0 0 52px', width: 52, padding: '4px 6px', fontSize: '0.75rem', ...(portionSize === '' || portionSize == null ? { backgroundColor: '#dcfce7', border: '1px solid #22c55e' } : {}) }}
                                                            />
                                                            <select
                                                              value={portionUnit}
                                                              onChange={(e) => {
                                                                const v = e.target.value;
                                                                setTableEditDraft((prev) => {
                                                                  if (!prev?.daily_plans) return prev;
                                                                  const next = JSON.parse(JSON.stringify(prev));
                                                                  const ingRef = next.daily_plans[dayIdx]?.meals?.[slotIdx]?.ingredients?.[ingIdx];
                                                                  if (ingRef) {
                                                                    ingRef.portion_unit = v;
                                                                    if (v && (v.includes('/') || /\d/.test(v))) {
                                                                      ingRef.portion_size = v;
                                                                      ingRef.portion_display = v;
                                                                    } else {
                                                                      ingRef.portion_display = [ingRef.portion_size, v].filter(Boolean).join(' ').trim() || undefined;
                                                                    }
                                                                  }
                                                                  return next;
                                                                });
                                                              }}
                                                              style={{ flex: '0 0 auto', minWidth: 72, padding: '4px 6px', fontSize: '0.75rem', ...(portionUnit === '' || !portionUnit ? { backgroundColor: '#dcfce7', border: '1px solid #22c55e' } : {}) }}
                                                            >
                                                              <option value="">Unit</option>
                                                              {unitOptions.map((opt) => (
                                                                <option key={opt} value={opt}>{opt}</option>
                                                              ))}
                                                            </select>
                                                            <input
                                                              type="text"
                                                              value={ing.exception ?? ''}
                                                              onChange={(e) => {
                                                                const v = e.target.value;
                                                                setTableEditDraft((prev) => {
                                                                  if (!prev?.daily_plans) return prev;
                                                                  const next = JSON.parse(JSON.stringify(prev));
                                                                  const ingRef = next.daily_plans[dayIdx]?.meals?.[slotIdx]?.ingredients?.[ingIdx];
                                                                  if (ingRef) ingRef.exception = v || undefined;
                                                                  return next;
                                                                });
                                                              }}
                                                              placeholder="e.g. except banana"
                                                              title="Dish exception"
                                                              style={{ flex: '1 1 100px', minWidth: 0, fontSize: '0.75rem', padding: '4px 6px' }}
                                                            />
                                                            <button
                                                              type="button"
                                                              onClick={() => {
                                                                setTableEditDraft((prev) => {
                                                                  if (!prev?.daily_plans) return prev;
                                                                  const next = JSON.parse(JSON.stringify(prev));
                                                                  const ingList = next.daily_plans[dayIdx]?.meals?.[slotIdx]?.ingredients;
                                                                  if (Array.isArray(ingList)) {
                                                                    ingList.splice(ingIdx, 1);
                                                                  }
                                                                  return next;
                                                                });
                                                              }}
                                                              title="Remove ingredient"
                                                              style={{ flex: '0 0 auto', width: 28, height: 28, padding: 0, border: '1px solid #e5e7eb', borderRadius: 4, background: '#fff', color: '#dc2626', fontSize: '1rem', lineHeight: 1, cursor: 'pointer' }}
                                                            >
                                                              −
                                                            </button>
                                                            </div>
                                                          </div>
                                                        );
                                                      })}
                                                      <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8, marginTop: 8 }}>
                                                        <button
                                                          type="button"
                                                          onClick={() => {
                                                            setTableEditDraft((prev) => {
                                                              if (!prev?.daily_plans) return prev;
                                                              const next = JSON.parse(JSON.stringify(prev));
                                                              const m = next.daily_plans[dayIdx]?.meals?.[slotIdx];
                                                              if (!m) return next;
                                                              const currentDish = { dish_name: m.dish_name || '', ingredients: Array.isArray(m.ingredients) ? JSON.parse(JSON.stringify(m.ingredients)) : [] };
                                                              m.dish_groups = [{ alternatives: [currentDish, { dish_name: '', ingredients: [] }] }];
                                                              delete m.dish_name;
                                                              delete m.ingredients;
                                                              return next;
                                                            });
                                                          }}
                                                          title="Add dish as alternative (or)"
                                                          style={{ flex: '1 1 auto', minWidth: 100, padding: '6px 8px', border: '1px dashed #d1d5db', borderRadius: 4, background: '#f9fafb', color: '#2563eb', fontSize: '0.8rem', cursor: 'pointer' }}
                                                        >
                                                          + Add dish (or)
                                                        </button>
                                                        <button
                                                          type="button"
                                                          onClick={() => {
                                                            setTableEditDraft((prev) => {
                                                              if (!prev?.daily_plans) return prev;
                                                              const next = JSON.parse(JSON.stringify(prev));
                                                              const m = next.daily_plans[dayIdx]?.meals?.[slotIdx];
                                                              if (!m) return next;
                                                              const currentDish = { dish_name: m.dish_name || '', ingredients: Array.isArray(m.ingredients) ? JSON.parse(JSON.stringify(m.ingredients)) : [] };
                                                              m.dish_groups = [{ alternatives: [currentDish] }, { link_before: 'and', alternatives: [{ dish_name: '', ingredients: [] }] }];
                                                              delete m.dish_name;
                                                              delete m.ingredients;
                                                              return next;
                                                            });
                                                          }}
                                                          title="Add dish with previous (and)"
                                                          style={{ flex: '1 1 auto', minWidth: 100, padding: '6px 8px', border: '1px dashed #d1d5db', borderRadius: 4, background: '#f9fafb', color: '#059669', fontSize: '0.8rem', cursor: 'pointer' }}
                                                        >
                                                          + Add dish (and)
                                                        </button>
                                                        <button
                                                          type="button"
                                                          onClick={() => {
                                                            setTableEditDraft((prev) => {
                                                              if (!prev?.daily_plans) return prev;
                                                              const next = JSON.parse(JSON.stringify(prev));
                                                              const ingList = next.daily_plans[dayIdx]?.meals?.[slotIdx]?.ingredients;
                                                              if (!Array.isArray(ingList)) {
                                                                next.daily_plans[dayIdx].meals[slotIdx].ingredients = [];
                                                              }
                                                              next.daily_plans[dayIdx].meals[slotIdx].ingredients.push({ name: '', portion_display: '', portion_size: null, portion_unit: '' });
                                                              return next;
                                                            });
                                                          }}
                                                          title="Add ingredient"
                                                          style={{ flex: '1 1 auto', minWidth: 100, padding: '6px 8px', border: '1px dashed #d1d5db', borderRadius: 4, background: '#f9fafb', color: '#6b7280', fontSize: '0.8rem', cursor: 'pointer' }}
                                                        >
                                                          + Add ingredient
                                                        </button>
                                                      </div>
                                                    </div>
                                            )}
                                          </td>
                                        );
                                      })}
                                    </tr>
                                    );
                                  });
                                })()}
                              </tbody>
                            </table>
                          </div>
                          {/* Overall exceptions (to be avoided) - editable list below table */}
                          <div style={{ marginTop: 16, marginBottom: 12 }}>
                            <div style={{ fontWeight: 600, color: '#374151', marginBottom: 8, fontSize: '0.9rem' }}>Overall exceptions (to avoid)</div>
                            <div style={{ border: '1px solid #e5e7eb', borderRadius: 8, overflow: 'hidden', backgroundColor: '#fff' }}>
                              {((tableEditDraft as any)?.overall_exceptions ?? []).map((item: string, idx: number) => (
                                <div key={idx} style={{ display: 'flex', alignItems: 'center', gap: 8, padding: '8px 12px', borderBottom: idx < ((tableEditDraft as any)?.overall_exceptions?.length ?? 1) - 1 ? '1px solid #e5e7eb' : 'none' }}>
                                  <input
                                    type="text"
                                    value={item}
                                    onChange={(e) => {
                                      const v = e.target.value;
                                      setTableEditDraft((prev) => {
                                        if (!prev) return prev;
                                        const next = JSON.parse(JSON.stringify(prev));
                                        if (!Array.isArray(next.overall_exceptions)) next.overall_exceptions = [];
                                        if (next.overall_exceptions[idx] !== undefined) next.overall_exceptions[idx] = v;
                                        return next;
                                      });
                                    }}
                                    placeholder="e.g. banana, fried foods"
                                    style={{ flex: 1, minWidth: 0, padding: '8px 10px', fontSize: '0.875rem' }}
                                  />
                                  <button
                                    type="button"
                                    onClick={() => {
                                      setTableEditDraft((prev) => {
                                        if (!prev) return prev;
                                        const next = JSON.parse(JSON.stringify(prev));
                                        if (Array.isArray(next.overall_exceptions)) next.overall_exceptions.splice(idx, 1);
                                        return next;
                                      });
                                    }}
                                    title="Remove"
                                    style={{ flexShrink: 0, width: 32, height: 32, padding: 0, border: '1px solid #e5e7eb', borderRadius: 6, background: '#fff', color: '#dc2626', fontSize: '1.1rem', lineHeight: 1, cursor: 'pointer' }}
                                  >
                                    −
                                  </button>
                                </div>
                              ))}
                              {(!(tableEditDraft as any)?.overall_exceptions || (tableEditDraft as any).overall_exceptions.length === 0) && (
                                <div style={{ padding: '12px', color: '#9ca3af', fontSize: '0.85rem' }}>No overall exceptions. Add items to avoid plan-wide.</div>
                              )}
                            </div>
                            <button
                              type="button"
                              onClick={() => {
                                setTableEditDraft((prev) => {
                                  if (!prev) return prev;
                                  const next = JSON.parse(JSON.stringify(prev));
                                  if (!Array.isArray(next.overall_exceptions)) next.overall_exceptions = [];
                                  next.overall_exceptions.push('');
                                  return next;
                                });
                              }}
                              style={{ marginTop: 8, padding: '8px 14px', border: '1px dashed #d1d5db', borderRadius: 6, background: '#f9fafb', color: '#059669', fontSize: '0.85rem', cursor: 'pointer', fontWeight: 500 }}
                            >
                              + Add exception
                            </button>
                          </div>
                          {tableVerifyValidationMessage ? (
                            <p style={{ marginBottom: 10, color: '#b91c1c', fontSize: '0.9rem' }}>{tableVerifyValidationMessage}</p>
                          ) : null}
                          {tableVerifyWarnings.length > 0 && !tableVerifyWarningsModalOpen ? (
                            <div style={{ marginBottom: 10, padding: '10px 12px', backgroundColor: '#fef3c7', border: '1px solid #f59e0b', borderRadius: 8, fontSize: '0.9rem' }}>
                              <strong style={{ color: '#92400e' }}>Warnings:</strong>
                              <span style={{ color: '#b45309', marginLeft: 6 }}>Review and click Verify & Continue again to see options (submit anyway or go back and change).</span>
                              <ul style={{ margin: '6px 0 0 0', paddingLeft: 18, color: '#b45309' }}>
                                {tableVerifyWarnings.map((w, i) => (
                                  <li key={i}>{w}</li>
                                ))}
                              </ul>
                            </div>
                          ) : null}
                          <button
                            type="button"
                            disabled={submittingTableVerify || !selectedPlanId || !(uploadedSessionId ?? chatStatus?.session_id)}
                            onClick={async () => {
                              const sid = uploadedSessionId ?? chatStatus?.session_id;
                              if (!selectedPlanId || sid == null || !tableEditDraft) return;
                              setTableVerifyWarnings([]);
                              // Binding: ensure all ingredients have quantity and unit filled
                              const dailyPlans = tableEditDraft.daily_plans || [];
                              for (let dayIdx = 0; dayIdx < dailyPlans.length; dayIdx++) {
                                const day = dailyPlans[dayIdx];
                                const meals = day?.meals || [];
                                const dayLabel = day?.date
                                  ? new Date(day.date).toLocaleDateString('en-US', { weekday: 'short', month: 'short', day: 'numeric' })
                                  : `Day ${dayIdx + 1}`;
                                for (let slotIdx = 0; slotIdx < meals.length; slotIdx++) {
                                  const meal = meals[slotIdx];
                                  if (!meal) continue;
                                  const checkIngredient = (ing: any, location: string) => {
                                    const qty = ing?.portion_size != null && String(ing.portion_size).trim() !== '' ? String(ing.portion_size).trim() : (ing?.total_amount != null && String(ing.total_amount).trim() !== '' ? String(ing.total_amount).trim() : '');
                                    const unit = (ing?.portion_unit || ing?.unit || '').trim();
                                    if (!qty || !unit) {
                                      const name = (ing?.name || ing?.ingredient_name || 'Ingredient').toString().trim() || 'Ingredient';
                                      return `${location}: "${name}" is missing ${!qty && !unit ? 'quantity and unit' : !qty ? 'quantity' : 'unit'}.`;
                                    }
                                    return null;
                                  };
                                  const locationStr = `${dayLabel}, Slot ${slotIdx + 1}`;
                                  if (Array.isArray(meal.dish_groups) && meal.dish_groups.length > 0) {
                                    for (let gi = 0; gi < meal.dish_groups.length; gi++) {
                                      const alts = meal.dish_groups[gi]?.alternatives || [];
                                      for (let ai = 0; ai < alts.length; ai++) {
                                        const ings = alts[ai]?.ingredients || [];
                                        for (let ii = 0; ii < ings.length; ii++) {
                                          const err = checkIngredient(ings[ii], locationStr);
                                          if (err) {
                                            setTableVerifyValidationMessage(err);
                                            return;
                                          }
                                        }
                                      }
                                    }
                                  } else {
                                    const ings = meal.ingredients || [];
                                    for (let ii = 0; ii < ings.length; ii++) {
                                      const err = checkIngredient(ings[ii], locationStr);
                                      if (err) {
                                        setTableVerifyValidationMessage(err);
                                        return;
                                      }
                                    }
                                  }
                                }
                              }
                              // Non-blocking warnings: duplicate slot time/meal type, and meal-type vs time sanity
                              const firstDay = dailyPlans[0];
                              const meals = (firstDay && firstDay.meals) || [];
                              const warnings: string[] = [];
                              const seenTimes = new Map<string, number>();
                              const seenMealTypes = new Map<string, number>();
                              const timeHour = (t: string) => {
                                const parts = (t || '').trim().split(/[.:]/);
                                const h = parseInt(parts[0], 10);
                                const m = parts[1] ? parseInt(parts[1], 10) : 0;
                                return Number.isNaN(h) ? 0 : h + m / 60;
                              };
                              // Reasonable hour ranges for common meal types (24h): breakfast 4-11, lunch 10-15, snack 12-20, dinner 16-22, post-dinner 18-23
                              const mealTypeTimeRanges: Record<string, [number, number]> = {
                                breakfast: [4, 11],
                                lunch: [10, 15],
                                snack: [12, 20],
                                'evening snack': [14, 20],
                                dinner: [18, 23],
                                'post dinner': [18, 23],
                              };
                              for (let slotIdx = 0; slotIdx < meals.length; slotIdx++) {
                                const m = meals[slotIdx];
                                if (!m) continue;
                                const mealType = (m.meal_type || m.meal_key || '').trim().toLowerCase();
                                let timeStr = (m.meal_time || m.time || '08:00').trim();
                                if (typeof timeStr === 'string' && timeStr.length >= 5) timeStr = timeStr.slice(0, 5).replace('.', ':');
                                const hour = timeHour(timeStr);
                                if (timeStr) {
                                  const prevSlot = seenTimes.get(timeStr);
                                  if (prevSlot !== undefined) warnings.push(`Slot ${slotIdx + 1} and Slot ${prevSlot + 1} have the same time (${timeStr}).`);
                                  else seenTimes.set(timeStr, slotIdx);
                                }
                                if (mealType) {
                                  const prevSlot = seenMealTypes.get(mealType);
                                  if (prevSlot !== undefined) warnings.push(`Slot ${slotIdx + 1} and Slot ${prevSlot + 1} have the same meal type ("${mealType}").`);
                                  else seenMealTypes.set(mealType, slotIdx);
                                }
                                if (mealType && mealType in mealTypeTimeRanges) {
                                  const [minH, maxH] = mealTypeTimeRanges[mealType];
                                  if (hour < minH || hour > maxH) {
                                    const label = mealType.charAt(0).toUpperCase() + mealType.slice(1);
                                    warnings.push(`Slot ${slotIdx + 1}: "${label}" at ${timeStr} may be an unusual time (typically ${minH}:00–${maxH}:00).`);
                                  }
                                }
                              }
                              setTableVerifyValidationMessage(null);
                              setTableVerifyWarnings(warnings);
                              if (warnings.length > 0) {
                                tableVerifyPendingSubmitRef.current = {
                                  draft: tableEditDraft,
                                  planId: selectedPlanId,
                                  sid,
                                  versionId: chatStatus?.version_id || undefined,
                                };
                                setTableVerifyWarningsModalOpen(true);
                                return;
                              }
                              setSubmittingTableVerify(true);
                              try {
                                const meal_types = meals.map((m: any) => (m.meal_type || m.meal_key || '').trim()).filter(Boolean);
                                const meal_times_by_type: Record<string, string> = {};
                                meals.forEach((m: any) => {
                                  const mt = (m.meal_type || m.meal_key || '').trim();
                                  if (mt) {
                                    let t = m.meal_time || m.time || '08:00';
                                    if (typeof t === 'string' && t.length >= 5) t = t.slice(0, 5).replace('.', ':');
                                    meal_times_by_type[mt] = t;
                                  }
                                });
                                const payload = {
                                  ...tableEditDraft,
                                  ...(meal_types.length > 0 && { meal_types, meal_times_by_type }),
                                };
                                await mealPlanService.submitUploadVerify(selectedPlanId, { canonical_meal_plan: payload }, sid);
                                await fetchChatStatus(selectedPlanId, sid, chatStatus?.version_id || undefined);
                                const data = await mealPlanService.listPlansForPatient(patientId, goalId ?? undefined);
                                setPlans(data || []);
                                const p = (data || []).find((x) => x.plan_id === selectedPlanId);
                                if (p?.meal_timing_json && p.plan_id === selectedPlanId) {
                                  const planMeals = p.meals_per_day || 3;
                                  setMealSlots(buildMealSlotsFromTiming(p.meal_timing_json as Record<string, unknown>, planMeals, mealTypeValues));
                                  setMealsPerDayInput(String(planMeals));
                                }
                              } finally {
                                setSubmittingTableVerify(false);
                              }
                            }}
                            style={{
                              padding: '10px 20px',
                              backgroundColor: submittingTableVerify ? '#9ca3af' : '#2563eb',
                              color: '#fff',
                              border: 'none',
                              borderRadius: 8,
                              fontWeight: 600,
                              fontSize: '0.9rem',
                              cursor: submittingTableVerify ? 'not-allowed' : 'pointer',
                            }}
                          >
                            {submittingTableVerify ? 'Submitting...' : 'Verify & Continue'}
                          </button>
                        </div>
                      )}

                      {/* Conversation Log - only show when we have a session and either log or waiting for response */}
                      {(uploadedSessionId || chatStatus) && (
                        <div
                          ref={chatMessagesContainerRef}
                          style={{ 
                            border: '1px solid #e5e7eb', 
                            borderRadius: 10, 
                            maxHeight: 420, 
                            overflowY: 'auto', 
                            padding: 16, 
                            marginBottom: 16,
                            backgroundColor: '#fff',
                            fontFamily: "'Inter', -apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif"
                          }}
                        >
                          {chatStatus && (chatStatus.conversation_log?.length > 0 || (chatStatus.waiting_for_doctor_response && chatStatus.current_ambiguity_question)) ? (
                            <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
                              {/* When waiting for doctor but conversation_log empty (e.g. race), show current question from API */}
                              {chatStatus.waiting_for_doctor_response && chatStatus.current_ambiguity_question && chatStatus.conversation_log?.length === 0 && (
                                <div style={{ alignSelf: 'flex-start', maxWidth: '75%' }}>
                                  <div style={{ display: 'flex', alignItems: 'flex-start', gap: 8 }}>
                                    <span style={{ color: '#94a3b8', flexShrink: 0, marginTop: 2 }}><Sparkles size={16} /></span>
                                    <div>
                                      <div style={{ padding: '12px 16px', borderRadius: 12, backgroundColor: '#f1f5f9', color: '#0f172a', fontSize: 14, lineHeight: 1.5, whiteSpace: 'pre-wrap', wordBreak: 'break-word', fontFamily: 'inherit' }}>
                                        {chatStatus.current_ambiguity_question.question || 'Please provide your response.'}
                                      </div>
                                      {(chatStatus.current_ambiguity_question.suggestions?.length || chatStatus.current_ambiguity_question.options?.length) ? (
                                        <div style={{ marginTop: 10, display: 'flex', flexWrap: 'wrap', gap: 8 }}>
                                          {(chatStatus.current_ambiguity_question.suggestions || chatStatus.current_ambiguity_question.options || []).map((opt: string, optIdx: number) => {
                                            const sessionId = uploadedSessionId || chatStatus?.session_id;
                                            return (
                                              <button key={optIdx} type="button" onClick={async () => { if (selectedPlanId) await sendChatMessage(selectedPlanId, opt, sessionId, fixedManualSelectedVersionId || chatStatus?.version_id || undefined); }}
                                                style={{ padding: '6px 12px', backgroundColor: '#2563eb', color: '#fff', border: 'none', borderRadius: 8, fontSize: 13, cursor: sendingChatMessage ? 'not-allowed' : 'pointer', opacity: sendingChatMessage ? 0.6 : 1 }}
                                                disabled={sendingChatMessage || !selectedPlanId}
                                              >{opt}</button>
                                            );
                                          })}
                                        </div>
                                      ) : null}
                                      <div style={{ fontSize: '0.7rem', color: '#94a3b8', marginTop: 6, fontWeight: 600, letterSpacing: '0.02em', fontFamily: 'inherit' }}>AI ASSISTANT</div>
                                    </div>
                                  </div>
                                </div>
                              )}
                              {chatStatus.conversation_log?.map((msg, idx) => {
                                const isDoctor = msg.role === 'user' || msg.role === 'doctor';
                                
                                // Parse options from message content (normalize step uses "**Options:**", ambiguity uses "**Recommended options:**")
                                const optionsMatch = msg.content.match(/\*\*(?:Recommended )?[Oo]ptions:\*\*\s*([^\n]+)/);
                                const optionsText = optionsMatch ? optionsMatch[1].trim() : null;
                                const options: string[] = optionsText ? optionsText.split(',').map((opt: string) => opt.trim()).filter((val: string) => Boolean(val)) : [];
                                
                                // Extract question text without options
                                const questionText = optionsMatch 
                                  ? msg.content.substring(0, optionsMatch.index).trim()
                                  : msg.content;
                                
                                // Create a unique key for this message (use idx as fallback)
                                const messageKey = msg.created_at ? `${msg.created_at}-${idx}` : `msg-${idx}`;
                                
                                return (
                                  <div
                                    key={idx}
                                    style={{
                                      alignSelf: isDoctor ? 'flex-end' : 'flex-start',
                                      maxWidth: '70%',
                                    }}
                                  >
                                    <div style={{ display: 'flex', alignItems: 'flex-start', gap: 8, flexDirection: isDoctor ? 'row-reverse' : 'row' }}>
                                      {!isDoctor && <span style={{ color: '#94a3b8', flexShrink: 0, marginTop: 2 }}><Sparkles size={16} /></span>}
                                      <div>
                                        <div
                                          style={{
                                            padding: '12px 16px',
                                            borderRadius: 12,
                                            backgroundColor: isDoctor ? '#2563eb' : '#f1f5f9',
                                            color: isDoctor ? '#ffffff' : '#0f172a',
                                            fontSize: '14px',
                                            lineHeight: 1.5,
                                            whiteSpace: 'pre-wrap',
                                            wordBreak: 'break-word',
                                            fontFamily: 'inherit',
                                          }}
                                        >
                                          {questionText}
                                        </div>
                                        <div style={{ fontSize: '0.7rem', color: '#94a3b8', marginTop: 6, fontWeight: 600, letterSpacing: '0.02em', textAlign: isDoctor ? 'right' : 'left', fontFamily: 'inherit' }}>
                                          {isDoctor ? 'YOU' : 'AI ASSISTANT'} {msg.created_at ? ` · ${new Date(msg.created_at).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}` : ''}
                                        </div>
                                      </div>
                                    </div>
                                    {/* Display options as clickable buttons */}
                                      {!isDoctor && options.length > 0 && (
                                        <div style={{ marginTop: '12px', display: 'flex', flexWrap: 'wrap', gap: '8px' }}>
                                          {options.map((option: string, optIdx: number) => {
                                            const isSelected = selectedOptions[messageKey] === option;
                                            const sessionId = uploadedSessionId || chatStatus?.session_id;
                                            return (
                                              <button
                                                key={optIdx}
                                                type="button"
                                                onClick={async () => {
                                                  // Mark as selected immediately for visual feedback
                                                  setSelectedOptions(prev => ({ ...prev, [messageKey]: option }));
                                                  // Set input value and send automatically
                                                  if (selectedPlanId) {
                                                    await sendChatMessage(
                                                      selectedPlanId,
                                                      option,
                                                      sessionId,
                                                      fixedManualSelectedVersionId || chatStatus?.version_id || undefined
                                                    );
                                                    // Scroll is handled by useEffect when conversation_log updates (avoids scroll-up-then-down jump)
                                                  }
                                                }}
                                                style={{
                                                  padding: '6px 12px',
                                                  backgroundColor: isSelected ? '#2563eb' : '#ffffff',
                                                  color: isSelected ? '#ffffff' : '#374151',
                                                  border: `1px solid ${isSelected ? '#2563eb' : '#e2e8f0'}`,
                                                  borderRadius: 8,
                                                  fontSize: '13px',
                                                  cursor: sendingChatMessage ? 'not-allowed' : 'pointer',
                                                  transition: 'all 0.2s ease',
                                                  opacity: sendingChatMessage ? 0.6 : 1,
                                                }}
                                                disabled={sendingChatMessage || !selectedPlanId}
                                              >
                                                {option}
                                              </button>
                                            );
                                          })}
                                        </div>
                                      )}
                                  </div>
                                );
                              })}
                              <div ref={chatBottomRef} />
                            </div>
                          ) : (
                            <div style={{ color: '#9ca3af', fontSize: '14px', textAlign: 'center', padding: 20, fontFamily: 'inherit' }}>
                              {chatStatus?.waiting_for_doctor_response ? (
                                <div>
                                  <div style={{ marginBottom: 8 }}>Loading question...</div>
                                  <div style={{ fontSize: '0.85rem' }}>The system is waiting for your response. If the question does not appear shortly, try refreshing or type your answer in the box below and click Send.</div>
                                </div>
                              ) : chatStatus && chatStatus.status === 'processing' ? (
                                <div>
                                  <div style={{ marginBottom: 8 }}>Processing your meal plan document...</div>
                                  <div style={{ fontSize: '0.85rem' }}>The system is extracting and analyzing your document. You'll be able to chat once processing completes.</div>
                                </div>
                              ) : (
                                'No messages yet. Start a conversation to request modifications.'
                              )}
                            </div>
                          )}
                        </div>
                      )}
                      
                      {/* Chat Input - allow first message to bootstrap a session */}
                      {(selectedPlanId || chatStatus?.plan_id) && (
                        <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                          <input
                            type="text"
                            value={chatInput}
                            onChange={(e) => setChatInput(e.target.value)}
                            onKeyDown={(e) => {
                              if (e.key === 'Enter' && !e.shiftKey && chatInput.trim() && !sendingChatMessage) {
                                e.preventDefault();
                                const sessionId = uploadedSessionId || chatStatus?.session_id;
                                const planId = selectedPlanId || chatStatus?.plan_id;
                                if (planId) {
                                  void sendChatMessage(
                                    planId,
                                    chatInput.trim(),
                                    sessionId,
                                    fixedManualSelectedVersionId || chatStatus?.version_id || undefined
                                  );
                                }
                              }
                            }}
                            placeholder="Type a modification request..."
                            style={{
                              flex: 1,
                              padding: '12px 16px',
                              border: '1px solid #e2e8f0',
                              borderRadius: 10,
                              fontSize: 14,
                              backgroundColor: '#f8fafc',
                              fontFamily: "'Inter', -apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif",
                            }}
                            disabled={sendingChatMessage || !(selectedPlanId || chatStatus?.plan_id)}
                          />
                          <button
                            type="button"
                            onClick={() => { const sessionId = uploadedSessionId || chatStatus?.session_id; const planId = selectedPlanId || chatStatus?.plan_id; const versionId = fixedManualSelectedVersionId || chatStatus?.version_id || undefined; if (planId && chatInput.trim()) void sendChatMessage(planId, chatInput.trim(), sessionId, versionId); }}
                            disabled={sendingChatMessage || !chatInput.trim() || !(selectedPlanId || chatStatus?.plan_id)}
                            style={{ width: 44, height: 44, borderRadius: '50%', border: 'none', background: '#2563eb', color: '#fff', cursor: sendingChatMessage || !chatInput.trim() ? 'not-allowed' : 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0, opacity: sendingChatMessage || !chatInput.trim() ? 0.7 : 1 }}
                          >
                            <Send size={18} />
                          </button>
                        </div>
                      )}

                    </div>
                  )}

                  {/* Selected Version: Day Meals (Daily Meal Plan tab) — only in version_detail; in upload view we show plan_preview only to avoid showing another version's details. Hidden in showOnlyChat. */}
                  {!showOnlyChat && fixedManualSubView === 'meal_plan' &&
                    existingPlanDetailTab === 'daily_meal_plan' &&
                    !chatStatus?.waiting_for_doctor_response &&
                    fixedManualSelectedVersion &&
                    Array.isArray(fixedManualSelectedVersion.days) &&
                    fixedManualSelectedVersion.days.length > 0 && (
                      <div className="np-subcard" style={{ marginTop: 24, padding: 16 }}>
                        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 12 }}>
                          <h3 style={{ margin: 0, fontSize: '1.1rem', fontWeight: 600 }}>Weekly Schedule</h3>
                          {weeklyScheduleInnerTab === 'schedule' && (() => {
                            const totalDays = fixedManualSelectedVersion.days?.length || 0;
                            const DAYS_PER_WEEK = 7;
                            const totalPages = Math.max(1, Math.ceil(totalDays / DAYS_PER_WEEK));
                            const currentPage = weeklyScheduleWeekOffset + 1;
                            const maxOffset = Math.max(0, totalPages - 1);
                            return (
                              <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                                <button
                                  type="button"
                                  disabled={weeklyScheduleWeekOffset <= 0}
                                  onClick={() => setWeeklyScheduleWeekOffset((o) => Math.max(0, o - 1))}
                                  style={{ padding: 6, border: '1px solid #e5e7eb', borderRadius: 6, background: '#fff', cursor: weeklyScheduleWeekOffset <= 0 ? 'not-allowed' : 'pointer', opacity: weeklyScheduleWeekOffset <= 0 ? 0.5 : 1 }}
                                >
                                  <ChevronLeft size={18} />
                                </button>
                                <span style={{ fontSize: '0.9rem', fontWeight: 500, color: '#374151', minWidth: 72, textAlign: 'center' }}>
                                  Page {currentPage} of {totalPages}
                                </span>
                                <button
                                  type="button"
                                  disabled={weeklyScheduleWeekOffset >= maxOffset}
                                  onClick={() => setWeeklyScheduleWeekOffset((o) => Math.min(maxOffset, o + 1))}
                                  style={{ padding: 6, border: '1px solid #e5e7eb', borderRadius: 6, background: '#fff', cursor: weeklyScheduleWeekOffset >= maxOffset ? 'not-allowed' : 'pointer', opacity: weeklyScheduleWeekOffset >= maxOffset ? 0.5 : 1 }}
                                >
                                  <ChevronRight size={18} />
                                </button>
                              </div>
                            );
                          })()}
                        </div>
                        <div style={{ display: 'flex', gap: 4, marginBottom: 16, borderBottom: '1px solid #e5e7eb' }}>
                          <button type="button" onClick={() => setWeeklyScheduleInnerTab('schedule')} style={{ padding: '8px 14px', border: 'none', background: 'none', fontSize: '0.9rem', fontWeight: 500, color: weeklyScheduleInnerTab === 'schedule' ? '#2563eb' : '#6b7280', borderBottom: weeklyScheduleInnerTab === 'schedule' ? '2px solid #2563eb' : '2px solid transparent', marginBottom: -1, cursor: 'pointer' }}>Schedule</button>
                          <button type="button" onClick={() => setWeeklyScheduleInnerTab('grocery_list')} style={{ padding: '8px 14px', border: 'none', background: 'none', fontSize: '0.9rem', fontWeight: 500, color: weeklyScheduleInnerTab === 'grocery_list' ? '#2563eb' : '#6b7280', borderBottom: weeklyScheduleInnerTab === 'grocery_list' ? '2px solid #2563eb' : '2px solid transparent', marginBottom: -1, cursor: 'pointer' }}>Grocery List</button>
                          <button type="button" onClick={() => setWeeklyScheduleInnerTab('exclusions')} style={{ padding: '8px 14px', border: 'none', background: 'none', fontSize: '0.9rem', fontWeight: 500, color: weeklyScheduleInnerTab === 'exclusions' ? '#2563eb' : '#6b7280', borderBottom: weeklyScheduleInnerTab === 'exclusions' ? '2px solid #2563eb' : '2px solid transparent', marginBottom: -1, cursor: 'pointer' }}>Exclusions</button>
                        </div>
                        {weeklyScheduleInnerTab === 'schedule' && (() => {
                          const days = fixedManualSelectedVersion.days || [];
                          const DAYS_PER_WEEK = 7;
                          const start = weeklyScheduleWeekOffset * DAYS_PER_WEEK;
                          const weekDays = days.slice(start, start + DAYS_PER_WEEK);
                          const selectedDayIdx = weeklyScheduleExpandedDay != null && weeklyScheduleExpandedDay >= start && weeklyScheduleExpandedDay < start + weekDays.length
                            ? weeklyScheduleExpandedDay
                            : start;
                          const selectedDay = weekDays[selectedDayIdx - start];
                          const weekStartDate = weekDays.length > 0 && weekDays[0]?.date ? new Date((weekDays[0] as any).date) : null;
                          const weekEndDate = weekDays.length > 0 && weekDays[weekDays.length - 1]?.date ? new Date((weekDays[weekDays.length - 1] as any).date) : null;
                          const dateRangeLabel = weekStartDate && weekEndDate
                            ? `${weekStartDate.toLocaleDateString('en-US', { month: 'short', day: 'numeric' })} - ${weekEndDate.toLocaleDateString('en-US', { month: 'short', day: 'numeric' })}`
                            : '—';
                          const dayCal = selectedDay ? (selectedDay.items || []).reduce((sum: number, it: any) => sum + (Number(it.dish_nutrients?.values?.calories) || 0), 0) : 0;
                          return (
                            <>
                              <div style={{ marginBottom: 16 }}>
                                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 12 }}>
                                  <h4 style={{ margin: 0, fontSize: '1rem', fontWeight: 700, color: '#111827' }}>Current Week</h4>
                                  <span style={{ fontSize: '0.9rem', color: '#6b7280' }}>{dateRangeLabel}</span>
                                </div>
                                <div style={{ display: 'flex', gap: 8, width: '100%', flexWrap: 'wrap' }}>
                                  {weekDays.map((day, idx) => {
                                    const dayIdx = start + idx;
                                    const isSelected = selectedDayIdx === dayIdx;
                                    const d = day.date ? new Date(day.date as string) : new Date();
                                    const dayLabel = d.toLocaleDateString('en-US', { weekday: 'short' }) + ' ' + d.getDate();
                                    return (
                                      <button
                                        key={dayIdx}
                                        type="button"
                                        onClick={() => setWeeklyScheduleExpandedDay(dayIdx)}
                                        style={{
                                          flex: '1 1 0',
                                          minWidth: 0,
                                          padding: '10px 8px',
                                          borderRadius: 8,
                                          border: '1px solid #e5e7eb',
                                          background: isSelected ? '#1e40af' : '#f9fafb',
                                          color: isSelected ? '#fff' : '#374151',
                                          cursor: 'pointer',
                                          textAlign: 'center',
                                          fontSize: '0.9rem',
                                          fontWeight: 600,
                                          boxShadow: isSelected ? '0 1px 3px rgba(0,0,0,0.15)' : 'none',
                                        }}
                                      >
                                        {dayLabel}
                                      </button>
                                    );
                                  })}
                                </div>
                              </div>
                              {selectedDay && (
                                <div style={{ padding: '16px', marginBottom: 16 }}>
                                  <div style={{ marginBottom: 16, fontSize: '0.85rem', color: '#6b7280' }}>
                                    Target achieved: {dayCal > 0 ? `${Math.round(dayCal).toLocaleString()} kcal` : '— kcal'}
                                  </div>
                                  {(() => {
                                    const formatTime = (t: string) => {
                                      const [h, m] = (t || '00:00').split(':').map(Number);
                                      const h12 = h % 12 || 12;
                                      const ampm = h < 12 ? 'AM' : 'PM';
                                      return `${String(h12).padStart(2, '0')}:${String(m || 0).padStart(2, '0')} ${ampm}`;
                                    };
                                    const mealTimeByKey: Record<string, string> = {
                                      ...Object.fromEntries(MEAL_TYPE_OPTIONS.map((k) => [k, MEAL_TYPE_DEFAULT_TIMES[k] || '08:00'])),
                                      breakfast: defaultMealTiming.breakfast,
                                      lunch: defaultMealTiming.lunch,
                                      dinner: defaultMealTiming.dinner,
                                      snack: defaultMealTiming.snacks,
                                    };
                                    const mealTypeMetaByKey: Record<string, { label: string; icon: React.ReactNode; color: string }> = {
                                      breakfast: { label: 'Breakfast', icon: <Coffee size={22} />, color: '#c2410c' },
                                      lunch: { label: 'Lunch', icon: <Sun size={22} />, color: '#ca8a04' },
                                      dinner: { label: 'Dinner', icon: <Moon size={22} />, color: '#1e40af' },
                                      snack: { label: 'Snacks', icon: <Cookie size={22} />, color: '#16a34a' },
                                    };
                                    const mealOrder: Record<string, number> = { breakfast: 0, lunch: 1, dinner: 2, snack: 3 };
                                    const getMeta = (mealKey: string) => {
                                      const known = mealTypeMetaByKey[mealKey] || mealTypeMetaByKey[(mealKey || '').toLowerCase().replace(/\s+/g, '_')];
                                      if (known) return known;
                                      return { label: formatMealKeyForDisplay(mealKey || 'snack'), icon: mealTypeMetaByKey.snack.icon, color: mealTypeMetaByKey.snack.color };
                                    };
                                    const timeToMinutes = (t: string | null | undefined): number | null => {
                                      if (!t || typeof t !== 'string') return null;
                                      const parts = t.trim().split(':').map(Number);
                                      const h = parts[0] ?? 0, m = parts[1] ?? 0;
                                      if (!Number.isFinite(h)) return null;
                                      return h * 60 + m;
                                    };
                                    const getTimeForItem = (item: any) => item?.meal_time || mealTimeByKey[item?.meal_key] || mealTimeByKey[(item?.meal_key || '').toLowerCase().replace(/\s+/g, '_')];
                                    const dayItems = ((selectedDay as any)?.items || []).slice().sort((a: any, b: any) => {
                                      const ma = timeToMinutes(getTimeForItem(a)), mb = timeToMinutes(getTimeForItem(b));
                                      if (ma != null && mb != null) return ma - mb;
                                      if (ma != null) return -1;
                                      if (mb != null) return 1;
                                      return (mealOrder[a?.meal_key] ?? 4) - (mealOrder[b?.meal_key] ?? 4);
                                    });
                                    const macroColors = { protein_g: '#8b5cf6', carbs_g: '#7dd3fc', fat_g: '#1e40af', fiber_g: '#9ca3af' };
                                    if (dayItems.length === 0) {
                                      return <div style={{ padding: 16, color: '#9ca3af', fontSize: '0.9rem' }}>No meals for this day.</div>;
                                    }
                                    const mealKeyNorm = (item: any) => (item?.meal_key ?? 'snack').toString().toLowerCase().replace(/\s+/g, '_');
                                    const groupedByMeal = new Map<string, any[]>();
                                    for (const item of dayItems) {
                                      const k = mealKeyNorm(item);
                                      if (!groupedByMeal.has(k)) groupedByMeal.set(k, []);
                                      groupedByMeal.get(k)!.push(item);
                                    }
                                    const mealOrderFallback: Record<string, number> = { breakfast: 0, lunch: 1, dinner: 2, snack: 3 };
                                    const allGroupKeys = Array.from(groupedByMeal.keys());
                                    const orderedGroups = allGroupKeys.sort((a, b) => {
                                      const itemsA = groupedByMeal.get(a)!;
                                      const itemsB = groupedByMeal.get(b)!;
                                      const timeA = timeToMinutes(getTimeForItem(itemsA[0]));
                                      const timeB = timeToMinutes(getTimeForItem(itemsB[0]));
                                      if (timeA != null && timeB != null) return timeA - timeB;
                                      if (timeA != null) return -1;
                                      if (timeB != null) return 1;
                                      return (mealOrderFallback[a] ?? 99) - (mealOrderFallback[b] ?? 99);
                                    });
                                    return orderedGroups.map((mealKey) => {
                                      const items = groupedByMeal.get(mealKey)!;
                                      const first = items[0];
                                      const mt = first?.meal_key ?? mealKey;
                                      const meta = getMeta(mt);
                                      const rawTime = first?.meal_time || mealTimeByKey[mealKey] || mealTimeByKey[mt];
                                      const timeStr = rawTime ? formatTime(rawTime) : '—';
                                      const groupKcal = items.reduce((sum, m) => sum + (m?.dish_nutrients?.values?.calories != null ? Number(m.dish_nutrients.values.calories) : 0), 0);
                                      const groupKcalRounded = Math.round(groupKcal);
                                      const groupTabKey = `${selectedDayIdx}-${mealKey}`;
                                      const detailTab = weeklyScheduleDetailTab[groupTabKey] ?? 'ingredients';
                                      const setDetailTab = (tab: 'ingredients' | 'preparations' | 'nutrients') => setWeeklyScheduleDetailTab((prev) => ({ ...prev, [groupTabKey]: tab }));
                                      const summedMacros = items.reduce((acc, m) => {
                                        const v = m?.dish_nutrients?.values || {};
                                        acc.protein_g += Number(v.protein_g) || 0;
                                        acc.carbs_g += Number(v.carbs_g) || 0;
                                        acc.fat_g += Number(v.fat_g) || 0;
                                        acc.fiber_g += Number(v.fiber_g) || 0;
                                        return acc;
                                      }, { protein_g: 0, carbs_g: 0, fat_g: 0, fiber_g: 0 });
                                      const macroTotal = summedMacros.protein_g + summedMacros.carbs_g + summedMacros.fat_g + summedMacros.fiber_g;
                                      const segments = macroTotal > 0 ? [
                                        { key: 'protein_g', value: summedMacros.protein_g, color: macroColors.protein_g, label: 'Protein' },
                                        { key: 'carbs_g', value: summedMacros.carbs_g, color: macroColors.carbs_g, label: 'Carbs' },
                                        { key: 'fat_g', value: summedMacros.fat_g, color: macroColors.fat_g, label: 'Fats' },
                                        { key: 'fiber_g', value: summedMacros.fiber_g, color: macroColors.fiber_g, label: 'Fiber' },
                                      ].filter((s) => s.value > 0) : [];
                                      const chartSize = 200;
                                      return (
                                        <div key={mealKey} style={{ marginBottom: 28, border: '1px solid #e5e7eb', borderRadius: 10, overflow: 'hidden', background: '#fff', boxShadow: '0 1px 3px rgba(0,0,0,0.06)' }}>
                                          <div style={{ padding: '16px 16px 12px', borderBottom: `2px solid ${meta.color}`, backgroundColor: '#fafafa' }}>
                                            <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                                              <span style={{ color: meta.color, flexShrink: 0 }}>{meta.icon}</span>
                                              <span style={{ fontSize: '1.1rem', fontWeight: 700, color: '#111827' }}>{meta.label}</span>
                                              <span style={{ fontSize: '0.9rem', color: '#6b7280', fontWeight: 500 }}>• {timeStr}{groupKcalRounded > 0 ? ` • ${groupKcalRounded} kcal total` : ''}</span>
                                            </div>
                                          </div>
                                          <div style={{ padding: '12px 16px 8px', borderBottom: '1px solid #e5e7eb', display: 'flex', gap: 0 }}>
                                            <button type="button" onClick={() => setDetailTab('ingredients')} style={{ padding: '8px 14px', border: 'none', background: 'none', fontSize: '0.85rem', fontWeight: 500, color: detailTab === 'ingredients' ? '#2563eb' : '#6b7280', borderBottom: detailTab === 'ingredients' ? '2px solid #2563eb' : '2px solid transparent', marginBottom: -1, cursor: 'pointer' }}>
                                              Ingredients & Nutrients
                                            </button>
                                            <button type="button" onClick={() => setDetailTab('preparations')} style={{ padding: '8px 14px', border: 'none', background: 'none', fontSize: '0.85rem', fontWeight: 500, color: detailTab === 'preparations' ? '#2563eb' : '#6b7280', borderBottom: detailTab === 'preparations' ? '2px solid #2563eb' : '2px solid transparent', marginBottom: -1, cursor: 'pointer' }}>
                                              Preparation
                                            </button>
                                          </div>
                                          <div style={{ padding: 16 }}>
                                            {detailTab === 'ingredients' && (
                                              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(220px, 1fr))', gap: 24, alignItems: 'start' }}>
                                                <div>
                                                  <div style={{ fontSize: '0.7rem', fontWeight: 700, color: '#6b7280', letterSpacing: '0.05em', marginBottom: 10 }}>INGREDIENTS</div>
                                                  <div style={{ fontSize: '0.9rem', lineHeight: 1.8, color: '#374151' }}>
                                                    {items.flatMap((meal: any) => (meal?.ingredients || []).map((ing: any, i: number) => typeof ing === 'string' ? ing : (ing.name || '') + (ing.portion_display ? ` ${ing.portion_display}` : '') + (ing.exception ? ` (${ing.exception})` : ''))).map((line: string, idx: number) => (
                                                      <div key={idx}>{line}</div>
                                                    ))}
                                                    {items.some((m: any) => m?.exception) && items.filter((m: any) => m?.exception).map((meal: any, i: number) => (
                                                      <div key={`ex-${i}`} style={{ color: '#b45309', marginTop: 4 }}>{meal.dish_name || 'Dish'}: {meal.exception}</div>
                                                    ))}
                                                    {items.every((m: any) => !(m?.ingredients?.length)) && !items.some((m: any) => m?.exception) && <div style={{ color: '#9ca3af' }}>No ingredients</div>}
                                                  </div>
                                                </div>
                                                <div>
                                                  <div style={{ fontSize: '0.7rem', fontWeight: 700, color: '#6b7280', letterSpacing: '0.05em', marginBottom: 10 }}>MACRONUTRIENTS DISTRIBUTION</div>
                                                  <div style={{ display: 'flex', alignItems: 'center', gap: 20, flexWrap: 'wrap' }}>
                                                    <div style={{ position: 'relative', width: chartSize, height: chartSize, flexShrink: 0 }}>
                                                      {segments.length > 0 ? (
                                                        <>
                                                          <svg width={chartSize} height={chartSize} viewBox="0 0 120 120" style={{ transform: 'rotate(-90deg)', display: 'block' }}>
                                                            {segments.reduce((acc, s) => {
                                                              const pct = s.value / macroTotal;
                                                              const start = acc.offset;
                                                              const end = start + pct;
                                                              const x1 = 60 + 48 * Math.cos(start * 2 * Math.PI);
                                                              const y1 = 60 + 48 * Math.sin(start * 2 * Math.PI);
                                                              const x2 = 60 + 48 * Math.cos(end * 2 * Math.PI);
                                                              const y2 = 60 + 48 * Math.sin(end * 2 * Math.PI);
                                                              const large = pct > 0.5 ? 1 : 0;
                                                              acc.elements.push(<path key={s.key} d={`M 60 60 L ${x1} ${y1} A 48 48 0 ${large} 1 ${x2} ${y2} Z`} fill={s.color} />);
                                                              acc.offset = end;
                                                              return acc;
                                                            }, { offset: 0, elements: [] as React.ReactNode[] }).elements}
                                                            <circle cx={60} cy={60} r={28} fill="#fff" />
                                                          </svg>
                                                          <div style={{ position: 'absolute', left: 0, right: 0, top: 0, bottom: 0, display: 'flex', alignItems: 'center', justifyContent: 'center', pointerEvents: 'none', fontWeight: 700, fontSize: '1.1rem', color: '#111827' }}>
                                                            {groupKcalRounded} KCAL
                                                          </div>
                                                        </>
                                                      ) : (
                                                        <div style={{ width: chartSize, height: chartSize, borderRadius: '50%', background: '#f3f4f6', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: '0.75rem', color: '#9ca3af' }}>No data</div>
                                                      )}
                                                    </div>
                                                    <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
                                                      {segments.length > 0 ? segments.map((s) => (
                                                        <div key={s.key} style={{ display: 'flex', alignItems: 'center', gap: 8, fontSize: '0.9rem' }}>
                                                          <span style={{ width: 10, height: 10, borderRadius: '50%', backgroundColor: s.color, flexShrink: 0 }} />
                                                          <span style={{ color: '#374151' }}>{s.label}: {Number(summedMacros[s.key as keyof typeof summedMacros])?.toFixed(0) ?? 0}g</span>
                                                        </div>
                                                      )) : <div style={{ fontSize: '0.85rem', color: '#9ca3af' }}>No macro data</div>}
                                                    </div>
                                                  </div>
                                                </div>
                                              </div>
                                            )}
                                            {detailTab === 'preparations' && (
                                              <div>
                                                <div style={{ fontSize: '0.7rem', fontWeight: 700, color: '#6b7280', letterSpacing: '0.05em', marginBottom: 10 }}>STEP-BY-STEP INSTRUCTIONS</div>
                                                {items.map((meal: any, mealIdx: number) => {
                                                  const steps = meal?.recipe_steps || meal?.premium_recipe?.steps || [];
                                                  return (
                                                    <div key={mealIdx} style={{ marginBottom: mealIdx < items.length - 1 ? 16 : 0 }}>
                                                      <div style={{ fontWeight: 600, color: '#111827', marginBottom: 6, fontSize: '0.95rem' }}>{meal?.dish_name || 'Dish'}</div>
                                                      <ol style={{ margin: 0, paddingLeft: 20, fontSize: '0.9rem', lineHeight: 1.7, color: '#374151' }}>
                                                        {steps.length > 0 ? steps.map((step: string, i: number) => <li key={i} style={{ marginBottom: 8 }}>{step}</li>) : <li style={{ color: '#9ca3af' }}>No preparation steps</li>}
                                                      </ol>
                                                    </div>
                                                  );
                                                })}
                                              </div>
                                            )}
                                          </div>
                                        </div>
                                      );
                                    });
                                  })()}
                                </div>
                              )}
                              </>
                          );
                        })()}
                        {weeklyScheduleInnerTab === 'grocery_list' && (() => {
                          const gl = (fixedManualSelectedVersion as any).grocery_list || [];
                          if (!Array.isArray(gl) || gl.length === 0) return <div style={{ padding: 24, color: '#6b7280', fontSize: '0.95rem' }}>No grocery list for this version.</div>;
                          return (
                            <div style={{ padding: '16px 0', overflowX: 'auto' }}>
                              <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '0.9rem' }}>
                                <thead>
                                  <tr style={{ borderBottom: '2px solid #e5e7eb', backgroundColor: '#f9fafb' }}>
                                    <th style={{ padding: '8px 12px', textAlign: 'left', fontWeight: 600 }}>Ingredient</th>
                                    <th style={{ padding: '8px 12px', textAlign: 'right', fontWeight: 600 }}>Amount</th>
                                    <th style={{ padding: '8px 12px', textAlign: 'left', fontWeight: 600 }}>Category</th>
                                    <th style={{ padding: '8px 12px', textAlign: 'left', fontWeight: 600 }}>Notes</th>
                                  </tr>
                                </thead>
                                <tbody>
                                  {gl.map((gi: any, gIdx: number) => {
                                    const ingredientName = typeof gi === 'string' ? gi : gi.ingredient_name || gi.name || gi.item || 'Unknown';
                                    const amount = typeof gi === 'object' ? (gi.total_amount || gi.quantity || gi.amount || '') : '';
                                    const unit = typeof gi === 'object' ? (gi.unit || gi.portion_display || '') : '';
                                    const category = typeof gi === 'object' ? (gi.category || gi.food_group || '') : '';
                                    const notes = typeof gi === 'object' ? (gi.notes || gi.source || '') : '';
                                    const displayAmount = amount && unit ? `${amount} ${unit}` : amount || unit || '-';
                                    return (
                                      <tr key={gIdx} style={{ borderBottom: '1px solid #f3f4f6' }}>
                                        <td style={{ padding: '8px 12px', fontWeight: 500 }}>{ingredientName}</td>
                                        <td style={{ padding: '8px 12px', textAlign: 'right' }}>{displayAmount}</td>
                                        <td style={{ padding: '8px 12px', color: '#6b7280' }}>{category || '-'}</td>
                                        <td style={{ padding: '8px 12px', color: '#6b7280', fontSize: '0.85em' }}>{notes || '-'}</td>
                                      </tr>
                                    );
                                  })}
                                </tbody>
                              </table>
                            </div>
                          );
                        })()}
                        {weeklyScheduleInnerTab === 'exclusions' && (() => {
                          const exclusions = (fixedManualSelectedVersion as any).overall_exceptions ?? (fixedManualSelectedVersion as any).diff_summary_json?.overall_exceptions ?? [];
                          const list = Array.isArray(exclusions) ? exclusions : [];
                          if (list.length === 0) return <div style={{ padding: 24, color: '#6b7280', fontSize: '0.95rem' }}>No exclusions (to avoid) for this version.</div>;
                          return (
                            <div style={{ padding: '16px 0' }}>
                              <p style={{ margin: '0 0 12px', fontSize: '0.85rem', color: '#6b7280' }}>Items to avoid plan-wide:</p>
                              <ul style={{ margin: 0, paddingLeft: 20, fontSize: '0.95rem', lineHeight: 1.8 }}>{list.map((item: string, i: number) => <li key={i}>{typeof item === 'string' ? item : String(item)}</li>)}</ul>
                            </div>
                          );
                        })()}
                      </div>
                    )}

                    </>
                  )}

                  {/* Versions view: hint only; do not use chat status (versions view uses version API only). */}
                  {fixedManualSubView === 'versions' && (
                    <div style={{ marginTop: 12, color: '#6b7280', fontSize: '0.9rem' }}>
                      Select a version to review or create a new version to upload a document.
                    </div>
                  )}
                </>
              ) : null}

              <div className="np-actions" style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: 12 }}>
                <button
                  type="button"
                  className="secondary-button"
                  onClick={() => {
                    if (fixedManualSubView === 'upload') {
                      const planId = selectedPlanId;
                      const versionId = fixedManualSelectedVersionId;
                      setFixedManualSubView('versions');
                      // Refetch versions so the table shows the newly created version (merge if API list doesn't include it yet)
                      if (planId && versionId) {
                        void loadFixedManualVersionsAndSelect(planId, versionId);
                      }
                    } else if (fixedManualSubView === 'meal_plan') {
                      setFixedManualSubView('versions');
                    } else if (fixedManualSubView === 'versions') {
                      setFixedManualSubView('versions');
                    } else {
                      handleBack();
                    }
                  }}
                  disabled={loading}
                >
                  Back
                </button>
                {!showOnlyChat && isFixedManual && fixedManualSubView === 'upload' && !uploadedSessionId && (
                  <button
                    type="button"
                    className="primary-button"
                    onClick={handleUploadDocument}
                    disabled={uploadingDocument || !selectedFile || (uploadedSessionId != null && currentProgress < 1)}
                  >
                    {uploadingDocument ? 'Uploading...' : (uploadedSessionId != null && currentProgress < 1) ? 'Processing...' : 'Generate Plan'}
                  </button>
                )}
                {(existingPlanDetailTab === 'chat' || showOnlyChat) &&
                  (uploadedSessionId || chatStatus?.session_id) &&
                  !isPlanApprovedFromTable && (
                  <button
                    type="button"
                    className="primary-button"
                    onClick={() => selectedPlanId && handleApprovePlan(selectedPlanId)}
                    disabled={loading || !selectedPlanId || isPlanApprovedFromTable || approveDisabledNotInReview}
                    style={{ display: 'inline-flex', alignItems: 'center', gap: 8 }}
                  >
                    <Check size={18} /> Approve & Send to Patient
                  </button>
                )}
              </div>
            </>
          )}
        </div>
      </div>
      )}

      <PdfViewerModal
        open={!!pdfViewerUrl}
        pdfUrl={pdfViewerUrl}
        title="Meal Plan"
        onClose={handleClosePdfViewer}
      />
      <AlertModal
        open={!!whatsappMessage}
        title="Send diet plan via WhatsApp"
        message={whatsappMessage?.text ?? ''}
        variant={whatsappMessage?.type === 'error' ? 'error' : 'success'}
        onClose={() => setWhatsappMessage(null)}
      />
      {tableVerifyWarningsModalOpen && (
        <div
          style={{
            position: 'fixed',
            inset: 0,
            zIndex: 10000,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            backgroundColor: 'rgba(0,0,0,0.4)',
          }}
          onClick={(e) => e.target === e.currentTarget && setTableVerifyWarningsModalOpen(false)}
        >
          <div
            style={{
              background: '#fff',
              borderRadius: 12,
              boxShadow: '0 8px 32px rgba(0,0,0,0.2)',
              maxWidth: 440,
              width: '90%',
              maxHeight: '80vh',
              overflow: 'auto',
              padding: 20,
            }}
            onClick={(e) => e.stopPropagation()}
          >
            <h3 style={{ margin: '0 0 12px 0', fontSize: '1.1rem', color: '#92400e' }}>Warnings</h3>
            <p style={{ margin: '0 0 12px 0', fontSize: '0.9rem', color: '#374151' }}>
              Please review the following. You can click <strong>Cancel</strong> to go back and change the table, then click Verify & Continue again. Or click <strong>OK</strong> to submit anyway.
            </p>
            <ul style={{ margin: '0 0 20px 0', paddingLeft: 20, color: '#b45309', fontSize: '0.9rem' }}>
              {tableVerifyWarnings.map((w, i) => (
                <li key={i} style={{ marginBottom: 4 }}>{w}</li>
              ))}
            </ul>
            <div style={{ display: 'flex', gap: 12, justifyContent: 'flex-end' }}>
              <button
                type="button"
                onClick={() => {
                  setTableVerifyWarningsModalOpen(false);
                  tableVerifyPendingSubmitRef.current = null;
                }}
                style={{ padding: '8px 16px', border: '1px solid #d1d5db', borderRadius: 8, background: '#fff', color: '#374151', fontWeight: 500, cursor: 'pointer' }}
              >
                Cancel
              </button>
              <button
                type="button"
                disabled={submittingTableVerify}
                onClick={async () => {
                  const pending = tableVerifyPendingSubmitRef.current;
                  setTableVerifyWarningsModalOpen(false);
                  tableVerifyPendingSubmitRef.current = null;
                  setTableVerifyWarnings([]);
                  if (!pending) return;
                  setSubmittingTableVerify(true);
                  try {
                    const { draft, planId, sid, versionId } = pending;
                    const firstDay = (draft?.daily_plans || [])[0];
                    const meals = (firstDay?.meals || []) as any[];
                    const meal_types = meals.map((m: any) => (m.meal_type || m.meal_key || '').trim()).filter(Boolean);
                    const meal_times_by_type: Record<string, string> = {};
                    meals.forEach((m: any) => {
                      const mt = (m.meal_type || m.meal_key || '').trim();
                      if (mt) {
                        let t = m.meal_time || m.time || '08:00';
                        if (typeof t === 'string' && t.length >= 5) t = t.slice(0, 5).replace('.', ':');
                        meal_times_by_type[mt] = t;
                      }
                    });
                    const payload = {
                      ...draft,
                      ...(meal_types.length > 0 && { meal_types, meal_times_by_type }),
                    };
                    await mealPlanService.submitUploadVerify(planId, { canonical_meal_plan: payload }, sid);
                    await fetchChatStatus(planId, sid, versionId);
                    const data = await mealPlanService.listPlansForPatient(patientId, goalId ?? undefined);
                    setPlans(data || []);
                    const p = (data || []).find((x) => x.plan_id === planId);
                    if (p?.meal_timing_json && p.plan_id === planId) {
                      const planMeals = p.meals_per_day || 3;
                      setMealSlots(buildMealSlotsFromTiming(p.meal_timing_json as Record<string, unknown>, planMeals, mealTypeValues));
                      setMealsPerDayInput(String(planMeals));
                    }
                  } finally {
                    setSubmittingTableVerify(false);
                  }
                }}
                style={{ padding: '8px 16px', border: 'none', borderRadius: 8, background: '#2563eb', color: '#fff', fontWeight: 600, cursor: submittingTableVerify ? 'not-allowed' : 'pointer' }}
              >
                OK
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default PatientMealPlanTab;
